import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiAlertTriangle,
  FiBell,
  FiMessageCircle,
  FiCheckCircle,
  FiCheck,
  FiMail,
  FiActivity,
  FiTrash2,
  FiBriefcase,
  FiPaperclip,
  FiFileText,
  FiTarget,
  FiBarChart2,
  FiClipboard,
  FiCalendar,
  FiEye,
  FiEyeOff,
  FiUser,
  FiSearch,
  FiFolder,
  FiRefreshCcw,
  FiSend,
  FiLogOut,
  FiPenTool,
  FiSmartphone,
  FiVideo,
  FiCamera,
  FiMusic,
  FiKey,
  FiLock,
  FiInfo,
  FiSave,
  FiX,
  FiXCircle,
  FiPlus,
  FiClock,
  FiMapPin,
  FiGlobe,
  FiMaximize,
  FiMaximize2,
  FiDownload,
  FiSettings,
  FiStar,
  FiZap,
  FiMenu,
  FiSmile,
  FiLoader,
  FiAlertCircle,
  FiGift,
  FiUsers,
  FiTrendingUp,
  FiImage,
  FiEdit2,
  FiEdit3,
  FiArrowUp,
  FiArrowDown,
  FiArrowRight,
  FiUnlock,
  FiBookOpen,
  FiLink,
  FiHeadphones,
  FiHeart,
  FiLayers,
  FiShield,
  FiAward,
  FiRepeat,
  FiSun,
  FiUpload,
  FiCreditCard,
  FiChevronDown,
  FiChevronUp,
  FiChevronsLeft,
  FiChevronsRight,
  FiExternalLink,
  FiUserPlus,
  FiUserCheck,
  FiMoreVertical
} from 'react-icons/fi';
import { FaEuroSign, FaTwitter, FaLinkedin, FaInstagram, FaFacebookF, FaBehance, FaBug } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useToast } from './ToastContext';
import './App.css';

// Dashboard widgets
import { DeadlineTimeline, ConversionRateChart, ProjectStatusDonut } from './DashboardWidgets';

// Electric Border (React Bits)
import ElectricBorder from './ElectricBorder';

// Logo SVG imports
import logoHorizontalDark from './assets/logos/vissory-logo-horizontal-dark.svg';
import logoHorizontalLight from './assets/logos/vissory-logo-horizontal-light.svg';
import isotipoOrange from './assets/logos/vissory-isotipo-orange.svg';
import isotipoWhite from './assets/logos/vissory-isotipo-white.svg';

// Onboarding components (Vix guided tour)
import VixOnboardingTour, { ONBOARDING_STORAGE_KEY } from './components/onboarding/VixOnboardingTour';
import PublicFeaturedAgencyStandalone from './components/featured-agency/PublicFeaturedAgencyStandalone';
import VixFloatingBubble, { PORTFOLIO_BUBBLE_KEY } from './components/onboarding/VixFloatingBubble';
import VixPortfolioCallout from './components/onboarding/VixPortfolioCallout';

// Services & contexts (modular)
import { API, UPLOAD_BASE, getAvatarUrl, getUploadUrl } from './services/api';
import imageCompression from 'browser-image-compression';
import { CacheProvider, useCache, useCachedFetch } from './contexts/CacheContext';
import { CHANGELOG_ENTRIES } from './data/changelog';

/** Categorías de brief/proyecto: valor guardado en API → etiqueta en UI */
const BRIEF_CATEGORY_OPTIONS = [
  { value: 'copywriting', label: 'Copywriting' },
  { value: 'design', label: 'Diseño' },
  { value: 'video', label: 'Vídeo' },
  { value: 'photography', label: 'Fotografía' },
  { value: 'strategy', label: 'Estrategia' },
  { value: 'web-positioning', label: 'Posicionamiento Web' },
  { value: 'social-media', label: 'Redes Sociales' },
  { value: 'animation', label: 'Animación' },
  { value: 'web-development', label: 'Desarrollo Web' }
];

const getBriefCategoryLabel = (slug) =>
  BRIEF_CATEGORY_OPTIONS.find((o) => o.value === slug)?.label ?? slug;

// Modular components
import ContactsView from './components/contacts/ContactsView';
import AddContactButton from './components/contacts/AddContactButton';
import BriefDetailPage from './components/briefs/BriefDetailPage';

const DEFAULT_USER_AVATAR_DATA_URI = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="64 64 512 512"><defs><linearGradient id="av-bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#ff5500"/><stop offset="100%" style="stop-color:#ff7733"/></linearGradient></defs><path d="M64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576C178.6 576 64 461.4 64 320z" fill="url(#av-bg)"/><path d="M463 448.2C440.9 409.8 399.4 384 352 384L288 384C240.6 384 199.1 409.8 177 448.2C212.2 487.4 263.2 512 320 512C376.8 512 427.8 487.3 463 448.2z" fill="rgba(255,255,255,0.9)"/><path d="M320 336C359.8 336 392 303.8 392 264C392 224.2 359.8 192 320 192C280.2 192 248 224.2 248 264C248 303.8 280.2 336 320 336z" fill="rgba(255,255,255,0.9)"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
})();

// Auth Context (still in App – used everywhere)
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

// Interceptor: ante 401 (token expirado) hace logout automático y muestra toast
function Axios401Interceptor() {
  const { logout, token } = useAuth();
  const { addToast } = useToast();
  const handledRef = useRef(false);
  useEffect(() => {
    if (!token || !logout) return;
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) {
          if (!handledRef.current) {
            handledRef.current = true;
            logout();
            addToast({ type: 'error', title: 'Sesión expirada', description: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.' });
          }
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.response.eject(id);
      handledRef.current = false;
    };
  }, [token, logout, addToast]);
  return null;
}

// Invalida la caché al hacer logout o login para no mostrar datos de otra sesión
function ClearCacheOnAuthChange() {
  const { token } = useAuth();
  const { invalidateCache } = useCache();
  const prevTokenRef = useRef(token);
  useEffect(() => {
    const hadToken = !!prevTokenRef.current;
    const hasToken = !!token;
    if (hadToken !== hasToken) {
      invalidateCache();
    }
    prevTokenRef.current = token;
  }, [token, invalidateCache]);
  return null;
}

// Error Message Component - Global component
const ErrorMessage = ({ error }) => {
  if (!error) return null;
  
  return (
    <div className="error-message">
      <span className="error-icon" aria-hidden="true">
        <FiAlertTriangle />
      </span>
      <span>{error}</span>
    </div>
  );
};

// Loading Spinner Component - Reutilizable para toda la app
const LoadingSpinner = ({ 
  size = 'medium', // 'small', 'medium', 'large'
  text = 'Cargando...', 
  fullScreen = false,
  overlay = false 
}) => {
  const sizeClasses = {
    small: 'loading-spinner-small',
    medium: 'loading-spinner-medium',
    large: 'loading-spinner-large'
  };

  const content = (
    <div className={`loading-spinner-container ${sizeClasses[size]}`}>
      <div className="loading-spinner-simple">
        <img src={isotipoOrange} alt="Cargando" className="spinner-logo-img" />
      </div>
      {text && <p className="loading-text">{text}</p>}
    </div>
  );

  if (fullScreen) {
    return <div className="loading-fullscreen">{content}</div>;
  }

  if (overlay) {
    return <div className="loading-overlay">{content}</div>;
  }

  return content;
};

// Logo Component
const Logo = ({ 
  type = 'horizontal', // 'horizontal' or 'isotipo'
  theme = 'dark', // 'dark' or 'light' 
  className = '',
  style = {},
  onClick = null,
  ...props 
}) => {
  const getSvgPath = () => {
    if (type === 'horizontal') {
      return theme === 'dark' ? logoHorizontalLight : logoHorizontalDark;
    } else {
      return theme === 'dark' ? isotipoOrange : isotipoWhite;
    }
  };

  const getAltText = () => {
    const logoType = type === 'horizontal' ? 'Logo' : 'Isotipo';
    return `${logoType} Vissory`;
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <img 
      src={getSvgPath()}
      alt={getAltText()}
      className={`vissory-logo ${className}`}
      style={style}
      role="img"
      aria-label="Logo Vissory"
      onClick={handleClick}
      {...props}
    />
  );
};

// Tooltip Component
const Tooltip = ({ 
  children, 
  message, 
  position = 'top', 
  disabled = false,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);

  if (disabled) {
    return children;
  }

  return (
    <div 
      className={`tooltip-container ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {children}
      {isVisible && (
        <div 
          className={`tooltip tooltip-${position}`}
          role="tooltip"
          aria-hidden="false"
        >
          {message}
        </div>
      )}
    </div>
  );
};

// Reglas de contraseña (mismo criterio que el backend)
const validatePasswordStrength = (password) => {
  if (!password || password.trim() === '') return '';
  if (password.length < 8) return 'Al menos 8 caracteres.';
  if (!/[a-z]/.test(password)) return 'Al menos una letra minúscula (a-z).';
  if (!/[A-Z]/.test(password)) return 'Al menos una letra mayúscula (A-Z).';
  if (!/[0-9]/.test(password)) return 'Al menos un número (0-9).';
  if (!/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/'`~]/.test(password)) return 'Al menos un carácter especial (ej. !@#$%^&*).';
  const types = [/[a-z]/.test(password), /[A-Z]/.test(password), /[0-9]/.test(password), /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/'`~]/.test(password)].filter(Boolean).length;
  if (types < 3) return 'Al menos 3 de: minúsculas, mayúsculas, números y caracteres especiales.';
  if (/(.)\1{2,}/.test(password)) return 'No más de 2 caracteres idénticos seguidos.';
  return '';
};

// Form Validation Hook - Simplified to prevent infinite loops
const useFormValidation = (initialState, validationRules) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = useCallback((name, value) => {
    const rules = validationRules[name];
    if (!rules) return '';

    if (rules.required && (!value || value.toString().trim() === '')) {
      return 'Este campo es obligatorio';
    }

    if (rules.minLength && value && value.length < rules.minLength) {
      return `Debe tener al menos ${rules.minLength} caracteres`;
    }

    if (rules.maxLength && value && value.length > rules.maxLength) {
      return `No puede exceder ${rules.maxLength} caracteres`;
    }

    if (rules.email && value && !/\S+@\S+\.\S+/.test(value)) {
      return 'Por favor ingresa un email válido';
    }

    if (rules.min && value && parseFloat(value) < rules.min) {
      return `El valor mínimo es ${rules.min}`;
    }

    if (rules.max && value && parseFloat(value) > rules.max) {
      return `El valor máximo es ${rules.max}`;
    }

    if (rules.pattern && value && !rules.pattern.test(value)) {
      return rules.message || 'Formato inválido';
    }

    if (rules.custom) {
      return rules.custom(value, values) || '';
    }

    return '';
  }, [validationRules, values]);

  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const setTouchedField = useCallback((name, value = null) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Get value to validate - either passed value or current value
    const valueToValidate = value !== null ? value : values[name];
    const error = validateField(name, valueToValidate);
    
    setErrors(prev => ({ ...prev, [name]: error }));
  }, [validateField, values]);

  const validateAll = useCallback(() => {
    const newErrors = {};
    const newTouched = {};
    let allValid = true;
    
    Object.keys(validationRules).forEach(name => {
      newTouched[name] = true;
      const error = validateField(name, values[name]);
      newErrors[name] = error;
      if (error) allValid = false;
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    return allValid;
  }, [validateField, validationRules, values]);

  const resetForm = useCallback(() => {
    setValues(initialState);
    setErrors({});
    setTouched({});
  }, [initialState]);

  return {
    values,
    errors,
    touched,
    setValue,
    setTouchedField,
    validateAll,
    resetForm,
    setValues: setValue
  };
};

// Notifications Component
const Notifications = () => {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAsRead, setMarkingAsRead] = useState(new Set()); // Track which notifications are being marked as read
  const [loadingMarkAll, setLoadingMarkAll] = useState(false);

  const showToast = (message, type = 'info') => {
    addToast({ type, title: message });
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    setMarkingAsRead(prev => new Set([...prev, notificationId]));
    
    try {
      const response = await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.already_read) {
        // Update local state only if it wasn't already read
        setNotifications(prev => prev.map(n => 
          n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
        ));
        setUnreadCount(response.data.unread_count);
        
        // Show success feedback
        showToast('Notificación marcada como leída', 'success');
      }
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showToast('Error al marcar la notificación como leída', 'error');
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const markAllAsRead = async () => {
    if (unreadCount === 0) {
      showToast('No hay notificaciones sin leer', 'info');
      return;
    }

    setLoadingMarkAll(true);
    try {
      const response = await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update all notifications to read
      setNotifications(prev => prev.map(n => ({ 
        ...n, 
        read: true, 
        read_at: new Date().toISOString() 
      })));
      setUnreadCount(0);
      
      showToast(`${response.data.notifications_updated} notificaciones marcadas como leídas`, 'success');
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showToast('Error al marcar todas las notificaciones como leídas', 'error');
    } finally {
      setLoadingMarkAll(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(response.data.unread_count);
      
      showToast('Notificación eliminada', 'success');
      
    } catch (error) {
      console.error('Error deleting notification:', error);
      showToast('Error al eliminar la notificación', 'error');
    }
  };

  const clearAllNotifications = async () => {
    if (notifications.length === 0) {
      showToast('No hay notificaciones para eliminar', 'info');
      return;
    }

    if (!window.confirm('¿Estás seguro de que quieres eliminar todas las notificaciones?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications([]);
      setUnreadCount(0);
      
      showToast(`${response.data.notifications_deleted} notificaciones eliminadas`, 'success');
      
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      showToast('Error al eliminar todas las notificaciones', 'error');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message': return <FiMessageCircle />;
      case 'proposal_accepted': return <FiCheckCircle />;
      case 'creative_selected_project_opened': return <FiAward />;
      case 'proposal_received': return <FiMail />;
      case 'project_update': return <FiActivity />;
      case 'invoice_requested': return <FiFileText />;
      default: return <FiBell />;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Hace un momento';
    if (diffInHours < 24) return `Hace ${diffInHours}h`;
    if (diffInHours < 168) return `Hace ${Math.floor(diffInHours / 24)}d`;
    return date.toLocaleDateString('es-ES');
  };

  if (loading) return <LoadingSpinner size="small" text="Cargando notificaciones..." />;

  return (
    <div className="notifications">
      <div className="page-header">
        <div className="notifications-title">
          <h2 className="flex items-center gap-2">
            <FiBell aria-hidden="true" />
            Notificaciones
          </h2>
          <p>Mantente al día con las últimas actualizaciones</p>
          {unreadCount > 0 && (
            <div className="unread-summary">
              <span className="unread-badge">{unreadCount} sin leer</span>
            </div>
          )}
        </div>
        
        <div className="notification-header-actions">
          {unreadCount > 0 && (
            <motion.button 
              onClick={markAllAsRead} 
              className="btn-mark-all"
              disabled={loadingMarkAll}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loadingMarkAll ? (
                <>
                  <span className="loading-spinner"></span>
                  Marcando...
                </>
              ) : (
                <>
                  ✓ Marcar todas como leídas
                </>
              )}
            </motion.button>
          )}
          
          {notifications.length > 0 && (
            <motion.button 
              onClick={clearAllNotifications}
              className="btn-clear-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiTrash2 className="mr-2" aria-hidden="true" />
              Limpiar todo
            </motion.button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <motion.div 
          className="empty-state"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="empty-icon" aria-hidden="true">
            <FiBell />
          </div>
          <h3>No hay notificaciones</h3>
          <p>Te notificaremos cuando tengas nuevas actualizaciones</p>
        </motion.div>
      ) : (
        <AnimatePresence mode="popLayout">
          <motion.div className="notifications-list">
            {notifications.map(notification => (
              <motion.div 
                key={notification.id} 
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className={`notification-card ${!notification.read ? 'unread' : ''} ${notification.type === 'creative_selected_project_opened' ? 'notification-creative-selected' : ''}`}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                
                <div className="notification-content">
                  <div className="notification-header">
                    <h4>{notification.title}</h4>
                    <span className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                  </div>
                  <p>{notification.message}</p>
                </div>
                
                <div className="notification-actions">
                  {!notification.read && (
                    <motion.button 
                      onClick={() => markAsRead(notification.id)}
                      className="btn-mark-read"
                      title="Marcar como leída"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={markingAsRead.has(notification.id)}
                    >
                      {markingAsRead.has(notification.id) ? (
                        <>
                          <span className="loading-spinner"></span>
                          Marcando...
                        </>
                      ) : (
                        <>✓ Leída</>
                      )}
                    </motion.button>
                  )}
                  
                  <motion.button 
                    onClick={() => deleteNotification(notification.id)}
                    className="btn-delete"
                    title="Eliminar notificación"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FiTrash2 aria-hidden="true" />
                  </motion.button>
                </div>
                
                {!notification.read && <div className="unread-indicator"></div>}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

// Floating chat avatars for active project chats (bottom-right)
const ChatNotificationDock = ({ unreadCount, refreshKey, dismissList, onOpenInlineChat }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!token) {
      setItems([]);
      return;
    }

    const fetchDockData = async () => {
      try {
        // Fetch projects where the user participates
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const projects = Array.isArray(response.data) ? response.data : [];

        // Active projects where there has been at least one message exchanged (chat activo)
        const nextItems = projects
          .filter((project) => {
            const isActive =
              project.status === 'active' || project.status === 'under_review' || project.status === 'pending_completion';
            const hasMessages = (project.message_count || 0) > 0;
            const isDismissed = Array.isArray(dismissList)
              ? dismissList.includes(project.id)
              : false;
            return isActive && hasMessages && !isDismissed;
          })
          .map((project) => {
            const otherUser = project.other_user || {};
            const fallbackName = project.other_user_name || 'Contacto';
            const name = otherUser.name || fallbackName;

            const avatarUrl = getAvatarUrl(otherUser.profile_image, name);

            const unreadCount = project.unread_message_count || 0;

            return {
              projectId: project.id,
              name,
              avatarUrl,
              unreadCount
            };
          })
          // Ordenar por número de mensajes no leídos (más urgentes primero)
          .sort((a, b) => b.unreadCount - a.unreadCount);

        setItems(nextItems);
      } catch (error) {
        console.error('Error fetching chat notification dock data:', error);
      }
    };

    fetchDockData();
  }, [token, unreadCount, refreshKey]);

  if (!items.length) return null;

  const activeChats = items.length;
  const visibleItems = items.slice(0, 4); // Máximo 4 avatares, como en el diseño

  return (
    <div className="chat-notification-dock" aria-label="Nuevos mensajes en proyectos activos">
      <div className="chat-notification-avatar-stack">
        {visibleItems.map((item) => (
          <div key={item.projectId} className="chat-notification-avatar-wrapper">
            <button
              type="button"
              className="chat-notification-avatar"
              onClick={() => onOpenInlineChat && onOpenInlineChat(item.projectId)}
              title={`Ir al chat con ${item.name}`}
            >
              <img src={item.avatarUrl} alt={`Ir al chat con ${item.name}`} />
              {item.unreadCount > 0 && (
                <span className="chat-notification-badge">
                  {item.unreadCount > 9 ? '9+' : item.unreadCount}
                </span>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Compact inline chat widget (bottom-right), similar to a chatbot
const InlineChatWidget = ({ projectId, onClose, onOpenFullChat, onDismissChat }) => {
  const { user, token } = useAuth();
  const widgetRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inlineInputRef = useRef(null);
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleteMenuMessageId, setDeleteMenuMessageId] = useState(null);

  useEffect(() => {
    if (!projectId || !token) return;

    const fetchData = async () => {
      try {
        const [projectResponse, messagesResponse] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/projects/${projectId}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setProject(projectResponse.data);
        // En el popup solo mostramos mensajes del hilo General (sin milestone_id)
        const allMessages = messagesResponse.data || [];
        const generalOnly = allMessages.filter((m) => !m.milestone_id);
        setMessages(generalOnly);
      } catch (error) {
        console.error('Error cargando el chat inline:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, token]);

  // Cerrar al hacer click fuera del widget
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      formData.append('message_type', 'text');

      const response = await axios.post(`${API}/projects/${projectId}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessages((prev) => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error enviando mensaje en el chat inline:', error);
    } finally {
      setSending(false);
      // Mantener foco en el input para seguir escribiendo
      if (inlineInputRef.current) {
        inlineInputRef.current.focus();
      }
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteMessage = async (messageId, scope) => {
    setDeleteMenuMessageId(null);
    try {
      await axios.delete(`${API}/projects/${projectId}/messages/${messageId}?scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (scope === 'me') {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      } else {
        setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, deleted_for_everyone: true, content: '' } : m));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  useEffect(() => {
    if (!deleteMenuMessageId) return;
    const close = () => setDeleteMenuMessageId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [deleteMenuMessageId]);

  // Nombre de la otra persona en el proyecto (para título y placeholder)
  const otherUserName =
    project?.other_user?.name ||
    project?.other_user_name ||
    'Contacto';
  const otherProfileImage = getAvatarUrl(project?.other_user?.profile_image, otherUserName);

  // Scroll al último mensaje cuando cambian los mensajes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages]);

  return (
    <div
      ref={widgetRef}
      className="inline-chat-widget"
      aria-label={`Chat con ${otherUserName}`}
    >
      <div className="inline-chat-header">
        <div className="inline-chat-user">
          <img src={otherProfileImage} alt={otherUserName} className="inline-chat-avatar" />
          <div className="inline-chat-title">
            <span className="inline-chat-name">{otherUserName}</span>
            <span className="inline-chat-subtitle">
              Proyecto: {project?.title || 'Cargando...'}
            </span>
          </div>
        </div>
        <div className="inline-chat-header-actions">
          {onOpenFullChat && (
            <button
              type="button"
              className="inline-chat-open-full"
              onClick={() => onOpenFullChat(projectId)}
              title="Abrir chat completo"
            >
              <FiMaximize2 aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="inline-chat-close"
            onClick={() => {
              if (onDismissChat) {
                onDismissChat(projectId);
              } else if (onClose) {
                onClose();
              }
            }}
            title="Cerrar chat"
          >
            ×
          </button>
        </div>
      </div>

      <div className="inline-chat-body">
        {loading ? (
          <LoadingSpinner size="small" text="Cargando conversación..." />
        ) : messages.length === 0 ? (
          <div className="inline-chat-empty">
            <span>Empieza la conversación con {otherUserName}</span>
          </div>
        ) : (
          <div className="inline-chat-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`inline-chat-message ${
                  message.sender_id === user.id ? 'own' : 'other'
                }`}
              >
                <div className="inline-chat-bubble">
                  <p>{message.deleted_for_everyone ? (
                    <span className="message-deleted-placeholder">Este mensaje ha sido eliminado</span>
                  ) : (
                    message.content
                  )}</p>
                  <span className="inline-chat-meta">
                    <span className="inline-chat-time">{formatTime(message.created_at)}</span>
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <span className="message-sent-indicator" title="Enviado">
                        <FiCheck aria-hidden="true" /> Enviado
                      </span>
                    )}
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <span className="message-actions-wrap inline-chat-actions">
                        <button
                          type="button"
                          className="message-action-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteMenuMessageId(deleteMenuMessageId === message.id ? null : message.id); }}
                          title="Más opciones"
                          aria-expanded={deleteMenuMessageId === message.id}
                        >
                          <FiMoreVertical aria-hidden="true" />
                        </button>
                        {deleteMenuMessageId === message.id && (
                          <div className="message-delete-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => deleteMessage(message.id, 'me')}>Eliminar para mí</button>
                            <button type="button" onClick={() => deleteMessage(message.id, 'everyone')}>Eliminar para todos</button>
                          </div>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="inline-chat-footer-label">
        Hablando con <strong>{otherUserName}</strong>
      </div>

      <form className="inline-chat-form" onSubmit={sendMessage}>
        <input
          type="text"
          ref={inlineInputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={`Escribe a ${otherUserName}...`}
          disabled={sending}
        />
        <button type="submit" disabled={sending || !newMessage.trim()}>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

// Payment form for milestone (pago de fase)
const MilestonePaymentForm = ({ projectId, milestoneId, amountEur, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const { token } = useAuth();

  // Si el Payment Element no carga en 15s, mostrar mensaje para cerrar y reintentar
  useEffect(() => {
    if (!clientSecret || elementReady || loadFailed) return;
    const t = setTimeout(() => {
      setLoadFailed(true);
    }, 15000);
    return () => clearTimeout(t);
  }, [clientSecret, elementReady, loadFailed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;
    setLoading(true);
    setError(null);
    const returnUrl = `${window.location.origin}${window.location.pathname || '/'}`;
    sessionStorage.setItem('pending_milestone_payment', JSON.stringify({ projectId, milestoneId }));
    try {
      let paymentIntentId = clientSecret && clientSecret.split('_secret_')[0];
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      });
      if (confirmError) {
        // If the PI is already succeeded (retry after backend error), still record the payment
        if (confirmError.payment_intent?.status === 'succeeded') {
          paymentIntentId = confirmError.payment_intent.id || paymentIntentId;
        } else {
          setError(confirmError.message || 'Error al procesar el pago');
          setLoading(false);
          sessionStorage.removeItem('pending_milestone_payment');
          return;
        }
      } else if (paymentIntent) {
        paymentIntentId = paymentIntent.id || paymentIntentId;
      }
      if (paymentIntentId) {
        await axios.post(`${API}/projects/${projectId}/milestones/${milestoneId}/pay-with-stripe`, { payment_intent_id: paymentIntentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        sessionStorage.removeItem('pending_milestone_payment');
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al procesar el pago');
      sessionStorage.removeItem('pending_milestone_payment');
    } finally {
      setLoading(false);
    }
  };

  if (loadFailed) {
    return (
      <div className="payment-form-stripe payment-form-load-failed">
        <p className="payment-error">
          El formulario de pago no ha cargado. Puede deberse a la conexión o al navegador.
        </p>
        <p className="payment-loading-hint">Cierra este diálogo y vuelve a hacer clic en &quot;Pagar&quot; en la fase. Si sigue fallando, prueba en otro navegador o en modo incógnito.</p>
        <div className="payment-form-actions">
          <button type="button" onClick={onCancel} className="btn-primary">
            Cerrar e intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form-stripe">
      <div className="payment-element-wrap">
        {!elementReady && (
          <p className="payment-loading-hint" aria-live="polite">
            Cargando formulario de pago… (puede tardar unos segundos)
          </p>
        )}
        <PaymentElement
          onReady={() => setElementReady(true)}
          onLoadError={(ev) => {
            setElementReady(false);
            setError(ev?.error?.message || 'Error al cargar el formulario de pago');
          }}
        />
      </div>
      {error && <p className="payment-error">{error}</p>}
      <div className="payment-form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={!stripe || !elementReady || loading}>
          {loading ? 'Procesando...' : `Pagar ${amountEur != null ? amountEur.toFixed(2) : ''} €`}
        </button>
      </div>
    </form>
  );
};

// Payment form for completion (pago al completar proyecto)
const CompletionPaymentForm = ({ projectId, amountEur, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const { token } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;
    setLoading(true);
    setError(null);
    const returnUrl = `${window.location.origin}${window.location.pathname || '/'}`;
    sessionStorage.setItem('pending_complete_project_id', projectId);
    try {
      let paymentIntentId = clientSecret && clientSecret.split('_secret_')[0];
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      });
      if (confirmError) {
        if (confirmError.payment_intent?.status === 'succeeded') {
          paymentIntentId = confirmError.payment_intent.id || paymentIntentId;
        } else {
          setError(confirmError.message || 'Error al procesar el pago');
          setLoading(false);
          sessionStorage.removeItem('pending_complete_project_id');
          return;
        }
      } else if (paymentIntent) {
        paymentIntentId = paymentIntent.id || paymentIntentId;
      }
      if (paymentIntentId) {
        await axios.post(`${API}/projects/${projectId}/complete-with-payment`, { payment_intent_id: paymentIntentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        sessionStorage.removeItem('pending_complete_project_id');
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al procesar el pago');
      sessionStorage.removeItem('pending_complete_project_id');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form-stripe">
      <PaymentElement
        onReady={() => setElementReady(true)}
        onLoadError={() => setElementReady(false)}
      />
      {error && <p className="payment-error">{error}</p>}
      <div className="payment-form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={!stripe || !elementReady || loading}>
          {loading ? 'Procesando...' : `Pagar ${amountEur != null ? amountEur.toFixed(2) : ''} €`}
        </button>
      </div>
    </form>
  );
};

// Payment form for Stripe (used inside Elements)
const PaymentForm = ({ proposalId, proposal, amountEur, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const { token } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;
    setLoading(true);
    setError(null);
    const returnUrl = `${window.location.origin}${window.location.pathname || '/'}`;
    sessionStorage.setItem('pending_accept_proposal_id', proposalId);
    try {
      let paymentIntentId = clientSecret && clientSecret.split('_secret_')[0];
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      });
      if (confirmError) {
        if (confirmError.payment_intent?.status === 'succeeded') {
          paymentIntentId = confirmError.payment_intent.id || paymentIntentId;
        } else {
          setError(confirmError.message || 'Error al procesar el pago');
          setLoading(false);
          sessionStorage.removeItem('pending_accept_proposal_id');
          return;
        }
      } else if (paymentIntent) {
        paymentIntentId = paymentIntent.id || paymentIntentId;
      }
      if (paymentIntentId) {
        const res = await axios.post(`${API}/proposals/${proposalId}/accept-with-payment`, { payment_intent_id: paymentIntentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        sessionStorage.removeItem('pending_accept_proposal_id');
        onSuccess(res.data.project_id, proposal);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al procesar el pago');
      sessionStorage.removeItem('pending_accept_proposal_id');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form-stripe">
      <PaymentElement
        onReady={() => setElementReady(true)}
        onLoadError={() => setElementReady(false)}
      />
      {error && <p className="payment-error">{error}</p>}
      <div className="payment-form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={!stripe || !elementReady || loading}>
          {loading ? 'Procesando...' : `Pagar ${amountEur != null ? amountEur.toFixed(2) : ''} €`}
        </button>
      </div>
    </form>
  );
};

// Components
const ProposalsView = ({ briefId, briefTitle, onBack, navigateToProject }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [brief, setBrief] = useState(null); // Add brief state
  const [loading, setLoading] = useState(true);
  const [proposalsError, setProposalsError] = useState(null); // Error al cargar propuestas
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  
  // Shortlist functionality
  const [shortlist, setShortlist] = useState([]);
  const [isShortlistMode, setIsShortlistMode] = useState(false);
  const [showPricePhase, setShowPricePhase] = useState(false);
  const [updatingShortlist, setUpdatingShortlist] = useState(false);
  
  // Reveal pricing confirmation modal
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [phaseLocked, setPhaseLocked] = useState(false); // Track if phase transition is irreversible
  
  // Accept proposal success modal
  const [showAcceptSuccessModal, setShowAcceptSuccessModal] = useState(false);
  const [acceptedProposalInfo, setAcceptedProposalInfo] = useState(null);
  const [acceptingProposal, setAcceptingProposal] = useState(false);
  const [acceptError, setAcceptError] = useState(null);

  // Payment modal (Stripe)
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  // Aplicaciones (creativos aplican; creador acepta máx 5; no hay fase de propuestas)
  const MAX_APPLICANTS = 5;
  const [applications, setApplications] = useState([]);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [acceptingApplicants, setAcceptingApplicants] = useState(false);
  const [selectedApplicantIds, setSelectedApplicantIds] = useState([]);

  // Primer contacto: reuniones con creativos aceptados (enlace Google Calendar)
  const [firstContactMeetings, setFirstContactMeetings] = useState([]);
  const [showFirstContactModal, setShowFirstContactModal] = useState(false);
  const [firstContactCreative, setFirstContactCreative] = useState(null); // { creative_id, creative_name }
  const [firstContactScheduledAt, setFirstContactScheduledAt] = useState('');
  const [firstContactDuration, setFirstContactDuration] = useState(30);
  const [firstContactTitle, setFirstContactTitle] = useState('Primer contacto - Vissory');
  const [firstContactNotes, setFirstContactNotes] = useState('');
  const [savingFirstContact, setSavingFirstContact] = useState(false);
  const [firstContactError, setFirstContactError] = useState(null);
  const [createdMeetingCalendarUrl, setCreatedMeetingCalendarUrl] = useState(null);

  // Confirmar creativo definitivo (tras reuniones): flujo en pasos
  const [showConfirmIntentModal, setShowConfirmIntentModal] = useState(false);   // Paso 1: confirmar que quieres elegir
  const [showChooseCreativeModal, setShowChooseCreativeModal] = useState(false); // Paso 2: elegir uno de la lista
  const [confirmingCreativeId, setConfirmingCreativeId] = useState(null);
  const [confirmCreativeError, setConfirmCreativeError] = useState(null);
  const [showConfirmCreativeModal, setShowConfirmCreativeModal] = useState(false); // Paso 3: confirmar "Quedarte con X?"
  const [creativeToConfirm, setCreativeToConfirm] = useState(null); // { creative_id, creative_name }

  // Solicitar ver propuesta: loading por propuesta
  const [requestingViewProposalId, setRequestingViewProposalId] = useState(null);

  // Notas del creador por creativo (primer contacto / reuniones)
  const [savingNotesCreativeId, setSavingNotesCreativeId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [briefId]);

  const fetchData = async () => {
    setProposalsError(null);
    try {
      const briefResponse = await axios.get(`${API}/briefs/${briefId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrief(briefResponse.data);
      const briefPhase = briefResponse.data.phase || 'quality';

      try {
        const appRes = await axios.get(`${API}/briefs/${briefId}/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApplications(appRes.data?.applications || []);
        setAcceptedCount(appRes.data?.accepted_count ?? 0);
      } catch (_) {
        setApplications([]);
        setAcceptedCount(0);
      }

      try {
        const fcRes = await axios.get(`${API}/briefs/${briefId}/first-contact`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFirstContactMeetings(fcRes.data?.meetings || []);
      } catch (_) {
        setFirstContactMeetings([]);
      }

      try {
        const proposalsResponse = await axios.get(`${API}/briefs/${briefId}/proposals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = proposalsResponse.data || [];
        setProposals(data);
        setProposalsError(null);

        const shortlistedProposals = data.filter(p => p.is_shortlisted);
        setShortlist(shortlistedProposals.map(p => p.creative_id));

        if (briefPhase === 'price') {
          setIsShortlistMode(true);
          setShowPricePhase(true);
          setPhaseLocked(true);
        } else if (shortlistedProposals.length > 0) {
          setIsShortlistMode(true);
          setShowPricePhase(false);
          setPhaseLocked(false);
        }
      } catch (proposalsErr) {
        console.error('Error fetching proposals:', proposalsErr);
        const msg = proposalsErr.response?.status === 403
          ? 'No tienes permiso para ver las aplicaciones de este brief. Solo el creador puede verlas.'
          : proposalsErr.response?.data?.detail || proposalsErr.message || 'No se pudieron cargar las aplicaciones.';
        setProposalsError(msg);
        setProposals([]);
      }
    } catch (error) {
      console.error('Error fetching brief:', error);
      setProposalsError(error.response?.data?.detail || error.message || 'No se pudo cargar el brief.');
      if (error.response?.status === 403 || error.response?.status === 404) {
        onBack?.();
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptApplicants = async () => {
    if (selectedApplicantIds.length === 0 || selectedApplicantIds.length > MAX_APPLICANTS) return;
    setAcceptingApplicants(true);
    try {
      await axios.post(`${API}/briefs/${briefId}/applications/accept`, { creative_ids: selectedApplicantIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedApplicantIds([]);
      fetchData();
    } catch (err) {
      console.error('Error accepting applicants:', err);
      alert(err.response?.data?.detail || 'No se pudieron aceptar los aplicantes');
    } finally {
      setAcceptingApplicants(false);
    }
  };

  const openFirstContactModal = (creativeId, creativeName) => {
    setFirstContactCreative({ creative_id: creativeId, creative_name: creativeName });
    setFirstContactScheduledAt('');
    setFirstContactDuration(30);
    setFirstContactTitle('Primer contacto - Vissory');
    setFirstContactNotes('');
    setFirstContactError(null);
    setCreatedMeetingCalendarUrl(null);
    setShowFirstContactModal(true);
  };

  const closeFirstContactModal = () => {
    setShowFirstContactModal(false);
    setFirstContactCreative(null);
    setCreatedMeetingCalendarUrl(null);
    setFirstContactError(null);
  };

  const saveApplicationNotes = async (creativeId, notes) => {
    if (savingNotesCreativeId) return;
    setSavingNotesCreativeId(creativeId);
    try {
      const res = await axios.patch(
        `${API}/briefs/${briefId}/applications/${creativeId}/notes`,
        { notes: notes ?? '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplications(prev => prev.map(a =>
        a.creative_id === creativeId ? { ...a, creator_notes: res.data?.notes ?? notes ?? '' } : a
      ));
    } catch (err) {
      console.error('Error saving notes:', err);
      alert(err.response?.data?.detail || 'No se pudieron guardar las notas');
    } finally {
      setSavingNotesCreativeId(null);
    }
  };

  const saveFirstContactMeeting = async () => {
    if (!firstContactCreative || !firstContactScheduledAt) {
      setFirstContactError('Indica fecha y hora de la reunión.');
      return;
    }
    setSavingFirstContact(true);
    setFirstContactError(null);
    try {
      const dt = new Date(firstContactScheduledAt);
      const iso = dt.toISOString();
      const res = await axios.post(`${API}/briefs/${briefId}/first-contact`, {
        creative_id: firstContactCreative.creative_id,
        scheduled_at: iso,
        duration_minutes: firstContactDuration,
        title: firstContactTitle,
        notes: firstContactNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCreatedMeetingCalendarUrl(res.data?.google_calendar_url || null);
      fetchData();
    } catch (err) {
      setFirstContactError(err.response?.data?.detail || 'No se pudo crear la reunión');
    } finally {
      setSavingFirstContact(false);
    }
  };

  const openConfirmIntentModal = () => {
    setShowConfirmIntentModal(true);
  };

  const closeConfirmIntentModal = () => {
    setShowConfirmIntentModal(false);
  };

  const confirmIntentAndChooseCreative = () => {
    setShowConfirmIntentModal(false);
    setShowChooseCreativeModal(true);
  };

  const closeChooseCreativeModal = () => {
    setShowChooseCreativeModal(false);
  };

  const openConfirmCreativeModal = (creativeId, creativeName) => {
    setCreativeToConfirm({ creative_id: creativeId, creative_name: creativeName });
    setConfirmCreativeError(null);
    setShowChooseCreativeModal(false);
    setShowConfirmCreativeModal(true);
  };

  const closeConfirmCreativeModal = () => {
    setShowConfirmCreativeModal(false);
    setCreativeToConfirm(null);
    setConfirmingCreativeId(null);
    setConfirmCreativeError(null);
  };

  const confirmCreative = async () => {
    if (!creativeToConfirm) return;
    setConfirmingCreativeId(creativeToConfirm.creative_id);
    setConfirmCreativeError(null);
    try {
      const res = await axios.post(`${API}/briefs/${briefId}/confirm-creative`, { creative_id: creativeToConfirm.creative_id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closeConfirmCreativeModal();
      await fetchData();
      if (res.data?.project_id && onNavigateToProject) {
        onNavigateToProject(res.data.project_id, 'detail');
      }
    } catch (err) {
      setConfirmCreativeError(err.response?.data?.detail || 'No se pudo confirmar al creativo');
    } finally {
      setConfirmingCreativeId(null);
    }
  };

  const requestProposalView = async (proposalId) => {
    if (requestingViewProposalId) return;
    setRequestingViewProposalId(proposalId);
    try {
      await axios.post(`${API}/proposals/${proposalId}/request-view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData();
    } catch (err) {
      console.error('Error requesting view:', err);
      alert(err.response?.data?.detail || 'No se pudo enviar la solicitud');
    } finally {
      setRequestingViewProposalId(null);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/briefs/${briefId}/proposals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProposals(response.data || []);
      setProposalsError(null);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      setProposalsError(error.response?.data?.detail || error.message || 'No se pudieron cargar las aplicaciones.');
    }
  };

  const acceptProposal = async (proposalId) => {
    setAcceptingProposal(true);
    setAcceptError(null);
    const proposal = proposals.find(p => p.id === proposalId);

    try {
      const piRes = await axios.post(`${API}/stripe/create-payment-intent`, { proposal_id: proposalId }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (piRes.data.requires_payment && piRes.data.client_secret) {
        let promise = stripePromise;
        if (!promise) {
          const configRes = await axios.get(`${API}/stripe/config`);
          const pk = configRes.data?.publishableKey;
          if (pk) {
            promise = loadStripe(pk);
            setStripePromise(promise);
          }
        }
        if (promise) {
          setPaymentModalData({
            proposalId,
            proposal,
            amountEur: piRes.data.amount_eur,
            originalAmountEur: piRes.data.original_amount_eur,
            founderDiscountPct: piRes.data.founder_discount_pct,
            clientSecret: piRes.data.client_secret,
            paymentConditions: piRes.data.payment_conditions,
          });
          setShowPaymentModal(true);
        }
        setAcceptingProposal(false);
        return;
      }

      const response = await axios.post(`${API}/proposals/${proposalId}/accept-with-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAcceptedProposalInfo({
        creativeName: proposal?.creative_name || 'Creativo',
        proposedPrice: proposal?.proposed_price,
        deliveryDays: proposal?.delivery_days,
        projectId: response.data?.project_id
      });
      setShowAcceptSuccessModal(true);
      fetchProposals();
    } catch (error) {
      console.error('Error accepting proposal:', error);
      setAcceptError(error.response?.data?.detail || 'Error al aceptar la propuesta');
      setShowAcceptSuccessModal(true);
    } finally {
      setAcceptingProposal(false);
    }
  };

  const onPaymentSuccess = (projectId, proposal) => {
    setShowPaymentModal(false);
    setPaymentModalData(null);
    setAcceptedProposalInfo({
      creativeName: proposal?.creative_name || 'Creativo',
      proposedPrice: proposal?.proposed_price,
      deliveryDays: proposal?.delivery_days,
      projectId
    });
    setShowAcceptSuccessModal(true);
    fetchProposals();
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentModalData(null);
  };

  const closeAcceptSuccessModal = () => {
    setShowAcceptSuccessModal(false);
    setAcceptedProposalInfo(null);
    setAcceptError(null);
  };

  // Shortlist functions - max 5 (solo para briefs sin flujo de aplicaciones)
  const MAX_SHORTLIST = 5;
  
  const toggleShortlist = async (creativeId) => {
    if (updatingShortlist) return;
    
    let newShortlist;
    if (shortlist.includes(creativeId)) {
      // Remove from shortlist
      newShortlist = shortlist.filter(id => id !== creativeId);
    } else {
      // Add to shortlist (max 10)
      if (shortlist.length >= MAX_SHORTLIST) {
        return; // Button should be disabled, but just in case
      }
      newShortlist = [...shortlist, creativeId];
    }
    
    // Optimistic update
    setShortlist(newShortlist);
    setUpdatingShortlist(true);
    
    try {
      await axios.patch(`${API}/briefs/${briefId}/shortlist`, {
        creative_ids: newShortlist
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update proposals state to reflect shortlist changes
      setProposals(prevProposals => prevProposals.map(p => ({
        ...p,
        is_shortlisted: newShortlist.includes(p.creative_id)
      })));
      
    } catch (error) {
      console.error('Error updating shortlist:', error);
      // Rollback optimistic update
      setShortlist(shortlist);
    } finally {
      setUpdatingShortlist(false);
    }
  };

  const startShortlistMode = () => {
    setIsShortlistMode(true);
    setShowPricePhase(false);
  };

  const revealPrices = () => {
    if (shortlist.length === 0) {
      return; // Button should be disabled
    }
    setShowRevealModal(true);
  };

  const closeRevealModal = () => {
    setShowRevealModal(false);
    setRevealError(null);
  };

  const confirmReveal = async () => {
    setRevealError(null);
    setIsRevealing(true);
    
    try {
      // Update the brief phase in the backend
      await axios.patch(`${API}/briefs/${briefId}`, {
        phase: 'price'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Close modal first, then animate the transition
      setShowRevealModal(false);
      
      setTimeout(() => {
        setShowPricePhase(true);
        setPhaseLocked(true); // Lock the phase to make it irreversible
        setIsRevealing(false);
        
        // Update brief state with new phase
        if (brief) {
          setBrief({ ...brief, phase: 'price' });
        }
      }, 300); // Small delay for modal close animation
      
    } catch (error) {
      console.error('Error updating brief phase:', error);
      setIsRevealing(false);
      const msg = error.response?.data?.detail || error.message || 'No se pudo cambiar la fase. Inténtalo de nuevo.';
      setRevealError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const backToShortlist = () => {
    // Only allow going back if phase is not locked
    if (!phaseLocked && brief?.phase !== 'price') {
      setShowPricePhase(false);
    }
  };

  // Filter proposals based on mode
  const filteredProposals = useMemo(() => {
    if (showPricePhase) {
      return proposals.filter(p => shortlist.includes(p.creative_id));
    } else if (isShortlistMode) {
      return proposals;
    } else {
      return proposals;
    }
  }, [proposals, shortlist, showPricePhase, isShortlistMode]);

  const openRejectModal = (proposal) => {
    setSelectedProposal(proposal);
    setRejectionComment('');
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedProposal(null);
    setRejectionComment('');
  };

  const rejectProposal = async () => {
    if (!rejectionComment.trim()) {
      return;
    }

    if (rejectionComment.length > 500) {
      return;
    }

    setRejecting(true);
    try {
      await axios.post(`${API}/proposals/${selectedProposal.id}/reject`, {
        comment: rejectionComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      closeRejectModal();
      fetchProposals(); // Refresh proposals
    } catch (error) {
      console.error('Error rejecting proposal:', error);
    } finally {
      setRejecting(false);
    }
  };

  // Hooks deben ejecutarse siempre en el mismo orden (antes de cualquier return)
  const hasApplyFlow = applications.length > 0;
  const applyPhaseComplete = !hasApplyFlow || acceptedCount > 0;
  const acceptedCreatives = useMemo(() => 
    applications.filter(a => a.status === 'accepted'),
    [applications]
  );
  const proposalsDisplayList = useMemo(() => {
    if (!hasApplyFlow || !applyPhaseComplete) return null;
    const list = [];
    for (const app of acceptedCreatives) {
      const proposal = proposals.find(p => p.creative_id === app.creative_id);
      if (proposal) {
        list.push({ type: 'proposal', proposal, creative_name: app.creative_name });
      } else {
        list.push({ type: 'pending', creative_id: app.creative_id, creative_name: app.creative_name || 'Creativo' });
      }
    }
    return list;
  }, [hasApplyFlow, applyPhaseComplete, acceptedCreatives, proposals]);

  if (loading) return <LoadingSpinner text="Cargando aplicaciones..." />;

  return (
    <div className="proposals-view">
      <div className="proposals-header">
        <button onClick={onBack} className="btn-back">
          ← Volver
        </button>
        <div>
          <h2>Aplicaciones para: {briefTitle}</h2>
          <p>
            {!applyPhaseComplete && hasApplyFlow
              ? `Elige hasta ${MAX_APPLICANTS} creativos. Revisa las aplicaciones recibidas.`
              : applyPhaseComplete && hasApplyFlow
                ? `${acceptedCount} creativo${acceptedCount !== 1 ? 's' : ''} aceptado${acceptedCount !== 1 ? 's' : ''}`
                : `${proposals.length} aplicación${proposals.length !== 1 ? 'es' : ''} recibida${proposals.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {brief?.hasAcceptedProposal && brief?.projectId && navigateToProject && (
          <button
            type="button"
            className="btn-primary proposals-view-go-project"
            onClick={() => navigateToProject(brief.projectId, 'detail')}
          >
            <FiFolder aria-hidden="true" /> Ir al proyecto
          </button>
        )}
      </div>

      {/* Paso 1: Aplicaciones — solo si hay aplicaciones y aún no se ha aceptado a nadie */}
      {hasApplyFlow && !applyPhaseComplete && (
        <div className="applications-section">
          <div className="applications-section-header">
            <h3>Aplicaciones <span className="applications-count-badge">{applications.length}</span></h3>
            {selectedApplicantIds.length > 0 && (
              <span className="applications-selection-hint">{selectedApplicantIds.length}/{MAX_APPLICANTS} seleccionados</span>
            )}
          </div>
          <p className="applications-hint">Elige hasta {MAX_APPLICANTS} creativos. Revisa su portfolio y proyectos.</p>
          <div className="applications-grid">
            {applications.map(app => {
              const isAccepted = app.status === 'accepted';
              const isSelected = selectedApplicantIds.includes(app.creative_id);
              const canSelect = app.status === 'applied' && selectedApplicantIds.length < MAX_APPLICANTS;
              return (
                <div key={app.id} className={`application-card ${isAccepted ? 'accepted' : ''} ${isSelected ? 'selected' : ''}`}>
                  <div className="application-creative">
                    <button
                      type="button"
                      className="application-creative-link"
                      onClick={(e) => { e.stopPropagation(); navigate(`/p/${app.creative_id}`); }}
                      aria-label={`Ver perfil de ${app.creative_name}`}
                    >
                      <img
                        src={app.creative_avatar ? getUploadUrl(app.creative_avatar) : DEFAULT_USER_AVATAR_DATA_URI}
                        alt=""
                        className="application-avatar"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                        }}
                      />
                      <div className="application-creative-head">
                        <strong>{app.creative_name}</strong>
                        {app.creative_rating > 0 && <span className="rating">⭐ {app.creative_rating}</span>}
                        {app.completed_projects > 0 && <span className="application-meta">{app.completed_projects} proyecto{app.completed_projects !== 1 ? 's' : ''} completado{app.completed_projects !== 1 ? 's' : ''}</span>}
                      </div>
                    </button>
                  </div>
                  {app.bio && <p className="application-bio">{app.bio}</p>}
                  {app.specialties?.length > 0 && (
                    <div className="application-specialties">
                      {app.specialties.map((s, i) => <span key={i} className="specialty-tag">{s}</span>)}
                    </div>
                  )}
                  <div className="application-details">
                    {app.location && <span className="application-detail"><FiMapPin className="application-detail-icon" aria-hidden="true" /> {app.location}</span>}
                    {app.experience_years != null && <span className="application-detail"><FiClock className="application-detail-icon" aria-hidden="true" /> {app.experience_years} año{app.experience_years !== 1 ? 's' : ''}</span>}
                    {app.languages?.length > 0 && <span className="application-detail"><FiGlobe className="application-detail-icon" aria-hidden="true" /> {app.languages.join(', ')}</span>}
                    {app.hourly_rate != null && app.hourly_rate > 0 && <span className="application-detail application-detail-rate"><FaEuroSign className="application-detail-icon" aria-hidden="true" /> {app.hourly_rate}/h</span>}
                  </div>
                  {app.portfolio_preview_urls?.length > 0 && (
                    <div className="application-portfolio">
                      <strong className="application-label">Portfolio</strong>
                      <div className="application-portfolio-imgs">
                        {app.portfolio_preview_urls.slice(0, 1).map((url, i) => (
                          <img key={i} src={getUploadUrl(url)} alt="" />
                        ))}
                      </div>
                    </div>
                  )}
                  {app.portfolio_links?.length > 0 && (
                    <div className="application-portfolio-links">
                      <strong className="application-label">Enlaces</strong>
                      {app.portfolio_links.map((link, i) => {
                        const href = (link && (link.startsWith('http://') || link.startsWith('https://'))) ? link : `https://${link || ''}`;
                        let label = href.replace(/^https?:\/\//, '').split('/')[0] || 'Enlace';
                        try { label = new URL(href).hostname; } catch (_) {}
                        return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="application-link">{label}</a>;
                      })}
                    </div>
                  )}
                  {app.projects_preview?.length > 0 && (
                    <div className="application-projects">
                      <strong className="application-label">Proyectos recientes</strong>
                      {app.projects_preview.slice(0, 5).map(p => <span key={p.id} className="project-tag">{p.title}</span>)}
                    </div>
                  )}
                  {!app.bio && !(app.specialties?.length > 0) && !(app.portfolio_preview_urls?.length > 0) && !(app.portfolio_links?.length > 0) && !(app.projects_preview?.length > 0) && (
                    <p className="application-profile-incomplete" aria-label="Perfil sin completar">Este creativo aún no ha completado su perfil (bio, especialidades, portfolio o proyectos).</p>
                  )}
                  {(app.proposed_price != null && app.proposed_price > 0) || (app.offer_message && app.offer_message.trim()) || app.payment_structure ? (
                    <div className="application-offer">
                      {app.proposed_price != null && app.proposed_price > 0 && (
                        <p className="application-offer-price">Precio propuesto: <strong>{Number(app.proposed_price).toLocaleString('es-ES')} €</strong></p>
                      )}
                      {app.payment_structure && (
                        <p className="application-payment-structure">
                          <strong>Forma de cobro:</strong>{' '}
                          {app.payment_structure === '50_50' && '50% anticipo / 50% al entregar'}
                          {app.payment_structure === '100' && '100% al completar el proyecto'}
                          {app.payment_structure === 'phases' && app.payment_phases?.length > 0 && (
                            <>Por fases: {app.payment_phases.map((ph, i) => `${ph.title || 'Fase'} (${ph.percentage}%)`).join(', ')}</>
                          )}
                          {app.payment_structure === 'phases' && (!app.payment_phases || app.payment_phases.length === 0) && 'Por fases'}
                        </p>
                      )}
                      {app.offer_message && app.offer_message.trim() && (
                        <p className="application-offer-message">"{app.offer_message.trim()}"</p>
                      )}
                    </div>
                  ) : null}
                  <div className="application-actions">
                    {isAccepted ? (
                      <span className="badge-accepted">Aceptado</span>
                    ) : canSelect ? (
                      <button
                        type="button"
                        className={isSelected ? 'btn-selected' : 'btn-select-applicant'}
                        onClick={() => setSelectedApplicantIds(prev => isSelected ? prev.filter(id => id !== app.creative_id) : [...prev, app.creative_id])}
                      >
                        {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
                      </button>
                    ) : (
                      <span className="badge-pending">Pendiente</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {selectedApplicantIds.length > 0 && (
            <div className="applications-accept-bar">
              <span>{selectedApplicantIds.length} seleccionado(s) (máx. {MAX_APPLICANTS})</span>
              <button type="button" className="btn-primary" disabled={acceptingApplicants || selectedApplicantIds.length > MAX_APPLICANTS} onClick={acceptApplicants}>
                {acceptingApplicants ? 'Aceptando...' : 'Aceptar seleccionados'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tras aceptar aplicantes: resumen de aceptados + fase Primer contacto */}
      {hasApplyFlow && applyPhaseComplete && (
        <>
        <div className="applications-section applications-accepted-summary">
          <h3>Creativos aceptados <span className="applications-count-badge">{acceptedCount}</span></h3>
          <p className="applications-hint">Has seleccionado a {acceptedCount} creativo{acceptedCount !== 1 ? 's' : ''} (máximo {MAX_APPLICANTS}).</p>
          <div className="applications-grid">
            {applications.filter(a => a.status === 'accepted').map(app => (
              <div key={app.id} className="application-card accepted">
                <div className="application-creative">
                  <button
                    type="button"
                    className="application-creative-link"
                    onClick={(e) => { e.stopPropagation(); navigate(`/p/${app.creative_id}`); }}
                    aria-label={`Ver perfil de ${app.creative_name}`}
                  >
                    <img
                      src={app.creative_avatar ? getUploadUrl(app.creative_avatar) : DEFAULT_USER_AVATAR_DATA_URI}
                      alt=""
                      className="application-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                      }}
                    />
                    <div className="application-creative-head">
                      <strong>{app.creative_name}</strong>
                      {app.creative_rating > 0 && <span className="rating">⭐ {app.creative_rating}</span>}
                      {app.completed_projects > 0 && <span className="application-meta">{app.completed_projects} proyecto{app.completed_projects !== 1 ? 's' : ''} completado{app.completed_projects !== 1 ? 's' : ''}</span>}
                    </div>
                  </button>
                </div>
                {app.bio && <p className="application-bio">{app.bio}</p>}
                {app.specialties?.length > 0 && (
                  <div className="application-specialties">
                    {app.specialties.map((s, i) => <span key={i} className="specialty-tag">{s}</span>)}
                  </div>
                )}
                {(app.proposed_price != null && app.proposed_price > 0) || (app.offer_message && app.offer_message.trim()) || app.payment_structure ? (
                  <div className="application-offer">
                    {app.proposed_price != null && app.proposed_price > 0 && (
                      <p className="application-offer-price">Precio propuesto: <strong>{Number(app.proposed_price).toLocaleString('es-ES')} €</strong></p>
                    )}
                    {app.payment_structure && (
                      <p className="application-payment-structure">
                        <strong>Forma de cobro:</strong>{' '}
                        {app.payment_structure === '50_50' && '50% anticipo / 50% al entregar'}
                        {app.payment_structure === '100' && '100% al completar el proyecto'}
                        {app.payment_structure === 'phases' && app.payment_phases?.length > 0 && (
                          <>Por fases: {app.payment_phases.map((ph) => `${ph.title || 'Fase'} (${ph.percentage}%)`).join(', ')}</>
                        )}
                        {app.payment_structure === 'phases' && (!app.payment_phases || app.payment_phases.length === 0) && 'Por fases'}
                      </p>
                    )}
                    {app.offer_message && app.offer_message.trim() && (
                      <p className="application-offer-message">"{app.offer_message.trim()}"</p>
                    )}
                  </div>
                ) : null}
                <div className="application-actions">
                  <span className="badge-accepted">Aceptado</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fase Primer contacto: agendar reunión con cada creativo (Google Calendar) */}
        <div className="first-contact-section">
          <h3 className="first-contact-title"><FiCalendar aria-hidden="true" /> Primer contacto</h3>
          <p className="first-contact-hint">Agenda una reunión con cada creativo seleccionado para tener un primer contacto. Puedes crear el evento en tu Google Calendar y compartir el enlace con el creativo.</p>
          <ul className="first-contact-list">
            {applications.filter(a => a.status === 'accepted').map(app => {
              const meeting = firstContactMeetings.find(m => m.creative_id === app.creative_id);
              const scheduledStr = meeting?.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : null;
              return (
                <li key={app.id} className="first-contact-item">
                  <div className="first-contact-creative">
                    <button
                      type="button"
                      className="application-creative-link first-contact-creative-link"
                      onClick={(e) => { e.stopPropagation(); navigate(`/p/${app.creative_id}`); }}
                      aria-label={`Ver perfil de ${app.creative_name}`}
                    >
                      <img
                        src={app.creative_avatar ? getUploadUrl(app.creative_avatar) : DEFAULT_USER_AVATAR_DATA_URI}
                        alt=""
                        className="application-avatar"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                        }}
                      />
                      <div>
                        <span className="first-contact-creative-name">{app.creative_name}</span>
                        {app.proposed_price != null && app.proposed_price > 0 && (
                          <span className="first-contact-offer-price"> — {Number(app.proposed_price).toLocaleString('es-ES')} € propuestos</span>
                        )}
                      </div>
                    </button>
                  </div>
                  <div className="first-contact-actions">
                    {meeting ? (
                      <>
                        <span className="first-contact-date"><FiClock aria-hidden="true" /> {scheduledStr}</span>
                        <a href={meeting.google_calendar_url} target="_blank" rel="noopener noreferrer" className="btn-add-google-calendar">
                          <FiCalendar aria-hidden="true" /> Añadir a Google Calendar
                        </a>
                      </>
                    ) : (
                      <button type="button" className="btn-primary btn-schedule-first-contact" onClick={() => openFirstContactModal(app.creative_id, app.creative_name)}>
                        <FiVideo aria-hidden="true" /> Agendar reunión
                      </button>
                    )}
                  </div>
                  <div className="first-contact-creative-notes">
                    <label className="first-contact-notes-label" htmlFor={`notes-${app.creative_id}`}>
                      <FiEdit2 aria-hidden="true" /> Notas (para reuniones)
                    </label>
                    <textarea
                      id={`notes-${app.creative_id}`}
                      className="first-contact-creative-notes-input"
                      placeholder="Añade notas tras la reunión con este creativo..."
                      value={app.creator_notes ?? ''}
                      onChange={(e) => setApplications(prev => prev.map(a => a.creative_id === app.creative_id ? { ...a, creator_notes: e.target.value } : a))}
                      rows={2}
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-save-notes"
                      disabled={savingNotesCreativeId === app.creative_id}
                      onClick={() => saveApplicationNotes(app.creative_id, applications.find(a => a.creative_id === app.creative_id)?.creator_notes ?? '')}
                    >
                      {savingNotesCreativeId === app.creative_id ? <><FiLoader className="spin" aria-hidden="true" /> Guardando...</> : <><FiSave aria-hidden="true" /> Guardar notas</>}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Botón: Elegir creativo definitivo y empezar el proyecto (flujo por pasos) */}
        {!brief.hasAcceptedProposal && applications.filter(a => a.status === 'accepted').length > 0 && (
          <div className="first-contact-section confirm-creative-cta">
            <button
              type="button"
              className="btn-primary btn-confirm-creative-cta"
              onClick={openConfirmIntentModal}
            >
              <FiUserCheck aria-hidden="true" /> Elegir creativo definitivo y empezar el proyecto
            </button>
          </div>
        )}

        {/* Modal paso 1: Confirmar intención de elegir creativo definitivo */}
        {showConfirmIntentModal && (
          <div className="modal-overlay" onClick={closeConfirmIntentModal} role="dialog" aria-modal="true" aria-labelledby="confirm-intent-modal-title">
            <div className="modal-content first-contact-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="confirm-intent-modal-title">Elegir creativo definitivo y empezar el proyecto</h3>
                <button type="button" className="modal-close" onClick={closeConfirmIntentModal} aria-label="Cerrar"><FiX /></button>
              </div>
              <div className="modal-body">
                <p>¿Confirmas que quieres elegir al creativo definitivo y empezar el proyecto? A continuación podrás elegir entre los creativos con los que has tenido reunión. Se creará el proyecto.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeConfirmIntentModal}>Cancelar</button>
                <button type="button" className="btn-primary" onClick={confirmIntentAndChooseCreative}>Sí, continuar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal paso 2: Elegir uno de los creativos aceptados */}
        {showChooseCreativeModal && (
          <div className="modal-overlay" onClick={closeChooseCreativeModal} role="dialog" aria-modal="true" aria-labelledby="choose-creative-modal-title">
            <div className="modal-content first-contact-modal choose-creative-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="choose-creative-modal-title">Elegir creativo definitivo</h3>
                <button type="button" className="modal-close" onClick={closeChooseCreativeModal} aria-label="Cerrar"><FiX /></button>
              </div>
              <div className="modal-body">
                <p className="first-contact-hint">Elige con qué creativo quieres trabajar. Se creará el proyecto.</p>
                <ul className="first-contact-list">
                  {applications.filter(a => a.status === 'accepted').map(app => (
                    <li key={app.id} className="first-contact-item">
                      <div className="first-contact-creative">
                        <button
                          type="button"
                          className="application-creative-link first-contact-creative-link"
                          onClick={(e) => { e.stopPropagation(); navigate(`/p/${app.creative_id}`); }}
                          aria-label={`Ver perfil de ${app.creative_name}`}
                        >
                          <img
                            src={app.creative_avatar ? getUploadUrl(app.creative_avatar) : DEFAULT_USER_AVATAR_DATA_URI}
                            alt=""
                            className="application-avatar"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                            }}
                          />
                          <span className="first-contact-creative-name">{app.creative_name}</span>
                        </button>
                      </div>
                      <div className="first-contact-actions">
                        <button
                          type="button"
                          className="btn-primary btn-confirm-creative"
                          onClick={() => openConfirmCreativeModal(app.creative_id, app.creative_name)}
                          disabled={!!confirmingCreativeId}
                        >
                          <FiUserCheck aria-hidden="true" /> Quedarme con {app.creative_name}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeChooseCreativeModal}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal paso 3: Confirmar creativo definitivo (ya elegido) */}
        {showConfirmCreativeModal && creativeToConfirm && (
          <div className="modal-overlay" onClick={closeConfirmCreativeModal} role="dialog" aria-modal="true" aria-labelledby="confirm-creative-modal-title">
            <div className="modal-content first-contact-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="confirm-creative-modal-title">Confirmar creativo</h3>
                <button type="button" className="modal-close" onClick={closeConfirmCreativeModal} aria-label="Cerrar"><FiX /></button>
              </div>
              <div className="modal-body">
                <p>¿Confirmas que te quedas con <strong>{creativeToConfirm.creative_name}</strong>? Se creará el proyecto.</p>
                {confirmCreativeError && <p className="first-contact-error">{confirmCreativeError}</p>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeConfirmCreativeModal}>Cancelar</button>
                <button type="button" className="btn-primary" disabled={!!confirmingCreativeId} onClick={confirmCreative}>
                  {confirmingCreativeId ? 'Confirmando...' : 'Sí, quedarme con este creativo'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Agendar reunión de primer contacto */}
        {showFirstContactModal && firstContactCreative && (
          <div className="modal-overlay" onClick={closeFirstContactModal} role="dialog" aria-modal="true" aria-labelledby="first-contact-modal-title">
            <div className="modal-content first-contact-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="first-contact-modal-title">Agendar reunión con {firstContactCreative.creative_name}</h3>
                <button type="button" className="modal-close" onClick={closeFirstContactModal} aria-label="Cerrar"><FiX /></button>
              </div>
              {createdMeetingCalendarUrl ? (
                <div className="first-contact-success">
                  <p className="first-contact-success-text">Reunión creada. Añádela a tu Google Calendar y comparte el enlace con el creativo para que también la tenga en la suya.</p>
                  <a href={createdMeetingCalendarUrl} target="_blank" rel="noopener noreferrer" className="btn-primary btn-add-google-calendar-inline">
                    <FiCalendar aria-hidden="true" /> Abrir Google Calendar
                  </a>
                  <button type="button" className="btn-secondary" onClick={closeFirstContactModal}>Cerrar</button>
                </div>
              ) : (
                <>
                  <div className="modal-body">
                    <label className="first-contact-label">Fecha y hora</label>
                    <input type="datetime-local" className="first-contact-input" value={firstContactScheduledAt} onChange={e => setFirstContactScheduledAt(e.target.value)} min={new Date().toISOString().slice(0, 16)} />
                    <label className="first-contact-label">Duración (minutos)</label>
                    <select className="first-contact-input" value={firstContactDuration} onChange={e => setFirstContactDuration(Number(e.target.value))}>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1 h</option>
                      <option value={90}>1 h 30 min</option>
                      <option value={120}>2 h</option>
                    </select>
                    <label className="first-contact-label">Título del evento</label>
                    <input type="text" className="first-contact-input" value={firstContactTitle} onChange={e => setFirstContactTitle(e.target.value)} placeholder="Primer contacto - Vissory" />
                    <label className="first-contact-label">Notas (opcional)</label>
                    <textarea className="first-contact-input first-contact-notes" value={firstContactNotes} onChange={e => setFirstContactNotes(e.target.value)} placeholder="Detalles o enlace de videollamada..." rows={3} />
                    {firstContactError && <p className="first-contact-error">{firstContactError}</p>}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={closeFirstContactModal}>Cancelar</button>
                    <button type="button" className="btn-primary" disabled={savingFirstContact || !firstContactScheduledAt} onClick={saveFirstContactMeeting}>
                      {savingFirstContact ? 'Guardando...' : 'Crear reunión'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        </>
      )}

      {/* Paso 2: solo para briefs sin flujo de aplicaciones (oculto: ya no se usan propuestas) */}
      {!hasApplyFlow && applyPhaseComplete && (
        <>
      {/* Shortlist Phase Indicator (Fase de Calidad) */}
      {proposals.filter(p => p.status === 'pending').length > 0 && (
        <div className="shortlist-controls">
          {/* Info Box */}
          <div className="shortlist-info-box">
            <div className="info-box-icon">
              <FiInfo aria-hidden="true" />
            </div>
            <div className="info-box-content">
              <strong>¿Por qué este sistema?</strong>
              <p>El sistema de Shortlist te permite evaluar primero la calidad, creatividad y experiencia de cada propuesta. Así podrás tomar una decisión basada en el valor real del trabajo, asegurando la mejor colaboración para tu proyecto.</p>
            </div>
          </div>
          
          {!showPricePhase ? (
            <div className="quality-phase-container">
              <div className="phase-info">
                <div className="phase-badge quality">
                  <FiEye aria-hidden="true" />
                  Fase de Calidad
                </div>
                <p className="phase-description">
                  Los precios están ocultos. Evalúa las aplicaciones basándote en la calidad del trabajo y experiencia del creativo.
                </p>
              </div>
              <div className="shortlist-status">
                <div className="shortlist-counter">
                  Shortlist: {shortlist.length} / {MAX_SHORTLIST} máx.
                </div>
                <div className="shortlist-actions">
                  <button 
                    onClick={revealPrices}
                    className="btn-reveal-prices"
                    disabled={shortlist.length < 1}
                  >
                    <FiEye aria-hidden="true" />
                    Revelar Tarifas ({shortlist.length})
                  </button>
                </div>
              </div>
              {shortlist.length < 1 && (
                <p className="shortlist-hint">
                  Selecciona al menos 1 propuesta (máximo {MAX_SHORTLIST}) para poder revelar las tarifas
                </p>
              )}
            </div>
          ) : (
            <div className="price-phase-container">
              <div className="phase-info">
                <div className="phase-badge price">
                  <FiCheckCircle aria-hidden="true" />
                  Fase de Tarifas
                </div>
                <p className="phase-description">
                  Ahora puedes ver los precios de tus {shortlist.length} aplicaciones seleccionadas. Elige la que mejor se adapte a tu presupuesto.
                </p>
              </div>
              <div className="phase-indicator">
                <FiCheckCircle aria-hidden="true" />
                <span>Shortlist confirmado - {shortlist.length} creativos</span>
              </div>
            </div>
          )}
        </div>
      )}

      {proposalsError ? (
        <div className="empty-state proposals-error-state">
          <div className="empty-icon" aria-hidden="true">
            <FiAlertCircle />
          </div>
          <h3>Error al cargar las aplicaciones</h3>
          <p>{proposalsError}</p>
          <button type="button" className="btn-primary" onClick={() => fetchData()}>
            Reintentar
          </button>
        </div>
      ) : (hasApplyFlow && applyPhaseComplete && (!proposalsDisplayList || proposalsDisplayList.length === 0)) ? (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            <FiUsers />
          </div>
          <h3>Ningún creativo aceptado aún</h3>
          <p>Vuelve al paso anterior y acepta hasta {MAX_APPLICANTS} aplicantes.</p>
        </div>
      ) : proposals.length === 0 && !proposalsDisplayList ? (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            <FiBriefcase />
          </div>
          <h3>No hay aplicaciones</h3>
          <p>Aún no has recibido aplicaciones para este proyecto. Asegúrate de que sea atractivo y detallado.</p>
        </div>
      ) : (
        <motion.div 
          className="proposals-grid"
          layout
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence mode="popLayout">
            {(proposalsDisplayList != null ? proposalsDisplayList : filteredProposals.map(p => ({ type: 'proposal', proposal: p }))).map(item => {
              const isPendingItem = item.type === 'pending';
              const proposal = isPendingItem ? null : item.proposal;
              if (isPendingItem) {
                return (
                  <motion.div
                    key={`pending-${item.creative_id}`}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="proposal-card proposal-card-pending-delivery"
                  >
                    <div className="proposal-header">
                      <div className="creative-info">
                        <button
                          type="button"
                          className="application-creative-link creative-info-link"
                          onClick={(e) => { e.stopPropagation(); if (item.creative_id) navigate(`/p/${item.creative_id}`); }}
                          aria-label={item.creative_name ? `Ver perfil de ${item.creative_name}` : 'Ver perfil'}
                        >
                          <div className="creative-avatar creative-avatar-placeholder">
                            <FiUser aria-hidden="true" />
                          </div>
                          <div>
                            <h3>{item.creative_name}</h3>
                            <span className="proposal-status-label">Pendiente de propuesta</span>
                          </div>
                        </button>
                      </div>
                      <span className="status-badge pending">Pendiente</span>
                    </div>
                    <p className="proposal-pending-hint">El creativo aún no ha enviado su propuesta. Cuando la envíe, aparecerá aquí.</p>
                  </motion.div>
                );
              }
              const validSamples = (proposal.portfolio_samples || []).filter(
                s => s && typeof s === 'string' && s.trim() !== ''
              );
              return (
              <motion.div 
                key={proposal.id} 
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className={`proposal-card enhanced shortlist-card ${
                  shortlist.includes(proposal.creative_id) ? 'shortlisted' : ''
                }`}
              >
                {/* Cabecera: creativo + estado */}
                <div className="proposal-header">
                  <div className="creative-info">
                    <button
                      type="button"
                      className="application-creative-link creative-info-link"
                      onClick={(e) => { e.stopPropagation(); navigate(`/p/${proposal.creative_id}`); }}
                      aria-label={`Ver perfil de ${proposal.creative_name}`}
                    >
                      <img 
                        src={proposal.creative_avatar ? getAvatarUrl(proposal.creative_avatar, proposal.creative_name) : DEFAULT_USER_AVATAR_DATA_URI}
                        alt={proposal.creative_name}
                        className="creative-avatar"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                        }}
                      />
                      <div>
                        <h3>{proposal.creative_name}</h3>
                        <div className="creative-meta-row">
                          {proposal.creative_rating > 0 && (
                            <span className="rating">⭐ {proposal.creative_rating}</span>
                          )}
                          {validSamples.length > 0 && (
                            <span className="portfolio-badge">
                              <FiPaperclip aria-hidden="true" />
                              {validSamples.length} recurso{validSamples.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="proposal-status-container">
                    <span className={`status-badge ${proposal.status}`}>
                      {proposal.status === 'pending' ? 'Pendiente' : 
                       proposal.status === 'accepted' ? 'Aceptada' : 'Rechazada'}
                    </span>
                    {shortlist.includes(proposal.creative_id) && (
                      <span className="shortlist-indicator">
                        <FiStar aria-hidden="true" />
                      </span>
                    )}
                  </div>
                </div>

                {/* Solicitud de vista: creador debe pedir ver; creativo acepta → histórico */}
                <div className="proposal-view-request-block">
                    {(proposal.view_request_status == null || proposal.view_request_status === 'none') && (
                      <div className="view-request-actions">
                        <p className="view-request-hint">El contenido de la propuesta está oculto. Solicita al creativo poder verla.</p>
                        <button
                          type="button"
                          className="btn-request-view"
                          onClick={() => requestProposalView(proposal.id)}
                          disabled={requestingViewProposalId === proposal.id}
                        >
                          {requestingViewProposalId === proposal.id ? (
                            <>Enviando...</>
                          ) : (
                            <><FiEye aria-hidden="true" /> Solicitar ver propuesta</>
                          )}
                        </button>
                      </div>
                    )}
                    {proposal.view_request_status === 'pending' && (
                      <p className="view-request-pending">Solicitud enviada. Esperando a que el creativo acepte mostrarte la propuesta.</p>
                    )}
                    {proposal.view_request_status === 'rejected' && (
                      <p className="view-request-rejected">El creativo no ha aceptado mostrarte esta propuesta.</p>
                    )}
                    {proposal.view_request_status === 'accepted' && (
                      <>
                        {proposal.creative_accepted_at && (
                          <p className="view-request-record view-request-accepted-at">
                            El creativo aceptó mostrarte esta propuesta el {new Date(proposal.creative_accepted_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                        {proposal.creator_has_viewed && (
                          <p className="view-request-record">Registro: has visto esta propuesta (queda constancia del acceso).</p>
                        )}
                      </>
                    )}
                </div>

                {/* Mensaje de la propuesta */}
                <div className="proposal-card-section">
                  <h4 className="proposal-section-title">
                    <FiFileText aria-hidden="true" />
                    Mensaje de la propuesta
                  </h4>
                  <div className="proposal-message-block">
                    {proposal.message ? (
                      proposal.message.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                        part.startsWith('**') && part.endsWith('**')
                          ? <strong key={i}>{part.slice(2, -2)}</strong>
                          : part
                      )
                    ) : '—'}
                  </div>
                  {/* Desglose de pago según condiciones (solo cuando tarifas visibles) */}
                  {showPricePhase && shortlist.includes(proposal.creative_id) && proposal.proposed_price != null && proposal.payment_conditions && (
                    (() => {
                      const pc = proposal.payment_conditions;
                      const total = proposal.proposed_price;
                      const fmt = (n) => n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

                      if (/por fases/i.test(pc) && proposal.milestones?.length > 0 && proposal.milestones.some(m => m.amount != null || m.percentage != null)) {
                        return (
                          <div className="proposal-milestone-breakdown">
                            <h5 className="milestone-breakdown-title">
                              <FaEuroSign aria-hidden="true" />
                              Desglose por fases
                            </h5>
                            <div className="milestone-breakdown-list">
                              {proposal.milestones.map((m, idx) => {
                                const amount = m.amount != null ? m.amount : (total * ((m.percentage || 0) / 100));
                                return (
                                  <div key={idx} className="milestone-breakdown-item">
                                    <span className="milestone-name">{m.title || `Fase ${idx + 1}`}</span>
                                    <span className="milestone-amount">
                                      {fmt(amount)}
                                      {m.percentage != null && <span className="milestone-pct"> ({m.percentage}%)</span>}
                                    </span>
                                  </div>
                                );
                              })}
                              <div className="milestone-breakdown-total">
                                <span>Total</span>
                                <span>{fmt(total)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      if (/50% al aceptar|50% al inicio/i.test(pc)) {
                        const mitad = total * 0.5;
                        return (
                          <div className="proposal-payment-split">
                            <h5 className="milestone-breakdown-title">
                              <FaEuroSign aria-hidden="true" />
                              Desglose de pago
                            </h5>
                            <div className="milestone-breakdown-list">
                              <div className="milestone-breakdown-item">
                                <span className="milestone-name">Al aceptar</span>
                                <span className="milestone-amount">{fmt(mitad)} <span className="milestone-pct">(50%)</span></span>
                              </div>
                              <div className="milestone-breakdown-item">
                                <span className="milestone-name">Al entregar</span>
                                <span className="milestone-amount">{fmt(mitad)} <span className="milestone-pct">(50%)</span></span>
                              </div>
                              <div className="milestone-breakdown-total">
                                <span>Total</span>
                                <span>{fmt(total)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      if (/30% al inicio/i.test(pc)) {
                        const inicio = total * 0.30;
                        const final = total * 0.70;
                        return (
                          <div className="proposal-payment-split">
                            <h5 className="milestone-breakdown-title">
                              <FaEuroSign aria-hidden="true" />
                              Desglose de pago
                            </h5>
                            <div className="milestone-breakdown-list">
                              <div className="milestone-breakdown-item">
                                <span className="milestone-name">Al inicio</span>
                                <span className="milestone-amount">{fmt(inicio)} <span className="milestone-pct">(30%)</span></span>
                              </div>
                              <div className="milestone-breakdown-item">
                                <span className="milestone-name">Al entregar</span>
                                <span className="milestone-amount">{fmt(final)} <span className="milestone-pct">(70%)</span></span>
                              </div>
                              <div className="milestone-breakdown-total">
                                <span>Total</span>
                                <span>{fmt(total)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      if (/100% al entregar/i.test(pc)) {
                        return (
                          <div className="proposal-payment-split">
                            <h5 className="milestone-breakdown-title">
                              <FaEuroSign aria-hidden="true" />
                              Desglose de pago
                            </h5>
                            <div className="milestone-breakdown-list">
                              <div className="milestone-breakdown-item">
                                <span className="milestone-name">Al entregar</span>
                                <span className="milestone-amount">{fmt(total)} <span className="milestone-pct">(100%)</span></span>
                              </div>
                              <div className="milestone-breakdown-total">
                                <span>Total</span>
                                <span>{fmt(total)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                </div>

                {/* Portafolio del creativo (imágenes de perfil) */}
                {proposal.creative_portfolio_preview_urls && proposal.creative_portfolio_preview_urls.length > 0 && (
                  <div className="proposal-card-section">
                    <h4 className="proposal-section-title">
                      <FiImage aria-hidden="true" />
                      Portafolio del creativo
                    </h4>
                    <div className="proposal-portfolio-preview">
                      {proposal.creative_portfolio_preview_urls.slice(0, 1).map((url, idx) => (
                        <div key={idx} className="proposal-portfolio-thumb-wrap">
                          <img
                            src={getUploadUrl(url)}
                            alt={`Portafolio ${idx + 1}`}
                            className="proposal-portfolio-thumb"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Adjuntos / recursos (solo URLs válidas) */}
                {validSamples.length > 0 && (
                  <div className="proposal-card-section">
                    <h4 className="proposal-section-title">
                      <FiPaperclip aria-hidden="true" />
                      Archivos adjuntos
                    </h4>
                    <div className="samples-grid">
                      {validSamples.map((sample, index) => {
                        const href = getUploadUrl(sample);
                        if (!href || href === 'about:blank' || href.startsWith('about:')) return null;
                        return (
                          <a
                            key={index}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sample-link"
                          >
                            <FiFileText className="mr-2" aria-hidden="true" />
                            Ver recurso {index + 1}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Precio y entrega */}
                {showPricePhase && shortlist.includes(proposal.creative_id) ? (
                  <div className="proposal-card-section proposal-details">
                    <div className="detail-item">
                      <span className="detail-label">
                        <FaEuroSign aria-hidden="true" />
                        Precio:
                      </span>
                      <span className="detail-value price-revealed">{proposal.proposed_price}€</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <FiClock aria-hidden="true" />
                        Entrega:
                      </span>
                      <span className="detail-value">{proposal.delivery_days} días</span>
                    </div>
                    {proposal.payment_conditions && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FiFileText aria-hidden="true" />
                          Condiciones:
                        </span>
                        <span className="detail-value payment-conditions">{proposal.payment_conditions}</span>
                      </div>
                    )}
                    {proposal.response_time_hours && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FiZap aria-hidden="true" />
                          Respuesta:
                        </span>
                        <span className="detail-value">
                          {proposal.response_time_hours < 24 
                            ? `${Math.round(proposal.response_time_hours)}h`
                            : `${Math.round(proposal.response_time_hours / 24)}d`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                ) : !showPricePhase ? (
                  <div className="proposal-card-section proposal-details hidden-prices">
                    <div className="detail-item">
                      <span className="detail-label">
                        <FaEuroSign aria-hidden="true" />
                        Precio:
                      </span>
                      <span className="detail-value price-hidden">
                        <FiEye aria-hidden="true" />
                        Oculto
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <FiClock aria-hidden="true" />
                        Entrega:
                      </span>
                      <span className="detail-value">{proposal.delivery_days} días</span>
                    </div>
                    {proposal.payment_conditions && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FiFileText aria-hidden="true" />
                          Condiciones:
                        </span>
                        <span className="detail-value payment-conditions">{proposal.payment_conditions}</span>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Action buttons - only shown in price phase or when not in shortlist mode */}
                {/* Action buttons - ONLY in price phase for shortlisted proposals */}
                {showPricePhase && shortlist.includes(proposal.creative_id) && proposal.status === 'pending' && (
                  <div className="proposal-actions">
                    <button 
                      onClick={() => acceptProposal(proposal.id)}
                      className="btn-accept"
                      disabled={acceptingProposal}
                    >
                      <FiCheck aria-hidden="true" />
                      {acceptingProposal ? 'Aceptando...' : 'Aceptar aplicación'}
                    </button>
                    <button 
                      onClick={() => openRejectModal(proposal)}
                      className="btn-reject"
                    >
                      <FiX aria-hidden="true" />
                      Rechazar
                    </button>
                  </div>
                )}
                
                {/* Add to shortlist button when not in price phase */}
                {!showPricePhase && proposal.status === 'pending' && (
                  <div className="proposal-actions">
                    <button
                      onClick={() => toggleShortlist(proposal.creative_id)}
                      className={`btn-shortlist-toggle ${shortlist.includes(proposal.creative_id) ? 'selected' : ''}`}
                      disabled={updatingShortlist || (!shortlist.includes(proposal.creative_id) && shortlist.length >= MAX_SHORTLIST)}
                    >
                      {shortlist.includes(proposal.creative_id) ? (
                        <>
                          <FiCheckCircle aria-hidden="true" />
                          En Shortlist
                        </>
                      ) : shortlist.length >= MAX_SHORTLIST ? (
                        <>
                          <FiX aria-hidden="true" />
                          Límite alcanzado
                        </>
                      ) : (
                        <>
                          <FiPlus aria-hidden="true" />
                          Añadir al Shortlist
                        </>
                      )}
                    </button>
                  </div>
                )}

                {proposal.status === 'accepted' && (
                  <div className="accepted-notice">
                    <span className="accepted-icon" aria-hidden="true">
                      <FiCheckCircle />
                    </span>
                    <span>¡Aplicación aceptada! Se ha creado un proyecto.</span>
                  </div>
                )}

                {proposal.status === 'rejected' && (
                  <div className="rejected-notice">
                    <span className="rejected-icon">❌</span>
                    <span>Aplicación rechazada</span>
                    {proposal.rejection_comment && (
                      <div className="rejection-comment">
                        <strong>Comentario:</strong> "{proposal.rejection_comment}"
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}
        </>
      )}

      {/* Reveal Pricing Confirmation Modal */}
      {showRevealModal && (
        <div className="modal-overlay">
          <div className="modal-content confirm-reveal-modal">
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FaEuroSign aria-hidden="true" />
                Confirmar revelado de tarifas
              </h3>
              <button 
                onClick={closeRevealModal} 
                className="modal-close"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="confirm-message">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <p className="warning-text">
                    Una vez reveladas las tarifas, <strong>no podrás volver a la fase de selección por calidad</strong>. 
                    ¿Quieres continuar?
                  </p>
                  <div className="shortlist-summary">
                    <span className="summary-label">Creativos seleccionados:</span>
                    <span className="summary-count">{shortlist.length}/{MAX_SHORTLIST}</span>
                  </div>
                  {revealError && (
                    <div className="reveal-error-message" role="alert">
                      {revealError}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={closeRevealModal} 
                className="btn-secondary"
                disabled={isRevealing}
              >
                Cancelar
              </button>
              <button 
                onClick={confirmReveal}
                disabled={isRevealing}
                className="btn-primary confirm-reveal-btn"
                aria-describedby="reveal-warning"
              >
                {isRevealing ? (
                  <>
                    <span className="loading-spinner"></span>
                    Revelando...
                  </>
                ) : (
                  <>
                    <FaEuroSign className="mr-2" aria-hidden="true" />
                    Confirmar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Proposal Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-content reject-modal">
            <div className="modal-header">
              <h3>❌ Rechazar aplicación</h3>
              <button onClick={closeRejectModal} className="modal-close">×</button>
            </div>
            
            <div className="modal-body">
              <p>¿Estás seguro de que quieres rechazar la propuesta de <strong>{selectedProposal?.creative_name}</strong>?</p>
              
              <div className="form-group">
                <label>Comentario sobre el rechazo *</label>
                <textarea
                  value={rejectionComment}
                  onChange={(e) => setRejectionComment(e.target.value)}
                  placeholder="Explica brevemente por qué rechazas esta aplicación. Esto ayudará al creativo a mejorar."
                  rows="4"
                  maxLength="500"
                  className="rejection-textarea"
                />
                <div className="char-count">
                  {rejectionComment.length}/500 caracteres
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button onClick={closeRejectModal} className="btn-secondary">
                Cancelar
              </button>
              <button 
                onClick={rejectProposal}
                disabled={rejecting || !rejectionComment.trim()}
                className="btn-reject"
              >
                {rejecting ? 'Rechazando...' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal (Stripe) */}
      {showPaymentModal && paymentModalData && stripePromise && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header payment-modal-header">
              <h3 className="payment-modal-title">Completar pago</h3>
              <button type="button" className="modal-close" onClick={closePaymentModal} aria-label="Cerrar">
                <FiX />
              </button>
            </div>
            <div className="payment-modal-summary">
              {paymentModalData.proposal?.creative_name && (
                <p className="payment-modal-context">
                  Aplicación de <strong>{paymentModalData.proposal.creative_name}</strong>
                </p>
              )}
              <p className="payment-modal-conditions">
                {paymentModalData.paymentConditions || 'N/A'}
              </p>
              {paymentModalData.paymentConditions && /por fases/i.test(paymentModalData.paymentConditions) ? (
                <p className="payment-modal-fase">
                  Pago del {paymentModalData.proposal?.milestones?.[0]?.title || 'primera fase'}:{' '}
                  <strong>{paymentModalData.amountEur?.toFixed(2)} €</strong>
                </p>
              ) : (
                <p className="payment-modal-amount">
                  A pagar ahora: <strong>{paymentModalData.amountEur?.toFixed(2)} €</strong>
                </p>
              )}
              {paymentModalData.founderDiscountPct > 0 && paymentModalData.originalAmountEur != null && (
                <div className="payment-modal-founder-discount">
                  <p style={{ margin: '8px 0 4px 0', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                    Subtotal: <span style={{ textDecoration: 'line-through' }}>{paymentModalData.originalAmountEur.toFixed(2)} €</span>
                  </p>
                  <p style={{ margin: '4px 0', color: '#FF5500', fontWeight: 700, fontSize: 14 }}>
                    Descuento Founder ({paymentModalData.founderDiscountPct}%):{' '}
                    −{(paymentModalData.originalAmountEur - paymentModalData.amountEur).toFixed(2)} €
                  </p>
                </div>
              )}
            </div>
            <div className="payment-modal-form-wrap">
            <Elements stripe={stripePromise} options={{ clientSecret: paymentModalData.clientSecret }}>
              <PaymentForm
                proposalId={paymentModalData.proposalId}
                proposal={paymentModalData.proposal}
                amountEur={paymentModalData.amountEur}
                clientSecret={paymentModalData.clientSecret}
                onSuccess={onPaymentSuccess}
                onCancel={closePaymentModal}
              />
            </Elements>
            </div>
          </div>
        </div>
      )}

      {/* Accept Proposal Success Modal */}
      {showAcceptSuccessModal && (
        <div className="modal-overlay">
          <div className="modal-content accept-success-modal">
            {acceptError ? (
              <>
                <div className="modal-icon error">
                  <FiAlertTriangle />
                </div>
                <h3>Error al aceptar propuesta</h3>
                <p className="modal-message error">{acceptError}</p>
                <div className="modal-footer centered">
                  <button onClick={closeAcceptSuccessModal} className="btn-primary">
                    Cerrar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-icon success">
                  <FiCheckCircle />
                </div>
                <h3>¡Aplicación aceptada!</h3>
                <p className="modal-message">
                  Has aceptado la propuesta de <strong>{acceptedProposalInfo?.creativeName}</strong>.
                  Se ha creado un nuevo proyecto automáticamente.
                </p>
                {acceptedProposalInfo && (
                  <div className="accepted-details">
                    <div className="accepted-detail-item">
                      <FaEuroSign aria-hidden="true" />
                      <span>Precio acordado: <strong>{acceptedProposalInfo.proposedPrice}€</strong></span>
                    </div>
                    <div className="accepted-detail-item">
                      <FiClock aria-hidden="true" />
                      <span>Tiempo de entrega: <strong>{acceptedProposalInfo.deliveryDays} días</strong></span>
                    </div>
                  </div>
                )}
                <div className="modal-footer centered">
                  <button onClick={closeAcceptSuccessModal} className="btn-secondary">
                    Cerrar
                  </button>
                  <button onClick={() => { closeAcceptSuccessModal(); onBack(); }} className="btn-primary">
                    <FiActivity aria-hidden="true" />
                    Ir a Proyectos
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Comprobar si el Client ID de Google está configurado (evita error "Missing required parameter: client_id")
const getHasValidGoogleClientId = () => {
  const id = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
  if (id.length < 10) return false;
  if (/^tu-client-id|your-client-id|xxx\.apps\.googleusercontent\.com$/i.test(id)) return false;
  return true;
};
const hasValidGoogleClientId = getHasValidGoogleClientId();

// Página de verificación de correo (enlace del email)
const VerifyEmailPage = ({ token, onVerified }) => {
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const requestSent = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Enlace inválido. Falta el token de verificación.');
      return;
    }
    // Evitar doble petición (React Strict Mode ejecuta efectos dos veces en desarrollo)
    if (requestSent.current) return;
    requestSent.current = true;

    axios.get(`${API}/auth/verify-email`, { params: { token } })
      .then((res) => {
        setStatus('success');
        setMessage(res.data.message || 'Correo verificado. Ya puedes iniciar sesión.');
        sessionStorage.setItem('email_verified', '1');
        setTimeout(() => {
          window.history.replaceState({}, '', '/');
          window.location.reload();
        }, 2500);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Enlace inválido o expirado.');
      });
  }, [token]);

  return (
    <div className="auth-page verify-email-page">
      <div className="auth-layout-split">
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-top">
              <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
            </div>
          </div>
        </div>
        <div className="auth-form-panel">
          <div className="auth-login-card">
            {status === 'loading' && (
              <>
                <h2 className="auth-login-title">Verificando tu correo...</h2>
                <p className="auth-login-subtitle">Un momento, por favor.</p>
                <LoadingSpinner size="medium" text="Verificando..." />
              </>
            )}
            {status === 'success' && (
              <>
                <h2 className="auth-login-title">Cuenta verificada</h2>
                <p className="auth-login-subtitle">{message}</p>
                <p className="auth-login-subtitle">Redirigiendo al inicio de sesión...</p>
              </>
            )}
            {status === 'error' && (
              <>
                <h2 className="auth-login-title">Error de verificación</h2>
                <p className="auth-login-subtitle">{message}</p>
                <button type="button" className="btn-primary" onClick={() => { window.history.replaceState({}, '', '/'); window.location.reload(); }}>
                  Ir a iniciar sesión
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Página de restablecer contraseña (enlace del correo)
const ResetPasswordPage = ({ token }) => {
  const [status, setStatus] = useState('form'); // form | loading | success | error
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    const pwdError = validatePasswordStrength(password);
    if (pwdError) {
      setFieldError(pwdError);
      return;
    }
    if (password !== confirmPassword) {
      setFieldError('Las contraseñas no coinciden.');
      return;
    }
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { timeout: 10000 });
      setStatus('success');
      setMessage(res.data.message || 'Contraseña actualizada. Ya puedes iniciar sesión.');
      sessionStorage.setItem('password_reset', '1');
      setTimeout(() => {
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }, 2500);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Enlace inválido o expirado. Solicita uno nuevo desde «¿Olvidaste tu contraseña?».');
    }
  };

  const goToLogin = () => {
    window.history.replaceState({}, '', '/');
    window.location.reload();
  };

  if (!token) {
    return (
      <div className="auth-page verify-email-page">
        <div className="auth-layout-split">
          <div className="auth-brand-panel">
            <div className="auth-brand-content">
              <div className="auth-brand-top">
                <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
              </div>
            </div>
          </div>
          <div className="auth-form-panel">
            <div className="auth-login-card">
              <h2 className="auth-login-title">Enlace inválido</h2>
              <p className="auth-login-subtitle">Falta el token de restablecimiento. Solicita un nuevo enlace desde «¿Olvidaste tu contraseña?».</p>
              <button type="button" className="btn-primary" onClick={goToLogin}>Ir a iniciar sesión</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page verify-email-page">
      <div className="auth-layout-split">
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-top">
              <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
            </div>
            <div className="auth-brand-desktop-only">
              <div className="auth-brand-center">
                <h1 className="auth-brand-hero-title">Nueva contraseña</h1>
                <p className="auth-brand-description">Elige una contraseña segura para tu cuenta.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-panel">
          <div className="auth-login-card">
            {status === 'form' && (
              <form onSubmit={handleSubmit} className="auth-form auth-form-login">
                <h2 className="auth-login-title">Restablecer contraseña</h2>
                <p className="auth-login-subtitle">Introduce tu nueva contraseña</p>
                {fieldError && <div className="error-message">{fieldError}</div>}
                <div className="form-group">
                  <label htmlFor="reset-password">Nueva contraseña</label>
                  <input
                    id="reset-password"
                    type="password"
                    placeholder="Mín. 8 caracteres, mayúsculas, minúsculas, números y especiales"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <p className="auth-password-requirements">
                    Mín. 8 caracteres; minúsculas, mayúsculas, números y caracteres especiales; no más de 2 caracteres iguales seguidos.
                  </p>
                </div>
                <div className="form-group">
                  <label htmlFor="reset-confirm">Confirmar contraseña</label>
                  <input
                    id="reset-confirm"
                    type="password"
                    placeholder="Repite la contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary auth-login-btn">Guardar contraseña</button>
                <div className="auth-switch" style={{ marginTop: '1rem' }}>
                  <button type="button" className="btn-link" onClick={goToLogin}>← Volver a iniciar sesión</button>
                </div>
              </form>
            )}
            {status === 'loading' && (
              <>
                <h2 className="auth-login-title">Guardando...</h2>
                <p className="auth-login-subtitle">Un momento, por favor.</p>
                <LoadingSpinner size="medium" text="Actualizando contraseña..." />
              </>
            )}
            {status === 'success' && (
              <>
                <h2 className="auth-login-title">Contraseña actualizada</h2>
                <p className="auth-login-subtitle">{message}</p>
                <p className="auth-login-subtitle">Redirigiendo al inicio de sesión...</p>
              </>
            )}
            {status === 'error' && (
              <>
                <h2 className="auth-login-title">Error</h2>
                <p className="auth-login-subtitle">{message}</p>
                <button type="button" className="btn-primary" onClick={goToLogin}>Ir a iniciar sesión</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pantalla para activar la cuenta con token (cuando user.active === false)
const ActivateAccountScreen = ({ token, updateUser, onBackToLogin }) => {
  const [activationToken, setActivationToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activationToken.trim()) {
      setError('Introduce el token de activación.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API}/auth/activate`,
        { activation_token: activationToken.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (err) {
      setError(err.response?.data?.detail || 'Token inválido o expirado. Comprueba el token e inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div className="auth-login-card" style={{ maxWidth: 420, width: '100%' }}>
        {onBackToLogin && (
          <button
            type="button"
            className="auth-token-gate-back-top"
            onClick={onBackToLogin}
          >
            ← Volver al inicio de sesión
          </button>
        )}
        <h2 className="auth-login-title">Activa tu cuenta</h2>
        <p className="auth-login-subtitle">
          Introduce el token de activación que te hemos proporcionado para poder usar la aplicación.
        </p>
        <form onSubmit={handleSubmit} className="auth-form auth-form-login">
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="activation-token">Token de activación</label>
            <input
              id="activation-token"
              type="text"
              value={activationToken}
              onChange={(e) => setActivationToken(e.target.value)}
              placeholder="Pega aquí tu token"
              autoComplete="off"
              disabled={loading}
            />
          </div>
          <button type="submit" className="btn-primary auth-login-btn" disabled={loading}>
            {loading ? 'Activando...' : 'Activar cuenta'}
          </button>
          {onBackToLogin && (
            <button
              type="button"
              className="btn-secondary auth-token-gate-back"
              onClick={onBackToLogin}
            >
              ← Volver al inicio de sesión
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

// Auth Components
const AuthPage = ({ onAuthSuccess }) => {
  const { addToast } = useToast();
  // Captura ?founder_token=XYZ de la URL una sola vez al cargar.
  // Si está presente, salta automáticamente al flujo de registro con rol "creator"
  // y aplica el token en el registro para auto-otorgar founder=true.
  const [founderToken] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('founder_token') || '').trim();
    } catch { return ''; }
  });
  const [authMode, setAuthMode] = useState(() => founderToken ? 'register_form' : 'login');
  const [selectedRole, setSelectedRole] = useState(() => founderToken ? 'creator' : '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationEmail, setVerificationEmail] = useState(''); // email pendiente de verificar (para mostrar "Reenviar correo")
  const [resending, setResending] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const googleButtonRef = useRef(null);
  const googleSuccessRef = useRef(null);
  const googleButtonRenderedRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem('email_verified') === '1') {
      sessionStorage.removeItem('email_verified');
      setSuccess('Cuenta verificada. Ya puedes iniciar sesión.');
    }
    if (sessionStorage.getItem('password_reset') === '1') {
      sessionStorage.removeItem('password_reset');
      setSuccess('Contraseña actualizada. Ya puedes iniciar sesión.');
    }
  }, []);

  // Validación para login
  const loginValidation = useFormValidation({
    email: '',
    password: ''
  }, {
    email: { required: true, email: true },
    password: { required: true }
  });

  // Validación para registro
  const registerValidation = useFormValidation({
    name: '',
    email: '',
    password: ''
  }, {
    name: { required: true },
    email: { required: true, email: true },
    password: { required: true, custom: (value) => validatePasswordStrength(value) }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const currentValidation = authMode === 'login' ? loginValidation : registerValidation;
    
    if (!currentValidation.validateAll()) {
      setLoading(false);
      return;
    }

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const dataToSend = authMode === 'login'
        ? { email: (loginValidation.values.email || '').trim().toLowerCase(), password: loginValidation.values.password }
        : {
            ...registerValidation.values,
            email: (registerValidation.values.email || '').trim().toLowerCase(),
            role: (selectedRole || 'creative').toLowerCase(),
            ...(founderToken ? { invite_token: founderToken } : {}),
          };
      
      console.log('Sending request to:', `${API}${endpoint}`);
      console.log('Data:', dataToSend);

      // En producción (Railway) el cold start puede tardar 1–2 min; margen 2,5 min. Registro ya no espera al correo (backend responde tras crear usuario).
      const isProduction = (API || '').startsWith('https://');
      const response = await axios.post(`${API}${endpoint}`, dataToSend, { timeout: isProduction ? 150000 : 15000 });
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        const successMsg = authMode === 'login' ? '¡Bienvenido de vuelta!' : '¡Cuenta creada exitosamente!';
        setSuccess(successMsg);
        if (authMode !== 'login') addToast({ type: 'success', title: 'Cuenta creada', description: successMsg });
        setTimeout(() => onAuthSuccess(response.data.token, response.data.user), 1000);
      } else if (authMode !== 'login' && response.data.message) {
        setSuccess(response.data.message);
        addToast({ type: 'success', title: 'Registro', description: response.data.message });
        setAuthMode('login');
        registerValidation.resetForm();
      }
    } catch (error) {
      setLoading(false);
      console.error('Auth error:', error);
      console.error('Response:', error.response?.data);
      const isNetworkError = error.message === 'Network Error' || error.code === 'ERR_NETWORK';
      if (isNetworkError) {
        const baseUrl = API.replace('/api', '');
        const msg = 'No se pudo conectar al servidor. Comprueba que el backend esté en marcha en ' + baseUrl + ' (ej. ejecuta ./start-dev.sh o en otra terminal: cd backend && python -m uvicorn server:sio_app --port 8001). Si usas la app desplegada, REACT_APP_BACKEND_URL debe apuntar a la URL del backend.';
        setError(msg);
        addToast({ type: 'error', title: 'Error de conexión', description: msg });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const base = API.replace('/api', '');
        const isProd = base.startsWith('https://');
        const msg = isProd
          ? `El backend en ${base} no ha respondido a tiempo. Prueba de nuevo (cold start puede tardar 1–2 min). Si sigue: Railway → Deployments → Logs (¿crash?). MongoDB Atlas → Network Access → 0.0.0.0/0.`
          : 'El servidor no responde. ¿Está el backend en marcha en ' + base + '?';
        setError(msg);
        addToast({ type: 'error', title: 'Timeout', description: msg });
      } else if (error.response?.status === 403 && error.response?.data?.detail?.includes('verificar')) {
        const msg = error.response.data.detail;
        setError(msg);
        setVerificationEmail(loginValidation.values.email);
        addToast({ type: 'error', title: 'Correo sin verificar', description: msg });
      } else if (error.response?.status === 400 || error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((e) => e.msg || e.message || JSON.stringify(e)).join('. ')
          : (detail || error.message || 'Error en la autenticación');
        const errMsg = typeof msg === 'string' ? msg : 'Error en el formulario. Revisa los datos.';
        setError(errMsg);
        addToast({ type: 'error', title: 'Error en el formulario', description: errMsg });
      } else {
        const msg = error.response?.data?.detail || error.message || 'Error en la autenticación';
        setError(msg);
        addToast({ type: 'error', title: 'Error de autenticación', description: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  // Tras elegir Google: intentar entrar sin token primero (admins y excepciones); si el backend pide token, mostrar pantalla de token
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const authUrl = `${API}/auth/google`;
      const isProduction = (API || '').startsWith('https://');
      const response = await axios.post(authUrl, {
        token: credentialResponse.credential,
        role: selectedRole || undefined
      }, { timeout: isProduction ? 90000 : 15000 });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.needs_role_selection) {
          sessionStorage.setItem('showGoogleRoleModal', '1');
        }
        const name = response.data.user?.name?.trim();
        const successMsg = name ? `¡Bienvenido de vuelta, ${name}!` : '¡Bienvenido con Google!';
        setSuccess(successMsg);
        setLoading(false);
        setTimeout(() => onAuthSuccess(response.data.token, response.data.user), 1000);
        return;
      }
    } catch (err) {
      const isNetworkError = err.message === 'Network Error' || err.code === 'ERR_NETWORK';
      if (isNetworkError) {
        const baseUrl = (API || '').replace('/api', '');
        const msg = 'No se pudo conectar al servidor. Comprueba que el backend esté en marcha en ' + (baseUrl || 'http://localhost:8001') + '.';
        setError(msg);
        addToast({ type: 'error', title: 'Error de conexión', description: msg });
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        const base = (API || '').replace('/api', '');
        const msg = base.startsWith('https://')
          ? `El backend en ${base} no ha respondido a tiempo. Revisa en Railway: Deployments → Logs (¿crash al arrancar?). Si usas MongoDB Atlas, en Network Access permite 0.0.0.0/0.`
          : 'El servidor no responde. Comprueba REACT_APP_BACKEND_URL en frontend/.env.';
        setError(msg);
        addToast({ type: 'error', title: 'Timeout', description: msg });
      } else {
        const msg = err.response?.data?.detail || 'Error al iniciar sesión con Google';
        setError(msg);
        addToast({ type: 'error', title: 'Error con Google', description: msg });
      }
    }
    setLoading(false);
  };

  const handleGoogleError = () => {
    const msg = 'Error al iniciar sesión con Google';
    setError(msg);
    addToast({ type: 'error', title: 'Google', description: msg });
    setLoading(false);
  };

  useEffect(() => { googleSuccessRef.current = handleGoogleSuccess; });

  useEffect(() => {
    if (!hasValidGoogleClientId || authMode !== 'login') return;
    const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
    if (!clientId) return;
    let cancelled = false;
    let initialized = false;

    const renderBtn = () => {
      const el = googleButtonRef.current;
      if (cancelled || !el || !window.google?.accounts?.id) return false;
      const wrapper = el.parentElement;
      const containerW = wrapper?.offsetWidth || el.closest?.('.auth-form-panel')?.offsetWidth || 0;
      const w = Math.min(Math.max(Math.floor(containerW), 200), 400);
      if (!initialized) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => { googleSuccessRef.current?.({ credential: response.credential }); }
        });
        initialized = true;
      }
      el.innerHTML = '';
      window.google.accounts.id.renderButton(el, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        width: w
      });
      googleButtonRenderedRef.current = true;
      return true;
    };

    const tryFirstRender = () => {
      if (cancelled) return;
      if (renderBtn()) { clearInterval(pollInterval); return; }
      if (!window.google?.accounts?.id) return;
      clearInterval(pollInterval);
    };
    const pollInterval = setInterval(tryFirstRender, 400);
    const initialTimer = setTimeout(tryFirstRender, 300);
    const maxWait = setTimeout(() => clearInterval(pollInterval), 10000);

    let resizeRaf = null;
    const onResize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => { if (!cancelled) renderBtn(); });
    };
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && googleButtonRef.current?.parentElement) {
      ro = new ResizeObserver(onResize);
      ro.observe(googleButtonRef.current.parentElement);
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      googleButtonRenderedRef.current = false;
      clearInterval(pollInterval);
      clearTimeout(initialTimer);
      clearTimeout(maxWait);
      window.removeEventListener('resize', onResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      if (ro) ro.disconnect();
    };
  }, [hasValidGoogleClientId, authMode]);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleContinue = () => {
    if (selectedRole) {
      setAuthMode('register_form'); // Ir al Paso 2: Crear Cuenta (registro)
    }
  };

  const handleBack = () => {
    if (authMode === 'register_form') {
      setAuthMode('register_role');
    } else {
      setAuthMode('register_role'); // Volver a selección de rol (página inicial)
    }
    setError('');
    setSuccess('');
  };

  const switchToRegister = () => {
    setAuthMode('register_role'); // Siempre al Paso 1: selección de rol (Creativo / Creador de contenido)
    loginValidation.resetForm();
    registerValidation.resetForm();
    setError('');
    setSuccess('');
  };

  const handleResendVerification = async () => {
    const email = verificationEmail || loginValidation.values.email;
    if (!email) return;
    setResending(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/resend-verification-email`, { email });
      setSuccess(res.data.message || 'Correo reenviado. Revisa tu bandeja (y spam).');
      setVerificationEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reenviar el correo');
    } finally {
      setResending(false);
    }
  };

  const switchToLogin = () => {
    setAuthMode('login');
    loginValidation.resetForm();
    registerValidation.resetForm();
    setError('');
    setSuccess('');
    setVerificationEmail('');
    setForgotPasswordSent(false);
    setForgotPasswordEmail('');
    // No borrar selectedRole: si luego inician con Google, se usará el rol elegido
  };

  const switchToForgotPassword = () => {
    setAuthMode('forgot_password');
    setError('');
    setSuccess('');
    setForgotPasswordEmail('');
    setForgotPasswordSent(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const email = forgotPasswordEmail.trim().toLowerCase();
    if (!email) {
      setError('Introduce tu correo electrónico.');
      return;
    }
    setForgotPasswordLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email }, { timeout: 10000 });
      setForgotPasswordSent(true);
      setSuccess('Si ese correo está registrado, recibirás un enlace para restablecer tu contraseña. Revisa tu bandeja y spam.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el correo. Inténtalo de nuevo.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const isLoginLayout = authMode === 'login' || authMode === 'register_role' || authMode === 'register_form';

  return (
    <div className={`auth-screen ${isLoginLayout ? 'auth-screen-login' : ''}`}>
      {authMode === 'register_role' ? (
        /* Página inicial: selección de rol (Paso 1 de 2) */
        <div className="auth-layout-split">
          <div className="auth-brand-panel">
            <div className="auth-brand-content">
              <div className="auth-brand-top">
                <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
              </div>
              <div className="auth-brand-desktop-only">
                <div className="auth-brand-center">
                  <h1 className="auth-brand-hero-title">Comienza tu viaje hacia el éxito.</h1>
                  <p className="auth-brand-description">
                    Crea tu cuenta y descubre todas las herramientas que Vissory tiene para ayudarte a alcanzar tus metas.
                  </p>
                </div>
                <div className="auth-brand-bottom">
                  <div className="auth-testimonial auth-benefits-block">
                    <p className="auth-benefits-intro">Con Vissory puedes:</p>
                    <ul className="auth-benefits-list">
                      <li>Gestionar briefs, equipos y entregas en un solo lugar</li>
                      <li>Mantener plazos y tareas bajo control</li>
                      <li>Conectar con talento creativo y seguir el progreso de cada proyecto</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="auth-form-panel">
            <div className="auth-login-card auth-step-card">
              <p className="auth-step-indicator">Paso 1 de 2</p>
              <h2 className="auth-login-title">¿Cuál es tu objetivo?</h2>
              <p className="auth-login-subtitle">Elige cómo planeas usar Vissory para personalizar tu experiencia.</p>
              {error && <div className="error-message">{error}</div>}
              <div className="auth-role-list auth-role-list-inline">
                <button
                  type="button"
                  role="listitem"
                  className={`auth-role-card auth-role-card-inline ${selectedRole === 'creative' ? 'is-selected' : ''}`}
                  onClick={() => handleRoleSelect('creative')}
                  aria-pressed={selectedRole === 'creative'}
                >
                  <div className="auth-role-icon" aria-hidden="true">
                    <FiPenTool />
                  </div>
                  <div className="auth-role-body">
                    <h3 className="auth-role-title">Soy Creativo</h3>
                    <p className="auth-role-description">
                      Quiero mostrar mi trabajo, conseguir clientes y gestionar mis proyectos.
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  role="listitem"
                  className={`auth-role-card auth-role-card-inline ${selectedRole === 'creator' ? 'is-selected' : ''}`}
                  onClick={() => handleRoleSelect('creator')}
                  aria-pressed={selectedRole === 'creator'}
                >
                  <div className="auth-role-icon" aria-hidden="true">
                    <FiBriefcase />
                  </div>
                  <div className="auth-role-body">
                    <h3 className="auth-role-title">Soy Creador de contenido</h3>
                    <p className="auth-role-description">
                      Publico briefs y colaboro con talento creativo en mis proyectos.
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  role="listitem"
                  className={`auth-role-card auth-role-card-inline ${selectedRole === 'brand' ? 'is-selected' : ''}`}
                  onClick={() => handleRoleSelect('brand')}
                  aria-pressed={selectedRole === 'brand'}
                >
                  <div className="auth-role-icon" aria-hidden="true">
                    <FiLayers />
                  </div>
                  <div className="auth-role-body">
                    <h3 className="auth-role-title">Marca o agencia</h3>
                    <p className="auth-role-description">
                      Somos una marca o agencia y queremos publicar proyectos y trabajar con creativos.
                    </p>
                  </div>
                </button>
              </div>
              <div className="auth-step-cta-wrap">
                <button
                  onClick={handleContinue}
                  className="btn-primary auth-login-btn"
                  disabled={!selectedRole}
                  type="button"
                >
                  Continuar
                </button>
              </div>
              <div className="auth-switch auth-switch-step">
                ¿Ya tienes una cuenta?{' '}
                <button type="button" className="btn-link" onClick={switchToLogin}>
                  Inicia sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : authMode === 'forgot_password' ? (
        <div className="auth-layout-split">
          <div className="auth-brand-panel">
            <div className="auth-brand-content">
              <div className="auth-brand-top">
                <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
              </div>
              <div className="auth-brand-desktop-only">
                <div className="auth-brand-center">
                  <h1 className="auth-brand-hero-title">¿Olvidaste tu contraseña?</h1>
                  <p className="auth-brand-description">
                    Te enviaremos un enlace a tu correo para restablecerla de forma segura.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="auth-form-panel">
            <div className="auth-login-card">
              <h2 className="auth-login-title">Restablecer contraseña</h2>
              <p className="auth-login-subtitle">Introduce el correo con el que te registraste</p>
              {forgotPasswordSent ? (
                <>
                  {success && <div className="success-message">{success}</div>}
                  <button type="button" className="btn-primary auth-login-btn" onClick={switchToLogin}>
                    Volver a iniciar sesión
                  </button>
                </>
              ) : (
                <form onSubmit={handleForgotPasswordSubmit} className="auth-form auth-form-login">
                  {error && <div className="error-message">{error}</div>}
                  <div className="form-group">
                    <label htmlFor="forgot-email">Correo electrónico</label>
                    <input
                      id="forgot-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn-primary auth-login-btn" disabled={forgotPasswordLoading}>
                    {forgotPasswordLoading ? 'Enviando...' : 'Enviar enlace'}
                  </button>
                </form>
              )}
              <div className="auth-switch" style={{ marginTop: '1rem' }}>
                <button type="button" className="btn-link" onClick={switchToLogin}>
                  ← Volver a iniciar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : authMode === 'login' ? (
        <div className="auth-layout-split">
          <div className="auth-brand-panel">
            <div className="auth-brand-content">
              <div className="auth-brand-top">
                <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
              </div>
              <div className="auth-brand-desktop-only">
                <div className="auth-brand-center">
                  <h1 className="auth-brand-hero-title">Transforma tu visión en resultados.</h1>
                  <p className="auth-brand-description">
                    Únete a miles de equipos que ya están optimizando sus procesos y alcanzando sus metas con Vissory.
                  </p>
                </div>
                <div className="auth-brand-bottom">
                  <div className="auth-testimonial auth-benefits-block">
                    <p className="auth-benefits-intro">Con Vissory puedes:</p>
                    <ul className="auth-benefits-list">
                      <li>Gestionar briefs, equipos y entregas en un solo lugar</li>
                      <li>Mantener plazos y tareas bajo control</li>
                      <li>Conectar con talento creativo y seguir el progreso de cada proyecto</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="auth-form-panel">
            <div className="auth-login-card">
              <h2 className="auth-login-title">Bienvenido de nuevo</h2>
              <p className="auth-login-subtitle">Ingresa tus credenciales para acceder a tu cuenta</p>
              <form onSubmit={handleSubmit} className="auth-form auth-form-login">
                {error && <div className="error-message">{error}</div>}
                {verificationEmail && (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="btn-link auth-resend-verification"
                  >
                    {resending ? 'Enviando...' : 'Reenviar correo de verificación'}
                  </button>
                )}
                {success && <div className="success-message">{success}</div>}
                <div className="form-group">
                  <label htmlFor="auth-email">Correo electrónico</label>
                  <input
                    id="auth-email"
                    type="email"
                    name="email"
                    placeholder="tu@email.com"
                    value={loginValidation.values.email}
                    onChange={(e) => loginValidation.setValue('email', e.target.value)}
                    onBlur={() => loginValidation.setTouchedField('email')}
                    className={loginValidation.errors.email && loginValidation.touched.email ? 'error' : ''}
                    required
                  />
                  <ErrorMessage error={loginValidation.touched.email ? loginValidation.errors.email : ''} />
                </div>

                <div className="form-group auth-password-group">
                  <label htmlFor="auth-password">Contraseña</label>
                  <div className="auth-password-input-wrap">
                    <input
                      id="auth-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="••••••••"
                      value={loginValidation.values.password}
                      onChange={(e) => loginValidation.setValue('password', e.target.value)}
                      onBlur={() => loginValidation.setTouchedField('password')}
                      className={loginValidation.errors.password && loginValidation.touched.password ? 'error' : ''}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <ErrorMessage error={loginValidation.touched.password ? loginValidation.errors.password : ''} />
                </div>

                <div className="auth-remember-forgot">
                  <label className="auth-remember">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      aria-label="Recordarme"
                    />
                    <span>Recordarme</span>
                  </label>
                  <button type="button" className="auth-forgot-link" onClick={switchToForgotPassword}>
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                <button type="submit" className="btn-primary auth-login-btn" disabled={loading}>
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </button>

                <div className="auth-divider">
                  <span>o continúa con</span>
                </div>

                <div className="auth-social-login">
                  {hasValidGoogleClientId ? (
                    <div className="auth-google-wrapper" style={{ width: '100%' }}>
                      <div ref={googleButtonRef} className="auth-google-login-custom" style={{ width: '100%', minHeight: 44 }} />
                    </div>
                  ) : (
                    <div className="auth-google-missing-config">
                      <p>Para usar &quot;Iniciar sesión con Google&quot;, añade tu Client ID en <code>frontend/.env</code>:</p>
                      <code>REACT_APP_GOOGLE_CLIENT_ID=tu-id.apps.googleusercontent.com</code>
                      <p>Luego reinicia el servidor (yarn start). Ver <code>GOOGLE_OAUTH_SETUP.md</code>.</p>
                    </div>
                  )}
                </div>

                <div className="auth-switch">
                  ¿No tienes una cuenta?{' '}
                  <button type="button" className="btn-link" onClick={switchToRegister}>
                    Regístrate
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : authMode === 'register_form' ? (
        /* Paso 2: Crear cuenta (layout dos columnas como imagen) */
        <div className="auth-layout-split">
          <div className="auth-brand-panel">
            <div className="auth-brand-content">
              <div className="auth-brand-top">
                <img src={logoHorizontalLight} alt="Vissory" className="auth-brand-logo-full" />
              </div>
              <div className="auth-brand-desktop-only">
                <div className="auth-brand-center">
                  <h1 className="auth-brand-hero-title">Cuéntanos sobre ti.</h1>
                  <p className="auth-brand-description">
                    Tu información nos ayuda a personalizar tu experiencia y conectarte con las mejores oportunidades.
                  </p>
                </div>
                <div className="auth-brand-bottom">
                  <div className="auth-testimonial auth-benefits-block">
                    <p className="auth-benefits-intro">Con Vissory puedes:</p>
                    <ul className="auth-benefits-list">
                      <li>Gestionar briefs, equipos y entregas en un solo lugar</li>
                      <li>Mantener plazos y tareas bajo control</li>
                      <li>Conectar con talento creativo y seguir el progreso de cada proyecto</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="auth-form-panel">
            <div className="auth-login-card auth-step-card auth-step-card-register">
              <button onClick={handleBack} type="button" className="btn-back-auth btn-back-auth-inline">
                ← Cambiar rol
              </button>
              <p className="auth-step-indicator">Paso 2 de 2</p>
              <h2 className="auth-login-title">Crear Cuenta</h2>
              <p className="auth-login-subtitle">Ingresa tus datos para crear tu cuenta en Vissory.</p>
              {founderToken && (
                <div className="founder-token-banner" style={{
                  background: 'linear-gradient(90deg, #FF5500, #FF7733)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  marginBottom: 16,
                  color: '#fff',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: '0 2px 12px rgba(255,85,0,.3)',
                }}>
                  <span style={{ fontSize: 20 }}>★</span>
                  <span>Te registrarás como <strong>Founding Creator</strong>. Descuento 15% vitalicio aplicado a tu cuenta.</span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="auth-form auth-form-login">
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                <div className="form-group">
                  <label htmlFor="register-email">Email</label>
                  <input
                    id="register-email"
                    type="email"
                    name="email"
                    placeholder="tu@email.com"
                    value={registerValidation.values.email}
                    onChange={(e) => registerValidation.setValue('email', e.target.value)}
                    onBlur={() => registerValidation.setTouchedField('email')}
                    className={registerValidation.errors.email && registerValidation.touched.email ? 'error' : ''}
                  />
                  <ErrorMessage error={registerValidation.touched.email ? registerValidation.errors.email : ''} />
                </div>

                <div className="form-group">
                  <label htmlFor="register-name">Nombre</label>
                  <input
                    id="register-name"
                    type="text"
                    name="name"
                    placeholder="Tu nombre completo"
                    value={registerValidation.values.name}
                    onChange={(e) => registerValidation.setValue('name', e.target.value)}
                    onBlur={() => registerValidation.setTouchedField('name')}
                    className={registerValidation.errors.name && registerValidation.touched.name ? 'error' : ''}
                  />
                  <ErrorMessage error={registerValidation.touched.name ? registerValidation.errors.name : ''} />
                </div>

                <div className="form-group auth-password-group">
                  <label htmlFor="register-password">Contraseña</label>
                  <div className="auth-password-input-wrap">
                    <input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="********"
                      value={registerValidation.values.password}
                      onChange={(e) => registerValidation.setValue('password', e.target.value)}
                      onBlur={() => registerValidation.setTouchedField('password')}
                      className={registerValidation.errors.password && registerValidation.touched.password ? 'error' : ''}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <ErrorMessage error={registerValidation.touched.password ? registerValidation.errors.password : ''} />
                  <p className="auth-password-requirements">
                    Mín. 8 caracteres; minúsculas, mayúsculas, números y caracteres especiales; no más de 2 caracteres iguales seguidos.
                  </p>
                </div>

                <button type="submit" className="btn-primary auth-login-btn" disabled={loading}>
                  {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                </button>

                <div className="auth-switch">
                  ¿Ya tienes una cuenta?{' '}
                  <button type="button" className="btn-link" onClick={switchToLogin}>
                    Inicia sesión
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Chat Component - con subhilos por fase (General + Fase 1, Fase 2, ...)
const Chat = ({ projectId, onBack, initialMilestoneId = null, onMilestoneChange, onOpenProject }) => {
  const { user, token, notifyProjectMessaged } = useAuth();
  const [messages, setMessages] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedThread, setSelectedThread] = useState(initialMilestoneId || 'general');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [deleteMenuMessageId, setDeleteMenuMessageId] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    if (initialMilestoneId) setSelectedThread(initialMilestoneId);
    else setSelectedThread('general');
  }, [initialMilestoneId]);

  const handleThreadChange = (threadId) => {
    setSelectedThread(threadId);
    if (onMilestoneChange) onMilestoneChange(threadId === 'general' ? null : threadId);
  };

  useEffect(() => {
    fetchProject();
    fetchMessages();
    fetchMilestones();
  }, [projectId]);

  const fetchMilestones = async () => {
    if (!projectId || !token) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMilestones(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMilestones([]);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Scroll al último mensaje cuando cambian los mensajes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages]);

  const filteredMessages = selectedThread === 'general'
    ? messages.filter((m) => !m.milestone_id)
    : messages.filter((m) => m.milestone_id === selectedThread);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      formData.append('message_type', 'text');
      if (selectedThread !== 'general') formData.append('milestone_id', selectedThread);

      const response = await axios.post(`${API}/projects/${projectId}/messages`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessages([...messages, response.data]);
      setNewMessage('');

      // Notificar a la app principal para que actualice el dock de chats
      if (notifyProjectMessaged) {
        notifyProjectMessaged(projectId);
      }

      // Mantener foco en el input para seguir escribiendo
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteMessage = async (messageId, scope) => {
    setDeleteMenuMessageId(null);
    try {
      await axios.delete(`${API}/projects/${projectId}/messages/${messageId}?scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (scope === 'me') {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      } else {
        setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, deleted_for_everyone: true, content: '' } : m));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  useEffect(() => {
    if (!deleteMenuMessageId) return;
    const close = () => setDeleteMenuMessageId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [deleteMenuMessageId]);

  if (loading) return <LoadingSpinner text="Cargando chat..." />;

  const threads = [
    { id: 'general', label: 'General' },
    ...milestones.map((m) => ({ id: m.id, label: m.title }))
  ];

  return (
    <div className="chat-container">
      <div className="chat-header">
        {onBack && (
          <button onClick={onBack} className="btn-back">
            ← Volver a Proyectos
          </button>
        )}
        <div className="chat-title">
          <div className="chat-title-with-avatar">
            <div className="chat-header-avatar">
              <img src={getAvatarUrl(project?.other_user?.profile_image, project?.other_user_name || project?.other_user?.name)} alt={project?.other_user_name || project?.other_user?.name} />
            </div>
            <div className="chat-title-text-and-actions">
              <div className="chat-title-text-wrap">
                <h2 className="chat-header-title">
                  <FiMessageCircle className="chat-header-title-icon" aria-hidden="true" />
                  <span className="chat-header-title-text">Chat - {project?.title || '…'}</span>
                </h2>
                <p className="chat-con-label">
                  Con {project?.other_user_name || project?.other_user?.name || '—'}
                </p>
              </div>
              {onOpenProject && projectId && (
                <button
                  type="button"
                  className="chat-go-to-project-btn"
                  onClick={() => onOpenProject(projectId)}
                >
                  <FiFolder aria-hidden="true" />
                  Ir al proyecto
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {threads.length > 1 && (
        <div className="chat-threads">
          {threads.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`chat-thread-tab ${selectedThread === t.id ? 'active' : ''}`}
              onClick={() => handleThreadChange(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="chat-messages">
        {filteredMessages.length === 0 ? (
          <div className="empty-chat">
            <div className="empty-icon" aria-hidden="true">
              <FiMessageCircle />
            </div>
            <h3>Inicia la conversación</h3>
            <p>{selectedThread === 'general' ? 'Aún no hay mensajes en este proyecto' : `Aún no hay mensajes sobre esta fase`}</p>
          </div>
        ) : (
          <>
            {filteredMessages.map(message => (
              <div 
                key={message.id} 
                className={`message ${message.sender_id === user.id ? 'own' : 'other'}`}
              >
                <div className="message-content">
                  <p>{message.deleted_for_everyone ? (
                    <span className="message-deleted-placeholder">Este mensaje ha sido eliminado</span>
                  ) : (
                    message.content
                  )}</p>
                  <div className="message-time-wrap">
                    <span className="message-time">{formatDate(message.created_at)}</span>
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <span className="message-sent-indicator" title="Enviado">
                        <FiCheck aria-hidden="true" /> Enviado
                      </span>
                    )}
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <div className="message-actions-wrap">
                        <button
                          type="button"
                          className="message-action-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteMenuMessageId(deleteMenuMessageId === message.id ? null : message.id); }}
                          title="Más opciones"
                          aria-expanded={deleteMenuMessageId === message.id}
                        >
                          <FiMoreVertical aria-hidden="true" />
                        </button>
                        {deleteMenuMessageId === message.id && (
                          <div className="message-delete-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => deleteMessage(message.id, 'me')}>Eliminar para mí</button>
                            <button type="button" onClick={() => deleteMessage(message.id, 'everyone')}>Eliminar para todos</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <form onSubmit={sendMessage} className="chat-form">
        <input
          type="text"
          ref={chatInputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={selectedThread === 'general' ? 'Escribe tu mensaje...' : 'Mensaje sobre esta fase...'}
          className="message-input"
        />
        <button type="submit" className="btn-send" disabled={!newMessage.trim() || sending}>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

// DirectChat: chat without project (e.g. from featured creative). Only creators see "Crear briefing".
const DirectChat = ({ conversationId, onOpenCreateBrief, onBack }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState(null);
  const [deleteMenuMessageId, setDeleteMenuMessageId] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    if (!conversationId || !token) return;
    const fetchConv = async () => {
      try {
        const [convRes, msgRes] = await Promise.all([
          axios.get(`${API}/conversations/${conversationId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations/${conversationId}/messages`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setConversation(convRes.data);
        setMessages(msgRes.data);
      } catch (err) {
        console.error('Error loading direct chat:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConv();
  }, [conversationId, token]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      const response = await axios.post(
        `${API}/conversations/${conversationId}/messages`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, response.data]);
      setNewMessage('');
      if (chatInputRef.current) chatInputRef.current.focus();
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteMessage = async (messageId, scope) => {
    setDeleteMenuMessageId(null);
    try {
      await axios.delete(`${API}/conversations/${conversationId}/messages/${messageId}?scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (scope === 'me') {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      } else {
        setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, deleted_for_everyone: true, content: '' } : m));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  useEffect(() => {
    if (!deleteMenuMessageId) return;
    const close = () => setDeleteMenuMessageId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [deleteMenuMessageId]);

  if (loading) return <LoadingSpinner text="Cargando chat..." />;
  if (!conversation) return <LoadingSpinner size="small" text="Conversación no encontrada" />;

  const otherUserId = conversation.user_a_id === user.id ? conversation.user_b_id : conversation.user_a_id;
  const otherUserName = conversation.other_user_name || 'Contacto';
  const isCreator = user.role === 'creator';

  return (
    <div className="chat-container">
      <div className="chat-header">
        {onBack && (
          <button type="button" onClick={onBack} className="btn-back">
            ← Volver
          </button>
        )}
        <div className="chat-title">
          <div className="chat-title-with-avatar">
            <div className="chat-header-avatar">
              <img src={getAvatarUrl(conversation?.other_user?.profile_image, otherUserName)} alt={otherUserName} />
            </div>
            <div>
              <h2 className="flex items-center gap-2">
                <FiMessageCircle aria-hidden="true" />
                Chat con {otherUserName}
              </h2>
              <p>Conversación directa (sin proyecto)</p>
            </div>
          </div>
        </div>
      </div>
      {isCreator && (
        <div className="chat-create-brief-bar">
          <button
            type="button"
            className="btn-create-brief-from-chat"
            onClick={() => onOpenCreateBrief && onOpenCreateBrief(otherUserId)}
          >
            <FiFileText aria-hidden="true" />
            Crear proyecto para este creativo
          </button>
        </div>
      )}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <div className="empty-icon" aria-hidden="true">
              <FiMessageCircle />
            </div>
            <h3>Inicia la conversación</h3>
            <p>Aún no hay mensajes en este chat</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender_id === user.id ? 'own' : 'other'}`}
              >
                <div className="message-content">
                  <p>{message.deleted_for_everyone ? (
                    <span className="message-deleted-placeholder">Este mensaje ha sido eliminado</span>
                  ) : (
                    message.content
                  )}</p>
                  <div className="message-time-wrap">
                    <span className="message-time">{formatDate(message.created_at)}</span>
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <span className="message-sent-indicator" title="Enviado">
                        <FiCheck aria-hidden="true" /> Enviado
                      </span>
                    )}
                    {message.sender_id === user.id && !message.deleted_for_everyone && (
                      <div className="message-actions-wrap">
                        <button
                          type="button"
                          className="message-action-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteMenuMessageId(deleteMenuMessageId === message.id ? null : message.id); }}
                          title="Más opciones"
                          aria-expanded={deleteMenuMessageId === message.id}
                        >
                          <FiMoreVertical aria-hidden="true" />
                        </button>
                        {deleteMenuMessageId === message.id && (
                          <div className="message-delete-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => deleteMessage(message.id, 'me')}>Eliminar para mí</button>
                            <button type="button" onClick={() => deleteMessage(message.id, 'everyone')}>Eliminar para todos</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      <form onSubmit={sendMessage} className="chat-form">
        <input
          type="text"
          ref={chatInputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className="message-input"
        />
        <button type="submit" className="btn-send" disabled={!newMessage.trim() || sending}>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

// GroupChat: chat de grupo (ej. Fundadores y equipo)
const GroupChat = ({ groupId, groupName, onBack }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [groupDetails, setGroupDetails] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showMembers, setShowMembers] = useState(false);
  const [deleteMenuMessageId, setDeleteMenuMessageId] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    if (!groupId || !token) return;
    const fetchData = async () => {
      try {
        const [groupRes, msgRes] = await Promise.all([
          axios.get(`${API}/group-conversations/${groupId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/group-conversations/${groupId}/messages`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setGroupDetails(groupRes.data || null);
        setMessages(Array.isArray(msgRes.data) ? msgRes.data : []);
      } catch (err) {
        console.error('Error loading group chat:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [groupId, token]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      const response = await axios.post(
        `${API}/group-conversations/${groupId}/messages`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, response.data]);
      setNewMessage('');
      if (chatInputRef.current) chatInputRef.current.focus();
    } catch (err) {
      console.error('Error sending group message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteMessage = async (messageId, scope) => {
    setDeleteMenuMessageId(null);
    try {
      await axios.delete(`${API}/group-conversations/${groupId}/messages/${messageId}?scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (scope === 'me') {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      } else {
        setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, deleted_for_everyone: true, content: '' } : m));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  useEffect(() => {
    if (!deleteMenuMessageId) return;
    const close = () => setDeleteMenuMessageId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [deleteMenuMessageId]);

  if (loading) return <LoadingSpinner text="Cargando chat..." />;

  return (
    <div className="chat-container">
      <div className="chat-header">
        {onBack && (
          <button type="button" onClick={onBack} className="btn-back">
            ← Volver
          </button>
        )}
        <div className="chat-title">
          <div className="chat-title-with-avatar">
            <div className="chat-header-avatar chat-header-avatar--group">
              <img src={isotipoOrange} alt="" aria-hidden="true" className="chat-header-group-logo" />
            </div>
            <div>
              <h2 className="flex items-center gap-2">
                <FiMessageCircle aria-hidden="true" />
                {groupName || groupDetails?.name || 'Chat de grupo'}
              </h2>
              <p>Grupo · Todos los participantes pueden escribir</p>
              {Array.isArray(groupDetails?.members) && groupDetails.members.length > 0 && (
                <div className="group-chat-members">
                  <button
                    type="button"
                    className="group-chat-members-toggle"
                    onClick={() => setShowMembers(true)}
                    aria-expanded={showMembers}
                  >
                    Ver miembros ({groupDetails.members.length})
                  </button>
                  {showMembers && typeof document !== 'undefined' && document.getElementById('content-area') &&
                    ReactDOM.createPortal(
                      <div
                        className="modal-overlay group-chat-members-modal-overlay"
                        onClick={() => setShowMembers(false)}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="group-members-modal-title"
                      >
                        <div
                          className="modal-content group-chat-members-modal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="modal-header">
                            <h3 id="group-members-modal-title">Miembros del grupo</h3>
                            <button
                              type="button"
                              className="modal-close"
                              onClick={() => setShowMembers(false)}
                              aria-label="Cerrar"
                            >
                              <FiX />
                            </button>
                          </div>
                          <div className="modal-body">
                            <ul className="group-chat-members-list">
                              {groupDetails.members.map((m) => (
                                <li key={m.id} className="group-chat-member">
                                  <img
                                    src={getAvatarUrl(m.profile_image, m.name)}
                                    alt=""
                                    aria-hidden
                                    className="group-chat-member-avatar"
                                  />
                                  <span className="group-chat-member-name">{m.name}</span>
                                  {m.id === user?.id && <span className="group-chat-member-you">(tú)</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>,
                      document.getElementById('content-area')
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="chat-messages">
        {messages.length === 0 ? (
          <p className="chat-messages-empty">Aún no hay mensajes. ¡Sé el primero en escribir!</p>
        ) : (
          <>
            {messages.map((message) => {
              const isSystem = (message.sender_name || '').trim().toLowerCase() === 'sistema';
              return (
              <div
                key={message.id}
                className={`message ${message.sender_id === user.id ? 'own' : 'other'} ${message.sender_is_admin ? 'message--admin' : ''} ${isSystem ? 'message--system' : ''}`}
              >
                {isSystem ? (
                  <div className="message-sender-avatar message-sender-avatar--system">
                    <img src={isotipoOrange} alt="" aria-hidden className="message-sender-avatar-logo" />
                  </div>
                ) : (
                  <img
                    src={getAvatarUrl(message.sender_profile_image, message.sender_name)}
                    alt=""
                    aria-hidden
                    className="message-sender-avatar"
                  />
                )}
                <div className="message-content">
                  <span className="message-sender-line">
                    <span className="message-sender-name">{message.sender_name || 'Usuario'}</span>
                    {message.sender_is_admin && !isSystem && (
                      <span className="message-sender-badge message-sender-badge--admin" title="Administrador">Admin</span>
                    )}
                  </span>
                  <p>{message.deleted_for_everyone ? (
                    <span className="message-deleted-placeholder">Este mensaje ha sido eliminado</span>
                  ) : (
                    message.content
                  )}</p>
                  <div className="message-time-wrap">
                    <span className="message-time">{formatDate(message.created_at)}</span>
                    {!isSystem && message.sender_id === user.id && !message.deleted_for_everyone && (
                      <span className="message-sent-indicator" title="Enviado">
                        <FiCheck aria-hidden="true" /> Enviado
                      </span>
                    )}
                    {!isSystem && message.sender_id === user.id && !message.deleted_for_everyone && (
                      <div className="message-actions-wrap">
                        <button
                          type="button"
                          className="message-action-btn"
                          onClick={(e) => { e.stopPropagation(); setDeleteMenuMessageId(deleteMenuMessageId === message.id ? null : message.id); }}
                          title="Más opciones"
                          aria-expanded={deleteMenuMessageId === message.id}
                        >
                          <FiMoreVertical aria-hidden="true" />
                        </button>
                        {deleteMenuMessageId === message.id && (
                          <div className="message-delete-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => deleteMessage(message.id, 'me')}>Eliminar para mí</button>
                            <button type="button" onClick={() => deleteMessage(message.id, 'everyone')}>Eliminar para todos</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      <form onSubmit={sendMessage} className="chat-form">
        <input
          type="text"
          ref={chatInputRef}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className="message-input"
        />
        <button type="submit" className="btn-send" disabled={!newMessage.trim() || sending}>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

// Milestones Component
const Milestones = ({ projectId, onBack, onNavigateToChat }) => {
  const { user, token } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [showCompleteMilestoneModal, setShowCompleteMilestoneModal] = useState(false);
  const [milestoneToComplete, setMilestoneToComplete] = useState(null);
  const [completingMilestone, setCompletingMilestone] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    deadline: ''
  });

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [milestoneToApprove, setMilestoneToApprove] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');

  const [paymentsData, setPaymentsData] = useState(null);
  const [showMilestonePaymentModal, setShowMilestonePaymentModal] = useState(false);
  const [milestonePaymentData, setMilestonePaymentData] = useState(null);
  const [milestoneStripePromise, setMilestoneStripePromise] = useState(null);

  // Tareas por fase (validación mutua)
  const [tasksByMilestone, setTasksByMilestone] = useState({});
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [milestoneForNewTask, setMilestoneForNewTask] = useState(null);
  const [newTask, setNewTask] = useState({ title: '', description: '' });
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({ title: '', description: '' });
  const [showTaskApprovalModal, setShowTaskApprovalModal] = useState(false);
  const [taskToApprove, setTaskToApprove] = useState(null);
  const [taskApprovalComment, setTaskApprovalComment] = useState('');
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchMilestones();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !token || milestones.length === 0) return;
    const fetchAllTasks = async () => {
      const byMilestone = {};
      await Promise.all(milestones.map(async (m) => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}/milestones/${m.id}/tasks`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          byMilestone[m.id] = Array.isArray(res.data) ? res.data : [];
        } catch {
          byMilestone[m.id] = [];
        }
      }));
      setTasksByMilestone(byMilestone);
    };
    fetchAllTasks();
  }, [projectId, token, milestones]);

  const fetchPayments = async () => {
    if (!projectId || !token) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentsData(res.data);
    } catch {
      setPaymentsData(null);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [projectId, token]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Project data:', response.data);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Milestones data:', response.data);
      setMilestones(response.data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const createMilestone = async (e) => {
    e.preventDefault();
    
    if (!newMilestone.title.trim()) {
      return;
    }

    try {
      const milestoneData = {
        title: newMilestone.title,
        description: newMilestone.description,
        deadline: newMilestone.deadline ? new Date(newMilestone.deadline).toISOString() : null
      };

      const response = await axios.post(`${API}/projects/${projectId}/milestones`, milestoneData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMilestones([...milestones, response.data]);
      setNewMilestone({ title: '', description: '', deadline: '' });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating milestone:', error);
    }
  };

  const approveMilestoneOperation = async (action, comment = '') => {
    if (!milestoneToApprove) return;

    try {
      await axios.post(`${API}/projects/${projectId}/milestones/${milestoneToApprove.id}/approve`, {
        action: action,
        comment: comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchMilestones(); // Refresh milestones
      setShowApprovalModal(false);
      setMilestoneToApprove(null);
      setApprovalComment('');
    } catch (error) {
      console.error('Error processing approval:', error);
    }
  };

  const deleteMilestone = async (milestoneId, milestoneTitle) => {
    setMilestoneToDelete({ id: milestoneId, title: milestoneTitle });
    setShowDeleteModal(true);
  };

  const confirmDeleteMilestone = async () => {
    if (!milestoneToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectId}/milestones/${milestoneToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchMilestones(); // Refresh milestones
      setShowDeleteModal(false);
      setMilestoneToDelete(null);
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  const completeMilestone = (milestoneId, milestoneTitle) => {
    setMilestoneToComplete({ id: milestoneId, title: milestoneTitle });
    setShowCompleteMilestoneModal(true);
  };

  const confirmCompleteMilestone = async () => {
    if (!milestoneToComplete) return;

    setCompletingMilestone(true);
    try {
      await axios.post(`${API}/projects/${projectId}/milestones/${milestoneToComplete.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
      setShowCompleteMilestoneModal(false);
      setMilestoneToComplete(null);
    } catch (error) {
      console.error('Error completing milestone:', error);
    } finally {
      setCompletingMilestone(false);
    }
  };

  const calculateProgress = () => {
    if (milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.status === 'completed' && m.approved_by).length;
    return Math.round((completed / milestones.length) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha límite';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMilestoneStatus = (milestone) => {
    if (milestone.status === 'completed' || milestone.approved_by) {
      return { status: 'completed', text: 'Completado', class: 'approved' };
    } else if (milestone.status === 'completion_approved') {
      return { status: 'completion_approved', text: 'Confirmado — pendiente de pago', class: 'completed-waiting-approval' };
    } else if (milestone.status === 'pending_completion_approval') {
      return { status: 'pending_completion_approval', text: 'Esperando confirmación del creativo', class: 'completed-waiting-approval' };
    } else if (milestone.status === 'pending_creation_approval') {
      return { status: 'pending_creation', text: 'Pendiente de aprobación para crear', class: 'pending-approval' };
    } else if (milestone.status === 'pending_deletion_approval') {
      return { status: 'pending_deletion', text: 'Pendiente de aprobación para eliminar', class: 'pending-approval' };
    } else {
      return { status: 'pending', text: 'Pendiente', class: 'pending' };
    }
  };

  const canApproveOperation = (milestone) => {
    return milestone.pending_approval_from === user?.id;
  };

  const handleApprovalRequest = (milestone) => {
    setMilestoneToApprove(milestone);
    setShowApprovalModal(true);
  };

  const canCreateMilestones = project?.is_creator;
  const canCompleteMilestones = project?.is_creator; // creator requests phase completion
  const canApproveMilestones = !project?.is_creator; // creative confirms phase completion
  const isClient = project?.is_creator;

  const isMilestonePaid = (milestoneIndex) => {
    if (!paymentsData?.schedule) return false;
    const s = paymentsData.schedule[milestoneIndex];
    return s?.paid === true;
  };

  const payMilestone = async (milestone) => {
    if (!isClient) return;
    const amount = milestone.amount;
    if (!amount || amount <= 0) return;
    const idx = milestones.findIndex(m => m.id === milestone.id);
    if (isMilestonePaid(idx)) return;
    try {
      const piRes = await axios.post(`${API}/stripe/create-milestone-payment-intent`, {
        project_id: projectId,
        milestone_id: milestone.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (!piRes.data.requires_payment || !piRes.data.client_secret) return;
      let promise = milestoneStripePromise;
      if (!promise) {
        const configRes = await axios.get(`${API}/stripe/config`);
        const pk = configRes.data?.publishableKey;
        if (pk) {
          promise = loadStripe(pk);
          setMilestoneStripePromise(promise);
        }
      }
      if (promise) {
        setMilestonePaymentData({
          milestone,
          clientSecret: piRes.data.client_secret,
          amountEur: piRes.data.amount_eur,
          originalAmountEur: piRes.data.original_amount_eur,
          founderDiscountPct: piRes.data.founder_discount_pct,
        });
        setShowMilestonePaymentModal(true);
      }
    } catch (err) {
      console.error('Error creating milestone payment:', err);
    }
  };

  const onMilestonePaymentSuccess = () => {
    setShowMilestonePaymentModal(false);
    setMilestonePaymentData(null);
    fetchMilestones();
    fetchPayments();
  };

  const refreshTasksForMilestone = async (milestoneId) => {
    if (!projectId || !token || !milestoneId) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/milestones/${milestoneId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasksByMilestone(prev => ({ ...prev, [milestoneId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setTasksByMilestone(prev => ({ ...prev, [milestoneId]: [] }));
    }
  };

  const openCreateTaskModal = (milestone) => {
    setMilestoneForNewTask(milestone);
    setNewTask({ title: '', description: '' });
    setShowCreateTaskModal(true);
  };

  // Abrir modal de crear tarea vía evento (evita que un padre bloquee el onClick/setState)
  const createTaskModalEvent = 'vissory-open-create-task-modal';
  useEffect(() => {
    const handler = (e) => {
      const milestone = e.detail?.milestone;
      if (milestone) {
        setMilestoneForNewTask(milestone);
        setNewTask({ title: '', description: '' });
        setShowCreateTaskModal(true);
      }
    };
    window.addEventListener(createTaskModalEvent, handler);
    return () => window.removeEventListener(createTaskModalEvent, handler);
  }, []);

  const createTask = async (e) => {
    e.preventDefault();
    if (!milestoneForNewTask || !newTask.title.trim()) return;
    try {
      await axios.post(
        `${API}/projects/${projectId}/milestones/${milestoneForNewTask.id}/tasks`,
        { title: newTask.title.trim(), description: newTask.description?.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTasksForMilestone(milestoneForNewTask.id);
      setShowCreateTaskModal(false);
      setMilestoneForNewTask(null);
      setNewTask({ title: '', description: '' });
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  const openEditTaskModal = (task, milestone) => {
    setTaskToEdit({ task, milestone });
    setEditTaskForm({
      title: task.pending_operation === 'edit' && task.pending_title != null ? task.pending_title : task.title,
      description: (task.pending_operation === 'edit' && task.pending_description != null ? task.pending_description : task.description) || ''
    });
    setShowEditTaskModal(true);
  };

  const saveEditTask = async (e) => {
    e.preventDefault();
    if (!taskToEdit || !editTaskForm.title.trim()) return;
    try {
      await axios.patch(
        `${API}/projects/${projectId}/milestones/${taskToEdit.milestone.id}/tasks/${taskToEdit.task.id}`,
        { title: editTaskForm.title.trim(), description: editTaskForm.description?.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTasksForMilestone(taskToEdit.milestone.id);
      setShowEditTaskModal(false);
      setTaskToEdit(null);
    } catch (err) {
      console.error('Error editing task:', err);
    }
  };

  const requestDeleteTask = (task, milestone) => {
    setTaskToDelete({ task, milestone });
    setShowDeleteTaskModal(true);
  };

  const requestCompleteTask = (task, milestone) => {
    setTaskToComplete({ task, milestone });
    setShowCompleteTaskModal(true);
  };

  const confirmCompleteTask = async () => {
    if (!taskToComplete) return;
    try {
      await axios.post(
        `${API}/projects/${projectId}/milestones/${taskToComplete.milestone.id}/tasks/${taskToComplete.task.id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTasksForMilestone(taskToComplete.milestone.id);
      setShowCompleteTaskModal(false);
      setTaskToComplete(null);
    } catch (err) {
      console.error('Error requesting task completion:', err);
    }
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await axios.delete(
        `${API}/projects/${projectId}/milestones/${taskToDelete.milestone.id}/tasks/${taskToDelete.task.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTasksForMilestone(taskToDelete.milestone.id);
      setShowDeleteTaskModal(false);
      setTaskToDelete(null);
    } catch (err) {
      console.error('Error requesting task deletion:', err);
    }
  };

  const canApproveTask = (task) => task.pending_approval_from === user?.id;

  const openTaskApprovalModal = (task, milestone) => {
    setTaskToApprove({ task, milestone });
    setTaskApprovalComment('');
    setShowTaskApprovalModal(true);
  };

  // Abrir modal de revisar tarea vía evento (mismo patrón que "Añadir tarea")
  const taskApprovalModalEvent = 'vissory-open-task-approval-modal';
  useEffect(() => {
    const handler = (e) => {
      const { task, milestone } = e.detail || {};
      if (task && milestone) {
        setTaskToApprove({ task, milestone });
        setTaskApprovalComment('');
        setShowTaskApprovalModal(true);
      }
    };
    window.addEventListener(taskApprovalModalEvent, handler);
    return () => window.removeEventListener(taskApprovalModalEvent, handler);
  }, []);

  const approveTaskOperation = async (action) => {
    if (!taskToApprove) return;
    try {
      await axios.post(
        `${API}/projects/${projectId}/milestones/${taskToApprove.milestone.id}/tasks/${taskToApprove.task.id}/approve`,
        { action, comment: taskApprovalComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshTasksForMilestone(taskToApprove.milestone.id);
      setShowTaskApprovalModal(false);
      setTaskToApprove(null);
      setTaskApprovalComment('');
    } catch (err) {
      console.error('Error processing task approval:', err);
    }
  };

  const getTaskStatusText = (task) => {
    if (task.status === 'completed') return 'Completada';
    if (task.status === 'active') return null;
    if (task.pending_operation === 'create') return 'Pendiente de aprobación (crear)';
    if (task.pending_operation === 'edit') return 'Pendiente de aprobación (editar)';
    if (task.pending_operation === 'delete') return 'Pendiente de aprobación (eliminar)';
    if (task.pending_operation === 'complete') return 'Pendiente de aprobación (completar)';
    return 'Pendiente';
  };

  return (
    <div className="milestones-container">
      <div className="milestones-header">
        <div className="milestones-title">
          <h2 className="flex items-center gap-2">
            <FiTarget aria-hidden="true" />
            Fases del Proyecto - {project?.title || '…'}
          </h2>
          <p>Gestiona las fases y el progreso del proyecto</p>
        </div>
      </div>

      {loading ? (
        <div className="milestones-loading-inline">
          <LoadingSpinner size="small" text="Cargando fases..." />
        </div>
      ) : (
      <>
      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-header">
          <h3 className="flex items-center gap-2">
            <FiBarChart2 aria-hidden="true" />
            Progreso del Proyecto
          </h3>
          <span className="progress-percentage">{calculateProgress()}%</span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${calculateProgress()}%` }}
          ></div>
        </div>
        <p className="progress-text">
          {milestones.filter(m => m.approved_by).length} de {milestones.length} fases completadas
        </p>
      </div>

      {/* Create Milestone Button */}
      {canCreateMilestones && (
        <div className="create-milestone-section">
          <button 
            className="btn-primary btn-add-milestone-action"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus aria-hidden="true" className="mr-2" />
            Agregar Fase
          </button>
        </div>
      )}

      {/* Milestones List */}
      <div className="milestones-list">
        <h3 className="flex items-center gap-2">
          <FiClipboard aria-hidden="true" />
          Lista de Fases ({milestones.length})
        </h3>
        
        {milestones.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">
              <FiTarget />
            </div>
            <h3>
              {(project?.payment_structure === '50_50' || project?.payment_structure === '100')
                ? 'Pagos en la pestaña Pagos'
                : 'No hay fases definidas'}
            </h3>
            <p>
              {(project?.payment_structure === '50_50' || project?.payment_structure === '100')
                ? 'Los pagos de este proyecto (anticipo/al entregar o 100% al completar) se gestionan en la pestaña Pagos.'
                : canCreateMilestones
                  ? 'Agrega fases para organizar el trabajo del proyecto'
                  : 'El cliente aún no ha definido fases para este proyecto'}
            </p>
          </div>
        ) : (
          <div className="milestones-grid">
            {milestones.map((milestone, idx) => {
              const status = getMilestoneStatus(milestone);
              const paid = isMilestonePaid(idx);
              const displayStatus = (paid && status.status === 'pending')
                ? { status: 'completed', text: 'Completado', class: 'completed' }
                : status;
              return (
                <div key={milestone.id} className={`milestone-card ${displayStatus.class}`}>
                  <div className="milestone-header">
                    <div className="milestone-header-title-wrap">
                      <h4>{milestone.title}</h4>
                      {onNavigateToChat && (
                        <button
                          type="button"
                          className="phase-chat-icon-btn"
                          onClick={() => onNavigateToChat(projectId, milestone.id)}
                          title="Abrir chat de esta fase"
                          aria-label="Abrir chat de esta fase"
                        >
                          <FiMessageCircle aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    <span className={`milestone-status milestone-status-below ${displayStatus.class}`}>
                      {displayStatus.text}
                    </span>
                  </div>
                  
                  {milestone.description && (
                    <p className="milestone-description">{milestone.description}</p>
                  )}
                  
                  {milestone.amount != null && milestone.amount > 0 && (
                    <div className="milestone-amount-badge">
                      <span className="milestone-amount-value">
                        {milestone.amount.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>
                  )}
                  <div className="milestone-meta">
                    <div className="meta-item">
                      <span className="meta-label flex items-center gap-1">
                        <FiCalendar aria-hidden="true" />
                        Fecha límite:
                      </span>
                      <span className="meta-value">{formatDate(milestone.deadline)}</span>
                    </div>
                    {milestone.completed_by && (
                      <div className="meta-item">
                        <span className="meta-label flex items-center gap-1">
                          <FiCheckCircle aria-hidden="true" />
                          Completado:
                        </span>
                        <span className="meta-value">{formatDate(milestone.completed_at)}</span>
                      </div>
                    )}
                    {milestone.approved_by && (
                      <div className="meta-item">
                        <span className="meta-label flex items-center gap-1">
                          <FiCheckCircle aria-hidden="true" />
                          Aprobado:
                        </span>
                        <span className="meta-value">{formatDate(milestone.approved_at)}</span>
                      </div>
                    )}
                  </div>

                  {/* Tareas de la fase (validación mutua) */}
                  <div className="milestone-tasks-section">
                    <div className="milestone-tasks-header">
                      <span className="milestone-tasks-title">Tareas</span>
                      <button
                        type="button"
                        className="btn-add-task"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.dispatchEvent(new CustomEvent(createTaskModalEvent, { detail: { milestone } }));
                        }}
                        title="Añadir tarea (la otra parte debe aprobar)"
                      >
                        + Añadir tarea
                      </button>
                    </div>
                    <ul className="milestone-tasks-list">
                      {(tasksByMilestone[milestone.id] || []).map((task) => {
                        const statusText = getTaskStatusText(task);
                        const isActive = task.status === 'active' && !task.pending_operation;
                        const isCompleted = task.status === 'completed';
                        return (
                          <li key={task.id} className={`milestone-task-item ${task.pending_operation ? 'task-pending' : ''} ${isCompleted ? 'task-completed' : ''}`}>
                            <span className="milestone-task-checkbox-wrap">
                              <input
                                type="checkbox"
                                className="milestone-task-checkbox"
                                checked={isCompleted || task.pending_operation === 'complete'}
                                disabled={isCompleted || task.pending_operation === 'complete'}
                                readOnly={isActive}
                                onClick={(e) => {
                                  if (isActive) {
                                    e.preventDefault();
                                    requestCompleteTask(task, milestone);
                                  }
                                }}
                                title={isActive ? 'Marcar como completada (la otra parte debe aprobar)' : isCompleted ? 'Completada' : 'Pendiente de aprobación'}
                                aria-label={isActive ? 'Solicitar marcar tarea como completada' : isCompleted ? 'Tarea completada' : 'Completar pendiente de aprobación'}
                              />
                            </span>
                            <div className="milestone-task-content">
                              <span className="milestone-task-title">
                                {task.pending_operation === 'edit' && task.pending_title != null ? task.pending_title : task.title}
                              </span>
                              {(task.description || (task.pending_operation === 'edit' && task.pending_description != null)) && (
                                <span className="milestone-task-desc">
                                  {task.pending_operation === 'edit' && task.pending_description != null ? task.pending_description : task.description}
                                </span>
                              )}
                              {statusText && (
                                <span className="milestone-task-status-badge">{statusText}</span>
                              )}
                            </div>
                            <div className="milestone-task-actions">
                              {canApproveTask(task) && (
                                <button
                                  type="button"
                                  className="btn-approve-operation btn-approve-task"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.dispatchEvent(new CustomEvent(taskApprovalModalEvent, { detail: { task, milestone } }));
                                  }}
                                >
                                  <FiClipboard className="mr-2" aria-hidden="true" />
                                  Revisar
                                </button>
                              )}
                              {isActive && (
                                <>
                                  <button
                                    type="button"
                                    className="btn-edit-task"
                                    onClick={() => openEditTaskModal(task, milestone)}
                                    title="Editar (la otra parte debe aprobar)"
                                  >
                                    <FiEdit2 aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-delete-task"
                                    onClick={() => requestDeleteTask(task, milestone)}
                                    title="Eliminar (la otra parte debe aprobar)"
                                  >
                                    <FiTrash2 aria-hidden="true" />
                                  </button>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    {(tasksByMilestone[milestone.id] || []).length === 0 && (
                      <p className="milestone-tasks-empty">No hay tareas en esta fase.</p>
                    )}
                  </div>

                  <div className="milestone-actions">
                    {/* Cliente: Pagar fase solo cuando el creativo haya confirmado la entrega */}
                    {isClient && milestone.amount > 0 && !isMilestonePaid(idx) && (milestone.status === 'completion_approved' || milestone.status === 'pending_completion_approval') && project?.status !== 'completed' && project?.status !== 'cancelled' && (
                      <button
                        type="button"
                        className="btn-pay-milestone"
                        onClick={() => payMilestone(milestone)}
                        aria-label={`Pagar fase ${milestone.title}`}
                      >
                        <FiCreditCard className="mr-2" aria-hidden="true" />
                        Pagar {milestone.amount != null ? `${Number(milestone.amount).toFixed(2)} €` : ''}
                      </button>
                    )}
                    {/* Pending approval actions (create/delete) */}
                    {canApproveOperation(milestone) && milestone.status !== 'pending_completion_approval' && (
                      <button
                        className="btn-approve-operation"
                        onClick={() => handleApprovalRequest(milestone)}
                      >
                        <FiClipboard className="mr-2" aria-hidden="true" />
                        Revisar
                      </button>
                    )}
                    
                    {/* Creator: request phase completion */}
                    {displayStatus.status === 'pending' && canCompleteMilestones && project?.status !== 'completed' && project?.status !== 'cancelled' && (
                      <button
                        className="btn-complete-milestone"
                        onClick={() => completeMilestone(milestone.id, milestone.title)}
                      >
                        <FiCheck className="mr-2" aria-hidden="true" />
                        Marcar como completado
                      </button>
                    )}
                    {/* Creative: confirm phase delivery */}
                    {canApproveOperation(milestone) && milestone.status === 'pending_completion_approval' && project?.status !== 'completed' && project?.status !== 'cancelled' && (
                      <button
                        className="btn-complete-milestone"
                        onClick={() => handleApprovalRequest(milestone)}
                      >
                        <FiCheck className="mr-2" aria-hidden="true" />
                        Confirmar entrega
                      </button>
                    )}
                    
                    {canCreateMilestones && displayStatus.status === 'pending' && !paid && (
                      <button 
                        className="btn-delete-milestone"
                        onClick={() => deleteMilestone(milestone.id, milestone.title)}
                      >
                        <FiTrash2 aria-hidden="true" /> Eliminar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Milestone Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content milestone-modal">
            <div className="modal-header milestone-modal-header">
              <div className="milestone-modal-title">
                <span className="milestone-modal-icon">+</span>
                <div>
                  <h3>Crear nueva fase</h3>
                  <p className="milestone-modal-subtitle">
                    Define claramente qué debe lograrse y cuándo para mantener el proyecto bajo control.
                  </p>
                </div>
              </div>
              <button 
                className="btn-close"
                onClick={() => setShowCreateModal(false)}
                aria-label="Cerrar creación de fase"
              >
                ✕
              </button>
            </div>

            <form onSubmit={createMilestone} className="milestone-form">
              <div className="milestone-form-section">
                <div className="form-group">
                  <label>Título de la fase *</label>
                  <input
                    type="text"
                    value={newMilestone.title}
                    onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                    placeholder="Ej: Diseño inicial aprobado por el cliente"
                    required
                  />
                  <p className="field-helper">
                    Sé específico y accionable. Esto aparecerá en el timeline y en los reportes.
                  </p>
                </div>

                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    value={newMilestone.description}
                    onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                    placeholder="Describe qué debe entregarse exactamente en esta fase (formatos, responsables, criterios de éxito)..."
                    rows="4"
                  />
                </div>
              </div>

              <div className="milestone-form-row">
                <div className="form-group">
                  <label>Fecha límite</label>
                  <input
                    type="date"
                    value={newMilestone.deadline}
                    onChange={(e) => setNewMilestone({ ...newMilestone, deadline: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="field-helper">
                    Usaremos esta fecha para priorizar tareas y recordatorios automáticos.
                  </p>
                </div>
              </div>

              <div className="modal-actions milestone-modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Crear fase
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && milestoneToApprove && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiClipboard aria-hidden="true" />
                Revisar Solicitud de Fase
              </h3>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowApprovalModal(false);
                  setMilestoneToApprove(null);
                  setApprovalComment('');
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="approval-details">
                <h4>Detalles de la Fase</h4>
                <p><strong>Título:</strong> {milestoneToApprove.title}</p>
                {milestoneToApprove.description && (
                  <p><strong>Descripción:</strong> {milestoneToApprove.description}</p>
                )}
                <p><strong>Fecha límite:</strong> {formatDate(milestoneToApprove.deadline)}</p>
                
                <div className="operation-info">
                  {milestoneToApprove.pending_operation === 'create' && (
                    <p className="flex items-center gap-2">
                      <strong>Operación:</strong>
                      <FiClipboard aria-hidden="true" />
                      Crear nueva fase
                    </p>
                  )}
                  {milestoneToApprove.pending_operation === 'delete' && (
                    <p className="flex items-center gap-2">
                      <strong>Operación:</strong>
                      <FiTrash2 aria-hidden="true" />
                      Eliminar fase
                    </p>
                  )}
                  {milestoneToApprove.pending_operation === 'complete' && (
                    <p><strong>Operación:</strong> ✅ El cliente ha solicitado que confirmes la entrega de esta fase</p>
                  )}
                </div>
              </div>
              
              <div className="approval-comment">
                <label>Comentario (opcional):</label>
                <textarea
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder="Agrega un comentario sobre tu decisión..."
                  rows="3"
                />
              </div>
            </div>
            
            <div className="modal-actions approval-modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setShowApprovalModal(false);
                  setMilestoneToApprove(null);
                  setApprovalComment('');
                }}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-reject" 
                onClick={() => approveMilestoneOperation('reject', approvalComment)}
              >
                <FiX className="mr-2" aria-hidden="true" />
                Rechazar
              </button>
              <button 
                type="button" 
                className="btn-approve" 
                onClick={() => approveMilestoneOperation('approve', approvalComment)}
              >
                <FiCheck className="mr-2" aria-hidden="true" />
                Aprobar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Milestone Payment Modal */}
      {showMilestonePaymentModal && milestonePaymentData && milestoneStripePromise && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header payment-modal-header">
              <h3 className="payment-modal-title">Pagar fase</h3>
              <button type="button" className="modal-close" onClick={() => { setShowMilestonePaymentModal(false); setMilestonePaymentData(null); }} aria-label="Cerrar">
                <FiX />
              </button>
            </div>
            <div className="payment-modal-summary">
              <p className="payment-modal-amount">
                Fase: <strong>{milestonePaymentData.milestone?.title}</strong>
              </p>
              <p className="payment-modal-context">
                Cantidad: <strong>{milestonePaymentData.amountEur?.toFixed(2)} €</strong>
              </p>
              {milestonePaymentData.founderDiscountPct > 0 && milestonePaymentData.originalAmountEur != null && (
                <div className="payment-modal-founder-discount">
                  <p style={{ margin: '8px 0 4px 0', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                    Subtotal: <span style={{ textDecoration: 'line-through' }}>{milestonePaymentData.originalAmountEur.toFixed(2)} €</span>
                  </p>
                  <p style={{ margin: '4px 0', color: '#FF5500', fontWeight: 700, fontSize: 14 }}>
                    Descuento Founder ({milestonePaymentData.founderDiscountPct}%):{' '}
                    −{(milestonePaymentData.originalAmountEur - milestonePaymentData.amountEur).toFixed(2)} €
                  </p>
                </div>
              )}
            </div>
            <div className="payment-modal-form-wrap">
              <Elements
                key={milestonePaymentData.clientSecret?.slice(0, 30) || milestonePaymentData.milestone?.id}
                stripe={milestoneStripePromise}
                options={{ clientSecret: milestonePaymentData.clientSecret }}
              >
                <MilestonePaymentForm
                  projectId={projectId}
                  milestoneId={milestonePaymentData.milestone?.id}
                  amountEur={milestonePaymentData.amountEur}
                  clientSecret={milestonePaymentData.clientSecret}
                  onSuccess={onMilestonePaymentSuccess}
                  onCancel={() => { setShowMilestonePaymentModal(false); setMilestonePaymentData(null); }}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {/* Delete Milestone Modal */}
      {showDeleteModal && milestoneToDelete && (
        <div className="modal-overlay">
          <div className="modal-content milestone-confirm-modal">
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiTrash2 aria-hidden="true" />
                Eliminar Fase
              </h3>
              <button 
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowDeleteModal(false);
                  setMilestoneToDelete(null);
                }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-confirm-text">
                ¿Estás seguro de que quieres solicitar la eliminación de la fase <strong>"{milestoneToDelete.title}"</strong>?
              </p>
              <p className="modal-confirm-hint">Esta solicitud será enviada al creativo para su aprobación.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setMilestoneToDelete(null);
                }}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-delete-milestone" 
                onClick={confirmDeleteMilestone}
              >
                <FiTrash2 className="mr-2" aria-hidden="true" />
                Solicitar Eliminación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal - renderizado en document.body para que no lo bloquee ningún contenedor */}
      {showCreateTaskModal && milestoneForNewTask && ReactDOM.createPortal(
        <div className="modal-overlay modal-overlay-create-task" onClick={(e) => e.target === e.currentTarget && setShowCreateTaskModal(false)}>
          <div className="modal-content milestone-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header milestone-modal-header">
              <div className="milestone-modal-title">
                <span className="milestone-modal-icon">+</span>
                <div>
                  <h3>Añadir tarea a «{milestoneForNewTask.title}»</h3>
                  <p className="milestone-modal-subtitle">
                    La otra parte deberá aprobar la tarea para que quede creada.
                  </p>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={() => { setShowCreateTaskModal(false); setMilestoneForNewTask(null); }} aria-label="Cerrar">✕</button>
            </div>
            <form onSubmit={createTask} className="milestone-form">
              <div className="milestone-form-section">
                <div className="form-group">
                  <label>Título de la tarea *</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Ej: Revisión de guion"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Detalles opcionales..."
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-actions milestone-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateTaskModal(false); setMilestoneForNewTask(null); }}>Cancelar</button>
                <button type="submit" className="btn-primary">Crear tarea</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && taskToEdit && (
        <div className="modal-overlay">
          <div className="modal-content milestone-modal">
            <div className="modal-header milestone-modal-header">
              <div className="milestone-modal-title">
                <span className="milestone-modal-icon"><FiEdit2 aria-hidden="true" /></span>
                <div>
                  <h3>Editar tarea</h3>
                  <p className="milestone-modal-subtitle">La otra parte deberá aprobar los cambios.</p>
                </div>
              </div>
              <button className="btn-close" onClick={() => { setShowEditTaskModal(false); setTaskToEdit(null); }} aria-label="Cerrar">✕</button>
            </div>
            <form onSubmit={saveEditTask} className="milestone-form">
              <div className="milestone-form-section">
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    value={editTaskForm.title}
                    onChange={(e) => setEditTaskForm({ ...editTaskForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    value={editTaskForm.description}
                    onChange={(e) => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-actions milestone-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowEditTaskModal(false); setTaskToEdit(null); }}>Cancelar</button>
                <button type="submit" className="btn-primary">Guardar cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Approval Modal - renderizado en document.body para que no lo bloquee ningún contenedor */}
      {showTaskApprovalModal && taskToApprove && ReactDOM.createPortal(
        <div className="modal-overlay modal-overlay-create-task" onClick={(e) => { if (e.target === e.currentTarget) { setShowTaskApprovalModal(false); setTaskToApprove(null); setTaskApprovalComment(''); } }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2"><FiClipboard aria-hidden="true" /> Revisar tarea</h3>
              <button type="button" className="btn-close" onClick={() => { setShowTaskApprovalModal(false); setTaskToApprove(null); setTaskApprovalComment(''); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="approval-details">
                <h4>Tarea</h4>
                <p><strong>Título:</strong> {taskToApprove.task.pending_operation === 'edit' && taskToApprove.task.pending_title != null ? taskToApprove.task.pending_title : taskToApprove.task.title}</p>
                {(taskToApprove.task.description || taskToApprove.task.pending_description) && (
                  <p><strong>Descripción:</strong> {taskToApprove.task.pending_operation === 'edit' && taskToApprove.task.pending_description != null ? taskToApprove.task.pending_description : taskToApprove.task.description}</p>
                )}
                <p><strong>Fase:</strong> {taskToApprove.milestone.title}</p>
                <div className="operation-info">
                  {taskToApprove.task.pending_operation === 'create' && <p><strong>Operación:</strong> Crear tarea</p>}
                  {taskToApprove.task.pending_operation === 'edit' && <p><strong>Operación:</strong> Editar tarea</p>}
                  {taskToApprove.task.pending_operation === 'delete' && <p><strong>Operación:</strong> Eliminar tarea</p>}
                  {taskToApprove.task.pending_operation === 'complete' && <p><strong>Operación:</strong> Marcar como completada</p>}
                </div>
              </div>
              <div className="approval-comment">
                <label>Comentario (opcional):</label>
                <textarea value={taskApprovalComment} onChange={(e) => setTaskApprovalComment(e.target.value)} placeholder="Comentario sobre tu decisión..." rows="3" />
              </div>
            </div>
            <div className="modal-actions approval-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowTaskApprovalModal(false); setTaskToApprove(null); setTaskApprovalComment(''); }}>Cancelar</button>
              <button type="button" className="btn-reject" onClick={() => approveTaskOperation('reject')}><FiX className="mr-2" aria-hidden="true" /> Rechazar</button>
              <button type="button" className="btn-approve" onClick={() => approveTaskOperation('approve')}><FiCheck className="mr-2" aria-hidden="true" /> Aprobar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Task Confirmation Modal */}
      {showDeleteTaskModal && taskToDelete && (
        <div className="modal-overlay">
          <div className="modal-content milestone-confirm-modal">
            <div className="modal-header">
              <h3 className="flex items-center gap-2"><FiTrash2 aria-hidden="true" /> Eliminar tarea</h3>
              <button type="button" className="modal-close" onClick={() => { setShowDeleteTaskModal(false); setTaskToDelete(null); }} aria-label="Cerrar">×</button>
            </div>
            <div className="modal-body">
              <p className="modal-confirm-text">
                ¿Solicitar la eliminación de la tarea <strong>«{taskToDelete.task.title}»</strong>? La otra parte deberá aprobarla.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => { setShowDeleteTaskModal(false); setTaskToDelete(null); }}>Cancelar</button>
              <button type="button" className="btn-delete-milestone" onClick={confirmDeleteTask}><FiTrash2 className="mr-2" aria-hidden="true" /> Solicitar eliminación</button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Task Confirmation Modal */}
      {showCompleteTaskModal && taskToComplete && (
        <div className="modal-overlay">
          <div className="modal-content milestone-confirm-modal">
            <div className="modal-header">
              <h3 className="flex items-center gap-2"><FiCheck aria-hidden="true" /> Marcar tarea como completada</h3>
              <button type="button" className="modal-close" onClick={() => { setShowCompleteTaskModal(false); setTaskToComplete(null); }} aria-label="Cerrar">×</button>
            </div>
            <div className="modal-body">
              <p className="modal-confirm-text">
                ¿Solicitar marcar como completada la tarea <strong>«{taskToComplete.task.title}»</strong>? La otra parte deberá aprobarla.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => { setShowCompleteTaskModal(false); setTaskToComplete(null); }}>Cancelar</button>
              <button type="button" className="btn-complete-milestone" onClick={confirmCompleteTask}><FiCheck className="mr-2" aria-hidden="true" /> Solicitar completar</button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Milestone Modal (creator marks phase as complete, requests creative confirmation) */}
      {showCompleteMilestoneModal && milestoneToComplete && (
        <div className="modal-overlay">
          <div className="modal-content complete-milestone-modal">
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiCheck aria-hidden="true" />
                Completar Fase
              </h3>
              <button 
                type="button"
                className="modal-close"
                onClick={() => {
                  setShowCompleteMilestoneModal(false);
                  setMilestoneToComplete(null);
                }}
                disabled={completingMilestone}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-confirm-text">
                ¿Estás seguro de que quieres marcar como completada la fase <strong>"{milestoneToComplete.title}"</strong>?
              </p>
              <p className="modal-confirm-hint">Se enviará una solicitud de confirmación al creativo para que valide la entrega.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setShowCompleteMilestoneModal(false);
                  setMilestoneToComplete(null);
                }}
                disabled={completingMilestone}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-complete-milestone" 
                onClick={confirmCompleteMilestone}
                disabled={completingMilestone}
              >
                {completingMilestone ? (
                  <>
                    <span className="loading-spinner" style={{ width: 16, height: 16 }} aria-hidden="true" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiCheck className="mr-2" aria-hidden="true" />
                    Enviar solicitud
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

// Compact milestones timeline for project overview
const MilestonesTimeline = ({ projectId }) => {
  const { token } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}/milestones`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMilestones(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching milestones for overview:', error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchMilestones();
    }
  }, [projectId, token]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="milestones-timeline milestones-timeline-loading-inline">
        <LoadingSpinner size="small" text="Cargando fases..." />
      </div>
    );
  }

  if (!milestones.length) {
    return (
      <div className="milestones-timeline empty">
        <p className="milestones-timeline-title">Progreso del proyecto</p>
        <span className="milestones-timeline-empty">
          Todavía no hay fases definidas para este proyecto.
        </span>
      </div>
    );
  }

  const completedCount = milestones.filter(
    (m) => m.status === 'completed' && m.approved_by
  ).length;
  const progress = Math.round((completedCount / milestones.length) * 100);

  return (
    <div className="milestones-timeline">
      <div className="milestones-timeline-header">
        <p className="milestones-timeline-title">Progreso del proyecto</p>
        <span className="milestones-timeline-progress">
          {progress}% completado
        </span>
      </div>
      <div className="milestones-timeline-bar">
        <div
          className="milestones-timeline-bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="milestones-timeline-steps">
        {milestones.map((milestone) => {
          const isCompleted = milestone.status === 'completed' && milestone.approved_by;
          const isPending = !isCompleted;

          return (
            <div
              key={milestone.id}
              className={`milestones-timeline-step ${
                isCompleted ? 'completed' : isPending ? 'pending' : ''
              }`}
            >
              <div className="milestones-timeline-dot" />
              <div className="milestones-timeline-content">
                <p className="milestones-timeline-step-title">{milestone.title}</p>
                <p className="milestones-timeline-step-date">
                  {formatDate(milestone.deadline)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Payments Component - Histórico y pendientes de pago
const PaymentsView = ({ projectId, onBack, projectTitle, isCreator = false, onPaymentSuccess, projectStatus }) => {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayPendingModal, setShowPayPendingModal] = useState(false);
  const [payPendingData, setPayPendingData] = useState(null);
  const [payPendingStripePromise, setPayPendingStripePromise] = useState(null);
  const [payingMilestoneId, setPayingMilestoneId] = useState(null);
  const [requestInvoiceLoading, setRequestInvoiceLoading] = useState(null);
  const [requestInvoiceSuccess, setRequestInvoiceSuccess] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const fetchPayments = useCallback(async () => {
    if (!projectId || !token) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  if (loading) {
    return (
      <div className="payments-container">
        <div className="payments-header">
          <h2><FiCreditCard /> Pagos</h2>
        </div>
        <div className="payments-loading"><LoadingSpinner size="small" text="Cargando pagos..." /></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="payments-container">
        <div className="payments-header">
          <h2><FiCreditCard /> Pagos</h2>
        </div>
        <div className="payments-empty"><p>No se pudieron cargar los datos de pago.</p></div>
      </div>
    );
  }

  const { payments, schedule, total_budget, total_paid, total_pending, payment_conditions } = data;

  const requestInvoiceFromProject = async (payment) => {
    if (!isCreator || !token || !projectId) return;
    const key = payment?.id || 'global';
    setRequestInvoiceSuccess(null);
    setRequestInvoiceLoading(key);
    try {
      await axios.post(
        `${API}/profile/invoices/request-invoice`,
        { project_id: projectId, payment_id: payment?.id || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequestInvoiceSuccess('Solicitud enviada. El creativo recibirá un mensaje en el chat del proyecto y un correo.');
      addToast({ type: 'success', title: 'Solicitud enviada', description: 'El creativo ha recibido la solicitud de factura por chat y por correo.' });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al enviar la solicitud.';
      addToast({ type: 'error', title: 'Error', description: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setRequestInvoiceLoading(null);
    }
  };

  const handlePayScheduleItem = async (s) => {
    if (!isCreator || !token || !projectId || s.paid || projectStatus === 'completed' || projectStatus === 'cancelled') return;
    const type = s.type;
    let milestoneId;
    if (typeof type === 'string' && type.startsWith('milestone_')) {
      milestoneId = type.replace('milestone_', '');
    } else if (s.milestone_id) {
      milestoneId = s.milestone_id;
    } else {
      return;
    }
    setPayingMilestoneId(milestoneId);
    try {
      const piRes = await axios.post(`${API}/stripe/create-milestone-payment-intent`, {
        project_id: projectId,
        milestone_id: milestoneId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (!piRes.data.requires_payment || !piRes.data.client_secret) {
        setPayingMilestoneId(null);
        return;
      }
      let promise = payPendingStripePromise;
      if (!promise) {
        const configRes = await axios.get(`${API}/stripe/config`);
        const pk = configRes.data?.publishableKey;
        if (pk) {
          promise = loadStripe(pk);
          setPayPendingStripePromise(promise);
        }
      }
      if (promise) {
        setPayPendingData({
          milestoneId,
          clientSecret: piRes.data.client_secret,
          amountEur: piRes.data.amount_eur,
          originalAmountEur: piRes.data.original_amount_eur,
          founderDiscountPct: piRes.data.founder_discount_pct,
          milestoneTitle: piRes.data.milestone_title || s?.label || 'Pago',
        });
        setShowPayPendingModal(true);
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      const msg = err.response?.data?.detail || err.message || 'No se pudo preparar el pago. Inténtalo de nuevo.';
      addToast({ type: 'error', title: 'Error al preparar el pago', description: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setPayingMilestoneId(null);
    }
  };

  const canPayScheduleItem = (s) => isCreator && !s.paid && (
    (typeof s.type === 'string' && s.type.startsWith('milestone_')) || s.milestone_id
  ) && projectStatus !== 'completed' && projectStatus !== 'cancelled';

  const onPayPendingSuccess = () => {
    setShowPayPendingModal(false);
    setPayPendingData(null);
    fetchPayments();
    onPaymentSuccess?.();
  };

  return (
    <div className="payments-container">
      <div className="payments-header">
        <div className="payments-title">
          <h2 className="flex items-center gap-2">
            <FiCreditCard aria-hidden="true" />
            Pagos {projectTitle ? `- ${projectTitle}` : ''}
          </h2>
          <p>Histórico y pendientes de pago del proyecto</p>
        </div>
      </div>

      <div className="payments-summary">
        <div className="payments-summary-row">
          <span className="payments-summary-label">Presupuesto total</span>
          <span className="payments-summary-value">{total_budget?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
        </div>
        <div className="payments-summary-row payments-summary-paid">
          <span className="payments-summary-label">Pagado</span>
          <span className="payments-summary-value">{total_paid?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
        </div>
        <div className="payments-summary-row payments-summary-pending">
          <span className="payments-summary-label">Pendiente</span>
          <span className="payments-summary-value">{total_pending?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
        </div>
        {payment_conditions && (
          <p className="payments-conditions">Condiciones: {payment_conditions}</p>
        )}
      </div>

      <div className="payments-sections">
        <section className="payments-section">
          <h3><FiCheckCircle /> Pagos realizados</h3>
          {payments.length === 0 ? (
            <div className="payments-list-empty">
              <p>Aún no se ha registrado ningún pago.</p>
            </div>
          ) : (
            <div className="payments-list">
              {payments.map((p) => (
                <div key={p.id} className="payment-item payment-item-paid">
                  <div className="payment-item-main">
                    <span className="payment-item-label">{p.description || 'Pago'}</span>
                    <span className="payment-item-amount">{p.amount_eur?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <span className="payment-item-date">{formatDate(p.created_at)}</span>
                  {isCreator && (
                    <div className="payment-item-right">
                      <button
                        type="button"
                        className="btn-inline btn-invoice-download"
                        onClick={() => requestInvoiceFromProject(p)}
                        disabled={requestInvoiceLoading !== null}
                        title="Solicitar factura al creativo (recibirá un mensaje en el chat y un correo)"
                        aria-label={`Solicitar factura por ${p.amount_eur?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`}
                      >
                        {requestInvoiceLoading === p.id ? (
                          'Enviando...'
                        ) : (
                          <>
                            <FiSend aria-hidden="true" /> Solicitar factura
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="payments-section">
          <h3><FiClock /> Estado por concepto</h3>
          <div className="payments-schedule">
            {schedule.map((s, i) => (
              <div key={i} className={`payment-item ${s.paid ? 'payment-item-paid' : 'payment-item-pending'}`}>
                <div className="payment-item-main">
                  <span className="payment-item-label">
                    {s.label}
                    {s.paid && <FiCheckCircle className="payment-item-check" />}
                  </span>
                  <span className="payment-item-amount">{s.amount?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="payment-item-right">
                  <span className="payment-item-status">
                    {s.paid ? 'Pagado' : 'Pendiente'}
                  </span>
                  {canPayScheduleItem(s) && (
                    <button
                      type="button"
                      className="btn-pay-schedule-item"
                      onClick={() => handlePayScheduleItem(s)}
                      disabled={payingMilestoneId !== null}
                      aria-label={`Pagar ${s.label}`}
                    >
                      <FiCreditCard aria-hidden="true" />
                      {payingMilestoneId === (s.milestone_id || (typeof s.type === 'string' ? s.type.replace('milestone_', '') : '')) ? 'Preparando...' : 'Pagar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Modal pagar pendiente (primer concepto no pagado) */}
      {showPayPendingModal && payPendingData && payPendingStripePromise && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header payment-modal-header">
              <h3 className="payment-modal-title">Pagar pendiente</h3>
              <button type="button" className="modal-close" onClick={() => { setShowPayPendingModal(false); setPayPendingData(null); }} aria-label="Cerrar">
                <FiX />
              </button>
            </div>
            <div className="payment-modal-summary">
              <p className="payment-modal-amount">
                Concepto: <strong>{payPendingData.milestoneTitle}</strong>
              </p>
              <p className="payment-modal-context">
                Cantidad: <strong>{payPendingData.amountEur?.toFixed(2)} €</strong>
              </p>
              {payPendingData.founderDiscountPct > 0 && payPendingData.originalAmountEur != null && (
                <div className="payment-modal-founder-discount">
                  <p style={{ margin: '8px 0 4px 0', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                    Subtotal: <span style={{ textDecoration: 'line-through' }}>{payPendingData.originalAmountEur.toFixed(2)} €</span>
                  </p>
                  <p style={{ margin: '4px 0', color: '#FF5500', fontWeight: 700, fontSize: 14 }}>
                    Descuento Founder ({payPendingData.founderDiscountPct}%):{' '}
                    −{(payPendingData.originalAmountEur - payPendingData.amountEur).toFixed(2)} €
                  </p>
                </div>
              )}
            </div>
            <div className="payment-modal-form-wrap">
              <Elements
                key={payPendingData.clientSecret?.slice(0, 30) || payPendingData.milestoneId}
                stripe={payPendingStripePromise}
                options={{ clientSecret: payPendingData.clientSecret }}
              >
                <MilestonePaymentForm
                  projectId={projectId}
                  milestoneId={payPendingData.milestoneId}
                  amountEur={payPendingData.amountEur}
                  clientSecret={payPendingData.clientSecret}
                  onSuccess={onPayPendingSuccess}
                  onCancel={() => { setShowPayPendingModal(false); setPayPendingData(null); }}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Deliverables Component
const Deliverables = ({ projectId, onBack }) => {
  const { user, token } = useAuth();
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [project, setProject] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchDeliverables();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchDeliverables = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/deliverables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliverables(response.data);
    } catch (error) {
      console.error('Error fetching deliverables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('description', `Entregable subido: ${file.name}`);

        const response = await axios.post(`${API}/projects/${projectId}/deliverables`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setDeliverables([...deliverables, response.data]);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="deliverables-container">
      <div className="deliverables-header">
        <div className="deliverables-title">
          <h2 className="flex items-center gap-2">
            <FiPaperclip aria-hidden="true" />
            Entregables - {project?.title || '…'}
          </h2>
          <p>Gestiona los archivos del proyecto</p>
        </div>
      </div>

      {loading ? (
        <div className="deliverables-loading-inline">
          <LoadingSpinner size="small" text="Cargando entregables..." />
        </div>
      ) : (
      <div className="deliverables-main">
        {/* Upload Section */}
        <div className="deliverables-upload-col">
          <div className="upload-section">
            <div className="upload-area">
              <input
                type="file"
                id="fileUpload"
                multiple
                onChange={handleFileUpload}
                className="file-input"
                disabled={uploading}
              />
              <label htmlFor="fileUpload" className="upload-label">
                <div className="upload-icon" aria-hidden="true">
                  <FiFolder />
                </div>
                <h3>{uploading ? 'Subiendo archivos...' : 'Subir Entregables'}</h3>
                <p>Arrastra archivos aquí o haz clic para seleccionar</p>
                <small>Formatos soportados: PDF, DOC, JPG, PNG, ZIP</small>
              </label>
            </div>
          </div>
        </div>

        {/* Deliverables List */}
        <div className="deliverables-list-col">
      <div className="deliverables-list">
        <h3 className="flex items-center gap-2">
          <FiClipboard aria-hidden="true" />
          Archivos Subidos ({deliverables.length})
        </h3>
        
        {deliverables.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">
              <FiPaperclip />
            </div>
            <h3>No hay entregables</h3>
            <p>Sube archivos para compartir el progreso del proyecto</p>
          </div>
        ) : (
          <div className="deliverables-grid">
            {deliverables.map(deliverable => (
              <div key={deliverable.id} className="deliverable-card">
                <div className="deliverable-icon">
                  <FiFileText aria-hidden="true" />
                </div>
                <div className="deliverable-info">
                  <h4>{deliverable.title}</h4>
                  <p>{deliverable.description}</p>
                  <div className="deliverable-meta">
                    <span className="flex items-center gap-1">
                      <FiMaximize aria-hidden="true" />
                      {formatFileSize(deliverable.file_size)}
                    </span>
                    <span className="flex items-center gap-1">
                      <FiCalendar aria-hidden="true" />
                      {formatDate(deliverable.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <FiUser aria-hidden="true" />
                      {deliverable.uploader_name}
                    </span>
                  </div>
                </div>
                <div className="deliverable-actions">
                  <a 
                    href={getUploadUrl(deliverable.file_url)} 
                    download 
                    className="btn-download"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiDownload className="mr-2" aria-hidden="true" />
                    Descargar
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </div>
      </div>
      )}
    </div>
  );
};

// Cache key: al cerrar el recordatorio de recompensas con la X, no volver a mostrarlo hasta 24h
const REWARDS_REMINDER_DISMISS_KEY = (userId) => `vissory_rewards_reminder_dismissed_until_${userId || 'guest'}`;
const REWARDS_REMINDER_HIDE_MS = 24 * 60 * 60 * 1000; // 24 horas

// Banner de sugerencias - mismo aspecto en todas las tabs (icono + texto + Ir a Sugerencias)
const SuggestionsBanner = ({ onGoToSuggestions }) => {
  if (!onGoToSuggestions) return null;
  return (
    <div className="dashboard-sugerencias-banner">
      <div className="dashboard-sugerencias-banner-content">
        <div className="dashboard-sugerencias-banner-icon">
          <FiMessageCircle size={28} aria-hidden="true" />
        </div>
        <div className="dashboard-sugerencias-banner-text">
          <h4>¿Tienes ideas para mejorar Vissory?</h4>
          <p>Comparte tu sugerencia, vota las de la comunidad y sigue las respuestas de Vissory.</p>
        </div>
        <button
          type="button"
          className="btn-primary dashboard-sugerencias-banner-cta"
          onClick={onGoToSuggestions}
        >
          <FiSend size={18} aria-hidden="true" />
          Ir a Sugerencias
        </button>
      </div>
    </div>
  );
};

// Level Up Engagement Component - Solo para creativos
const LevelUpPrompt = ({ variant = 'default', showClose = true, onNavigateToRewards }) => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Sincronizar con localStorage cuando tengamos user: si estamos en la ventana de 24h, ocultar
  useEffect(() => {
    if (!user?.id) return;
    try {
      const key = REWARDS_REMINDER_DISMISS_KEY(user.id);
      const until = localStorage.getItem(key);
      if (!until) return;
      const ts = parseInt(until, 10);
      if (Number.isFinite(ts) && Date.now() < ts) setDismissed(true);
    } catch (_) {}
  }, [user?.id]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      const key = REWARDS_REMINDER_DISMISS_KEY(user?.id);
      localStorage.setItem(key, String(Date.now() + REWARDS_REMINDER_HIDE_MS));
    } catch (_) {}
  }, [user?.id]);

  // Solo mostrar a creativos
  if (!user || user.role !== 'creative' || dismissed) return null;
  
  // Determinar el nivel actual y mensajes según el nivel
  const currentLevel = user.season_level || 1;
  const levelName = user.season_level_name || 'Principiante';
  
  // Mensajes variados según el contexto/variante
  const messages = {
    dashboard: {
      title: '¡Sube de nivel y destaca!',
      subtitle: 'Sube de nivel para aparecer antes cuando te busquen',
      highlight: 'Los creativos Élite aparecen siempre primeros en la lista ⚡',
      cta: 'Ver cómo ganar XP →',
      icon: <FiZap />
    },
    briefs: {
      title: '¿Quieres más oportunidades?',
      subtitle: 'Completa proyectos y sube de nivel para destacar más',
      highlight: 'Los Élite aparecen primero cuando los clientes buscan creativos ⚡',
      cta: 'Ver recompensas →',
      icon: <FiTarget />
    },
    proposals: {
      title: '¡Haz que tus aplicaciones destaquen!',
      subtitle: 'Los creativos de mayor nivel aparecen antes en la lista',
      highlight: 'Nivel Élite = tu perfil siempre primero ⚡',
      cta: 'Descubre cómo subir →',
      icon: <FiMail />
    },
    projects: {
      title: '¡Cada proyecto te acerca al Élite!',
      subtitle: 'Completa proyectos con éxito para ganar XP y subir de nivel',
      highlight: 'Más XP = más visibilidad y mejor posición en la lista ⚡',
      cta: 'Ver mi progreso →',
      icon: <FiActivity />
    },
    'featured-creatives': {
      title: '¡Tú también apareces aquí!',
      subtitle: 'Todos los creativos son visibles; sube de nivel para destacar más',
      highlight: 'Nivel Élite = apareces siempre entre los primeros ⚡',
      cta: 'Ver cómo llegar →',
      icon: <FiStar />
    },
    chats: {
      title: '¡Buenas conversaciones = más XP!',
      subtitle: 'Completa proyectos exitosos para subir de nivel',
      highlight: 'Cada proyecto completado te acerca al siguiente nivel',
      cta: 'Ver mi progreso →',
      icon: <FiMessageCircle />
    },
    profile: {
      title: 'Tu nivel actual: ' + levelName,
      subtitle: 'Mejora tu perfil y completa proyectos para subir',
      highlight: 'Los creativos Élite obtienen el badge destacado en la app ⚡',
      cta: 'Ver recompensas →',
      icon: <FiStar />
    },
    sidebar: {
      title: 'Nivel ' + currentLevel,
      subtitle: levelName,
      cta: '¡Sigue subiendo!',
      icon: <FiZap />
    },
    default: {
      title: '¡Sube de nivel!',
      subtitle: 'Sube de nivel para destacar más y aparecer antes',
      highlight: 'Los Élite aparecen siempre primeros en la lista ⚡',
      cta: 'Ver recompensas →',
      icon: <FiZap />
    }
  };
  
  const content = messages[variant] || messages.default;
  
  // Variante compacta para sidebar
  if (variant === 'sidebar') {
    return (
      <motion.div 
        className="level-up-prompt level-up-sidebar"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
      >
        <div className="level-up-sidebar-icon">{content.icon}</div>
        <div className="level-up-sidebar-content">
          <span className="level-up-sidebar-level">{content.title}</span>
          <span className="level-up-sidebar-name">{content.subtitle}</span>
        </div>
        <div className="level-up-sidebar-progress">
          <div 
            className="level-up-sidebar-progress-bar" 
            style={{ width: `${Math.min((user.season_xp || 0) % 100, 100)}%` }}
          />
        </div>
      </motion.div>
    );
  }
  
  return (
    <motion.div 
      className={`level-up-prompt level-up-${variant}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="level-up-icon">{content.icon}</div>
      <div className="level-up-content">
        <h4 className="level-up-title">{content.title}</h4>
        <p className="level-up-subtitle">{content.subtitle}</p>
        {content.highlight && (
          <button 
            className="level-up-highlight-btn"
            onClick={onNavigateToRewards}
          >
            {content.highlight}
          </button>
        )}
        <button 
          className="level-up-cta-btn"
          onClick={onNavigateToRewards}
        >
          {content.cta}
        </button>
      </div>
      {showClose && (
        <button 
          className="level-up-close" 
          onClick={handleDismiss}
          aria-label="Cerrar"
        >
          <FiX />
        </button>
      )}
    </motion.div>
  );
};

const DashboardPostComposer = ({ token, user }) => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async () => {
    const text = content.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const api = API;
      const fd = new FormData();
      fd.append('content', text);
      if (image) fd.append('image', image);
      await axios.post(`${api}/posts`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setContent('');
      setImage(null);
      setImagePreview(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { /* silently */ }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div
      className="dashboard-post-composer-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55 }}
    >
      <div className="dashboard-post-composer-header">
        <h3 className="dashboard-post-composer-title">
          <FiEdit3 className="dashboard-post-composer-icon" aria-hidden="true" />
          Publicar en tu feed
        </h3>
        <p className="dashboard-post-composer-subtitle">Comparte actualizaciones, proyectos o ideas con tu comunidad</p>
      </div>
      <div className="pp-feed-composer" style={{ borderRadius: 12 }}>
        <div className="pp-feed-composer-avatar">
          {user?.profile_image ? (
            <img src={user.profile_image.startsWith('http') ? user.profile_image : `${UPLOAD_BASE}${user.profile_image}`} alt="" onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} />
          ) : (
            <FiUser />
          )}
        </div>
        <div className="pp-feed-composer-body">
          <textarea
            className="pp-feed-composer-input"
            placeholder="¿Qué quieres compartir?"
            value={content}
            onChange={e => setContent(e.target.value)}
            maxLength={5000}
            rows={3}
          />
          {imagePreview && (
            <div className="pp-feed-composer-img-preview">
              <img src={imagePreview} alt="Preview" />
              <button type="button" className="pp-feed-composer-img-remove" onClick={() => { setImage(null); setImagePreview(null); }}>
                <FiX />
              </button>
            </div>
          )}
          <div className="pp-feed-composer-actions">
            <button type="button" className="pp-feed-composer-img-btn" onClick={() => fileRef.current?.click()}>
              <FiImage /> Imagen
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileRef}
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) {
                  setImage(f);
                  setImagePreview(URL.createObjectURL(f));
                }
              }}
            />
            <span className="pp-feed-composer-chars">{content.length}/5000</span>
            {success && <span style={{ color: '#22c55e', fontSize: '0.82rem', fontWeight: 600 }}>Publicado correctamente</span>}
            <button
              type="button"
              className="pp-feed-composer-submit"
              disabled={!content.trim() || submitting}
              onClick={handleSubmit}
            >
              {submitting ? <FiLoader className="spin" /> : <><FiSend /> Publicar</>}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Dashboard = ({ navigateToProject, onStatsLoaded, onActiveProjectsLoaded, onNavigateToRewards, onNavigateToCreateBrief, onNavigateToSuggestions }) => {
  const { user, token } = useAuth();
  const { getCached, setCached, isCacheValid } = useCache();
  const cachedStats = getCached('dashboard_stats');
  const cachedProjects = getCached('dashboard_projects');
  const [stats, setStats] = useState(cachedStats || {});
  const [activeProjects, setActiveProjects] = useState(cachedProjects || []);
  // Solo mostrar loading si no hay datos cacheados
  const [loading, setLoading] = useState(!cachedStats || Object.keys(cachedStats).length === 0);
  const [projectsLoading, setProjectsLoading] = useState(!cachedProjects || cachedProjects.length === 0);

  useEffect(() => {
    // Siempre intentar cargar, pero solo mostrar loading si no hay datos
    fetchStats();
    fetchActiveProjects();
    
    // Notificar con datos cacheados inmediatamente si existen
    if (cachedStats && onStatsLoaded) onStatsLoaded(cachedStats);
    if (cachedProjects && onActiveProjectsLoaded) onActiveProjectsLoaded(cachedProjects.slice(0, 3));
  }, []);

  const fetchStats = async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid('dashboard_stats')) {
      setLoading(false);
      return;
    }
    // Solo mostrar loading si no hay datos
    if (Object.keys(stats).length === 0) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setCached('dashboard_stats', response.data);
      if (onStatsLoaded) {
        onStatsLoaded(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProjects = async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid('dashboard_projects')) {
      setProjectsLoading(false);
      return;
    }
    // Solo mostrar loading si no hay datos
    if (activeProjects.length === 0) {
      setProjectsLoading(true);
    }
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar solo proyectos activos (incluyendo pending_completion)
      const active = response.data
        .filter(project => ['active', 'under_review', 'pending_completion', 'pending_payment'].includes(project.status))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const projectsToShow = active.slice(0, 10);
      setActiveProjects(projectsToShow);
      setCached('dashboard_projects', projectsToShow);
      if (onActiveProjectsLoaded) {
        onActiveProjectsLoaded(active.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching active projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <LoadingSpinner size="large" text="Cargando estadísticas..." />
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome Header */}
      <motion.div 
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="dashboard-greeting">
          <h2>
            ¡Hola, {user.name}!
          </h2>
          <p className="dashboard-subtitle">Aquí tienes un resumen de tu actividad y análisis visual</p>
        </div>
        {user.role === 'creator' && (stats.total_briefs === 0 || !stats.total_briefs) && (
          <motion.button
            className="dashboard-cta"
            onClick={() => onNavigateToCreateBrief && onNavigateToCreateBrief()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiPlus className="mr-2" aria-hidden="true" />
            Crear tu primer proyecto
          </motion.button>
        )}
      </motion.div>

      {/* Level Up Prompt for Creatives */}
      {user.role === 'creative' && <LevelUpPrompt variant="dashboard" onNavigateToRewards={onNavigateToRewards} />}

      {/* Layout: métricas a la izquierda, proyectos activos a la derecha */}
      <div className="dashboard-main-grid">
        {/* Stats Grid - Enhanced cards */}
        <div className="stats-grid">
          {user.role === 'creator' ? (
          <>
            <motion.div 
              className="stat-card stat-card-briefs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="stat-content">
                <h3>{stats.total_briefs || 0}</h3>
                <p>Briefs Creados</p>
                {stats.total_briefs > 0 && (
                  <span className="stat-trend">+{stats.total_briefs} este mes</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-proposals"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="stat-content">
                <h3>{stats.total_proposals || 0}</h3>
                <p>Aplicaciones Recibidas</p>
                {stats.total_proposals > 0 && (
                  <span className="stat-trend">{Math.round((stats.accepted_proposals || 0) / stats.total_proposals * 100)}% aceptadas</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-projects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="stat-content">
                <h3>{stats.active_projects || 0}</h3>
                <p>Proyectos Activos</p>
                {stats.active_projects > 0 && (
                  <span className="stat-trend">En progreso</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-investment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="stat-content">
                <h3>{stats.total_spent || 0}€</h3>
                <p>Total Invertido</p>
                {stats.total_spent > 0 && (
                  <span className="stat-trend">Promedio: {Math.round(stats.total_spent / (stats.completed_projects || 1))}€/proyecto</span>
                )}
              </div>
            </motion.div>
          </>
        ) : (
          <>
            <motion.div 
              className="stat-card stat-card-proposals"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="stat-content">
                <h3>{stats.total_proposals || 0}</h3>
                <p>Aplicaciones Enviadas</p>
                {stats.total_proposals > 0 && (
                  <span className="stat-trend">{Math.round((stats.accepted_proposals || 0) / stats.total_proposals * 100)}% tasa de éxito</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-accepted"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="stat-content">
                <h3>{stats.accepted_proposals || 0}</h3>
                <p>Aplicaciones Aceptadas</p>
                {stats.accepted_proposals > 0 && (
                  <span className="stat-trend">¡Excelente trabajo!</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-rating"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="stat-content">
                <h3>{stats.average_rating ? stats.average_rating.toFixed(1) : '0.0'}</h3>
                <p>Calificación Promedio</p>
                {stats.average_rating > 0 && (
                  <span className="stat-trend">{stats.total_reviews || 0} reviews</span>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="stat-card stat-card-earnings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="stat-content">
                <h3>{stats.total_earnings || 0}€</h3>
                <p>Total Ganado</p>
                {stats.total_earnings > 0 && (
                  <span className="stat-trend">Promedio: {Math.round(stats.total_earnings / (stats.completed_projects || 1))}€/proyecto</span>
                )}
              </div>
            </motion.div>
          </>
          )}
        </div>

        {/* Proyectos activos (columna derecha) - siempre visible con título y subtítulo */}
        <motion.div 
          className="dashboard-projects-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="dashboard-projects-header">
            <div>
              <h3 className="dashboard-projects-title">
                <FiActivity className="dashboard-projects-icon" aria-hidden="true" />
                Proyectos Activos
              </h3>
              <p className="dashboard-projects-subtitle">Accede rápidamente a tus proyectos en curso</p>
            </div>
            {activeProjects.length > 0 && (
              <button 
                className="dashboard-view-all"
                onClick={() => navigateToProject && navigateToProject(null, 'list')}
              >
                Ver todos
                <FiArrowRight className="ml-2" aria-hidden="true" />
              </button>
            )}
          </div>
          
          {projectsLoading ? (
            <div className="dashboard-projects-empty">
              <p>Cargando proyectos...</p>
            </div>
          ) : activeProjects.length === 0 ? (
            <div className="dashboard-projects-empty">
              <p>No hay proyectos activos</p>
            </div>
          ) : (
            <div className="dashboard-projects-grid">
              {activeProjects.slice(0, 2).map(project => (
                <motion.div
                  key={project.id}
                  className="dashboard-project-card"
                  onClick={() => navigateToProject && navigateToProject(project.id, 'list')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="dashboard-project-header">
                    <h4 className="dashboard-project-title">{project.title}</h4>
                    <span className="dashboard-project-status active">Activo</span>
                  </div>
                  
                  <div className="dashboard-project-info">
                    <div className="dashboard-project-user">
                      <div className="dashboard-project-avatar">
                        <img src={getAvatarUrl(project?.other_user?.profile_image, project?.other_user_name)} alt={project?.other_user_name} />
                      </div>
                      <div>
                        <span className="dashboard-project-label">
                          {project.is_creator ? 'Creativo:' : 'Cliente:'}
                        </span>
                        <span className="dashboard-project-name">{project.other_user_name}</span>
                      </div>
                    </div>
                    
                    {project.budget && (
                      <div className="dashboard-project-budget">
                        <span>{project.budget}€</span>
                      </div>
                    )}
                  </div>
                  
                  {project.description && (
                    <p className="dashboard-project-description">
                      {project.description.length > 100 
                        ? `${project.description.substring(0, 100)}...` 
                        : project.description}
                    </p>
                  )}
                  
                  <div className="dashboard-project-footer">
                    <div className="dashboard-project-footer-left">
                      {project.created_at && (
                        <span className="dashboard-project-date">
                          <FiCalendar className="mr-1" aria-hidden="true" />
                          Iniciado: {formatDate(project.created_at)}
                        </span>
                      )}
                      {project.unread_message_count > 0 && (
                        <span className="dashboard-project-messages">
                          <FiMessageCircle className="mr-1" aria-hidden="true" />
                          {project.unread_message_count} mensaje{project.unread_message_count !== 1 ? 's' : ''} nuevo{project.unread_message_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    
                    <button
                      className="btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToProject && navigateToProject(project.id, 'detail');
                      }}
                    >
                      Ver proyecto
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Visual Widgets Section */}
      <motion.div 
        className="widgets-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="widgets-header">
          <h3 className="widgets-title">
            <FiBarChart2 className="widgets-title-icon" aria-hidden="true" />
            Análisis Visual
          </h3>
          <p className="widgets-subtitle">Métricas y tendencias de tu actividad</p>
        </div>
        
        {/* Responsive grid for widgets */}
        <div className="widgets-grid">
          {/* Charts row - Tasa de conversión y Estado (dos columnas) */}
          <div className="widgets-row">
            <div className="widget-half">
              <ConversionRateChart 
                data={stats.chartData || []} 
                totalProposals={stats.total_proposals}
                acceptedProposals={stats.accepted_proposals}
              />
            </div>
            
            <div className="widget-half">
              <ProjectStatusDonut data={stats.statusData || []} />
            </div>
          </div>

          {/* Línea de tiempo - ancho completo, debajo de los dos */}
          <div className="widget-full-width">
            <DeadlineTimeline briefs={stats.briefs || []} />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Create Brief Component
const CreateBrief = ({ onSuccess, targetCreativeId, onClearTargetCreativeId }) => {
  const { user, token } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const formValidation = useFormValidation({
    title: '',
    description: '',
    category: '',
    target_audience: '',
    references: '',
    budget_min: '',
    budget_max: '',
    deadline: '',
    start_date: '',
    experience_level: 'any',
    project_duration: '',
    requirements: ['']
  }, {
    title: { 
      required: true, 
      minLength: 10, 
      maxLength: 100,
      custom: (value) => {
        if (value && value.trim().length < 10) {
          return 'El título debe tener al menos 10 caracteres descriptivos';
        }
        return '';
      }
    },
    description: { 
      required: true, 
      minLength: 50, 
      maxLength: 1000,
      custom: (value) => {
        if (value && value.trim().length < 50) {
          return 'La descripción debe tener al menos 50 caracteres para ser clara';
        }
        return '';
      }
    },
    category: { required: true },
    budget_min: { 
      required: true, 
      min: 1,
      custom: (value, values) => {
        const min = parseFloat(value);
        const max = parseFloat(values.budget_max);
        if (min && max && min >= max) {
          return 'El presupuesto mínimo debe ser menor al máximo';
        }
        if (min && min < 1) {
          return 'El presupuesto mínimo debe ser al menos 1€';
        }
        return '';
      }
    },
    budget_max: { 
      required: true, 
      min: 1,
      custom: (value, values) => {
        const min = parseFloat(values.budget_min);
        const max = parseFloat(value);
        if (min && max && max <= min) {
          return 'El presupuesto máximo debe ser mayor al mínimo';
        }
        if (max && max < 1) {
          return 'El presupuesto máximo debe ser al menos 1€';
        }
        return '';
      }
    },
    deadline: { 
      required: true,
      custom: (value, values) => {
        if (value) {
          const deadline = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (deadline <= today) {
            return 'La fecha límite debe ser posterior a hoy';
          }
          const startDate = values.start_date ? new Date(values.start_date) : null;
          const duration = values.project_duration;
          if (startDate && duration) {
            const days = projectDurationToDays(duration);
            if (days != null) {
              const minDeadline = new Date(startDate);
              minDeadline.setDate(minDeadline.getDate() + days);
              minDeadline.setHours(0, 0, 0, 0);
              if (deadline < minDeadline) {
                return `La fecha límite debe ser al menos ${days} días después de la fecha de inicio`;
              }
            }
          }
        }
        return '';
      }
    },
    start_date: {
      required: true,
      custom: (value) => {
        if (value) {
          const start = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          start.setHours(0, 0, 0, 0);
          if (start < today) {
            return 'La fecha de inicio no puede ser anterior a hoy';
          }
        }
        return '';
      }
    },
    project_duration: { required: true }
  });

  // Convierte project_duration a días para validar fecha límite >= start_date + duración
  const projectDurationToDays = (duration) => {
    if (!duration) return null;
    const map = { '1-3 days': 3, '1 week': 7, '2 weeks': 14, '1 month': 30, '2+ months': 60 };
    return map[duration] ?? null;
  };

  // Form validation with proper computed state
  const isFormValid = useMemo(() => {
    // Check if all required fields have values
    const requiredFields = ['title', 'description', 'category', 'budget_min', 'budget_max', 'start_date', 'deadline', 'project_duration'];
    const hasAllRequiredFields = requiredFields.every(field => {
      const value = formValidation.values[field];
      return value && value.toString().trim() !== '';
    });

    // Check if there are any validation errors
    const hasNoErrors = Object.values(formValidation.errors).every(error => !error);

    // Check if requirements are valid
    const validRequirements = formValidation.values.requirements.filter(req => req.trim() !== '');
    const hasValidRequirements = validRequirements.length > 0;

    return hasAllRequiredFields && hasNoErrors && hasValidRequirements;
  }, [formValidation.values, formValidation.errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formValidation.validateAll()) {
      setError('Por favor, corrige los errores en el formulario antes de continuar');
      setLoading(false);
      return;
    }

    // Validar que haya al menos un requisito no vacío
    const validRequirements = formValidation.values.requirements.filter(req => req.trim() !== '');
    if (validRequirements.length === 0) {
      setError('Debes agregar al menos un requisito del proyecto');
      setLoading(false);
      return;
    }

    try {
      const budgetMin = formValidation.values.budget_min ? parseFloat(formValidation.values.budget_min) : null;
      const budgetMax = formValidation.values.budget_max ? parseFloat(formValidation.values.budget_max) : null;
      const briefData = {
        ...formValidation.values,
        budget_range: (budgetMin != null && budgetMax != null) ? `${Number(budgetMin).toLocaleString('es-ES')}€ - ${Number(budgetMax).toLocaleString('es-ES')}€` : '',
        budget_min: budgetMin,
        budget_max: budgetMax,
        deadline: formValidation.values.deadline ? new Date(formValidation.values.deadline).toISOString() : null,
        start_date: formValidation.values.start_date ? new Date(formValidation.values.start_date).toISOString() : null,
        requirements: validRequirements,
        ...(targetCreativeId ? { target_creative_id: targetCreativeId } : {})
      };

      await axios.post(`${API}/briefs`, briefData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('¡Proyecto creado exitosamente! Los creativos podrán ver tu proyecto pronto.');
      addToast({ type: 'success', title: 'Proyecto creado', description: 'Los creativos podrán ver tu proyecto pronto.' });
      formValidation.resetForm();
      
      // Redirigir a "Mis Proyectos" después de un breve delay
      setTimeout(() => {
        setSuccess('');
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error creating brief:', error);
      
      // Manejo detallado de errores del backend
      let errorMessage = 'Error al crear el proyecto';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Si es un array de errores de validación
          errorMessage = error.response.data.detail.map(err => err.msg || err.message || err).join(', ');
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Datos inválidos. Por favor verifica la información ingresada.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Error interno del servidor. Por favor intenta más tarde.';
      } else if (!error.response) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      }
      
      setError(errorMessage);
      addToast({ type: 'error', title: 'Error al crear el proyecto', description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    formValidation.setValue(e.target.name, e.target.value);
  };

  const handleInputBlur = (e) => {
    formValidation.setTouchedField(e.target.name, e.target.value);
  };

  const addRequirement = () => {
    formValidation.setValue('requirements', [...formValidation.values.requirements, '']);
  };

  const removeRequirement = (index) => {
    if (formValidation.values.requirements.length > 1) {
      const newReqs = formValidation.values.requirements.filter((_, i) => i !== index);
      formValidation.setValue('requirements', newReqs);
    }
  };

  const updateRequirement = (index, value) => {
    const newReqs = [...formValidation.values.requirements];
    newReqs[index] = value;
    formValidation.setValue('requirements', newReqs);
  };

  // Componente mejorado para mostrar errores inline
  const FieldError = ({ error, touched }) => {
    // Early return si no hay error o no está touched
    if (!error || !touched) {
      return null;
    }
    
    return (
      <div className="field-error">
        <span className="error-icon">⚠️</span>
        <span className="error-text">{error}</span>
      </div>
    );
  };

  return (
    <div className="create-brief">
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h2>
            <FiFileText aria-hidden="true" />
            Crear Nuevo Proyecto
          </h2>
          <p>Describe tu proyecto para atraer a los mejores creativos</p>
        </div>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="brief-form"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon"><FiAlertTriangle aria-hidden="true" /></span>
            <span className="alert-text">{error}</span>
          </div>
        )}
        {success && (
          <div className="alert alert-success">
            <span className="alert-icon"><FiCheckCircle aria-hidden="true" /></span>
            <span className="alert-text">{success}</span>
          </div>
        )}

        <div className="brief-form-grid">
          <div className="brief-form-col brief-form-col--left">
            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiClipboard aria-hidden="true" />
                Información Básica
              </h3>
          
          <div className="form-group">
            <label>Título del Proyecto *</label>
            <input
              type="text"
              name="title"
              value={formValidation.values.title}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="Ej: Diseño de logo para marca de ropa sostenible"
              className={formValidation.errors.title && formValidation.touched.title ? 'error' : ''}
              maxLength="100"
              required
            />
            <FieldError 
              error={formValidation.errors.title} 
              touched={formValidation.touched.title} 
            />
            <div className="input-hint">
              {formValidation.values.title.length}/100 caracteres
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Categoría *</label>
              <select
                name="category"
                value={formValidation.values.category}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={formValidation.errors.category && formValidation.touched.category ? 'error' : ''}
                required
              >
                <option value="">Seleccionar categoría</option>
                {BRIEF_CATEGORY_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <FieldError 
                error={formValidation.errors.category} 
                touched={formValidation.touched.category} 
              />
            </div>

            <div className="form-group">
              <label>Nivel de Experiencia</label>
              <select
                name="experience_level"
                value={formValidation.values.experience_level}
                onChange={handleInputChange}
              >
                <option value="any">Cualquier nivel</option>
                <option value="junior">Junior</option>
                <option value="mid">Intermedio</option>
                <option value="senior">Senior</option>
                <option value="expert">Experto</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Descripción del Proyecto *</label>
            <textarea
              name="description"
              value={formValidation.values.description}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="Describe detalladamente qué necesitas, el estilo que buscas, público objetivo, referentes, etc. Sé específico para atraer a los creativos más adecuados."
              rows="5"
              className={formValidation.errors.description && formValidation.touched.description ? 'error' : ''}
              maxLength="1000"
              required
            />
            <FieldError 
              error={formValidation.errors.description} 
              touched={formValidation.touched.description} 
            />
            <div className="input-hint">
              {formValidation.values.description.length}/1000 caracteres (mínimo 50)
            </div>
          </div>

          <div className="form-group">
            <label>Público objetivo</label>
            <textarea
              name="target_audience"
              value={formValidation.values.target_audience}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="Ejemplo: Usuarios base (viajeros que consultan costes). Usuario operador: consulta y edición limitada. Usuario administrador: capacidad de editar y configurar."
              rows="4"
              className={formValidation.errors.target_audience && formValidation.touched.target_audience ? 'error' : ''}
              maxLength="800"
            />
            <div className="input-hint">
              Describe a quién va dirigido el resultado del proyecto para que el creativo lo tenga en cuenta.
            </div>
          </div>

          <div className="form-group">
            <label>Referencias</label>
            <textarea
              name="references"
              value={formValidation.values.references}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="Enlaces, nombres de marcas, estilos o descripción de referencias que ayuden al creativo a entender el resultado deseado."
              rows="3"
              className={formValidation.errors.references && formValidation.touched.references ? 'error' : ''}
              maxLength="600"
            />
            <div className="input-hint">
              Algo con lo que el creativo pueda hacerse una idea clara del estilo o resultado que buscas.
            </div>
          </div>
            </div>
          </div>

          <div className="brief-form-col brief-form-col--right">
            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FaEuroSign aria-hidden="true" />
                Presupuesto y Tiempo
              </h3>
              <p className="brief-budget-notice">
                El mínimo que indiques será el importe que se cobrará en el proyecto si el creativo no propone un precio al aplicar.
              </p>
          
          <div className="form-row">
            <div className="form-group">
              <label>Presupuesto Mínimo (€) *</label>
              <input
                type="number"
                name="budget_min"
                value={formValidation.values.budget_min}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder="100"
                min="1"
                step="0.01"
                className={formValidation.errors.budget_min && formValidation.touched.budget_min ? 'error' : ''}
                required
              />
              <FieldError 
                error={formValidation.errors.budget_min} 
                touched={formValidation.touched.budget_min} 
              />
            </div>

            <div className="form-group">
              <label>Presupuesto Máximo (€) *</label>
              <input
                type="number"
                name="budget_max"
                value={formValidation.values.budget_max}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder="1000"
                min="1"
                step="0.01"
                className={formValidation.errors.budget_max && formValidation.touched.budget_max ? 'error' : ''}
                required
              />
              <FieldError 
                error={formValidation.errors.budget_max} 
                touched={formValidation.touched.budget_max} 
              />
            </div>

            <div className="form-group">
              <label>Duración del Proyecto *</label>
              <select
                name="project_duration"
                value={formValidation.values.project_duration}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={formValidation.errors.project_duration && formValidation.touched.project_duration ? 'error' : ''}
                required
              >
                <option value="">Seleccionar duración</option>
                <option value="1-3 days">1-3 días</option>
                <option value="1 week">1 semana</option>
                <option value="2 weeks">2 semanas</option>
                <option value="1 month">1 mes</option>
                <option value="2+ months">2+ meses</option>
              </select>
              <FieldError 
                error={formValidation.errors.project_duration} 
                touched={formValidation.touched.project_duration} 
              />
            </div>
          </div>

          <div className="form-group">
            <label>Fecha de inicio *</label>
            <input
              type="date"
              name="start_date"
              value={formValidation.values.start_date}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              min={new Date().toISOString().split('T')[0]}
              className={formValidation.errors.start_date && formValidation.touched.start_date ? 'error' : ''}
              required
            />
            <FieldError
              error={formValidation.errors.start_date}
              touched={formValidation.touched.start_date}
            />
          </div>

          <div className="form-group">
            <label>Fecha Límite *</label>
            <input
              type="date"
              name="deadline"
              value={formValidation.values.deadline}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              min={(() => {
                const start = formValidation.values.start_date ? new Date(formValidation.values.start_date) : null;
                const duration = projectDurationToDays(formValidation.values.project_duration);
                if (start && duration != null) {
                  const minD = new Date(start);
                  minD.setDate(minD.getDate() + duration);
                  return minD.toISOString().split('T')[0];
                }
                return new Date(Date.now() + 86400000).toISOString().split('T')[0];
              })()}
              className={formValidation.errors.deadline && formValidation.touched.deadline ? 'error' : ''}
              required
            />
            <FieldError 
              error={formValidation.errors.deadline} 
              touched={formValidation.touched.deadline} 
            />
          </div>
            </div>

            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiClipboard aria-hidden="true" />
                Requisitos del Proyecto
              </h3>
          <p className="section-description">
            Agrega requisitos específicos que ayuden a los creativos a entender exactamente qué necesitas.
          </p>
          
          {formValidation.values.requirements.map((requirement, index) => (
            <div key={index} className="requirement-item">
              <div className="requirement-input-group">
                <input
                  type="text"
                  value={requirement}
                  onChange={(e) => updateRequirement(index, e.target.value)}
                  placeholder={`Requisito ${index + 1} (ej: Logo debe incluir el nombre de la marca)`}
                  className="requirement-input"
                  maxLength="200"
                />
                {formValidation.values.requirements.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="btn-remove"
                    title="Eliminar requisito"
                  >
                    <FiX aria-hidden="true" />
                  </button>
                )}
              </div>
              {requirement && (
                <div className="input-hint">
                  {requirement.length}/200 caracteres
                </div>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={addRequirement}
            className="btn-add-requirement"
            disabled={formValidation.values.requirements.length >= 10}
          >
            <FiPlus aria-hidden="true" />
            Agregar Requisito {formValidation.values.requirements.length >= 10 && '(máximo 10)'}
          </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <>
                <FiLoader className="loading-spinner" aria-hidden="true" />
                Creando Brief...
              </>
            ) : (
              <>
                <FiSend aria-hidden="true" />
                Publicar Proyecto
              </>
            )}
          </button>
          
          <div className="form-actions-hint">
            {!isFormValid && !success && (
              <p className="validation-summary">
                <FiAlertCircle aria-hidden="true" />
                Completa todos los campos obligatorios para crear tu proyecto
              </p>
            )}
          </div>
        </div>
      </motion.form>
    </div>
  );
};

// Send Proposal Component
const SendProposal = ({ brief, onClose, onSuccess }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState('');
  
  const formValidation = useFormValidation({
    // Estructura básica
    title: '',
    objectives: '',
    methodology: '',
    deliverables: '',
    
    // Cronograma
    start_date: '',
    revisions_included: 2,
    
    // Económico
    total_budget: '',
    payment_conditions: '50% al aceptar, 50% al entregar',
    
    // Portafolio
    portfolio_urls: [''],
    success_cases: '',
    
    // Adicionales
    tools_software: [],
    communication_preference: 'email',
    availability: '',
    terms_accepted: false
  }, {
    title: { required: true },
    objectives: { required: true },
    methodology: { required: true },
    deliverables: { required: true },
    start_date: { required: true },
    total_budget: { required: true, min: 0.01 },
    terms_accepted: { 
      custom: (value) => !value ? 'Debes aceptar los términos y condiciones' : '' 
    }
  });

  const [milestones, setMilestones] = useState([{ title: '', date: '', deliverable: '', percentage: 100 }]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const MAX_PROPOSAL_FILE_MB = 5;
  const MAX_PROPOSAL_FILE_BYTES = MAX_PROPOSAL_FILE_MB * 1024 * 1024;

  const handleFileAttach = async (e) => {
    const files = e.target.files;
    if (!files?.length || !token) return;
    setStepError('');
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_PROPOSAL_FILE_BYTES) {
        setStepError(`"${files[i].name}" supera el límite. Máximo ${MAX_PROPOSAL_FILE_MB} MB por archivo.`);
        e.target.value = '';
        return;
      }
    }
    setUploadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${UPLOAD_BASE}/api/upload`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const url = res.data?.url;
        if (url) {
          setUploadedFiles(prev => [...prev, { name: file.name, url: getUploadUrl(url) || url }]);
        }
      }
    } catch (err) {
      console.error('Error subiendo archivo:', err);
      setStepError(err.response?.data?.detail || `Error al subir el archivo. Máx. ${MAX_PROPOSAL_FILE_MB} MB. Formatos: PDF, DOC, DOCX, JPG, PNG, etc.`);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const removeAttachedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Memoizar los pasos para evitar re-renders innecesarios
  const steps = useMemo(() => [
    { id: 1, title: 'Aplicación', icon: FiFileText },
    { id: 2, title: 'Cronograma', icon: FiCalendar },
    { id: 3, title: 'Económico', icon: FaEuroSign },
    { id: 4, title: 'Portafolio', icon: FiPenTool },
    { id: 5, title: 'Revisión', icon: FiCheckCircle }
  ], []);

  const toolsOptions = useMemo(() => [
    'Adobe Creative Suite', 'Figma', 'Sketch', 'Canva', 'Final Cut Pro',
    'Premiere Pro', 'After Effects', 'Cinema 4D', 'Blender', 'WordPress',
    'Shopify', 'WooCommerce', 'Mailchimp', 'Google Analytics', 'SEMrush'
  ], []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 SendProposal handleSubmit triggered');
    console.log('Current step:', currentStep);
    console.log('Form values:', formValidation.values);
    console.log('Is current step valid:', isCurrentStepValid);
    
    if (!validateCurrentStep()) {
      console.log('❌ Current step validation failed');
      return;
    }
    
    setLoading(true);
    setStepError('');

    try {
      // Preparar datos para backend (incluir fases y condiciones de pago)
      const payload = {
        brief_id: brief.id,
        message: generateFinalMessage(),
        proposed_price: parseFloat(formValidation.values.total_budget),
        delivery_days: calculateTotalDays(),
        payment_conditions: formValidation.values.payment_conditions,
        portfolio_samples: [
          ...formValidation.values.portfolio_urls.filter(url => url.trim() !== ''),
          ...uploadedFiles.map(f => f.url)
        ],
        milestones: milestones.map(milestone => {
          const totalBudget = parseFloat(formValidation.values.total_budget) || 0;
          const amount = totalBudget * (milestone.percentage || 0) / 100;
          return {
            title: milestone.title,
            date: milestone.date,
            deliverable: milestone.deliverable,
            percentage: milestone.percentage || 0,
            amount: amount
          };
        })
      };

      console.log('💾 Sending payload to backend:', payload);

      const response = await axios.post(`${API}/proposals`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Backend response:', response);

      if (response.status === 200 || response.status === 201) {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error sending proposal:', error);
      
      let errorMessage = 'Error al enviar la propuesta. ';
      
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already submitted')) {
        errorMessage = 'Ya has enviado una propuesta para este proyecto.';
      } else if (error.response?.status === 403) {
        errorMessage = 'No tienes permisos para enviar aplicaciones a este proyecto.';
      } else if (error.response?.status === 404) {
        errorMessage = 'El proyecto ya no está disponible.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Datos inválidos en la propuesta. Por favor revisa todos los campos.';
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Por favor, intenta de nuevo.';
      }
      
      setStepError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const generateFinalMessage = () => {
    return `
**${formValidation.values.title}**

**OBJETIVOS:**
${formValidation.values.objectives}

**METODOLOGÍA:**
${formValidation.values.methodology}

**ENTREGABLES:**
${formValidation.values.deliverables}

**CRONOGRAMA:**
Inicio: ${formValidation.values.start_date}
${milestones.map(m => `• ${m.title} - ${m.date} (${m.deliverable})`).join('\n')}
Revisiones incluidas: ${formValidation.values.revisions_included}

**CONDICIONES DE PAGO:**
${formValidation.values.payment_conditions}

**HERRAMIENTAS:**
${formValidation.values.tools_software.join(', ')}

**CASOS DE ÉXITO:**
${formValidation.values.success_cases}

**DISPONIBILIDAD:**
${formValidation.values.availability}
    `.trim();
  };

  const calculateTotalDays = () => {
    if (!formValidation.values.start_date || milestones.length === 0) return 7;
    
    const startDate = new Date(formValidation.values.start_date);
    const lastMilestone = milestones
      .filter(m => m.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    
    if (!lastMilestone) return 7;
    
    const endDate = new Date(lastMilestone.date);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays || 7;
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        const step1Fields = ['title', 'objectives', 'methodology', 'deliverables'];
        let isValid = true;
        let hasErrors = false;
        
        step1Fields.forEach(field => {
          formValidation.setTouchedField(field);
          if (formValidation.errors[field] || !formValidation.values[field] || formValidation.values[field].trim() === '') {
            isValid = false;
            hasErrors = true;
          }
        });
        
        if (!isValid && hasErrors) {
          setStepError('Por favor completa todos los campos obligatorios antes de continuar.');
        }
        
        return isValid;
        
      case 2:
        formValidation.setTouchedField('start_date');
        
        const hasStartDate = formValidation.values.start_date && !formValidation.errors.start_date;
        const hasFirstMilestone = milestones[0] && milestones[0].title && milestones[0].date;
        
        if (!hasStartDate) {
          setStepError('Por favor selecciona una fecha de inicio.');
          return false;
        }
        
        if (!hasFirstMilestone) {
          setStepError('Por favor completa al menos la primera fase con nombre y fecha.');
          return false;
        }
        
        return true;
        
      case 3:
        formValidation.setTouchedField('total_budget');
        
        const totalBudgetVal = parseFloat(formValidation.values.total_budget);
        const hasValidBudget = formValidation.values.total_budget && 
                              !formValidation.errors.total_budget && 
                              !Number.isNaN(totalBudgetVal) && totalBudgetVal > 0;
        
        if (!hasValidBudget) {
          setStepError('Por favor ingresa un presupuesto total válido mayor a 0.');
          return false;
        }

        const briefMin = brief.budget_min != null ? Number(brief.budget_min) : null;
        const briefMax = brief.budget_max != null ? Number(brief.budget_max) : null;
        if (briefMin != null && totalBudgetVal < briefMin) {
          setStepError(`El presupuesto no puede ser menor que el mínimo del proyecto (${briefMin}€).`);
          return false;
        }
        if (briefMax != null && totalBudgetVal > briefMax) {
          setStepError(`El presupuesto no puede ser mayor que el máximo del proyecto (${briefMax}€).`);
          return false;
        }
        
        // Validar que los porcentajes de las fases sumen exactamente 100%
        const totalPercentage = milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
        if (totalPercentage !== 100) {
          setStepError(`Los porcentajes de las fases deben sumar exactamente 100%. Actualmente suman ${totalPercentage}%.`);
          return false;
        }
        
        return true;
        
      case 4:
        const hasPortfolioUrls = formValidation.values.portfolio_urls.some(url => url.trim() !== '');
        const hasAttachments = uploadedFiles.length > 0;
        if (!hasPortfolioUrls && !hasAttachments) {
          setStepError('Por favor adjunta al menos un archivo (PDF, etc.) o agrega un enlace de portafolio para mostrar tu trabajo.');
          return false;
        }
        return true;
        
      case 5:
        // Para el paso 5 (revisión), verificamos que los términos estén aceptados
        formValidation.setTouchedField('terms_accepted');
        
        if (!formValidation.values.terms_accepted) {
          setStepError('Debes aceptar los términos y condiciones para enviar la propuesta.');
          return false;
        }
        
        // También verificamos que tenemos todos los datos necesarios para enviar
        const requiredForSubmission = [
          formValidation.values.title,
          formValidation.values.objectives,
          formValidation.values.methodology,
          formValidation.values.deliverables,
          formValidation.values.start_date,
          formValidation.values.total_budget,
          milestones[0]?.title,
          milestones[0]?.date
        ];
        
        const missingRequired = requiredForSubmission.some(field => !field);
        if (missingRequired) {
          setStepError('Faltan datos obligatorios en los pasos anteriores. Por favor revisa tu propuesta.');
          return false;
        }
        
        console.log('✅ Paso 5 validation passed - ready to submit');
        return true;
        
      default:
        return true;
    }
  };

  // Version readonly para validacion sin efectos secundarios
  const isCurrentStepValid = useMemo(() => {
    switch (currentStep) {
      case 1:
        const step1Fields = ['title', 'objectives', 'methodology', 'deliverables'];
        return step1Fields.every(field => 
          formValidation.values[field] && 
          formValidation.values[field].trim() !== '' && 
          !formValidation.errors[field]
        );
        
      case 2:
        const hasStartDate = formValidation.values.start_date && !formValidation.errors.start_date;
        const hasFirstMilestone = milestones[0] && milestones[0].title && milestones[0].date;
        return hasStartDate && hasFirstMilestone;
        
      case 3: {
        const v = parseFloat(formValidation.values.total_budget);
        if (!formValidation.values.total_budget || formValidation.errors.total_budget || Number.isNaN(v) || v <= 0) return false;
        const briefMin = brief.budget_min != null ? Number(brief.budget_min) : null;
        const briefMax = brief.budget_max != null ? Number(brief.budget_max) : null;
        if (briefMin != null && v < briefMin) return false;
        if (briefMax != null && v > briefMax) return false;
        return true;
      }
        
      case 4:
        return formValidation.values.portfolio_urls.some(url => url.trim() !== '') || uploadedFiles.length > 0;
        
      case 5:
        // Para el paso 5, verificamos términos aceptados y datos completos
        const termsAccepted = formValidation.values.terms_accepted;
        const hasRequiredData = formValidation.values.title && 
                               formValidation.values.objectives && 
                               formValidation.values.methodology && 
                               formValidation.values.deliverables &&
                               formValidation.values.start_date &&
                               formValidation.values.total_budget &&
                               milestones[0]?.title &&
                               milestones[0]?.date;
        
        const isValid = termsAccepted && hasRequiredData;
        console.log('🔍 Step 5 validation check:', {
          termsAccepted,
          hasRequiredData,
          isValid,
          totalBudget: formValidation.values.total_budget,
          milestonesCount: milestones.length
        });
        
        return isValid;
        
      default:
        return true;
    }
  }, [currentStep, formValidation.values, formValidation.errors, milestones, brief]);

  const handleInputChange = useCallback((name, value) => {
    formValidation.setValue(name, value);
  }, [formValidation]);

  const handleInputBlur = useCallback((name) => {
    formValidation.setTouchedField(name);
  }, [formValidation]);

  const addMilestone = useCallback(() => {
    setMilestones(prev => {
      // Calcular el total actual de porcentajes
      const currentTotal = prev.reduce((sum, m) => sum + (m.percentage || 0), 0);
      // La nueva fase toma el porcentaje restante para llegar a 100%
      const newPercentage = Math.max(0, 100 - currentTotal);
      
      return [...prev, { title: '', date: '', deliverable: '', percentage: newPercentage }];
    });
  }, []);

  const removeMilestone = useCallback((index) => {
    setMilestones(prev => {
      if (prev.length <= 1) return prev; // No permitir eliminar la última fase
      
      const removed = prev[index];
      const remaining = prev.filter((_, i) => i !== index);
      const removedPercentage = removed.percentage || 0;
      
      // Redistribuir el porcentaje de la fase eliminada entre los restantes
      if (removedPercentage > 0 && remaining.length > 0) {
        const totalRemaining = remaining.reduce((sum, m) => sum + (m.percentage || 0), 0);
        const toDistribute = removedPercentage;
        
        if (totalRemaining > 0) {
          // Distribuir proporcionalmente
          let distributed = 0;
          remaining.forEach((m, i) => {
            if (i === remaining.length - 1) {
              m.percentage = (m.percentage || 0) + (toDistribute - distributed);
            } else {
              const share = Math.round(toDistribute * (m.percentage || 0) / totalRemaining);
              m.percentage = (m.percentage || 0) + share;
              distributed += share;
            }
          });
        } else {
          // Distribuir equitativamente
          const perMilestone = Math.floor(toDistribute / remaining.length);
          remaining.forEach((m, i) => {
            m.percentage = (m.percentage || 0) + perMilestone + (i === 0 ? toDistribute % remaining.length : 0);
          });
        }
      }
      
      return remaining;
    });
  }, []);

  const updateMilestone = useCallback((index, field, value) => {
    if (field === 'percentage') {
      // Lógica especial para porcentajes - siempre ajustar para sumar 100%
      setMilestones(prev => {
        const newMilestones = prev.map(m => ({ ...m }));
        const newPercentage = Math.min(100, Math.max(0, parseFloat(value) || 0));
        
        // Establecer el nuevo porcentaje
        newMilestones[index].percentage = newPercentage;
        
        // Si solo hay una fase, mantener como está
        if (newMilestones.length === 1) {
          return newMilestones;
        }
        
        // Calcular cuánto queda para las demás fases
        const remaining = 100 - newPercentage;
        const otherMilestones = newMilestones.filter((_, i) => i !== index);
        const otherTotal = otherMilestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
        
        if (otherMilestones.length === 1) {
          // Si solo hay otra fase, esa fase toma todo el restante
          const otherIndex = newMilestones.findIndex((_, i) => i !== index);
          newMilestones[otherIndex].percentage = remaining;
        } else if (otherTotal > 0) {
          // Redistribuir proporcionalmente entre las otras fases
          const scaleFactor = remaining / otherTotal;
          let distributed = 0;
          
          newMilestones.forEach((m, i) => {
            if (i !== index) {
              if (i === newMilestones.length - 1 || (i > index && distributed < remaining)) {
                // La última fase recibe el resto para evitar errores de redondeo
                m.percentage = remaining - distributed;
              } else {
                const share = Math.round((m.percentage || 0) * scaleFactor);
                m.percentage = share;
                distributed += share;
              }
            }
          });
        } else {
          // Si los otros están en 0, distribuir equitativamente
          const otherCount = newMilestones.length - 1;
          const perOther = Math.floor(remaining / otherCount);
          let leftover = remaining - (perOther * otherCount);
          
          newMilestones.forEach((m, i) => {
            if (i !== index) {
              m.percentage = perOther + (leftover > 0 ? 1 : 0);
              if (leftover > 0) leftover--;
            }
          });
        }
        
        return newMilestones;
      });
    } else {
      // Para otros campos, actualización normal
      setMilestones(prev => prev.map((milestone, i) => 
        i === index ? { ...milestone, [field]: value } : milestone
      ));
    }
  }, []);

  const toggleTool = useCallback((tool) => {
    const currentTools = formValidation.values.tools_software;
    const newTools = currentTools.includes(tool)
      ? currentTools.filter(t => t !== tool)
      : [...currentTools, tool];
    formValidation.setValue('tools_software', newTools);
  }, [formValidation]);

  const [customToolInput, setCustomToolInput] = useState('');

  const addCustomTool = useCallback(() => {
    const value = customToolInput.trim();
    if (!value) return;
    const currentTools = formValidation.values.tools_software;
    if (currentTools.includes(value)) return;
    formValidation.setValue('tools_software', [...currentTools, value]);
    setCustomToolInput('');
  }, [formValidation, customToolInput]);

  const removeTool = useCallback((tool) => {
    const newTools = formValidation.values.tools_software.filter(t => t !== tool);
    formValidation.setValue('tools_software', newTools);
  }, [formValidation]);

  const saveDraft = useCallback(() => {
    const draftData = {
      ...formValidation.values,
      milestones
    };
    localStorage.setItem(`proposal_draft_${brief.id}`, JSON.stringify(draftData));
  }, [formValidation.values, milestones, brief.id]);

  const loadDraft = useCallback(() => {
    const draft = localStorage.getItem(`proposal_draft_${brief.id}`);
    if (draft) {
      const parsedDraft = JSON.parse(draft);
      formValidation.setValues(parsedDraft);
      if (parsedDraft.milestones) {
        setMilestones(parsedDraft.milestones);
      }
    }
  }, [brief.id, formValidation]);

  // Cargar borrador al montar componente
  useEffect(() => {
    loadDraft();
  }, []);

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setStepError(''); // Limpiar errores al retroceder
  }, []);

  // Componente para mostrar errores
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="error-message">
        <span className="error-icon">⚠️</span>
        {error}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3 className="flex items-center gap-2">
              <FiFileText aria-hidden="true" />
              Datos de la aplicación
            </h3>
            
            <div className="form-group">
              <label>Título o asunto *</label>
              <input
                type="text"
                value={formValidation.values.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                onBlur={() => handleInputBlur('title')}
                placeholder="Ej: Rediseño completo de identidad visual corporativa"
                className={formValidation.errors.title && formValidation.touched.title ? 'error' : ''}
              />
              <ErrorMessage error={formValidation.touched.title ? formValidation.errors.title : ''} />
            </div>

            <div className="form-group">
              <label>Objetivos *</label>
              <textarea
                value={formValidation.values.objectives}
                onChange={(e) => handleInputChange('objectives', e.target.value)}
                onBlur={() => handleInputBlur('objectives')}
                placeholder="Describe los objetivos principales que cumplirás con esta aplicación..."
                rows="3"
                className={formValidation.errors.objectives && formValidation.touched.objectives ? 'error' : ''}
              />
              <ErrorMessage error={formValidation.touched.objectives ? formValidation.errors.objectives : ''} />
            </div>

            <div className="form-group">
              <label>Metodología / Flujo de trabajo *</label>
              <textarea
                value={formValidation.values.methodology}
                onChange={(e) => handleInputChange('methodology', e.target.value)}
                onBlur={() => handleInputBlur('methodology')}
                placeholder="Explica tu proceso de trabajo paso a paso..."
                rows="3"
                className={formValidation.errors.methodology && formValidation.touched.methodology ? 'error' : ''}
              />
              <ErrorMessage error={formValidation.touched.methodology ? formValidation.errors.methodology : ''} />
            </div>

            <div className="form-group">
              <label>Entregables (formatos y número de archivos) *</label>
              <textarea
                value={formValidation.values.deliverables}
                onChange={(e) => handleInputChange('deliverables', e.target.value)}
                onBlur={() => handleInputBlur('deliverables')}
                placeholder="Detalla qué entregarás: formatos, cantidad, especificaciones..."
                rows="3"
                className={formValidation.errors.deliverables && formValidation.touched.deliverables ? 'error' : ''}
              />
              <ErrorMessage error={formValidation.touched.deliverables ? formValidation.errors.deliverables : ''} />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3 className="flex items-center gap-2">
              <FiCalendar aria-hidden="true" />
              Cronograma & Fases
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Fecha de inicio *</label>
                <input
                  type="date"
                  value={formValidation.values.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  onBlur={() => handleInputBlur('start_date')}
                  min={new Date().toISOString().split('T')[0]}
                  className={formValidation.errors.start_date && formValidation.touched.start_date ? 'error' : ''}
                />
                <ErrorMessage error={formValidation.touched.start_date ? formValidation.errors.start_date : ''} />
              </div>
              
              <div className="form-group">
                <label>Revisiones incluidas</label>
                <input
                  type="number"
                  value={formValidation.values.revisions_included}
                  onChange={(e) => handleInputChange('revisions_included', parseInt(e.target.value))}
                  min="0"
                  max="10"
                />
              </div>
            </div>

            <div className="milestones-section">
              <div className="section-header">
                <label>Fases del proyecto *</label>
                <button type="button" onClick={addMilestone} className="btn-add-milestone">
                  + Agregar Fase
                </button>
              </div>
              
              {milestones.map((milestone, index) => (
                <div key={index} className="milestone-item">
                  <div className="milestone-header">
                    <span className="milestone-number">{index + 1}</span>
                    {milestones.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeMilestone(index)}
                        className="btn-remove-milestone"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="milestone-fields">
                    <input
                      type="text"
                      placeholder="Nombre de la fase"
                      value={milestone.title}
                      onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                    />
                    <input
                      type="date"
                      value={milestone.date}
                      onChange={(e) => updateMilestone(index, 'date', e.target.value)}
                      min={formValidation.values.start_date}
                    />
                    <input
                      type="text"
                      placeholder="Entregable"
                      value={milestone.deliverable}
                      onChange={(e) => updateMilestone(index, 'deliverable', e.target.value)}
                    />
                  </div>
                </div>
              ))}
              
              {(!milestones[0]?.title || !milestones[0]?.date) && (
                <ErrorMessage error="Debes completar al menos el primera fase" />
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h3 className="flex items-center gap-2">
              <FaEuroSign aria-hidden="true" />
              Desglose Económico
            </h3>
            
            <div className="form-group">
              <label>Presupuesto Total (€) *</label>
              {(brief.budget_min != null || brief.budget_max != null) && (
                <p className="field-hint" style={{ marginBottom: '0.35rem' }}>
                  El brief indica un rango: entre {brief.budget_min != null ? `${Number(brief.budget_min)}€` : '—'} y {brief.budget_max != null ? `${Number(brief.budget_max)}€` : '—'}.
                </p>
              )}
              <input
                type="number"
                value={formValidation.values.total_budget}
                onChange={(e) => handleInputChange('total_budget', e.target.value)}
                onBlur={() => handleInputBlur('total_budget')}
                placeholder={brief.budget_min != null ? `${brief.budget_min}` : '0.00'}
                min={brief.budget_min != null ? Number(brief.budget_min) : 0}
                max={brief.budget_max != null ? Number(brief.budget_max) : undefined}
                step="0.01"
                className={formValidation.errors.total_budget && formValidation.touched.total_budget ? 'error' : ''}
              />
              <ErrorMessage error={formValidation.touched.total_budget ? formValidation.errors.total_budget : ''} />
            </div>

            <div className="form-group">
              <label>Condiciones de Pago</label>
              <select
                value={formValidation.values.payment_conditions}
                onChange={(e) => handleInputChange('payment_conditions', e.target.value)}
              >
                <option value="100% al entregar">100% al entregar</option>
                <option value="50% al aceptar, 50% al entregar">50% al aceptar, 50% al entregar</option>
                <option value="30% al inicio, 70% al entregar">30% al inicio, 70% al entregar</option>
                <option value="Por fases según cronograma">Por fases según cronograma</option>
              </select>
            </div>

            <div className="budget-breakdown">
              <h4>Desglose por Fases</h4>
              <p className="budget-breakdown-hint">
                Los porcentajes se ajustan automáticamente para sumar 100%
              </p>
              {milestones.map((milestone, index) => {
                const totalBudget = parseFloat(formValidation.values.total_budget) || 0;
                const amount = totalBudget * (milestone.percentage || 0) / 100;
                return (
                  <div key={index} className="budget-item">
                    <span className="milestone-name">{milestone.title || `Fase ${index + 1}`}</span>
                    <div className="budget-item-inputs">
                      <div className="percentage-input-wrapper">
                        <input
                          type="number"
                          value={milestone.percentage || 0}
                          onChange={(e) => updateMilestone(index, 'percentage', e.target.value)}
                          min="0"
                          max="100"
                          step="1"
                          className="percentage-input"
                        />
                        <span className="percentage-symbol">%</span>
                      </div>
                      <span className="amount-display">
                        {amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="budget-total-row">
                <span className="budget-total-label">Total:</span>
                <span className={`budget-total-percentage ${milestones.reduce((sum, m) => sum + (m.percentage || 0), 0) === 100 ? 'valid' : 'invalid'}`}>
                  {milestones.reduce((sum, m) => sum + (m.percentage || 0), 0)}%
                </span>
                <span className="budget-total-amount">
                  {(parseFloat(formValidation.values.total_budget) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h3 className="flex items-center gap-2">
              <FiPenTool aria-hidden="true" />
              Portafolio & Muestras
            </h3>

            <div className="form-group">
              <label>
                <FiPaperclip aria-hidden="true" className="mr-2" />
                Adjuntar archivos (PDF, documento, imágenes)
              </label>
              <p className="field-hint" style={{ marginBottom: '0.5rem' }}>
                El creador podrá ver estos archivos en tu aplicación. Máx. 5 MB por archivo. Formatos: PDF, DOC, DOCX, JPG, PNG, etc.
              </p>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.svg,.zip"
                onChange={handleFileAttach}
                disabled={uploadingFile}
                className="proposal-file-input"
                multiple
              />
              {uploadingFile && <span className="uploading-hint">Subiendo...</span>}
              {uploadedFiles.length > 0 && (
                <ul className="proposal-attachments-list">
                  {uploadedFiles.map((f, idx) => (
                    <li key={idx} className="proposal-attachment-item">
                      <FiFileText aria-hidden="true" />
                      <span title={f.name}>{f.name.length > 35 ? f.name.slice(0, 32) + '…' : f.name}</span>
                      <button type="button" onClick={() => removeAttachedFile(idx)} className="btn-remove-attachment" aria-label="Quitar archivo">
                        <FiX />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="form-group">
              <label>URLs de Portafolio (opcional)</label>
              {formValidation.values.portfolio_urls.map((url, index) => (
                <div key={index} className="url-input-group">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => {
                      const newUrls = [...formValidation.values.portfolio_urls];
                      newUrls[index] = e.target.value;
                      handleInputChange('portfolio_urls', newUrls);
                    }}
                    placeholder="https://mi-portafolio.com/proyecto"
                  />
                  {index === formValidation.values.portfolio_urls.length - 1 && formValidation.values.portfolio_urls.length < 3 && (
                    <button
                      type="button"
                      onClick={() => handleInputChange('portfolio_urls', [...formValidation.values.portfolio_urls, ''])}
                      className="btn-add-url"
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="form-group">
              <label>Casos de Éxito / Testimonios</label>
              <textarea
                value={formValidation.values.success_cases}
                onChange={(e) => handleInputChange('success_cases', e.target.value)}
                placeholder="Comparte casos de éxito relevantes, testimonios de clientes o resultados destacados..."
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Herramientas / Software</label>
              <div className="tools-grid">
                {toolsOptions.map(tool => (
                  <div
                    key={tool}
                    className={`tool-tag ${formValidation.values.tools_software.includes(tool) ? 'selected' : ''}`}
                    onClick={() => toggleTool(tool)}
                  >
                    {tool}
                  </div>
                ))}
              </div>
              <div className="tools-add-custom">
                <input
                  type="text"
                  value={customToolInput}
                  onChange={(e) => setCustomToolInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTool())}
                  placeholder="Añadir otra herramienta..."
                  className="tools-custom-input"
                />
                <button
                  type="button"
                  onClick={addCustomTool}
                  className="btn-add-tool"
                  disabled={!customToolInput.trim()}
                >
                  Añadir
                </button>
              </div>
              {formValidation.values.tools_software.filter(t => !toolsOptions.includes(t)).length > 0 && (
                <div className="tools-custom-list">
                  <span className="tools-custom-label">Añadidas por ti:</span>
                  {formValidation.values.tools_software
                    .filter(t => !toolsOptions.includes(t))
                    .map(tool => (
                      <span key={tool} className="tool-tag tool-tag-custom selected">
                        {tool}
                        <button
                          type="button"
                          onClick={() => removeTool(tool)}
                          className="tool-tag-remove"
                          aria-label={`Quitar ${tool}`}
                        >
                          <FiX />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Disponibilidad</label>
              <input
                type="text"
                value={formValidation.values.availability}
                onChange={(e) => handleInputChange('availability', e.target.value)}
                placeholder="Ej: Lunes a Viernes 9-18h, GMT-5"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h3>✅ Revisión Final</h3>
            
            <div className="proposal-preview">
              <div className="preview-section">
                <h4 className="flex items-center gap-2">
                  <FiFileText aria-hidden="true" />
                  Aplicación: {formValidation.values.title}
                </h4>
                <p><strong>Objetivos:</strong> {formValidation.values.objectives}</p>
                <p><strong>Metodología:</strong> {formValidation.values.methodology}</p>
                <p><strong>Entregables:</strong> {formValidation.values.deliverables}</p>
              </div>

              <div className="preview-section">
                <h4 className="flex items-center gap-2">
                  <FiCalendar aria-hidden="true" />
                  Cronograma
                </h4>
                <p><strong>Inicio:</strong> {formValidation.values.start_date}</p>
                <p><strong>Duración:</strong> {calculateTotalDays()} días</p>
                <p><strong>Revisiones:</strong> {formValidation.values.revisions_included}</p>
                <div className="milestones-preview">
                  {milestones.map((m, i) => (
                    <div key={i} className="milestone-preview">
                      {m.title} - {m.date}
                    </div>
                  ))}
                </div>
              </div>

              <div className="preview-section">
                <h4 className="flex items-center gap-2">
                  <FaEuroSign aria-hidden="true" />
                  Presupuesto
                </h4>
                <p className="total-budget">{formValidation.values.total_budget}€</p>
                <p><strong>Condiciones:</strong> {formValidation.values.payment_conditions}</p>
              </div>

              <div className="preview-section">
                <h4 className="flex items-center gap-2">
                  <FiSettings aria-hidden="true" />
                  Herramientas
                </h4>
                <div className="tools-preview">
                  {formValidation.values.tools_software.join(', ') || 'No especificadas'}
                </div>
              </div>
            </div>

            <div className="terms-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formValidation.values.terms_accepted}
                  onChange={(e) => handleInputChange('terms_accepted', e.target.checked)}
                  onBlur={() => handleInputBlur('terms_accepted')}
                  className={formValidation.errors.terms_accepted && formValidation.touched.terms_accepted ? 'error' : ''}
                />
                <span className="checkmark"></span>
                Acepto los términos de confidencialidad, derechos de autor y políticas de cancelación
              </label>
              <ErrorMessage error={formValidation.touched.terms_accepted ? formValidation.errors.terms_accepted : ''} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content proposal-modal">
        <div className="modal-header">
          <div className="proposal-header-info">
            <h2 className="flex items-center gap-2">
              <FiSend aria-hidden="true" />
              Enviar aplicación
            </h2>
            <p className="brief-title">{brief.title}</p>
          </div>
          <button onClick={onClose} className="btn-close">×</button>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          {steps.map(step => {
            const IconComponent = step.icon;
            return (
              <div
                key={step.id}
                className={`progress-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
              >
                <div className="step-icon" aria-hidden="true">
                  <IconComponent />
                </div>
                <span className="step-title">{step.title}</span>
              </div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="proposal-form-container">
          {stepError && (
            <div className="error-message step-error">
              <span className="error-icon">⚠️</span>
              {stepError}
            </div>
          )}
          
          {renderStepContent()}

          <div className="form-actions">
            <div className="left-actions">
              <button type="button" onClick={saveDraft} className="btn-draft">
                <FiSave className="mr-2" aria-hidden="true" />
                Guardar Borrador
              </button>
            </div>
            
            <div className="right-actions">
              {currentStep > 1 && (
                <button type="button" onClick={prevStep} className="btn-secondary">
                  ← Anterior
                </button>
              )}
              
              {currentStep < 5 ? (
                <button type="button" onClick={nextStep} className="btn-primary">
                  Siguiente →
                </button>
              ) : (
                <button type="submit" className="btn-primary" disabled={loading || !isCurrentStepValid}>
                  {loading ? 'Enviando...' : (
                    <>
                      <FiSend className="mr-2" aria-hidden="true" />
                      Enviar aplicación
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Briefs List Component
const BriefsList = ({ activeTab, refreshTrigger, setActiveTab, navigateToProject, settings, onNavigateToRewards, initialBriefId, initialApplyBriefId, onOpenBrief, onCloseBrief, onApplyModalOpened }) => {
  const { user, realUser, token } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { getCached, setCached, isCacheValid } = useCache();
  const cacheKey = `briefs_${user.role}`;
  // Estado para el modal de cerrar oferta (solo creator)
  const [showCloseBriefModal, setShowCloseBriefModal] = useState(false);
  const [closeBriefTarget, setCloseBriefTarget] = useState(null);
  const [closeBriefReason, setCloseBriefReason] = useState('');
  const [closeBriefLoading, setCloseBriefLoading] = useState(false);
  const [closeBriefError, setCloseBriefError] = useState(null);
  const [briefs, setBriefs] = useState(() => getCached(cacheKey) || []);
  const [loading, setLoading] = useState(!isCacheValid(cacheKey));
  const [showProposals, setShowProposals] = useState(null);
  const publicApi = API;
  const [companyModal, setCompanyModal] = useState({ open: false, loading: false, profile: null, creatorId: null });
  const [companyProfileCache, setCompanyProfileCache] = useState({}); // creatorId -> profile

  // Sincronizar vista con URL: al abrir /briefs/:id mostramos el detalle; si no hay id, lista
  useEffect(() => {
    if (!initialBriefId) {
      setShowProposals(null);
      return;
    }
    setShowProposals({ id: initialBriefId, title: '' });
  }, [initialBriefId]);
  const [showSendProposal, setShowSendProposal] = useState(null);
  const [myApplications, setMyApplications] = useState({}); // brief_id -> status (applied | accepted | rejected)
  const [stripeCanApply, setStripeCanApply] = useState(true); // creativos: false hasta que tengan Stripe Connect completado
  const isAdminViewingAsCreative = realUser?.role === 'admin' && user?.role === 'creative';
  const [applyingToBriefId, setApplyingToBriefId] = useState(null);
  const [showApplyConfirmModal, setShowApplyConfirmModal] = useState(false);
  const [briefToApply, setBriefToApply] = useState(null);
  const [applyOfferPrice, setApplyOfferPrice] = useState('');
  const [applyOfferMessage, setApplyOfferMessage] = useState('');
  const [applyPaymentStructure, setApplyPaymentStructure] = useState(''); // '' | '50_50' | '100' | 'phases'
  const [applyPaymentPhases, setApplyPaymentPhases] = useState([{ title: '', percentage: '' }]); // for phases: { title, percentage }[], percentages sum 100
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    budget_min: '',
    budget_max: '',
    experience_level: 'any',
    sort_by: 'created_at',
    sort_order: 'desc',
    applied_filter: 'not_applied' // creativos: 'not_applied' | 'applied' | 'all'
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    offset: 0,
    total: 0
  });
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);

  useEffect(() => {
    if (activeTab === 'briefs') {
      const filtersActive = Object.entries(filters).some(([key, value]) => 
        value && value !== '' && value !== 'any' && key !== 'sort_by' && key !== 'sort_order' && key !== 'applied_filter'
      );
      if (filtersActive || pagination.offset > 0 || !isCacheValid(cacheKey)) {
        setHasFiltersApplied(filtersActive || pagination.offset > 0);
        fetchBriefs();
      }
    }
  }, [activeTab, refreshTrigger, filters, pagination.offset]);

  // Creativos: cargar aplicaciones al ver la pestaña Briefs (y al montar) para no mostrar "Aplicar" si ya aplicó
  const fetchMyApplicationsForBriefs = useCallback(() => {
    if (user?.role !== 'creative' || !token) return;
    axios.get(`${API}/briefs/my-applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const map = {};
        const list = Array.isArray(res.data?.applications) ? res.data.applications : [];
        list.forEach(a => {
          const bid = a.brief_id != null ? String(a.brief_id) : '';
          if (bid) map[bid] = a.status || 'applied';
        });
        setMyApplications(map);
      })
      .catch(() => { /* no sobrescribir con {} para no perder estado previo */ });
  }, [user?.role, token]);

  useEffect(() => {
    if (activeTab === 'briefs' && user?.role === 'creative' && token) {
      fetchMyApplicationsForBriefs();
    }
  }, [activeTab, user?.role, token, fetchMyApplicationsForBriefs]);

  // Creativos: cargar si tienen Stripe Connect completado para habilitar/deshabilitar Aplicar. Admin viendo como creativo puede aplicar siempre.
  useEffect(() => {
    if (user?.role !== 'creative' || !token) {
      setStripeCanApply(true);
      return;
    }
    if (isAdminViewingAsCreative) {
      setStripeCanApply(true);
      return;
    }
    axios.get(`${API}/stripe/connect/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStripeCanApply(res.data?.onboarding_complete === true))
      .catch(() => setStripeCanApply(false));
  }, [user?.role, token, activeTab, isAdminViewingAsCreative]);

  const fetchBriefs = async (forceRefresh = false) => {
    // Si no hay filtros y el caché es válido, no hacer fetch
    const filtersActive = Object.entries(filters).some(([key, value]) => 
      value && value !== '' && value !== 'any' && key !== 'sort_by' && key !== 'sort_order' && key !== 'applied_filter'
    );
    
    if (!forceRefresh && !filtersActive && pagination.offset === 0 && isCacheValid(cacheKey)) {
      return;
    }
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // For creators and admins: fetch only their own briefs (admin briefs are admin_only)
      // For creatives: fetch all available briefs with filters
      if (user.role === 'creator' || user.role === 'admin') {
        params.append('creator_id', user.id);
        params.append('limit', pagination.limit);
        params.append('offset', pagination.offset);
      } else {
        // For creatives: apply all filters to find briefs to work on
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value !== '' && value !== 'any') {
            params.append(key, value);
          }
        });
        
        // Always add pagination for creatives too
        params.append('limit', pagination.limit);
        params.append('offset', pagination.offset);
      }

      console.log(`Fetching briefs for ${user.role} with params:`, params.toString());

      const response = await axios.get(`${API}/briefs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Received briefs:', response.data);
      
      // Handle different response formats
      let briefsData = [];
      if (Array.isArray(response.data)) {
        briefsData = response.data;
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }));
      } else if (response.data.items) {
        briefsData = response.data.items;
        setPagination(prev => ({
          ...prev,
          total: response.data.total || response.data.items.length
        }));
      }
      
      setBriefs(briefsData);
      
      // Guardar en caché solo si no hay filtros aplicados
      const filtersActive = Object.entries(filters).some(([key, value]) => 
        value && value !== '' && value !== 'any' && key !== 'sort_by' && key !== 'sort_order' && key !== 'applied_filter'
      );
      if (!filtersActive && pagination.offset === 0 && briefsData.length > 0) {
        setCached(cacheKey, briefsData);
      }
      
    } catch (error) {
      console.error('Error fetching briefs:', error);
      setBriefs([]);
      if (error.response?.status === 401) {
        console.error('Authentication failed - token may be expired');
      }
    } finally {
      setLoading(false);
    }

    if (user.role === 'creative' && token) {
      try {
        const appRes = await axios.get(`${API}/briefs/my-applications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const map = {};
        const list = Array.isArray(appRes.data?.applications) ? appRes.data.applications : [];
        list.forEach(a => {
          const bid = a.brief_id != null ? String(a.brief_id) : '';
          if (bid) map[bid] = a.status || 'applied';
        });
        setMyApplications(map);
      } catch (_) {
        /* no vaciar para no perder estado si falla la red */
      }
    }
  };

  const openApplyConfirmModal = (brief) => {
    if (user?.role === 'creative' && !stripeCanApply) {
      alert('Para aplicar a briefs debes configurar tu cuenta para recibir pagos. Ve a Pagos (menú de tu perfil).');
      return;
    }
    setBriefToApply(brief);
    setShowApplyConfirmModal(true);
  };

  // Cerrar oferta (creator)
  const openCloseBriefModal = (brief) => {
    setCloseBriefTarget(brief);
    setCloseBriefReason('');
    setCloseBriefError(null);
    setShowCloseBriefModal(true);
  };

  const submitCloseBrief = async () => {
    if (!closeBriefTarget) return;
    setCloseBriefLoading(true);
    setCloseBriefError(null);
    try {
      const res = await axios.post(
        `${API}/briefs/${closeBriefTarget.id}/close`,
        { reason: closeBriefReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Marcar el brief como cancelled en local + refrescar
      setBriefs(prev => prev.map(b => b.id === closeBriefTarget.id ? { ...b, status: 'cancelled' } : b));
      setShowCloseBriefModal(false);
      setCloseBriefTarget(null);
      const rejected = res.data?.applications_rejected ?? 0;
      addToast({
        type: 'success',
        title: 'Oferta cerrada',
        description: rejected > 0 ? `Se han notificado ${rejected} aplicación(es).` : undefined,
      });
      // Refetch para que la lista quede consistente con la respuesta backend
      try { fetchBriefs(); } catch (_) {}
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCloseBriefError(typeof detail === 'string' ? detail : 'No se pudo cerrar la oferta.');
    } finally {
      setCloseBriefLoading(false);
    }
  };

  // Auto-open apply modal cuando se llega desde /brief/:id con ?apply=<id>
  useEffect(() => {
    if (!initialApplyBriefId || !token) return;
    if (user?.role !== 'creative' && user?.role !== 'admin') {
      onApplyModalOpened?.();
      return;
    }
    if (showApplyConfirmModal) return;
    const local = briefs.find(b => String(b.id) === String(initialApplyBriefId));
    if (local) {
      openApplyConfirmModal(local);
      onApplyModalOpened?.();
      return;
    }
    if (loading) return;
    let cancelled = false;
    axios.get(`${API}/briefs/${initialApplyBriefId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (cancelled) return;
        openApplyConfirmModal(res.data);
        onApplyModalOpened?.();
      })
      .catch(() => {
        if (!cancelled) onApplyModalOpened?.();
      });
    return () => { cancelled = true; };
  }, [initialApplyBriefId, briefs, loading, token, user?.role]);

  const closeApplyConfirmModal = () => {
    if (!applyingToBriefId) {
      setShowApplyConfirmModal(false);
      setBriefToApply(null);
      setApplyOfferPrice('');
      setApplyOfferMessage('');
    }
    setApplyPaymentStructure('');
    setApplyPaymentPhases([{ title: '', percentage: '' }]);
  };

  const applyToBrief = async (brief, offer = {}) => {
    if (!token || applyingToBriefId) return;
    const briefId = String(brief.id);
    setApplyingToBriefId(briefId);
    const body = {};
    if (offer.proposed_price != null && offer.proposed_price !== '') {
      const num = Number(offer.proposed_price);
      if (!Number.isNaN(num) && num >= 0) body.proposed_price = num;
    }
    if (offer.offer_message != null && String(offer.offer_message).trim()) body.offer_message = String(offer.offer_message).trim();
    if (offer.payment_structure && ['50_50', '100', 'phases'].includes(offer.payment_structure)) {
      body.payment_structure = offer.payment_structure;
      if (offer.payment_structure === 'phases' && Array.isArray(offer.payment_phases) && offer.payment_phases.length > 0) {
        body.payment_phases = offer.payment_phases
          .map(p => ({ title: String(p.title || '').trim(), percentage: parseInt(p.percentage, 10) }))
          .filter(p => p.title && !Number.isNaN(p.percentage) && p.percentage >= 1 && p.percentage <= 100);
      }
    }
    try {
      await axios.post(`${API}/briefs/${briefId}/apply`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyApplications(prev => ({ ...prev, [briefId]: 'applied' }));
      setShowApplyConfirmModal(false);
      setBriefToApply(null);
      setApplyOfferPrice('');
      setApplyOfferMessage('');
      setApplyPaymentStructure('');
      setApplyPaymentPhases([{ title: '', percentage: '' }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const alreadyApplied = typeof detail === 'string' && (
        /ya has aplicado|already applied|duplicate/i.test(detail)
      );
      if (alreadyApplied) {
        setMyApplications(prev => ({ ...prev, [briefId]: 'applied' }));
        setShowApplyConfirmModal(false);
        setBriefToApply(null);
        setApplyOfferPrice('');
        setApplyOfferMessage('');
        setApplyPaymentStructure('');
        setApplyPaymentPhases([{ title: '', percentage: '' }]);
      } else {
        alert(detail || 'No se pudo enviar la aplicación');
        console.error('Error applying to brief:', err);
      }
    } finally {
      setApplyingToBriefId(null);
    }
  };

  // Briefs visibles en la lista (creadores: solo abiertos; creativos: según filtro de aplicados)
  const visibleBriefs = useMemo(() => {
    if (!Array.isArray(briefs)) return [];
    if (user.role === 'creator' || user.role === 'admin') {
      if (settings?.showProjectsInBriefs) {
        const open = briefs.filter(b => b.status === 'open');
        const others = briefs.filter(b => b.status !== 'open');
        return [...open, ...others];
      }
      return briefs.filter(brief => brief.status === 'open');
    }
    // Creativo: filtrar por si ha aplicado o no
    const appliedFilter = filters.applied_filter || 'not_applied';
    if (appliedFilter === 'not_applied') {
      return briefs.filter(b => !myApplications[b.id]);
    }
    if (appliedFilter === 'applied') {
      return briefs.filter(b => myApplications[b.id]);
    }
    return briefs;
  }, [briefs, user.role, settings, filters.applied_filter, myApplications]);

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
    setPagination({
      ...pagination,
      offset: 0
    });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      budget_min: '',
      budget_max: '',
      experience_level: 'any',
      sort_by: 'created_at',
      sort_order: 'desc',
      applied_filter: user?.role === 'creative' ? 'not_applied' : undefined
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatBudget = (min, max) => {
    if (min && max) return `${min}€ - ${max}€`;
    if (min) return `Desde ${min}€`;
    if (max) return `Hasta ${max}€`;
    return 'A negociar';
  };

  const closeCompanyModal = () => {
    setCompanyModal({ open: false, loading: false, profile: null, creatorId: null });
  };

  const openCompanyModal = async (creatorId, fallbackProfile = null) => {
    const id = creatorId != null ? String(creatorId) : '';
    if (!id) {
      setCompanyModal({ open: true, loading: false, profile: fallbackProfile, creatorId: null });
      return;
    }
    const cached = companyProfileCache[id];
    if (cached) {
      setCompanyModal({ open: true, loading: false, profile: cached, creatorId: id });
      return;
    }
    try {
      const res = await axios.get(`${publicApi}/public/profile/${id}`);
      const profile = res.data || null;
      if (profile) setCompanyProfileCache(prev => ({ ...prev, [id]: profile }));
      setCompanyModal({ open: true, loading: false, profile, creatorId: id });
    } catch {
      setCompanyModal({ open: true, loading: false, profile: fallbackProfile, creatorId: id });
    }
  };

  if (showProposals) {
    return (
      <ProposalsView
        briefId={showProposals.id}
        briefTitle={showProposals.title}
        onBack={() => {
          setShowProposals(null);
          if (onCloseBrief) onCloseBrief();
        }}
        navigateToProject={navigateToProject}
      />
    );
  }

  if (showSendProposal) {
    return (
      <SendProposal
        brief={showSendProposal}
        onClose={() => setShowSendProposal(null)}
        onSuccess={() => {
          fetchBriefs();
        }}
      />
    );
  }

  return (
    <div className="briefs-list">
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h2>
            {(user.role === 'creator' || user.role === 'admin') ? (
              <>
                <FiFileText aria-hidden="true" />
                Mis Proyectos
              </>
            ) : (
              <>
                <FiSearch aria-hidden="true" />
                Buscar Proyectos
              </>
            )}
          </h2>
          <p>
            {(user.role === 'creator' || user.role === 'admin')
              ? 'Gestiona tus proyectos y revisa las aplicaciones'
              : 'Encuentra proyectos perfectos para tus habilidades'
            }
          </p>
        </div>
        <button 
          onClick={() => fetchBriefs()} 
          className="btn-refresh"
          disabled={loading}
        >
          {loading ? (
            <>
              <FiLoader className="loading-spinner" aria-hidden="true" />
              Actualizando...
            </>
          ) : (
            <>
              <FiRefreshCcw aria-hidden="true" />
              Actualizar
            </>
          )}
        </button>
      </motion.div>

      {/* Level Up Prompt for Creatives */}
      {user.role === 'creative' && <LevelUpPrompt variant="briefs" onNavigateToRewards={onNavigateToRewards} />}

      {/* Enhanced Filters - Only show for creatives exploring briefs */}
      {user.role === 'creative' && (
        <motion.div
          className="enhanced-filters"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="filters-header">
            <h3 className="flex items-center gap-2">
              <FiSearch aria-hidden="true" />
              Filtros
            </h3>
            <button onClick={clearFilters} className="btn-clear-filters">
              Limpiar Filtros
            </button>
          </div>
          
          <div className="filters-grid">
            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FiSearch aria-hidden="true" />
                Buscar
              </label>
              <input
                type="text"
                className="search-input"
                placeholder="Título, descripción..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FiFolder aria-hidden="true" />
                Categoría
              </label>
              <select
                className="filter-select"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Todas las categorías</option>
                {BRIEF_CATEGORY_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FiTarget aria-hidden="true" />
                Experiencia
              </label>
              <select
                className="filter-select"
                value={filters.experience_level}
                onChange={(e) => handleFilterChange('experience_level', e.target.value)}
              >
                <option value="">Cualquier nivel</option>
                <option value="junior">Junior</option>
                <option value="mid">Intermedio</option>
                <option value="senior">Senior</option>
                <option value="expert">Experto</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FaEuroSign aria-hidden="true" />
                Presupuesto Mín.
              </label>
              <input
                type="number"
                className="budget-input"
                placeholder="0€"
                value={filters.budget_min}
                onChange={(e) => handleFilterChange('budget_min', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FaEuroSign aria-hidden="true" />
                Presupuesto Máx.
              </label>
              <input
                type="number"
                className="budget-input"
                placeholder="1000€"
                value={filters.budget_max}
                onChange={(e) => handleFilterChange('budget_max', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="flex items-center gap-1">
                <FiBarChart2 aria-hidden="true" />
                Ordenar por
              </label>
              <select
                className="filter-select"
                value={`${filters.sort_by}_${filters.sort_order}`}
                onChange={(e) => {
                  const [sort_by, sort_order] = e.target.value.split('_');
                  setFilters({...filters, sort_by, sort_order});
                }}
              >
                <option value="created_at_desc">Más recientes</option>
                <option value="created_at_asc">Más antiguos</option>
                <option value="budget_max_desc">Mayor presupuesto</option>
                <option value="budget_max_asc">Menor presupuesto</option>
                <option value="views_desc">Más vistos</option>
              </select>
            </div>

            {user.role === 'creative' && (
              <div className="filter-group">
                <label className="flex items-center gap-1">
                  <FiCheckCircle aria-hidden="true" />
                  Aplicados
                </label>
                <select
                  className="filter-select"
                  value={filters.applied_filter || 'not_applied'}
                  onChange={(e) => handleFilterChange('applied_filter', e.target.value)}
                >
                  <option value="not_applied">Solo no aplicados</option>
                  <option value="applied">Solo aplicados</option>
                  <option value="all">Todos</option>
                </select>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {loading ? (
        <LoadingSpinner text="Cargando briefs..." />
      ) : visibleBriefs.length === 0 ? (
        <motion.div
          className="empty-state-enhanced"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="empty-state-icon">
            <FiFileText />
          </div>
          <h3>No hay briefs disponibles</h3>
          <p>
            {(user.role === 'creator' || user.role === 'admin')
              ? 'Aún no has creado ningún proyecto. Publica tu primer proyecto y conecta con creativos talentosos.'
              : 'No hay briefs que coincidan con tus filtros. Intenta ajustar los criterios de búsqueda.'
            }
          </p>
          {(user.role === 'creator' || user.role === 'admin') && (
            <button 
              className="btn-create-first"
              onClick={() => setActiveTab('create-brief')}
              disabled
            >
              <FiPlus aria-hidden="true" />
              Crear mi primer Proyecto
            </button>
          )}
          {user.role === 'creative' && (
            <button 
              className="btn-secondary"
              onClick={() => {
                setFilters({
                  category: '',
                  experience_level: '',
                  budget_min: '',
                  budget_max: '',
                  search: '',
                  sort_by: 'created_at',
                  sort_order: 'desc'
                });
              }}
            >
              <FiRefreshCcw aria-hidden="true" />
              Limpiar filtros
            </button>
          )}
        </motion.div>
      ) : (
        <>
          {user.role === 'creative' && !stripeCanApply && (
            <div className="briefs-stripe-required-banner" role="alert">
              <strong>Para aplicar a briefs</strong> debes configurar tu cuenta para recibir pagos. Ve a <strong>Pagos</strong> (menú de tu perfil) y completa el proceso con Stripe.
            </div>
          )}
        <motion.div
          className="briefs-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {visibleBriefs.map((brief, index) => {
            const isExclusiveForMe = brief.target_creative_id && brief.target_creative_id === user.id;
            const goToBriefDetail = () => navigate(`/brief/${brief.id}`);
            return (
            <motion.div
              key={brief.id}
              className={`brief-card brief-card--clickable ${isExclusiveForMe ? 'brief-exclusive' : ''} ${brief.admin_only ? 'brief-has-admin-badge' : ''}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.05, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              role="button"
              tabIndex={0}
              onClick={goToBriefDetail}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToBriefDetail();
                }
              }}
              aria-label={`Ver detalles del proyecto: ${brief.title}`}
            >
              {isExclusiveForMe && (
                <div className="brief-exclusive-badge">
                  <FiStar aria-hidden="true" />
                  Único para ti
                </div>
              )}
              {brief.admin_only && (
                <div className="brief-admin-only-badge" title="Solo visible para administradores (pruebas)">
                  <FiShield aria-hidden="true" />
                  Solo visible por admin
                </div>
              )}
              <div className="brief-title-section">
                <h3>{brief.title}</h3>
                <div className="brief-meta-header">
                  <div className="brief-views">
                    <span className="flex items-center gap-1">
                      <FiEye aria-hidden="true" />
                      {brief.views}
                    </span>
                  </div>
                  {brief.hasAcceptedProposal ? (
                    <span className="status-badge accepted-proposal" aria-label="Brief con aplicación aceptada">
                      ✅ Aplicación Aceptada
                    </span>
                  ) : (
                    <span className={`status-badge ${brief.status}`}>
                      {brief.status === 'open' ? 'Abierto' : 
                       brief.status === 'in_progress' ? 'Proyecto Creado' : 'Completado'}
                    </span>
                  )}
                </div>
              </div>

              <div
                className="brief-publisher-row"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const fallback = {
                    name: brief.creator_name || 'Empresa',
                    profile_image: brief.creator_avatar || null,
                    role: 'brand',
                    company_legal_name: brief.creator_company_legal_name || brief.creator_company_name || null,
                    industry: brief.creator_industry || null,
                    company_type: brief.creator_company_type || null,
                    company_size: brief.creator_company_size || null,
                    founded_year: brief.creator_founded_year || null,
                    bio: brief.creator_bio || null,
                    website: brief.creator_website || null,
                    linkedin_url: brief.creator_linkedin_url || null,
                    instagram_url: brief.creator_instagram_url || null,
                    twitter_url: brief.creator_twitter_url || null,
                    facebook_url: brief.creator_facebook_url || null,
                    behance_url: brief.creator_behance_url || null,
                  };
                  openCompanyModal(brief.creator_id, fallback);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const fb = { name: brief.creator_name || 'Empresa', profile_image: brief.creator_avatar || null, role: 'brand', company_legal_name: brief.creator_company_legal_name || brief.creator_company_name || null };
                    openCompanyModal(brief.creator_id, fb);
                  }
                }}
                aria-label={brief.creator_name ? `Ver información de ${brief.creator_name}` : 'Ver información de la empresa'}
                title="Ver información de la empresa"
              >
                <div className="brief-publisher-left">
                  <img
                    className="brief-publisher-avatar"
                    src={getAvatarUrl(brief.creator_avatar, brief.creator_name) || DEFAULT_USER_AVATAR_DATA_URI}
                    alt={brief.creator_name ? `Foto de perfil de ${brief.creator_name}` : 'Foto de perfil'}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI; }}
                    loading="lazy"
                  />
                  <div className="brief-publisher-text">
                    <div className="brief-publisher-name">{brief.creator_name || 'Empresa'}</div>
                    <div className="brief-publisher-company">
                      {brief.creator_company_legal_name || brief.creator_company_name || 'Ver información de la empresa'}
                    </div>
                  </div>
                </div>
                <span className="brief-publisher-info-btn" aria-hidden="true">
                  <FiInfo />
                </span>
              </div>

              <p className="brief-description">{brief.description}</p>

              {brief.target_audience && (
                <p className="brief-extra brief-target-audience">
                  <span className="brief-extra-label">Público objetivo:</span>{' '}
                  {brief.target_audience.length > 120 ? `${brief.target_audience.slice(0, 120)}…` : brief.target_audience}
                </p>
              )}
              {brief.references && (
                <p className="brief-extra brief-references">
                  <span className="brief-extra-label">Referencias:</span>{' '}
                  {brief.references.length > 100 ? `${brief.references.slice(0, 100)}…` : brief.references}
                </p>
              )}

              <div className="brief-tags">
                <span className="tag category-tag">{getBriefCategoryLabel(brief.category)}</span>
                <span className="tag experience-tag">{brief.experience_level}</span>
                {brief.project_duration && (
                  <span className="tag duration-tag">{brief.project_duration}</span>
                )}
              </div>

              <div className="meta-row">
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FaEuroSign aria-hidden="true" />
                    Presupuesto:
                  </span>
                  <span className="meta-value">
                    {formatBudget(brief.budget_min, brief.budget_max)}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiCalendar aria-hidden="true" />
                    Creado:
                  </span>
                  <span className="meta-value">{formatDate(brief.created_at)}</span>
                </div>
              </div>

              <div className="meta-row">
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiUser aria-hidden="true" />
                    Creador de contenido:
                  </span>
                  <span className="meta-value">
                    {brief.creator_name}
                    {brief.creator_rating > 0 && (
                      <span className="rating">⭐ {brief.creator_rating}</span>
                    )}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiMail aria-hidden="true" />
                    Aplicaciones:
                  </span>
                  <span className="meta-value">{brief.applications_count ?? 0}</span>
                </div>
              </div>

              {brief.deadline && (
                <div className="brief-deadline">
                  <FiClock aria-hidden="true" />
                  Fecha límite: {formatDate(brief.deadline)}
                </div>
              )}

              {brief.requirements && brief.requirements.length > 0 && (
                <div className="brief-requirements">
                  <h4 className="flex items-center gap-2">
                    <FiClipboard aria-hidden="true" />
                    Requisitos:
                  </h4>
                  <ul>
                    {brief.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="button"
                className="brief-toggle-expand"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/brief/${brief.id}`);
                }}
              >
                <FiEye aria-hidden="true" />
                Ver más detalles
              </button>

              <div className="brief-actions" onClick={(e) => e.stopPropagation()}>
                {(user.role === 'creator' || user.role === 'admin') && brief.creator_id === user.id ? (
                  brief.hasAcceptedProposal ? (
                    <button
                      onClick={() => navigateToProject(brief.projectId, 'detail')}
                      className="btn-primary"
                    >
                      <FiEye className="mr-2" aria-hidden="true" />
                      Ver Proyecto
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => onOpenBrief ? onOpenBrief(brief.id) : setShowProposals(brief)}
                        className="btn-primary"
                      >
                        <FiEye className="mr-2" aria-hidden="true" />
                        Ver Aplicaciones ({brief.applications_count ?? 0})
                      </button>
                      {brief.status === 'open' && (
                        <button
                          onClick={() => openCloseBriefModal(brief)}
                          className="btn-secondary brief-action-close-btn"
                          title="Cerrar la oferta sin aceptar a nadie"
                        >
                          <FiXCircle className="mr-2" aria-hidden="true" />
                          Cerrar oferta
                        </button>
                      )}
                    </>
                  )
                ) : user.role === 'creative' && brief.status === 'open' ? (
                  (() => {
                    const appStatus = myApplications[String(brief.id)];
                    // Si ya aplicó (cualquier estado), no mostrar el botón Aplicar
                    if (appStatus === 'applied') {
                      return (
                        <span className="brief-action-badge brief-action-applied">
                          Aplicación enviada
                        </span>
                      );
                    }
                    if (appStatus === 'rejected') {
                      return (
                        <span className="brief-action-badge brief-action-rejected">
                          No seleccionado
                        </span>
                      );
                    }
                    if (appStatus === 'accepted') {
                      return (
                        <span className="brief-action-badge brief-action-accepted">
                          Seleccionado
                        </span>
                      );
                    }
                    if (appStatus) {
                      // Cualquier otro estado = ya aplicó, no mostrar Aplicar
                      return (
                        <span className="brief-action-badge brief-action-applied">
                          Aplicación enviada
                        </span>
                      );
                    }
                    return (
                      <button
                        onClick={() => openApplyConfirmModal(brief)}
                        className="btn-primary"
                        disabled={!!applyingToBriefId || !stripeCanApply}
                        title={!stripeCanApply ? 'Configura tu cuenta para recibir pagos en Pagos antes de aplicar a briefs' : ''}
                      >
                        {applyingToBriefId === String(brief.id) ? (
                          <>Enviando...</>
                        ) : !stripeCanApply ? (
                          <>Configura recibir pagos para aplicar</>
                        ) : (
                          <>
                            <FiSend className="mr-2" aria-hidden="true" />
                            Aplicar
                          </>
                        )}
                      </button>
                    );
                  })()
                ) : null}
              </div>
            </motion.div>
            );
          })}
        </motion.div>
        </>
      )}

      {/* Modal: aplicar al brief con oferta (precio propuesto + mensaje) */}
      {/* Modal: Cerrar oferta (creator) */}
      {showCloseBriefModal && closeBriefTarget && (
        <div className="modal-overlay" onClick={() => !closeBriefLoading && setShowCloseBriefModal(false)} role="dialog" aria-modal="true">
          <div className="modal-content close-brief-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiXCircle aria-hidden="true" /> Cerrar oferta
              </h3>
              <button type="button" className="modal-close" onClick={() => !closeBriefLoading && setShowCloseBriefModal(false)} disabled={closeBriefLoading} aria-label="Cerrar">
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Vas a cerrar la oferta <strong>«{closeBriefTarget.title}»</strong>. Esto significa que:
              </p>
              <ul className="close-request-bullets">
                <li>La oferta dejará de aparecer en "Buscar Proyectos" y no aceptará más aplicaciones.</li>
                {closeBriefTarget.applications_count > 0 ? (
                  <li>
                    Los <strong>{closeBriefTarget.applications_count} creativos</strong> que ya aplicaron recibirán una notificación de que la oferta se ha cerrado.
                  </li>
                ) : (
                  <li>Aún no hay aplicaciones, así que no se notificará a nadie.</li>
                )}
                <li>Esta acción no se puede deshacer (la oferta queda en estado <em>cancelada</em>).</li>
              </ul>
              <label className="close-request-label">
                Motivo (opcional, visible en la notificación a los creativos)
              </label>
              <textarea
                className="close-request-textarea"
                placeholder="Ej: la marca ha cambiado de planes, ya no necesitamos este servicio..."
                value={closeBriefReason}
                onChange={(e) => setCloseBriefReason(e.target.value)}
                maxLength={500}
                rows={3}
              />
              {closeBriefError && <p className="close-request-error">{closeBriefError}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowCloseBriefModal(false)} disabled={closeBriefLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={submitCloseBrief} disabled={closeBriefLoading}>
                {closeBriefLoading ? 'Cerrando…' : 'Cerrar oferta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showApplyConfirmModal && briefToApply && (
        <div className="modal-overlay modal-overlay--apply" onClick={closeApplyConfirmModal} role="dialog" aria-modal="true" aria-labelledby="apply-confirm-title">
          <div className="modal-content apply-confirm-modal apply-offer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="apply-confirm-title" className="flex items-center gap-2">
                <FiSend aria-hidden="true" />
                Aplicar al brief
              </h3>
              <button type="button" onClick={closeApplyConfirmModal} className="modal-close" aria-label="Cerrar">
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <p className="apply-offer-brief-title">Brief: <strong>«{briefToApply.title}»</strong></p>
              {(briefToApply.budget_min != null || briefToApply.budget_max != null) && (
                <p className="apply-offer-budget-hint">
                  Presupuesto indicado en el brief: {formatBudget(briefToApply.budget_min, briefToApply.budget_max)}
                </p>
              )}
              <label className="apply-offer-label">
                Precio que propones (€) <span className="apply-offer-optional">opcional</span>
              </label>
              <p className="apply-offer-price-default-hint" role="status">
                Si no indicas un precio, se usará el <strong>mínimo del proyecto</strong>
                {briefToApply.budget_min != null ? ` (${briefToApply.budget_min} €)` : ''}.
              </p>
              <input
                type="number"
                min={briefToApply.budget_min != null ? briefToApply.budget_min : 0}
                max={briefToApply.budget_max != null ? briefToApply.budget_max : undefined}
                step="0.01"
                className={`apply-offer-input ${(() => {
                  const v = applyOfferPrice.trim();
                  if (!v) return '';
                  const num = Number(v);
                  if (Number.isNaN(num)) return 'apply-offer-input-error';
                  if (briefToApply.budget_min != null && num < briefToApply.budget_min) return 'apply-offer-input-error';
                  if (briefToApply.budget_max != null && num > briefToApply.budget_max) return 'apply-offer-input-error';
                  return '';
                })()}`}
                placeholder="Ej. 500"
                value={applyOfferPrice}
                onChange={e => setApplyOfferPrice(e.target.value)}
              />
              {(() => {
                const v = applyOfferPrice.trim();
                if (!v) return null;
                const num = Number(v);
                if (Number.isNaN(num)) return <p className="apply-offer-error">Indica un número válido.</p>;
                if (briefToApply.budget_min != null && num < briefToApply.budget_min) {
                  return <p className="apply-offer-error">El precio no puede ser menor que el mínimo del proyecto ({briefToApply.budget_min} €).</p>;
                }
                if (briefToApply.budget_max != null && num > briefToApply.budget_max) {
                  return <p className="apply-offer-error">El precio no puede ser superior al máximo del proyecto ({briefToApply.budget_max} €).</p>;
                }
                return null;
              })()}
              <label className="apply-offer-label">
                Cómo quieres cobrar al creador/agencia
              </label>
              <div className="apply-payment-structure">
                <label className="apply-payment-option">
                  <input
                    type="radio"
                    name="payment_structure"
                    checked={applyPaymentStructure === '50_50'}
                    onChange={() => setApplyPaymentStructure('50_50')}
                  />
                  <span>50% anticipo / 50% al entregar</span>
                </label>
                <label className="apply-payment-option">
                  <input
                    type="radio"
                    name="payment_structure"
                    checked={applyPaymentStructure === '100'}
                    onChange={() => setApplyPaymentStructure('100')}
                  />
                  <span>100% al completar el proyecto</span>
                </label>
                <label className="apply-payment-option">
                  <input
                    type="radio"
                    name="payment_structure"
                    checked={applyPaymentStructure === 'phases'}
                    onChange={() => setApplyPaymentStructure('phases')}
                  />
                  <span>Por fases (define tú las fases y porcentajes)</span>
                </label>
              </div>
              {applyPaymentStructure === 'phases' && (
                <div className="apply-payment-phases">
                  <p className="apply-offer-hint">Indica las fases y el % de cada una (deben sumar 100%). Los importes se calcularán según el precio que hayas indicado arriba.</p>
                  {applyPaymentPhases.map((phase, idx) => (
                    <div key={idx} className="apply-phase-row">
                      <input
                        type="text"
                        className="apply-phase-title"
                        placeholder="Ej. Entrega primer borrador"
                        value={phase.title}
                        onChange={e => setApplyPaymentPhases(prev => prev.map((p, i) => i === idx ? { ...p, title: e.target.value } : p))}
                      />
                      <input
                        type="number"
                        min={1}
                        max={100}
                        className="apply-phase-pct"
                        placeholder="%"
                        value={phase.percentage}
                        onChange={e => setApplyPaymentPhases(prev => prev.map((p, i) => i === idx ? { ...p, percentage: e.target.value } : p))}
                      />
                      <span className="apply-phase-amount">
                        {(() => {
                          const total = Number(applyOfferPrice);
                          const pct = parseInt(phase.percentage, 10);
                          if (Number.isNaN(total) || total <= 0 || Number.isNaN(pct)) return '—';
                          return `${(total * pct / 100).toFixed(2)} €`;
                        })()}
                      </span>
                      {applyPaymentPhases.length > 1 && (
                        <button type="button" className="btn-remove-phase" onClick={() => setApplyPaymentPhases(prev => prev.filter((_, i) => i !== idx))} aria-label="Quitar fase">
                          <FiX aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn-add-phase" onClick={() => setApplyPaymentPhases(prev => [...prev, { title: '', percentage: '' }])}>
                    <FiPlus aria-hidden="true" /> Añadir fase
                  </button>
                  {(() => {
                    const totalPct = applyPaymentPhases.reduce((acc, p) => acc + (parseInt(p.percentage, 10) || 0), 0);
                    if (totalPct !== 0 && totalPct !== 100) return <p className="apply-offer-error">Los porcentajes deben sumar 100 (actual: {totalPct}%).</p>;
                    return null;
                  })()}
                </div>
              )}
              <label className="apply-offer-label">
                Mensaje para el creador <span className="apply-offer-optional">opcional</span>
              </label>
              <textarea
                className="apply-offer-textarea"
                placeholder="Cómo abordarías el proyecto, disponibilidad, preguntas..."
                value={applyOfferMessage}
                onChange={e => setApplyOfferMessage(e.target.value)}
                rows={4}
              />
              <p className="apply-offer-hint">
                El creador podrá revisar tu perfil y seleccionar hasta 5 creativos para una reunión de primer contacto. Si te elige, el proyecto se creará con el precio que hayas indicado (o el del brief si no indicas ninguno).
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={closeApplyConfirmModal} className="btn-secondary" disabled={!!applyingToBriefId}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => applyToBrief(briefToApply, {
                  proposed_price: applyOfferPrice,
                  offer_message: applyOfferMessage,
                  payment_structure: applyPaymentStructure || undefined,
                  payment_phases: applyPaymentStructure === 'phases' ? applyPaymentPhases : undefined
                })}
                className="btn-primary"
                disabled={!!applyingToBriefId || (() => {
                  const v = applyOfferPrice.trim();
                  if (!v) return false;
                  const num = Number(v);
                  if (Number.isNaN(num)) return true;
                  if (briefToApply.budget_min != null && num < briefToApply.budget_min) return true;
                  if (briefToApply.budget_max != null && num > briefToApply.budget_max) return true;
                  if (applyPaymentStructure === 'phases') {
                    const totalPct = applyPaymentPhases.reduce((acc, p) => acc + (parseInt(p.percentage, 10) || 0), 0);
                    const allTitles = applyPaymentPhases.every(p => (p.title || '').trim());
                    if (totalPct !== 100 || !allTitles) return true;
                  }
                  return false;
                })()}
              >
                {applyingToBriefId === String(briefToApply.id) ? 'Enviando...' : 'Enviar aplicación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {companyModal.open && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div className="modal-overlay modal-overlay--company-info" onClick={closeCompanyModal} role="dialog" aria-modal="true" aria-labelledby="company-info-modal-title">
          <div className="modal-content company-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="company-info-modal-title" className="flex items-center gap-2">
                <FiInfo aria-hidden="true" />
                Información de la empresa
              </h3>
              <button type="button" className="modal-close" onClick={closeCompanyModal} aria-label="Cerrar"><FiX /></button>
            </div>
            <div className="modal-body">
              {!companyModal.profile ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>No se pudo cargar la información de esta empresa.</p>
              ) : (
                (() => {
                  const p = companyModal.profile;
                  const avatarUrl = p.profile_image
                    ? (p.profile_image.startsWith('http') ? p.profile_image : `${UPLOAD_BASE}${p.profile_image}`)
                    : null;
                  const companyTypeLabel = (t) => ({ marca: 'Marca', agencia: 'Agencia', startup: 'Startup', corporacion: 'Corporación', otro: 'Otro' })[(t || '').toLowerCase()] || t;
                  const socialLinks = [
                    { key: 'linkedin', href: p.linkedin_url, label: 'LinkedIn', icon: <FaLinkedin aria-hidden="true" /> },
                    { key: 'instagram', href: p.instagram_url, label: 'Instagram', icon: <FaInstagram aria-hidden="true" /> },
                    { key: 'x', href: p.twitter_url, label: 'X', icon: <FaTwitter aria-hidden="true" /> },
                    { key: 'facebook', href: p.facebook_url, label: 'Facebook', icon: <FaFacebookF aria-hidden="true" /> },
                    { key: 'behance', href: p.behance_url, label: 'Behance', icon: <FaBehance aria-hidden="true" /> }
                  ].filter(link => !!link.href);
                  return (
                    <div className="company-info">
                      <div className="company-info__hero">
                        <img
                          className="company-info__avatar"
                          src={avatarUrl || getAvatarUrl(null, p.name) || DEFAULT_USER_AVATAR_DATA_URI}
                          alt={p.name ? `Foto de perfil de ${p.name}` : 'Foto de perfil'}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI; }}
                          loading="lazy"
                        />
                        <div className="company-info__title">
                          <div className="company-info__name">{p.company_legal_name || p.name}</div>
                          {(p.company_type || p.industry || p.company_size) && (
                            <div className="company-info__subtitle">
                              {[p.company_type && companyTypeLabel(p.company_type), p.industry, p.company_size].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      </div>

                      {p.bio && <p className="company-info__bio">{p.bio}</p>}

                      <dl className="company-info__details">
                        {p.company_legal_name && (
                          <div className="company-info__row">
                            <dt>Razón social</dt>
                            <dd>{p.company_legal_name}</dd>
                          </div>
                        )}
                        {p.founded_year && (
                          <div className="company-info__row">
                            <dt>Año de fundación</dt>
                            <dd>{p.founded_year}</dd>
                          </div>
                        )}
                        {p.website && (
                          <div className="company-info__row">
                            <dt>Web</dt>
                            <dd><a href={p.website} target="_blank" rel="noreferrer">{p.website}</a></dd>
                          </div>
                        )}
                      </dl>
                      {socialLinks.length > 0 && (
                        <div className="company-info__socials" aria-label="Redes sociales de la empresa">
                          {socialLinks.map(link => (
                            <a
                              key={link.key}
                              href={link.href}
                              target="_blank"
                              rel="noreferrer"
                              className="company-info__social-link"
                              aria-label={link.label}
                              title={link.label}
                            >
                              {link.icon}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pagination */}
      <div className="pagination">
        <button 
          onClick={() => setPagination({...pagination, offset: Math.max(0, pagination.offset - pagination.limit)})}
          disabled={pagination.offset === 0}
          className="btn-pagination"
        >
          ← Anterior
        </button>
        
        <span className="pagination-info">
          Mostrando {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, briefs.length)} de {briefs.length}
        </span>
        
        <button 
          onClick={() => setPagination({...pagination, offset: pagination.offset + pagination.limit})}
          disabled={briefs.length < pagination.limit}
          className="btn-pagination"
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
};

// My Proposals Component
const MyProposals = ({ initialSubView = null, onNavigateToRewards }) => {
  const { user, token } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, rejected
  const [expandedProposalId, setExpandedProposalId] = useState(null);
  const [viewRequests, setViewRequests] = useState([]);
  const [respondingToRequestId, setRespondingToRequestId] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [briefForProposal, setBriefForProposal] = useState(null);
  const [loadingBriefForProposal, setLoadingBriefForProposal] = useState(false);
  // Solo Aplicaciones (apliques); ya no se usan propuestas ni solicitudes
  const [proposalsSubView, setProposalsSubView] = useState(initialSubView || 'apliques');
  const [acceptedViewRequests, setAcceptedViewRequests] = useState([]);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [apliquesFilter, setApliquesFilter] = useState('all'); // all | pending | in_progress | rejected
  const [firstContactMeetingsCreative, setFirstContactMeetingsCreative] = useState([]); // reuniones donde yo soy el creativo

  useEffect(() => {
    if (initialSubView) setProposalsSubView(initialSubView);
  }, [initialSubView]);

  useEffect(() => {
    fetchProposals();
  }, []);

  useEffect(() => {
    if (user?.role === 'creative' && token) {
      axios.get(`${API}/proposals/view-requests`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setViewRequests(res.data?.view_requests || []))
        .catch(() => setViewRequests([]));
    }
  }, [user?.role, token]);

  const fetchAcceptedViewRequests = useCallback(() => {
    if (user?.role !== 'creative' || !token) return;
    axios.get(`${API}/proposals/view-requests/accepted`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAcceptedViewRequests(res.data?.accepted_requests || []))
      .catch(() => setAcceptedViewRequests([]));
  }, [user?.role, token]);

  useEffect(() => {
    if (user?.role === 'creative' && token && proposalsSubView === 'solicitudes') {
      fetchAcceptedViewRequests();
    }
  }, [user?.role, token, proposalsSubView, fetchAcceptedViewRequests]);

  const fetchMyApplications = useCallback(async () => {
    if (user?.role !== 'creative' || !token) return;
    try {
      const res = await axios.get(`${API}/briefs/my-applications`, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(res.data?.applications) ? res.data.applications : [];
      setMyApplications(list);
    } catch (err) {
      console.error('Error fetching my applications:', err?.response?.status, err?.response?.data || err.message);
      setMyApplications([]);
    }
  }, [user?.role, token]);

  useEffect(() => {
    if (user?.role === 'creative') fetchMyApplications();
  }, [user?.role, fetchMyApplications]);

  // Refetch apliques al abrir la pestaña Apliques (por si aplicó desde Buscar Proyectos y acaba de llegar)
  useEffect(() => {
    if (user?.role === 'creative' && token && proposalsSubView === 'apliques') {
      fetchMyApplications();
    }
  }, [user?.role, token, proposalsSubView, fetchMyApplications]);

  const fetchFirstContactMeetings = useCallback(async () => {
    if (user?.role !== 'creative' || !token) return;
    try {
      const res = await axios.get(`${API}/first-contact/me`, { headers: { Authorization: `Bearer ${token}` } });
      setFirstContactMeetingsCreative(Array.isArray(res.data?.meetings) ? res.data.meetings : []);
    } catch (_) {
      setFirstContactMeetingsCreative([]);
    }
  }, [user?.role, token]);

  useEffect(() => {
    if (user?.role === 'creative' && token) fetchFirstContactMeetings();
  }, [user?.role, token, fetchFirstContactMeetings]);

  useEffect(() => {
    if (user?.role === 'creative' && token && proposalsSubView === 'apliques') fetchFirstContactMeetings();
  }, [proposalsSubView, fetchFirstContactMeetings]);

  const respondToViewRequest = async (proposalId, action) => {
    setRespondingToRequestId(proposalId);
    try {
      await axios.post(`${API}/proposals/${proposalId}/view-request/respond`, { action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewRequests(prev => prev.filter(r => r.proposal_id !== proposalId));
      if (action === 'accept') fetchAcceptedViewRequests();
    } catch (err) {
      console.error('Error responding to view request:', err);
      alert(err.response?.data?.detail || 'No se pudo responder');
    } finally {
      setRespondingToRequestId(null);
    }
  };

  const openSendProposalForApplication = async (briefId) => {
    setLoadingBriefForProposal(true);
    try {
      const res = await axios.get(`${API}/briefs/${briefId}`, { headers: { Authorization: `Bearer ${token}` } });
      setBriefForProposal(res.data);
    } catch (err) {
      console.error('Error loading brief:', err);
      alert(err.response?.data?.detail || 'No se pudo cargar el brief');
    } finally {
      setLoadingBriefForProposal(false);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProposals = proposals.filter(proposal => {
    if (filter === 'all') return true;
    if (filter === 'in_progress') return proposal.status === 'accepted';
    return proposal.status === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <LoadingSpinner text="Cargando aplicaciones..." />;

  if (briefForProposal) {
    return (
      <SendProposal
        brief={briefForProposal}
        onClose={() => setBriefForProposal(null)}
        onSuccess={() => {
          setBriefForProposal(null);
          fetchProposals();
          fetchMyApplications();
        }}
      />
    );
  }

  return (
    <div className="proposals-list">
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="flex items-center gap-2">
          <FiMail aria-hidden="true" />
          Mis Aplicaciones
        </h2>
        <p>Estado de tus aplicaciones a briefs</p>
      </motion.div>

      {/* Level Up Prompt */}
      {user?.role === 'creative' && <LevelUpPrompt variant="proposals" onNavigateToRewards={onNavigateToRewards} />}

      {/* Solo Aplicaciones (ya no hay pestaña Propuestas ni Solicitudes) */}
      {user?.role === 'creative' && (
        <div className="proposals-subview-tabs" style={{ display: 'none' }}>
          <button type="button" className="proposals-subview-tab active" onClick={() => setProposalsSubView('apliques')}>
            <FiFileText aria-hidden="true" /> Aplicaciones {myApplications.length > 0 && `(${myApplications.length})`}
          </button>
        </div>
      )}

      {/* Vista Solicitudes (creativo): aceptar/rechazar + historial de aceptadas */}
      {user?.role === 'creative' && proposalsSubView === 'solicitudes' && (
        <div className="solicitudes-view">
          <div className="view-requests-section view-requests-section-standalone">
            <h3>Solicitudes para ver tu propuesta</h3>
            <p>Un creador quiere ver el contenido de tu propuesta. Si aceptas, quedará registro y el creador podrá verla. Tú y el creador podéis consultar que aceptaste.</p>
            {viewRequests.length === 0 ? (
              <p className="view-requests-empty">No tienes solicitudes pendientes.</p>
            ) : (
              <ul className="view-requests-list">
                {viewRequests.map(req => (
                  <li key={req.id} className="view-request-item">
                    <span><strong>{req.brief_title}</strong> — {req.creator_name} solicita ver la propuesta</span>
                    <div className="view-request-actions">
                      <button type="button" className="btn-reject-small" disabled={!!respondingToRequestId} onClick={() => respondToViewRequest(req.proposal_id, 'reject')}>
                        Rechazar
                      </button>
                      <button type="button" className="btn-primary btn-sm" disabled={!!respondingToRequestId} onClick={() => respondToViewRequest(req.proposal_id, 'accept')}>
                        {respondingToRequestId === req.proposal_id ? '...' : 'Aceptar'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="view-requests-history-section">
            <h3>Historial: solicitudes que aceptaste</h3>
            <p>Registro de las veces que aceptaste que un creador viera tu propuesta. Incluye la propuesta que enviaste (mensaje, archivos, hitos).</p>
            {acceptedViewRequests.length === 0 ? (
              <p className="view-requests-empty">Aún no has aceptado ninguna solicitud de vista.</p>
            ) : (
              <ul className="view-requests-history-list view-requests-history-cards">
                {acceptedViewRequests.map(rec => {
                  const prop = rec.proposal || {};
                  const expanded = expandedHistoryId === (rec.id || rec.proposal_id);
                  const validSamples = (prop.portfolio_samples || []).filter(s => s && typeof s === 'string' && s.trim() !== '');
                  return (
                    <li key={rec.id || rec.proposal_id} className="view-request-history-card">
                      <div className="view-request-history-header" onClick={() => setExpandedHistoryId(expanded ? null : (rec.id || rec.proposal_id))}>
                        <span><strong>{rec.brief_title}</strong> — {rec.creator_name}</span>
                        <span className="view-request-history-date">
                          Aceptado el {rec.creative_accepted_at ? new Date(rec.creative_accepted_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                        <button type="button" className="view-request-history-toggle" aria-expanded={expanded}>
                          {expanded ? <><FiChevronUp aria-hidden="true" /> Ocultar propuesta</> : <><FiChevronDown aria-hidden="true" /> Ver propuesta enviada</>}
                        </button>
                      </div>
                      {expanded && (
                        <div className="view-request-history-proposal">
                          <div className="proposal-card-section">
                            <h4 className="proposal-section-title"><FiFileText aria-hidden="true" /> Mensaje de la propuesta</h4>
                            <div className="proposal-message-block">
                              {prop.message ? (
                                prop.message.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                                  part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
                                )
                              ) : '—'}
                            </div>
                          </div>
                          <div className="proposal-card-section proposal-details">
                            {prop.proposed_price != null && (
                              <div className="detail-item">
                                <span className="detail-label"><FaEuroSign aria-hidden="true" /> Precio:</span>
                                <span className="detail-value">{prop.proposed_price}€</span>
                              </div>
                            )}
                            {prop.delivery_days != null && (
                              <div className="detail-item">
                                <span className="detail-label"><FiClock aria-hidden="true" /> Entrega:</span>
                                <span className="detail-value">{prop.delivery_days} días</span>
                              </div>
                            )}
                            {prop.payment_conditions && (
                              <div className="detail-item">
                                <span className="detail-label"><FiFileText aria-hidden="true" /> Condiciones de pago:</span>
                                <span className="detail-value">{prop.payment_conditions}</span>
                              </div>
                            )}
                            {prop.response_time_hours != null && (
                              <div className="detail-item">
                                <span className="detail-label"><FiZap aria-hidden="true" /> Tiempo de respuesta:</span>
                                <span className="detail-value">{prop.response_time_hours < 24 ? `${Math.round(prop.response_time_hours)}h` : `${Math.round(prop.response_time_hours / 24)}d`}</span>
                              </div>
                            )}
                          </div>
                          {(prop.milestones || []).length > 0 && (
                            <div className="proposal-card-section">
                              <h4 className="proposal-section-title"><FiCalendar aria-hidden="true" /> Hitos / Cronograma</h4>
                              <ul className="proposal-milestones-list">
                                {prop.milestones.map((m, idx) => (
                                  <li key={idx}>
                                    {m.title || `Fase ${idx + 1}`}
                                    {m.date && ` — ${m.date}`}
                                    {m.amount != null && ` — ${m.amount}€`}
                                    {m.deliverable && ` · ${m.deliverable}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {validSamples.length > 0 && (
                            <div className="proposal-card-section">
                              <h4 className="proposal-section-title"><FiPaperclip aria-hidden="true" /> Archivos adjuntos</h4>
                              <div className="samples-grid">
                                {validSamples.map((sample, index) => {
                                  const href = getUploadUrl(sample);
                                  if (!href || href === 'about:blank') return null;
                                  return (
                                    <a key={index} href={href} target="_blank" rel="noopener noreferrer" className="sample-link">
                                      <FiFileText className="mr-2" aria-hidden="true" /> Recurso {index + 1}
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Solicitudes del creador para ver una propuesta (cuando no estás en pestaña Solicitudes) */}
      {viewRequests.length > 0 && proposalsSubView !== 'solicitudes' && (
        <div className="view-requests-section">
          <h3>Solicitudes para ver tu propuesta</h3>
          <p>Un creador quiere ver el contenido de tu propuesta. Si aceptas, quedará registro de que la ha visto.</p>
          <ul className="view-requests-list">
            {viewRequests.map(req => (
              <li key={req.id} className="view-request-item">
                <span><strong>{req.brief_title}</strong> — {req.creator_name} solicita ver la propuesta</span>
                <div className="view-request-actions">
                  <button type="button" className="btn-reject-small" disabled={!!respondingToRequestId} onClick={() => respondToViewRequest(req.proposal_id, 'reject')}>
                    Rechazar
                  </button>
                  <button type="button" className="btn-primary btn-sm" disabled={!!respondingToRequestId} onClick={() => respondToViewRequest(req.proposal_id, 'accept')}>
                    {respondingToRequestId === req.proposal_id ? '...' : 'Aceptar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Vista Apliques (solo creativos): filtros Todas / Pendientes / En Progreso / Rechazadas + listado */}
      {user?.role === 'creative' && proposalsSubView === 'apliques' && (
        <div className="apliques-view">
          {firstContactMeetingsCreative.length > 0 && (
            <div className="first-contact-section first-contact-section-creative">
              <h3 className="first-contact-title"><FiCalendar aria-hidden="true" /> Reuniones de primer contacto</h3>
              <p className="first-contact-hint">El creador ha agendado una reunión contigo. Añádela a tu Google Calendar para no olvidarla.</p>
              <ul className="first-contact-list">
                {firstContactMeetingsCreative.map(m => {
                  const scheduledStr = m.scheduled_at ? new Date(m.scheduled_at).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : '';
                  return (
                    <li key={m.id} className="first-contact-item">
                      <div className="first-contact-creative">
                        <span className="first-contact-creative-name"><strong>{m.brief_title}</strong> — {m.creator_name}</span>
                      </div>
                      <div className="first-contact-actions">
                        <span className="first-contact-date"><FiClock aria-hidden="true" /> {scheduledStr}</span>
                        <a href={m.google_calendar_url} target="_blank" rel="noopener noreferrer" className="btn-add-google-calendar">
                          <FiCalendar aria-hidden="true" /> Añadir a Google Calendar
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {myApplications.length === 0 ? (
            <motion.div
              className="empty-state apliques-empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="empty-icon" aria-hidden="true">
                <FiFileText />
              </div>
              <h3>No tienes aplicaciones</h3>
              <p>Aún no has aplicado a ningún proyecto. Explora los proyectos disponibles y aplica a los que te interesen.</p>
            </motion.div>
          ) : (
            <>
              <div className="my-applications-section my-applications-section-full">
                <div className="my-applications-section-header">
                  <div>
                    <h3 className="my-applications-title">Mis aplicaciones</h3>
                    <p className="my-applications-hint">Aquí ves el estado de cada aplicación a un brief. Si te aceptaron, has sido seleccionado por el creador.</p>
                  </div>
                  <button type="button" className="btn-secondary btn-refresh-apliques" onClick={() => fetchMyApplications()} title="Actualizar lista">
                    <FiRefreshCcw aria-hidden="true" /> Actualizar
                  </button>
                </div>
                <motion.div className="filter-tabs apliques-filter-tabs" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                  <button
                    type="button"
                    className={`filter-tab ${apliquesFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setApliquesFilter('all')}
                  >
                    Todas ({myApplications.length})
                  </button>
                  <button
                    type="button"
                    className={`filter-tab ${apliquesFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => setApliquesFilter('pending')}
                  >
                    Pendientes ({myApplications.filter(a => (a.status || 'applied') === 'applied').length})
                  </button>
                  <button
                    type="button"
                    className={`filter-tab ${apliquesFilter === 'in_progress' ? 'active' : ''}`}
                    onClick={() => setApliquesFilter('in_progress')}
                  >
                    En Progreso ({myApplications.filter(a => a.status === 'accepted').length})
                  </button>
                  <button
                    type="button"
                    className={`filter-tab ${apliquesFilter === 'rejected' ? 'active' : ''}`}
                    onClick={() => setApliquesFilter('rejected')}
                  >
                    Rechazadas ({myApplications.filter(a => a.status === 'rejected').length})
                  </button>
                </motion.div>
                {(() => {
                  const filtered = apliquesFilter === 'all'
                    ? myApplications
                    : apliquesFilter === 'pending'
                      ? myApplications.filter(a => (a.status || 'applied') === 'applied')
                      : apliquesFilter === 'in_progress'
                        ? myApplications.filter(a => a.status === 'accepted')
                        : myApplications.filter(a => a.status === 'rejected');
                  return filtered.length === 0 ? (
                    <motion.div className="empty-state apliques-filter-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <p>No tienes aplicaciones {apliquesFilter === 'pending' ? 'pendientes' : apliquesFilter === 'in_progress' ? 'en progreso' : 'rechazadas'}.</p>
                    </motion.div>
                  ) : (
                    <ul className="my-applications-list">
                      {filtered.map(app => {
                        const statusLabel = app.status === 'accepted' ? 'Aceptado' : app.status === 'rejected' ? 'No seleccionado' : 'Pendiente de revisión';
                        const statusClass = app.status === 'accepted' ? 'accepted' : app.status === 'rejected' ? 'rejected' : 'pending';
                        const categoryLabel = app.brief_category
                          ? (app.brief_subcategory || getBriefCategoryLabel(app.brief_category))
                          : null;
                        return (
                          <li key={app.application_id || app.brief_id} className="my-application-item">
                            <div className="my-application-info">
                              <strong className="my-application-brief-title">{app.brief_title || 'Brief'}</strong>
                              {app.creator_name && (
                                <span className="my-application-creator">
                                  Creador: {app.creator_name}
                                </span>
                              )}
                              {(categoryLabel || app.brief_budget_range) && (
                                <div className="my-application-meta">
                                  {categoryLabel && <span className="my-application-category">{categoryLabel}</span>}
                                  {app.brief_budget_range && <span className="my-application-budget">{app.brief_budget_range}</span>}
                                  {app.brief_deadline && (
                                    <span className="my-application-deadline">
                                      Límite: {formatDate(app.brief_deadline)}
                                    </span>
                                  )}
                                </div>
                              )}
                              {app.brief_description && (
                                <p className="my-application-description">{app.brief_description}{app.brief_description.length >= 300 ? '…' : ''}</p>
                              )}
                              <div className="my-application-status-row">
                                <span className={`my-application-status my-application-status-${statusClass}`}>{statusLabel}</span>
                                {app.applied_at && (
                                  <span className="my-application-date">Aplicado: {formatDate(app.applied_at)}</span>
                                )}
                              </div>
                            </div>
                            <div className="my-application-actions">
                              {app.status === 'accepted' && (
                                <span className="my-application-sent">Seleccionado</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* Filter Tabs y listado de propuestas (solo en vista Propuestas o para no creativos) */}
      {(user?.role !== 'creative' || proposalsSubView === 'propuestas') && (
      <>
      <motion.div
        className="filter-tabs"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Todas ({proposals.length})
        </button>
        <button 
          className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pendientes ({proposals.filter(p => p.status === 'pending').length})
        </button>
        <button 
          className={`filter-tab ${filter === 'in_progress' ? 'active' : ''}`}
          onClick={() => setFilter('in_progress')}
        >
          En Progreso ({proposals.filter(p => p.status === 'accepted').length})
        </button>
        <button 
          className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rechazadas ({proposals.filter(p => p.status === 'rejected').length})
        </button>
      </motion.div>

      {filteredProposals.length === 0 ? (
        <motion.div
          className="empty-state"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="empty-icon" aria-hidden="true">
            <FiMail />
          </div>
          <h3>No hay aplicaciones</h3>
          <p>
            {filter === 'all' 
              ? 'Aún no has enviado ninguna aplicación. ¡Explora los briefs disponibles!'
              : `No tienes aplicaciones ${filter === 'in_progress' ? 'en progreso' : filter}.`
            }
          </p>
        </motion.div>
      ) : (
        <motion.div
          className="proposals-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {filteredProposals.map((proposal, index) => (
            <motion.div
              key={proposal.id}
              className="proposal-card brief-card my-proposal-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.05, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="proposal-header brief-title-section">
                <h3>{proposal.brief_title}</h3>
                <div className="brief-meta-header">
                  <span className={`status-badge ${proposal.status}`}>
                    {proposal.status === 'pending' ? 'Pendiente' : 
                     proposal.status === 'accepted' ? 'Aceptada' : 'Rechazada'}
                  </span>
                </div>
              </div>

              <p className="brief-description" aria-label="Resumen de la propuesta">
                {(typeof proposal.message === 'string' ? proposal.message : '')
                  .replace(/\*\*/g, '')
                  .replace(/\n/g, ' ')
                  .trim()
                  .slice(0, 220) || 'Sin descripción'}
                {(typeof proposal.message === 'string' ? proposal.message : '').trim().length > 220 ? '…' : ''}
              </p>

              {expandedProposalId === proposal.id && (
                <div className="proposal-message-block proposal-expanded-content">
                  <h4 className="flex items-center gap-2">
                    <FiClipboard aria-hidden="true" />
                    Aplicación completa
                  </h4>
                  <p className="proposal-message-text">
                    {(typeof proposal.message === 'string' ? proposal.message : '').trim() || 'Sin descripción'}
                  </p>
                </div>
              )}

              <div className="meta-row">
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FaEuroSign aria-hidden="true" />
                    Precio:
                  </span>
                  <span className="meta-value">{proposal.proposed_price}€</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiClock aria-hidden="true" />
                    Entrega:
                  </span>
                  <span className="meta-value">{proposal.delivery_days} días</span>
                </div>
              </div>

              <div className="meta-row">
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiUser aria-hidden="true" />
                    Creador de contenido:
                  </span>
                  <span className="meta-value">
                    {proposal.creator_avatar ? (
                      <span className="meta-value-creator">
                        <img
                          src={getAvatarUrl(proposal.creator_avatar, proposal.creator_name)}
                          alt=""
                          className="creator-avatar-tiny"
                          aria-hidden="true"
                        />
                        <span>{proposal.creator_name || '—'}</span>
                      </span>
                    ) : (
                      proposal.creator_name || '—'
                    )}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label flex items-center gap-1">
                    <FiCalendar aria-hidden="true" />
                    Enviada:
                  </span>
                  <span className="meta-value">{formatDate(proposal.created_at)}</span>
                </div>
              </div>

              {proposal.response_time_hours != null && (
                <div className="brief-deadline proposal-info-line">
                  <FiClock aria-hidden="true" />
                  Tiempo de respuesta: {proposal.response_time_hours < 24
                    ? `${Math.round(proposal.response_time_hours)}h`
                    : `${Math.round(proposal.response_time_hours / 24)}d`
                  }
                </div>
              )}

              {proposal.payment_conditions && (
                <div className="brief-deadline proposal-payment-line">
                  <FaEuroSign aria-hidden="true" />
                  Condiciones de pago: {proposal.payment_conditions}
                </div>
              )}

              {proposal.status === 'accepted' && (
                <div className="project-info">
                  <div className="project-status">
                    <span className="flex items-center gap-2">
                      <FiActivity aria-hidden="true" />
                      Proyecto activo - Puedes chatear con el cliente
                    </span>
                  </div>
                </div>
              )}

              {proposal.status === 'rejected' && (
                <div className="rejection-info">
                  <div className="rejection-status">
                    ❌ <span>Aplicación rechazada</span>
                  </div>
                  {proposal.rejection_comment && (
                    <div className="rejection-comment">
                      <strong>Comentario del cliente:</strong>
                      <p>"{proposal.rejection_comment}"</p>
                    </div>
                  )}
                </div>
              )}

              <div className="brief-actions proposal-card-actions">
                <button
                  type="button"
                  className="btn-secondary btn-ver-mas"
                  onClick={() => setExpandedProposalId(expandedProposalId === proposal.id ? null : proposal.id)}
                  aria-expanded={expandedProposalId === proposal.id}
                >
                  {expandedProposalId === proposal.id ? (
                    <>
                      <FiChevronUp aria-hidden="true" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <FiChevronDown aria-hidden="true" />
                      Ver más información
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
      </>
      )}

    </div>
  );
};

// Projects Component
const Projects = ({ initialProjectId = null, initialView = 'list', initialDetailTab = null, navigateToChat = null, onNavigateToRewards, refreshTrigger = 0 }) => {
  const { user, token } = useAuth();
  const { addToast } = useToast();
  const { getCached, setCached, isCacheValid, invalidateCache } = useCache();
  const cachedProjects = getCached('projects');
  const [projects, setProjects] = useState(cachedProjects || []);
  // Solo mostrar loading si no hay datos en caché
  const [loading, setLoading] = useState(!cachedProjects || cachedProjects.length === 0);
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState(initialView);
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId);
  const [detailTab, setDetailTab] = useState('overview');
  const [detailBrief, setDetailBrief] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Complete project modal states
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);
  const [completePreviewFile, setCompletePreviewFile] = useState(null);
  const [completePreviewPreviewUrl, setCompletePreviewPreviewUrl] = useState(null);
  const UPLOAD_BASE_PROJECTS = UPLOAD_BASE;
  
  // Rating modal states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [completedProjectForRating, setCompletedProjectForRating] = useState(null);

  // Completion payment modal (pago restante al completar)
  const [showCompletionPaymentModal, setShowCompletionPaymentModal] = useState(false);
  const [completionPaymentData, setCompletionPaymentData] = useState(null);
  const [completionStripePromise, setCompletionStripePromise] = useState(null);

  // Creative delivery confirmation modal
  const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false);
  const [deliveryConfirmProject, setDeliveryConfirmProject] = useState(null);
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [deliveryConfirmLoading, setDeliveryConfirmLoading] = useState(false);
  const [deliveryConfirmError, setDeliveryConfirmError] = useState(null);
  const [deliveryPendingAmount, setDeliveryPendingAmount] = useState(0);

  // Mutual close project — modal de solicitud y confirmación
  const [showCloseRequestModal, setShowCloseRequestModal] = useState(false);
  const [closeRequestProject, setCloseRequestProject] = useState(null);
  const [closeRequestReason, setCloseRequestReason] = useState('');
  const [closeRequestLoading, setCloseRequestLoading] = useState(false);
  const [closeRequestError, setCloseRequestError] = useState(null);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [closeConfirmProject, setCloseConfirmProject] = useState(null);
  const [closeConfirmPendingAmount, setCloseConfirmPendingAmount] = useState(0);
  const [closeConfirmLoading, setCloseConfirmLoading] = useState(false);
  const [closeConfirmError, setCloseConfirmError] = useState(null);

  // Pago pendiente (cliente): datos de pagos del proyecto en vista detalle + modal al entrar
  const [projectDetailPayments, setProjectDetailPayments] = useState(null);
  const [showPendingPaymentModal, setShowPendingPaymentModal] = useState(false);
  const [pendingPaymentModalDismissed, setPendingPaymentModalDismissed] = useState(false);

  // Solo hay "pago pendiente" exigible ahora si existe algún concepto no pagado que NO sea "al entregar" (type !== 'final')
  const hasImmediatePaymentDue = (paymentsData) => {
    if (!paymentsData) return false;
    if (!paymentsData.schedule?.length) return (paymentsData.total_pending || 0) > 0;
    return paymentsData.schedule.some((s) => !s.paid && s.type !== 'final');
  };

  useEffect(() => {
    // Siempre intentar actualizar en background, pero solo mostrar loading si no hay datos
    fetchProjects();
  }, []);

  useEffect(() => {
    if (refreshTrigger > 0) {
      invalidateCache('projects');
      fetchProjects(true);
    }
  }, [refreshTrigger]);

  // Al salir del detalle del proyecto, resetear el "modal de pago pendiente ya visto"
  useEffect(() => {
    if (activeView !== 'detail' || !selectedProjectId) {
      setPendingPaymentModalDismissed(false);
      setProjectDetailPayments(null);
    }
  }, [activeView, selectedProjectId]);

  // Cargar pagos del proyecto en vista detalle cuando el usuario es el cliente (quien paga)
  const fetchProjectDetailPayments = useCallback(async () => {
    if (!selectedProjectId || !token) return;
    try {
      const res = await axios.get(`${API}/projects/${selectedProjectId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectDetailPayments(res.data);
      if (hasImmediatePaymentDue(res.data) && !pendingPaymentModalDismissed) {
        setShowPendingPaymentModal(true);
      } else {
        setShowPendingPaymentModal(false);
      }
    } catch {
      setProjectDetailPayments(null);
    }
  }, [selectedProjectId, token, pendingPaymentModalDismissed]);

  useEffect(() => {
    if (activeView !== 'detail' || !selectedProjectId || !token) return;
    const project = projects.find(p => p.id === selectedProjectId);
    if (!project?.is_creator) return; // solo el creador (cliente) paga
    fetchProjectDetailPayments();
  }, [activeView, selectedProjectId, projects, token, pendingPaymentModalDismissed, detailTab, fetchProjectDetailPayments]);

  // Effect to handle initial project selection and detail tab (e.g. from notification click)
  useEffect(() => {
    if (initialProjectId && initialView !== 'list') {
      setSelectedProjectId(initialProjectId);
      setActiveView(initialView);
      if (initialDetailTab) setDetailTab(initialDetailTab);
    }
  }, [initialProjectId, initialView, initialDetailTab]);

  // Load associated brief for project detail view
  useEffect(() => {
    const fetchBriefForProject = async () => {
      const project = projects.find(p => p.id === selectedProjectId);
      if (!project || !project.brief_id) {
        setDetailBrief(null);
        return;
      }

      try {
        const response = await axios.get(`${API}/briefs/${project.brief_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDetailBrief(response.data);
      } catch (error) {
        console.error('Error fetching brief for project:', error);
        setDetailBrief(null);
      }
    };

    if (activeView === 'detail' && selectedProjectId) {
      fetchBriefForProject();
    }
  }, [activeView, selectedProjectId, projects, token]);

  const fetchProjects = async (forceRefresh = false) => {
    // Si hay caché válido y no es refresh forzado, no hacer nada
    if (!forceRefresh && isCacheValid('projects')) {
      setLoading(false);
      return;
    }
    
    // Solo mostrar indicador de carga si es refresh forzado o no hay proyectos
    if (forceRefresh) {
      setRefreshing(true);
    } else if (projects.length === 0) {
      setLoading(true);
    }
    
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
      setCached('projects', response.data);
      // También invalidar el caché del dashboard para que se actualice
      invalidateCache('dashboard_projects');
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchProjects(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineBadge = (deadline) => {
    const days = getDaysUntilDeadline(deadline);
    if (days === null || days > 3) return null;
    
    let badgeClass = 'deadline-badge';
    let badgeText = '';
    
    if (days < 0) {
      badgeClass += ' overdue';
      badgeText = `Vencido (${Math.abs(days)} días)`;
    } else if (days === 0) {
      badgeClass += ' today';
      badgeText = 'Vence hoy';
    } else if (days <= 3) {
      badgeClass += ' urgent';
      badgeText = `Próximo plazo: ${days} día${days > 1 ? 's' : ''}`;
    }
    
    return <span className={badgeClass}>{badgeText}</span>;
  };

  const completeProject = async (project) => {
    if (project.status === 'completed' || project.status === 'cancelled') return;
    setSelectedProject(project);

    if (!project.is_creator) {
      // Creative: just show confirm modal
      setShowCompleteModal(true);
      return;
    }

    // CREATOR flow: request completion → backend sets pending_completion
    if (project.status === 'active' || project.status === 'under_review') {
      try {
        await axios.patch(`${API}/projects/${project.id}`, { status: 'completed' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(prev => prev.map(p => p.id === project.id ? { ...p, status: 'pending_completion', completion_approved_by_creative: false } : p));
        invalidateCache('projects');
        fetchProjects(true);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setCompleteError(typeof detail === 'string' ? detail : 'No se pudo solicitar la finalización del proyecto.');
        setShowCompleteModal(true);
      }
      return;
    }

    if (project.status === 'pending_completion') {
      // Waiting for creative to confirm — project completes when creative approves
      setCompleteError('Esperando confirmación del creativo. Recibirás una notificación cuando confirme la entrega.');
      setShowCompleteModal(true);
    }
  };

  const approveProjectCompletion = async (project) => {
    let pending = 0;
    try {
      const res = await axios.get(`${API}/projects/${project.id}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      pending = res.data?.total_pending || 0;
    } catch (e) { /* show modal without payment info */ }
    setDeliveryPendingAmount(pending);
    setDeliveryConfirmProject(project);
    setDeliveryConfirmed(false);
    setDeliveryConfirmError(null);
    setShowDeliveryConfirmModal(true);
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryConfirmProject) return;
    setDeliveryConfirmLoading(true);
    setDeliveryConfirmError(null);
    try {
      const res = await axios.patch(`${API}/projects/${deliveryConfirmProject.id}`, { status: 'approve_completion' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newStatus = res.data?.status || 'completed';
      setProjects(prev => prev.map(p => p.id === deliveryConfirmProject.id
        ? { ...p, status: newStatus, completion_approved_by_creative: true }
        : p
      ));
      setDeliveryConfirmProject(prev => ({ ...prev, completion_approved_by_creative: true }));
      invalidateCache('projects');
      fetchProjects(true);
      setDeliveryConfirmed(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDeliveryConfirmError(typeof detail === 'string' ? detail : 'No se pudo confirmar la entrega.');
    } finally {
      setDeliveryConfirmLoading(false);
    }
  };

  const handleCancelDeliveryApproval = async () => {
    if (!deliveryConfirmProject) return;
    setDeliveryConfirmLoading(true);
    setDeliveryConfirmError(null);
    try {
      await axios.patch(`${API}/projects/${deliveryConfirmProject.id}`, { status: 'cancel_completion_approval' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(prev => prev.map(p => p.id === deliveryConfirmProject.id
        ? { ...p, completion_approved_by_creative: false }
        : p
      ));
      invalidateCache('projects');
      fetchProjects(true);
      setShowDeliveryConfirmModal(false);
      setDeliveryConfirmProject(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDeliveryConfirmError(typeof detail === 'string' ? detail : 'No se pudo cancelar la confirmación.');
    } finally {
      setDeliveryConfirmLoading(false);
    }
  };

  const handleRejectCompletion = async () => {
    if (!deliveryConfirmProject) return;
    setDeliveryConfirmLoading(true);
    setDeliveryConfirmError(null);
    try {
      await axios.patch(`${API}/projects/${deliveryConfirmProject.id}`, { status: 'reject_completion' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(prev => prev.map(p => p.id === deliveryConfirmProject.id
        ? { ...p, status: 'active', completion_approved_by_creative: false }
        : p
      ));
      invalidateCache('projects');
      fetchProjects(true);
      setShowDeliveryConfirmModal(false);
      setDeliveryConfirmProject(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDeliveryConfirmError(typeof detail === 'string' ? detail : 'No se pudo cancelar la entrega.');
    } finally {
      setDeliveryConfirmLoading(false);
    }
  };

  // ─── Cerrar proyecto de mutuo acuerdo ─────────────────────────────────────
  const openCloseRequestModal = (project) => {
    setCloseRequestProject(project);
    setCloseRequestReason('');
    setCloseRequestError(null);
    setShowCloseRequestModal(true);
  };

  const submitCloseRequest = async () => {
    if (!closeRequestProject) return;
    setCloseRequestLoading(true);
    setCloseRequestError(null);
    try {
      const res = await axios.post(
        `${API}/projects/${closeRequestProject.id}/close/request`,
        { reason: closeRequestReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const patch = {
        close_request_by_user_id: res.data?.close_request_by_user_id || user.id,
        close_request_reason: res.data?.close_request_reason ?? (closeRequestReason.trim() || null),
        close_request_created_at: res.data?.close_request_created_at || new Date().toISOString(),
      };
      setProjects(prev => prev.map(p => p.id === closeRequestProject.id ? { ...p, ...patch } : p));
      setSelectedProject(prev => prev && prev.id === closeRequestProject.id ? { ...prev, ...patch } : prev);
      invalidateCache('projects');
      fetchProjects(true);
      setShowCloseRequestModal(false);
      setCloseRequestProject(null);
      addToast({ type: 'success', title: 'Solicitud de cierre enviada' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCloseRequestError(typeof detail === 'string' ? detail : 'No se pudo enviar la solicitud de cierre.');
    } finally {
      setCloseRequestLoading(false);
    }
  };

  const withdrawCloseRequest = async (project) => {
    try {
      await axios.post(
        `${API}/projects/${project.id}/close/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjects(prev => prev.map(p => p.id === project.id ? {
        ...p,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : p));
      setSelectedProject(prev => prev && prev.id === project.id ? {
        ...prev,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : prev);
      invalidateCache('projects');
      fetchProjects(true);
      addToast({ type: 'success', title: 'Solicitud de cierre retirada' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      addToast({ type: 'error', title: typeof detail === 'string' ? detail : 'No se pudo retirar la solicitud.' });
    }
  };

  const openCloseConfirmModal = async (project) => {
    let pending = 0;
    try {
      const res = await axios.get(`${API}/projects/${project.id}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      pending = res.data?.total_pending || 0;
    } catch (e) { /* no bloquea */ }
    setCloseConfirmPendingAmount(pending);
    setCloseConfirmProject(project);
    setCloseConfirmError(null);
    setShowCloseConfirmModal(true);
  };

  const confirmCloseProject = async () => {
    if (!closeConfirmProject) return;
    setCloseConfirmLoading(true);
    setCloseConfirmError(null);
    try {
      await axios.post(
        `${API}/projects/${closeConfirmProject.id}/close/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjects(prev => prev.map(p => p.id === closeConfirmProject.id ? {
        ...p,
        status: 'cancelled',
        closed_by_mutual_consent: true,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : p));
      setSelectedProject(prev => prev && prev.id === closeConfirmProject.id ? {
        ...prev,
        status: 'cancelled',
        closed_by_mutual_consent: true,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : prev);
      invalidateCache('projects');
      fetchProjects(true);
      setShowCloseConfirmModal(false);
      setCloseConfirmProject(null);
      addToast({ type: 'success', title: 'Proyecto cerrado de mutuo acuerdo' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCloseConfirmError(typeof detail === 'string' ? detail : 'No se pudo cerrar el proyecto.');
    } finally {
      setCloseConfirmLoading(false);
    }
  };

  const rejectCloseRequest = async (project) => {
    try {
      await axios.post(
        `${API}/projects/${project.id}/close/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProjects(prev => prev.map(p => p.id === project.id ? {
        ...p,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : p));
      setSelectedProject(prev => prev && prev.id === project.id ? {
        ...prev,
        close_request_by_user_id: null,
        close_request_reason: null,
        close_request_created_at: null,
      } : prev);
      invalidateCache('projects');
      fetchProjects(true);
      addToast({ type: 'success', title: 'Solicitud de cierre rechazada' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      addToast({ type: 'error', title: typeof detail === 'string' ? detail : 'No se pudo rechazar la solicitud.' });
    }
  };

  const confirmCompleteProject = async () => {
    if (!selectedProject) return;

    setCompleteError(null);
    setCompleting(true);
    try {
      let preview_image_url = '';
      if (completePreviewFile) {
        const formData = new FormData();
        formData.append('file', completePreviewFile, completePreviewFile.name || 'preview.jpg');
        try {
          const uploadRes = await axios.post(`${UPLOAD_BASE_PROJECTS}/api/upload`, formData, {
            headers: { Authorization: `Bearer ${token}` },
            maxContentLength: 6 * 1024 * 1024,
            maxBodyLength: 6 * 1024 * 1024,
          });
          const url = uploadRes?.data?.url;
          if (url && typeof url === 'string') {
            preview_image_url = url;
          }
        } catch (uploadErr) {
          const detail = uploadErr.response?.data?.detail || uploadErr.message;
          setCompleteError(`No se pudo subir la imagen: ${detail}. Puedes completar sin imagen o cambiar a JPG/PNG (máx. 5 MB).`);
          setCompleting(false);
          return;
        }
      }
      const patchPayload = { status: "completed" };
      if (preview_image_url) patchPayload.preview_image_url = preview_image_url;
      await axios.patch(`${API}/projects/${selectedProject.id}`, patchPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCompleteModal(false);
      setCompletePreviewFile(null);
      setCompletePreviewPreviewUrl(null);
      setCompletedProjectForRating(selectedProject);
      setShowRatingModal(true);

      setProjects(prev => prev.map(p => p.id === selectedProject.id ? { ...p, status: 'completed' } : p));
      invalidateCache('projects');
      fetchProjects(true);
    } catch (error) {
      console.error('Error completing project:', error);
      const data = error.response?.data;
      const detail = data?.detail;
      let msg = 'No se puede completar el proyecto. Comprueba que todos los pagos estén hechos (pestaña «Pagos») y que el trabajo esté entregado.';
      if (typeof detail === 'string' && detail.trim()) {
        const d = detail.trim();
        if (!d.startsWith('Error completing project')) {
          msg = d;
        }
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        msg = (first && (first.msg || first.message)) ? String(first.msg || first.message) : msg;
      } else if (detail && typeof detail === 'object' && (detail.msg || detail.message)) {
        msg = String(detail.msg || detail.message);
      } else if (data?.message && typeof data.message === 'string') {
        msg = data.message;
      } else if (error.message && !error.response) {
        msg = 'No se pudo conectar. Comprueba tu conexión e inténtalo de nuevo.';
      }
      setCompleteError(msg);
    } finally {
      setCompleting(false);
    }
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setSelectedProject(null);
    setCompleteError(null);
    setCompletePreviewFile(null);
    setCompletePreviewPreviewUrl(null);
  };

  const onCompletePreviewFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setCompleteError('Solo se permiten imágenes (JPG, PNG, GIF, WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCompleteError('La imagen no puede superar 5 MB.');
      return;
    }
    setCompleteError(null);
    setCompletePreviewFile(file);
    setCompletePreviewPreviewUrl(URL.createObjectURL(file));
  };

  const onCompletionPaymentSuccess = () => {
    setShowCompletionPaymentModal(false);
    setCompletionPaymentData(null);
    setCompletedProjectForRating(selectedProject);
    setShowRatingModal(true);
    const completedId = completionPaymentData?.project?.id || selectedProject?.id;
    if (completedId) {
      setProjects(prev => prev.map(p => p.id === completedId ? { ...p, status: 'completed' } : p));
    }
    invalidateCache('projects');
    fetchProjects(true);
  };

  const closeCompletionPaymentModal = () => {
    setShowCompletionPaymentModal(false);
    setCompletionPaymentData(null);
    setSelectedProject(null);
  };

  const submitRating = async () => {
    if (rating === 0) {
      return;
    }

    setSubmittingRating(true);
    try {
      await axios.post(`${API}/projects/${completedProjectForRating.id}/rating`, {
        rating: rating,
        comment: ratingComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      closeRatingModal();
    } catch (error) {
      console.error('Error submitting rating:', error);
      let errorMessage = 'Error al enviar la valoración';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      console.error(errorMessage);
    } finally {
      setSubmittingRating(false);
    }
  };

  const skipRating = () => {
    closeRatingModal();
  };

  const closeRatingModal = () => {
    setShowRatingModal(false);
    setCompletedProjectForRating(null);
    setRating(0);
    setRatingComment('');
  };

  const StarRating = ({ rating, onRatingChange, disabled = false }) => {
    const [hoverRating, setHoverRating] = useState(0);

    const handleStarClick = (starValue) => {
      if (!disabled && onRatingChange) {
        onRatingChange(starValue);
      }
    };

    const handleStarHover = (starValue) => {
      if (!disabled) {
        setHoverRating(starValue);
      }
    };

    const handleStarLeave = () => {
      if (!disabled) {
        setHoverRating(0);
      }
    };

    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${(hoverRating || rating) >= star ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleStarClick(star)}
            onMouseEnter={() => handleStarHover(star)}
            onMouseLeave={handleStarLeave}
            disabled={disabled}
            aria-label={`${star} estrella${star > 1 ? 's' : ''}`}
          >
            ⭐
          </button>
        ))}
      </div>
    );
  };

  const openChat = (projectId) => {
    setSelectedProjectId(projectId);
    setActiveView('chat');
  };

  const openDeliverables = (projectId) => {
    setSelectedProjectId(projectId);
    setActiveView('deliverables');
  };

  const openMilestones = (projectId) => {
    setSelectedProjectId(projectId);
    setActiveView('milestones');
  };

  const openProjectDetail = (projectId, tab = 'overview') => {
    setSelectedProjectId(projectId);
    setDetailTab(tab);
    setActiveView('detail');
  };

  const backToList = () => {
    setActiveView('list');
    setSelectedProjectId(null);
  };

  const backToDetailOverview = () => {
    setDetailTab('overview');
  };

  // Modales de cierre mutuo: Portal a body para que aparezcan tanto en
  // la vista lista como en la detalle (este componente tiene returns
  // tempranos según activeView).
  const closeMutualModals = (typeof document !== 'undefined' && document.body) ? ReactDOM.createPortal(
    <>
      {showCloseRequestModal && closeRequestProject && (
        <div className="modal-overlay" onClick={() => !closeRequestLoading && setShowCloseRequestModal(false)} role="dialog" aria-modal="true">
          <div className="modal-content close-request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiXCircle aria-hidden="true" /> Cerrar proyecto de mutuo acuerdo
              </h3>
              <button type="button" className="modal-close" onClick={() => !closeRequestLoading && setShowCloseRequestModal(false)} disabled={closeRequestLoading} aria-label="Cerrar">
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Esto envía una solicitud a <strong>{closeRequestProject.other_user_name || 'la otra parte'}</strong> para cerrar el proyecto «{closeRequestProject.title}» de mutuo acuerdo.
              </p>
              <ul className="close-request-bullets">
                <li>Si acepta, las <strong>fases pendientes de pagar quedan canceladas</strong> (nadie debe nada).</li>
                <li>Las fases ya pagadas se mantienen — el creativo conserva lo cobrado por el trabajo realizado hasta ahora.</li>
                <li>El proyecto pasa a estado <em>Cancelado</em> y no se podrá retomar.</li>
              </ul>
              <label className="close-request-label">
                Motivo (opcional, visible para la otra parte)
              </label>
              <textarea
                className="close-request-textarea"
                placeholder="Ej: la marca prefiere parar el proyecto, ha cambiado el alcance..."
                value={closeRequestReason}
                onChange={(e) => setCloseRequestReason(e.target.value)}
                maxLength={500}
                rows={4}
              />
              {closeRequestError && (
                <p className="close-request-error">{closeRequestError}</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowCloseRequestModal(false)} disabled={closeRequestLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={submitCloseRequest} disabled={closeRequestLoading}>
                {closeRequestLoading ? 'Enviando…' : 'Enviar solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCloseConfirmModal && closeConfirmProject && (
        <div className="modal-overlay" onClick={() => !closeConfirmLoading && setShowCloseConfirmModal(false)} role="dialog" aria-modal="true">
          <div className="modal-content close-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiXCircle aria-hidden="true" /> Confirmar cierre del proyecto
              </h3>
              <button type="button" className="modal-close" onClick={() => !closeConfirmLoading && setShowCloseConfirmModal(false)} disabled={closeConfirmLoading} aria-label="Cerrar">
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {closeConfirmProject.other_user_name || 'La otra parte'} ha solicitado cerrar el proyecto «{closeConfirmProject.title}» de mutuo acuerdo.
              </p>
              {closeConfirmProject.close_request_reason && (
                <p className="close-confirm-reason">
                  <span className="close-confirm-reason-label">Motivo indicado:</span><br />
                  {closeConfirmProject.close_request_reason}
                </p>
              )}
              <div className="close-confirm-summary">
                <strong>Si confirmas:</strong>
                <ul className="close-request-bullets">
                  {closeConfirmPendingAmount > 0 ? (
                    <li>Las fases pendientes (<strong>{closeConfirmPendingAmount.toFixed(2)} €</strong>) quedarán canceladas — nadie deberá ese importe.</li>
                  ) : (
                    <li>No hay fases pendientes de pagar.</li>
                  )}
                  <li>Lo ya pagado se mantiene; el creativo conserva el cobro por el trabajo realizado.</li>
                  <li>El proyecto quedará en estado <em>Cancelado</em>.</li>
                </ul>
              </div>
              {closeConfirmError && (
                <p className="close-request-error">{closeConfirmError}</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowCloseConfirmModal(false)} disabled={closeConfirmLoading}>
                Volver
              </button>
              <button type="button" className="btn-primary" onClick={confirmCloseProject} disabled={closeConfirmLoading}>
                {closeConfirmLoading ? 'Cerrando…' : 'Confirmar cierre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  ) : null;

  if (activeView === 'detail') {
    const project = projects.find(p => p.id === selectedProjectId);
    if (!project) {
      return (
        <div className="project-detail-page">
          <div className="page-header">
            <div>
              <button onClick={backToList} className="btn-back mb-2">
                ← Volver a proyectos
              </button>
              <h2>Proyecto</h2>
              <p>Resumen, comunicación y seguimiento de este proyecto.</p>
            </div>
          </div>
          <div className="project-tab-content project-detail-loading-inline">
            <LoadingSpinner size="small" text="Cargando proyecto..." />
          </div>
        </div>
      );
    }

    return (
      <>
        {closeMutualModals}
        <div className="project-detail-page">
          <div className="page-header">
            <div>
              <button
                onClick={backToList}
                className="btn-back mb-2"
              >
                ← Volver a proyectos
              </button>
              <h2>{project.title}</h2>
              <p>Resumen, comunicación y seguimiento de este proyecto.</p>
            </div>
          </div>

          {/* Aviso de pago pendiente (cliente): solo si hay concepto exigible ahora (no el 50% "al entregar") */}
          {project.is_creator && projectDetailPayments && hasImmediatePaymentDue(projectDetailPayments) && (() => {
            const schedule = projectDetailPayments.schedule;
            const pendingAmount = schedule?.length
              ? (schedule.find(s => !s.paid && s.type !== 'final')?.amount ?? schedule.find(s => !s.paid)?.amount ?? projectDetailPayments.total_pending)
              : projectDetailPayments.total_pending;
            return (
            <div className="project-pending-payment-banner" role="alert">
              <FiCreditCard aria-hidden="true" />
              <span>Tienes un pago pendiente de <strong>{Number(pendingAmount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</strong>. Realízalo en la pestaña <strong>Pagos</strong> para continuar.</span>
              <button type="button" className="project-pending-payment-banner-btn" onClick={() => setDetailTab('payments')}>
                Ir a Pagos
              </button>
            </div>
            );
          })()}

        <div className="project-detail-meta">
          <div className="project-detail-user-cell">
            <div className="project-detail-avatar">
              <img src={getAvatarUrl(project?.other_user?.profile_image, project?.other_user_name)} alt={project?.other_user_name} />
            </div>
            <div>
              <p className="text-gray-400 mb-1">
                {project.is_creator ? 'Creativo:' : 'Cliente:'}
              </p>
              <p className="text-gray-100 font-medium project-detail-user-name-row">
                {project.other_user_name}
                {project.is_creator && (project.other_user_season_level_name != null || project.other_user_season_level != null) && (
                  <span className="user-role user-level-badge">
                    {project.other_user_season_level_name || `Nivel ${project.other_user_season_level}`}
                  </span>
                )}
                {!project.is_creator && <span className="user-role user-level-badge">Cliente</span>}
              </p>
            </div>
          </div>
          <div>
            <p className="text-gray-400 mb-1">Presupuesto</p>
            <p className="text-gray-100 font-semibold">{project.budget}€</p>
          </div>
          {project.deadline && (
            <div>
              <p className="text-gray-400 mb-1">Fecha límite</p>
              <p className="text-gray-100">{formatDate(project.deadline)}</p>
            </div>
          )}
        </div>

        <div className="project-tabs">
          <button
            className={`project-tab ${detailTab === 'overview' ? 'active' : ''}`}
            onClick={() => setDetailTab('overview')}
          >
            Resumen
          </button>
          <button
            className={`project-tab ${detailTab === 'deliverables' ? 'active' : ''}`}
            onClick={() => setDetailTab('deliverables')}
          >
            Entregables
          </button>
          <button
            className={`project-tab ${detailTab === 'milestones' ? 'active' : ''}`}
            onClick={() => setDetailTab('milestones')}
          >
            Fases
          </button>
          <button
            className={`project-tab ${detailTab === 'payments' ? 'active' : ''}`}
            onClick={() => setDetailTab('payments')}
          >
            Pagos
          </button>
        </div>

        <div className="project-tab-content">
          {detailTab === 'overview' && (
            <div className="project-overview">
              {/* Banner: el cliente ha solicitado completar el proyecto (status=pending_completion) */}
              {project.status === 'pending_completion' && !project.close_request_by_user_id && (
                !project.is_creator ? (
                  /* Creativo: el cliente quiere cerrar el proyecto, falta tu confirmación */
                  <div className="project-close-banner project-close-banner--theirs" role="alert">
                    <div className="project-close-banner-body">
                      <strong>{project.other_user_name || 'El cliente'} ha solicitado cerrar el proyecto.</strong>
                      <span> Si el trabajo está listo, confirma la entrega para que el proyecto pase a Completado y se liberen los pagos pendientes.</span>
                    </div>
                    <div className="project-close-banner-actions">
                      <button
                        type="button"
                        className="btn-primary project-close-banner-action"
                        onClick={() => approveProjectCompletion(project)}
                      >
                        <FiCheckCircle aria-hidden="true" /> Confirmar entrega
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Cliente: ya solicitó completar, esperando confirmación del creativo */
                  !project.completion_approved_by_creative && (
                    <div className="project-close-banner project-close-banner--mine" role="status">
                      <div className="project-close-banner-body">
                        <strong>Has solicitado cerrar el proyecto.</strong>
                        <span> Esperando que {project.other_user_name || 'el creativo'} confirme la entrega.</span>
                      </div>
                    </div>
                  )
                )
              )}
              {/* Banner: solicitud de cierre mutuo activa */}
              {project.close_request_by_user_id && project.status !== 'cancelled' && project.status !== 'completed' && (
                project.close_request_by_user_id === user.id ? (
                  <div className="project-close-banner project-close-banner--mine" role="status">
                    <div className="project-close-banner-body">
                      <strong>Has solicitado cerrar el proyecto.</strong>
                      <span> Esperando respuesta de la otra parte. Si acepta, las fases pendientes se cancelarán.</span>
                      {project.close_request_reason && (
                        <p className="project-close-banner-reason">
                          <span className="project-close-banner-label">Motivo:</span> {project.close_request_reason}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-secondary project-close-banner-action"
                      onClick={() => withdrawCloseRequest(project)}
                    >
                      Retirar solicitud
                    </button>
                  </div>
                ) : (
                  <div className="project-close-banner project-close-banner--theirs" role="alert">
                    <div className="project-close-banner-body">
                      <strong>{project.other_user_name || 'La otra parte'} ha solicitado cerrar el proyecto.</strong>
                      <span> Si confirmas, las fases pendientes quedarán canceladas y nadie deberá nada.</span>
                      {project.close_request_reason && (
                        <p className="project-close-banner-reason">
                          <span className="project-close-banner-label">Motivo:</span> {project.close_request_reason}
                        </p>
                      )}
                    </div>
                    <div className="project-close-banner-actions">
                      <button
                        type="button"
                        className="btn-primary project-close-banner-action"
                        onClick={() => openCloseConfirmModal(project)}
                      >
                        <FiCheckCircle aria-hidden="true" /> Confirmar cierre
                      </button>
                      <button
                        type="button"
                        className="btn-secondary project-close-banner-action"
                        onClick={() => rejectCloseRequest(project)}
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>
                )
              )}
              <div className="project-shortcuts">
                {navigateToChat && (
                  <button
                    className="project-shortcut-btn project-shortcut-btn-chat"
                    onClick={() => navigateToChat(project.id)}
                  >
                    <span className="project-shortcut-label">Ir al chat</span>
                    <span className="project-shortcut-description">
                      Conversación completa con el otro usuario.
                    </span>
                  </button>
                )}
                <button
                  className="project-shortcut-btn"
                  onClick={() => setDetailTab('deliverables')}
                >
                  <span className="project-shortcut-label">Ver entregables</span>
                  <span className="project-shortcut-description">
                    Archivos intercambiados y documentos finales.
                  </span>
                </button>
                <button
                  className="project-shortcut-btn"
                  onClick={() => setDetailTab('milestones')}
                >
                  <span className="project-shortcut-label">Gestionar fases</span>
                  <span className="project-shortcut-description">
                    Fechas clave y progreso del proyecto.
                  </span>
                </button>
                <button
                  className="project-shortcut-btn"
                  onClick={() => setDetailTab('payments')}
                >
                  <span className="project-shortcut-label">Ver pagos</span>
                  <span className="project-shortcut-description">
                    Histórico y pendientes de pago.
                  </span>
                </button>
                {project.is_creator && (project.status === 'active' || project.status === 'under_review') && (
                  <button
                    className="project-shortcut-btn project-shortcut-btn-complete"
                    onClick={() => completeProject(project)}
                  >
                    <span className="project-shortcut-label">
                      <FiCheckCircle aria-hidden="true" />
                      Completar proyecto
                    </span>
                    <span className="project-shortcut-description">
                      Solicitar cierre del proyecto al creativo.
                    </span>
                  </button>
                )}
                {/* Cerrar proyecto (mutuo) — disponible para ambas partes en estados activos sin solicitud previa */}
                {(project.status === 'active' || project.status === 'under_review' || project.status === 'pending_completion')
                  && !project.close_request_by_user_id && (
                  <button
                    className="project-shortcut-btn project-shortcut-btn-close"
                    onClick={() => openCloseRequestModal(project)}
                  >
                    <span className="project-shortcut-label">
                      <FiXCircle aria-hidden="true" />
                      Cerrar proyecto
                    </span>
                    <span className="project-shortcut-description">
                      Cerrar de mutuo acuerdo. Las fases pendientes se cancelan.
                    </span>
                  </button>
                )}
                {project.is_creator && project.status === 'pending_completion' && !project.completion_approved_by_creative && (
                  <button
                    className="project-shortcut-btn"
                    disabled
                    style={{ opacity: 0.6, cursor: 'default' }}
                  >
                    <span className="project-shortcut-label">
                      Pendiente de confirmar
                    </span>
                    <span className="project-shortcut-description">
                      Esperando que el creativo confirme la entrega.
                    </span>
                  </button>
                )}
                {!project.is_creator && project.status === 'pending_completion' && (
                  <button
                    className="project-shortcut-btn project-shortcut-btn-complete"
                    onClick={() => approveProjectCompletion(project)}
                  >
                    <span className="project-shortcut-label">
                      <FiCheckCircle aria-hidden="true" />
                      Confirmar entrega
                    </span>
                    <span className="project-shortcut-description">
                      El cliente quiere cerrar el proyecto. Confirma si el trabajo está listo.
                    </span>
                  </button>
                )}
              </div>

              {detailBrief && (
                <div className="project-brief-summary">
                  <h4 className="project-brief-title">Resumen del briefing</h4>
                  <div className="project-brief-grid">
                    <div>
                      <p className="text-gray-400 mb-1">Objetivo del proyecto</p>
                      <p className="text-gray-100">
                        {detailBrief.description}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Categoría</p>
                      <p className="text-gray-100">
                        {getBriefCategoryLabel(detailBrief.category)}
                        {detailBrief.subcategory ? ` · ${detailBrief.subcategory}` : ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Rango de presupuesto</p>
                      <p className="text-gray-100">
                        {detailBrief.budget_range}
                        {detailBrief.budget_min && detailBrief.budget_max
                          ? ` (${detailBrief.budget_min}€ - ${detailBrief.budget_max}€)`
                          : ''}
                      </p>
                    </div>
                    {detailBrief.project_duration && (
                      <div>
                        <p className="text-gray-400 mb-1">Duración estimada</p>
                        <p className="text-gray-100">
                          {detailBrief.project_duration}
                        </p>
                      </div>
                    )}
                    {Array.isArray(detailBrief.requirements) && detailBrief.requirements.length > 0 && (
                      <div className="project-brief-requirements">
                        <p className="text-gray-400 mb-1">Requisitos clave</p>
                        <ul>
                          {detailBrief.requirements.slice(0, 3).map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {detailBrief.target_audience && (
                      <div>
                        <p className="text-gray-400 mb-1">Público objetivo</p>
                        <p className="text-gray-100 whitespace-pre-line">{detailBrief.target_audience}</p>
                      </div>
                    )}
                    {detailBrief.references && (
                      <div>
                        <p className="text-gray-400 mb-1">Referencias</p>
                        <p className="text-gray-100 whitespace-pre-line">{detailBrief.references}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="project-overview-grid">
                <div>
                  <p className="text-gray-400 mb-1">Estado</p>
                  <span className={`status-badge ${project.status}`}>
                    {project.status === 'active' ? 'Activo' :
                      project.status === 'under_review' ? 'En Revisión' :
                      project.status === 'pending_completion' ? 'Pendiente de completar' :
                      project.status === 'pending_payment' ? 'Pendiente de pago' :
                      project.status === 'completed' ? 'Completado' :
                      project.status === 'cancelled' ? 'Cancelado' : project.status}
                  </span>
                </div>
                {project.message_count > 0 && (
                  <div>
                    <p className="text-gray-400 mb-1">Mensajes</p>
                    <p className="text-gray-100">{project.message_count}</p>
                  </div>
                )}
                {project.created_at && (
                  <div>
                    <p className="text-gray-400 mb-1">Iniciado</p>
                    <p className="text-gray-100">{formatDate(project.created_at)}</p>
                  </div>
                )}
                {project.deadline && (
                  <div>
                    <p className="text-gray-400 mb-1">Fecha límite</p>
                    <p className="text-gray-100">{formatDate(project.deadline)}</p>
                  </div>
                )}
                {project.budget && (
                  <div>
                    <p className="text-gray-400 mb-1">Presupuesto aprobado</p>
                    <p className="text-gray-100">{project.budget}€</p>
                  </div>
                )}
              </div>

              {/* Timeline de fases dentro del resumen */}
              <MilestonesTimeline projectId={project.id} />
            </div>
          )}

          {detailTab === 'deliverables' && (
            <Deliverables projectId={selectedProjectId} onBack={backToDetailOverview} />
          )}

          {detailTab === 'milestones' && (
            <Milestones projectId={selectedProjectId} onBack={backToDetailOverview} onNavigateToChat={navigateToChat} />
          )}
          {detailTab === 'payments' && (
            <PaymentsView projectId={selectedProjectId} onBack={backToDetailOverview} projectTitle={project?.title} isCreator={project?.is_creator} onPaymentSuccess={fetchProjectDetailPayments} projectStatus={project?.status} />
          )}
        </div>
      </div>

      {/* Modal: tienes pendiente pagar X € (solo si hay concepto exigible ahora; no el 50% "al entregar") */}
      {showPendingPaymentModal && projectDetailPayments && hasImmediatePaymentDue(projectDetailPayments) && project && (() => {
        const schedule = projectDetailPayments.schedule;
        const pendingAmount = schedule?.length
          ? (schedule.find(s => !s.paid && s.type !== 'final')?.amount ?? schedule.find(s => !s.paid)?.amount ?? projectDetailPayments.total_pending)
          : projectDetailPayments.total_pending;
        return (
        <div className="modal-overlay" onClick={() => { setShowPendingPaymentModal(false); setPendingPaymentModalDismissed(true); }} role="dialog" aria-modal="true" aria-labelledby="pending-payment-modal-title">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="pending-payment-modal-title" className="flex items-center gap-2">
                <FiCreditCard aria-hidden="true" />
                Pago pendiente
              </h3>
              <button type="button" className="modal-close" onClick={() => { setShowPendingPaymentModal(false); setPendingPaymentModalDismissed(true); }} aria-label="Cerrar"><FiX /></button>
            </div>
            <div className="modal-body">
              <p>Tienes pendiente pagar <strong>{Number(pendingAmount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</strong> en este proyecto.</p>
              <p className="text-secondary">Realiza el pago en la pestaña <strong>Pagos</strong> para continuar. Hasta entonces verás un aviso en la parte superior del proyecto.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => { setShowPendingPaymentModal(false); setPendingPaymentModalDismissed(true); }}>Cerrar</button>
              <button type="button" className="btn-primary" onClick={() => { setShowPendingPaymentModal(false); setPendingPaymentModalDismissed(true); setDetailTab('payments'); }}>Ir a Pagos</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Creative delivery confirmation modal */}
      {showDeliveryConfirmModal && deliveryConfirmProject && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiCheckCircle aria-hidden="true" />
                {deliveryConfirmed ? 'Entrega confirmada' : 'Confirmar entrega'}
              </h3>
              <button type="button" className="modal-close" onClick={() => { setShowDeliveryConfirmModal(false); setDeliveryConfirmProject(null); setDeliveryConfirmed(false); setDeliveryConfirmError(null); }} aria-label="Cerrar"><FiX /></button>
            </div>
            <div className="modal-body">
              {!deliveryConfirmed ? (
                <>
                  <p>El cliente ha solicitado cerrar el proyecto <strong>{deliveryConfirmProject.title}</strong>.</p>
                  {deliveryPendingAmount > 0 ? (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.4)', borderRadius: '0.5rem' }}>
                      <p style={{ color: '#eab308', fontWeight: 600 }}>Hay {deliveryPendingAmount.toFixed(2)} € pendientes de pago.</p>
                      <p style={{ marginTop: '0.35rem', fontSize: '0.9rem' }}>Al confirmar la entrega aceptas que la marca puede dejar este importe sin pagar. El proyecto se marcará como completado igualmente.</p>
                    </div>
                  ) : (
                    <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Si confirmas la entrega el proyecto se marcará como completado. Si cancelas, el proyecto volverá a estar en progreso.</p>
                  )}
                  {deliveryConfirmError && <p className="text-red-400 mt-2">{deliveryConfirmError}</p>}
                </>
              ) : (
                <>
                  <p>Entrega confirmada correctamente. El cliente ha sido notificado y debe realizar el pago final para completar el proyecto.</p>
                  <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Si cometiste un error, puedes cancelar la confirmación y el cliente será avisado.</p>
                  {deliveryConfirmError && <p className="text-red-400 mt-2">{deliveryConfirmError}</p>}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!deliveryConfirmed ? (
                <>
                  <button type="button" className="btn-danger" onClick={handleRejectCompletion} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Cancelando…' : 'Cancelar entrega'}
                  </button>
                  <button type="button" className="btn-primary" onClick={handleConfirmDelivery} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Confirmando…' : 'Confirmar entrega'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn-danger" onClick={handleCancelDeliveryApproval} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Cancelando…' : 'Cancelar confirmación'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => { setShowDeliveryConfirmModal(false); setDeliveryConfirmProject(null); setDeliveryConfirmed(false); }}>Cerrar</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modales de completar/valorar (también visibles desde la vista detalle) */}
      {showCompletionPaymentModal && completionPaymentData && completionStripePromise && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header payment-modal-header">
              <h3 className="payment-modal-title">Pago al completar proyecto</h3>
              <button type="button" className="modal-close" onClick={closeCompletionPaymentModal} aria-label="Cerrar">
                <FiX />
              </button>
            </div>
            <div className="payment-modal-summary">
              <p className="payment-modal-amount">
                Cantidad pendiente: <strong>{completionPaymentData.amountEur?.toFixed(2)} €</strong>
              </p>
              <p className="payment-modal-context">
                Proyecto: {completionPaymentData.project?.title}
              </p>
            </div>
            <div className="payment-modal-form-wrap">
              <Elements stripe={completionStripePromise} options={{ clientSecret: completionPaymentData.clientSecret }}>
                <CompletionPaymentForm
                  projectId={completionPaymentData.project?.id}
                  amountEur={completionPaymentData.amountEur}
                  clientSecret={completionPaymentData.clientSecret}
                  onSuccess={onCompletionPaymentSuccess}
                  onCancel={closeCompletionPaymentModal}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="modal-content complete-project-modal">
            <div className="modal-header">
              <h3>{completeError ? 'Estado del proyecto' : 'Cerrar proyecto'}</h3>
              <button
                onClick={closeCompleteModal}
                className="modal-close"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {completeError ? (
                <div className="alert alert-warning mb-3" role="alert">
                  {completeError}
                </div>
              ) : (
                <>
                  <div className="confirm-message">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-content">
                      <p className="warning-text">
                        ¿Estás seguro de que quieres marcar el proyecto
                        <strong> "{selectedProject?.title}"</strong> como completado?
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Esta acción no se puede deshacer y podrás valorar al otro usuario.
                      </p>
                    </div>
                  </div>
                  <div className="complete-preview-upload mt-3">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Imagen de preview del proyecto (opcional)
                    </label>
                    <p className="text-xs text-gray-400 mb-2">
                      Esta imagen aparecerá en el perfil del creativo como trabajo completado.
                    </p>
                    <input
                      type="file"
                      id="complete-preview-image"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={onCompletePreviewFileChange}
                      className="complete-preview-input"
                      disabled={completing}
                    />
                    <label htmlFor="complete-preview-image" className="btn-secondary btn-sm mt-2 inline-block cursor-pointer">
                      {completePreviewFile ? 'Cambiar imagen' : 'Subir imagen'}
                    </label>
                    {completePreviewPreviewUrl && (
                      <div className="complete-preview-thumb-wrap mt-2">
                        <img src={completePreviewPreviewUrl} alt="Preview" className="complete-preview-thumb" />
                        <button
                          type="button"
                          className="btn-remove-preview"
                          onClick={() => { setCompletePreviewFile(null); setCompletePreviewPreviewUrl(null); URL.revokeObjectURL(completePreviewPreviewUrl); }}
                          aria-label="Quitar imagen"
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={closeCompleteModal}
                className="btn-secondary"
                disabled={completing}
              >
                {completeError ? 'Cerrar' : 'Cancelar'}
              </button>
              {!completeError && (
                <button
                  onClick={confirmCompleteProject}
                  disabled={completing}
                  className="btn-primary"
                >
                  {completing ? (
                    <>
                      <span className="loading-spinner"></span>
                      Completando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showRatingModal && (
        <div className="modal-overlay">
          <div className="modal-content rating-modal">
            <div className="modal-header">
              <h3>⭐ Valorar proyecto</h3>
              <button 
                onClick={closeRatingModal} 
                className="modal-close"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="rating-content">
                <p className="text-center mb-4">
                  ¿Cómo fue tu experiencia con este proyecto?
                </p>
                <p className="text-center text-sm text-gray-400 mb-6">
                  Proyecto: <strong>{completedProjectForRating?.title}</strong>
                </p>
                <div className="rating-section">
                  <label className="rating-label">Valoración:</label>
                  <div className="rating-stars-container">
                    <StarRating 
                      rating={rating} 
                      onRatingChange={setRating}
                    />
                    {rating > 0 && (
                      <span className="rating-text">
                        {rating === 1 ? 'Muy malo' :
                         rating === 2 ? 'Malo' :
                         rating === 3 ? 'Regular' :
                         rating === 4 ? 'Bueno' : 'Excelente'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-group mt-4">
                  <label>Comentario (opcional):</label>
                  <textarea
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    placeholder="Comparte tu experiencia sobre este proyecto..."
                    rows="3"
                    maxLength="500"
                    className="rating-textarea"
                  />
                  <div className="char-count">
                    {ratingComment.length}/500 caracteres
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                onClick={skipRating} 
                className="btn-secondary"
                disabled={submittingRating}
              >
                Omitir
              </button>
              <button 
                onClick={submitRating}
                disabled={submittingRating || rating === 0}
                className="btn-primary"
              >
                {submittingRating ? (
                  <>
                    <span className="loading-spinner"></span>
                    Enviando...
                  </>
                ) : (
                  'Enviar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  if (activeView === 'chat') {
    return <Chat projectId={selectedProjectId} onBack={backToList} />;
  }

  if (activeView === 'deliverables') {
    return <Deliverables projectId={selectedProjectId} onBack={backToList} />;
  }

  if (activeView === 'milestones') {
    return <Milestones projectId={selectedProjectId} onBack={backToList} onNavigateToChat={navigateToChat} />;
  }

  if (loading) return <LoadingSpinner text="Cargando proyectos..." />;

  return (
    <>
    {closeMutualModals}
    <div className="projects-list">
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h2>
            <FiActivity aria-hidden="true" />
            Mis Proyectos
          </h2>
          <p>Gestiona tus proyectos activos y completos</p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-refresh"
          title="Actualizar proyectos"
          aria-label="Actualizar lista de proyectos"
        >
          {refreshing ? (
            <>
              <FiLoader className="loading-spinner" aria-hidden="true" />
              Actualizando...
            </>
          ) : (
            <>
              <FiRefreshCcw aria-hidden="true" />
              Actualizar
            </>
          )}
        </button>
      </motion.div>

      {/* Level Up Prompt for Creatives */}
      {user?.role === 'creative' && <LevelUpPrompt variant="projects" onNavigateToRewards={onNavigateToRewards} />}

      {/* Filtros de estado */}
      {projects.length > 0 && (
        <div className="projects-filter-tabs">
          {[
            { id: 'all',                label: 'Todos' },
            { id: 'active',             label: 'Activos' },
            { id: 'in_progress',        label: 'En progreso' },
            { id: 'under_review',       label: 'En revisión' },
            { id: 'pending_payment',    label: 'Pendiente de pago' },
            { id: 'pending_completion', label: 'Pendiente de completar' },
            { id: 'completed',          label: 'Completados' },
            { id: 'cancelled',          label: 'Cancelados' },
          ].map(f => {
            const count = f.id === 'all'
              ? projects.length
              : projects.filter(p => p.status === f.id).length;
            if (f.id !== 'all' && count === 0) return null;
            return (
              <button
                key={f.id}
                className={`projects-filter-tab${statusFilter === f.id ? ' active' : ''}`}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.label}
                <span className="projects-filter-count">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {projects.length === 0 ? (
        <motion.div
          className="empty-state"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="empty-icon" aria-hidden="true">
            <FiActivity />
          </div>
          <h3 className="text-gray-100">No hay proyectos</h3>
          <p className="text-gray-300">Aún no tienes proyectos activos. ¡Cuando aceptes o te acepten una propuesta, aparecerán aquí!</p>
          <button 
            onClick={handleRefresh}
            className="btn-secondary bg-gray-700 hover:bg-gray-600 text-white mt-4"
            disabled={refreshing}
            aria-label="Verificar si hay nuevos proyectos"
          >
            {refreshing ? 'Actualizando...' : (
              <>
                <FiRefreshCcw className="mr-2" aria-hidden="true" />
                Verificar proyectos
              </>
            )}
          </button>
        </motion.div>
      ) : (
        <motion.div
          className="projects-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {statusFilter !== 'all' && projects.filter(p => p.status === statusFilter).length === 0 && (
            <div className="projects-filter-empty">
              No tienes proyectos en este estado.
            </div>
          )}
          {(statusFilter === 'all' ? projects : projects.filter(p => p.status === statusFilter)).map((project, index) => (
            <motion.div
              key={project.id}
              className="project-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.05, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="project-header">
                <h3 className="text-xl font-bold text-gray-100">{project.title}</h3>
                <div className="project-status-container">
                  <span className={`status-badge ${project.status}`}>
                    {project.status === 'active' ? 'Activo' :
                     project.status === 'under_review' ? 'En Revisión' :
                     project.status === 'pending_completion' ? 'Pendiente de completar' :
                     project.status === 'pending_payment' ? 'Pendiente de pago' :
                     project.status === 'completed' ? 'Completado' :
                     project.status === 'cancelled' ? 'Cancelado' : project.status}
                  </span>
                  {getDeadlineBadge(project.deadline)}
                </div>
              </div>

              <div className="other-user-info">
                <div className="project-card-avatar">
                  <img src={getAvatarUrl(project?.other_user?.profile_image, project?.other_user_name)} alt={project?.other_user_name} />
                </div>
                <div>
                  <span className="role-label text-gray-400">
                    {project.is_creator ? 'Creativo:' : 'Cliente:'}
                  </span>
                  <span className="user-name text-gray-100 font-medium mb-0">{project.other_user_name}</span>
                  {project.is_creator && (project.other_user_season_level_name != null || project.other_user_season_level != null) && (
                    <span className="user-role user-level-badge">
                      {project.other_user_season_level_name || `Nivel ${project.other_user_season_level}`}
                    </span>
                  )}
                  {!project.is_creator && (
                    <span className="user-role user-level-badge">Cliente</span>
                  )}
                </div>
              </div>

              {(project.brief_category || project.brief_subcategory) && (
                <p className="project-brief-category text-gray-400 text-sm mb-1">
                  {[
                    project.brief_category ? getBriefCategoryLabel(project.brief_category) : null,
                    project.brief_subcategory
                  ].filter(Boolean).join(' · ')}
                </p>
              )}

              <p className="project-description text-gray-300 mb-2">
                {project.brief_description || project.description}
              </p>

              {Array.isArray(project.brief_requirements) && project.brief_requirements.length > 0 && (
                <div className="project-brief-requirements mb-3">
                  <span className="project-meta-label text-gray-400 text-sm">Requisitos clave:</span>
                  <ul className="project-requirements-list text-gray-300 text-sm mt-0.5">
                    {project.brief_requirements.slice(0, 3).map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="project-meta">
                <div className="meta-item">
                  <span className="meta-label text-gray-400 flex items-center gap-1">
                    <FaEuroSign aria-hidden="true" />
                    Presupuesto:
                  </span>
                  <span className="meta-value text-gray-100 font-semibold">{project.budget}€</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label text-gray-400 flex items-center gap-1">
                    <FiCalendar aria-hidden="true" />
                    Inicio:
                  </span>
                  <span className="meta-value text-gray-100">{formatDate(project.created_at)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label text-gray-400 flex items-center gap-1">
                    <FiMessageCircle aria-hidden="true" />
                    Mensajes:
                  </span>
                  <span className="meta-value text-gray-100">{project.message_count}</span>
                </div>
              </div>

              {project.deadline && (
                <div className="project-deadline">
                  ⏰ Fecha límite: {formatDate(project.deadline)}
                </div>
              )}

              <div className="project-actions space-y-3 mt-6">
                <button 
                  className="btn-primary w-full bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => openProjectDetail(project.id)}
                  aria-label={`Ver detalles del proyecto ${project.title}`}
                >
                  <FiEye className="mr-2" aria-hidden="true" />
                  Ver proyecto
                </button>
                {project.is_creator && (project.status === 'active' || project.status === 'under_review') && (
                  <div className="project-secondary-actions">
                    <button
                      className="btn-complete"
                      onClick={() => completeProject(project)}
                      aria-label={`Solicitar cierre del proyecto ${project.title}`}
                    >
                      <FiCheckCircle className="mr-2" aria-hidden="true" />
                      Completar
                    </button>
                  </div>
                )}
                {!project.is_creator && project.status === 'pending_completion' && (
                  <div className="project-secondary-actions">
                    <button
                      className="btn-complete"
                      onClick={() => approveProjectCompletion(project)}
                      aria-label={`Confirmar entrega del proyecto ${project.title}`}
                    >
                      <FiCheckCircle className="mr-2" aria-hidden="true" />
                      Confirmar entrega
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Creative delivery confirmation modal (project cards view) */}
      {showDeliveryConfirmModal && deliveryConfirmProject && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="flex items-center gap-2">
                <FiCheckCircle aria-hidden="true" />
                {deliveryConfirmed ? 'Entrega confirmada' : 'Confirmar entrega'}
              </h3>
              <button type="button" className="modal-close" onClick={() => { setShowDeliveryConfirmModal(false); setDeliveryConfirmProject(null); setDeliveryConfirmed(false); setDeliveryConfirmError(null); }} aria-label="Cerrar"><FiX /></button>
            </div>
            <div className="modal-body">
              {!deliveryConfirmed ? (
                <>
                  <p>El cliente ha solicitado cerrar el proyecto <strong>{deliveryConfirmProject.title}</strong>.</p>
                  {deliveryPendingAmount > 0 ? (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.4)', borderRadius: '0.5rem' }}>
                      <p style={{ color: '#eab308', fontWeight: 600 }}>Hay {deliveryPendingAmount.toFixed(2)} € pendientes de pago.</p>
                      <p style={{ marginTop: '0.35rem', fontSize: '0.9rem' }}>Al confirmar la entrega aceptas que la marca puede dejar este importe sin pagar. El proyecto se marcará como completado igualmente.</p>
                    </div>
                  ) : (
                    <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Si confirmas la entrega el proyecto se marcará como completado. Si cancelas, el proyecto volverá a estar en progreso.</p>
                  )}
                  {deliveryConfirmError && <p className="text-red-400 mt-2">{deliveryConfirmError}</p>}
                </>
              ) : (
                <>
                  <p>Entrega confirmada correctamente. El cliente ha sido notificado y debe realizar el pago final para completar el proyecto.</p>
                  <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Si cometiste un error, puedes cancelar la confirmación y el cliente será avisado.</p>
                  {deliveryConfirmError && <p className="text-red-400 mt-2">{deliveryConfirmError}</p>}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!deliveryConfirmed ? (
                <>
                  <button type="button" className="btn-danger" onClick={handleRejectCompletion} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Cancelando…' : 'Cancelar entrega'}
                  </button>
                  <button type="button" className="btn-primary" onClick={handleConfirmDelivery} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Confirmando…' : 'Confirmar entrega'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn-danger" onClick={handleCancelDeliveryApproval} disabled={deliveryConfirmLoading}>
                    {deliveryConfirmLoading ? 'Cancelando…' : 'Cancelar confirmación'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => { setShowDeliveryConfirmModal(false); setDeliveryConfirmProject(null); setDeliveryConfirmed(false); }}>Cerrar</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Payment Modal (pago restante al completar) */}
      {showCompletionPaymentModal && completionPaymentData && completionStripePromise && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <div className="modal-header payment-modal-header">
              <h3 className="payment-modal-title">Pago al completar proyecto</h3>
              <button type="button" className="modal-close" onClick={closeCompletionPaymentModal} aria-label="Cerrar">
                <FiX />
              </button>
            </div>
            <div className="payment-modal-summary">
              <p className="payment-modal-amount">
                Cantidad pendiente: <strong>{completionPaymentData.amountEur?.toFixed(2)} €</strong>
              </p>
              <p className="payment-modal-context">
                Proyecto: {completionPaymentData.project?.title}
              </p>
            </div>
            <div className="payment-modal-form-wrap">
              <Elements stripe={completionStripePromise} options={{ clientSecret: completionPaymentData.clientSecret }}>
                <CompletionPaymentForm
                  projectId={completionPaymentData.project?.id}
                  amountEur={completionPaymentData.amountEur}
                  clientSecret={completionPaymentData.clientSecret}
                  onSuccess={onCompletionPaymentSuccess}
                  onCancel={closeCompletionPaymentModal}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {/* Complete Project Modal */}
      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="modal-content complete-project-modal">
            <div className="modal-header">
              <h3>{completeError ? 'Estado del proyecto' : 'Cerrar proyecto'}</h3>
              <button
                onClick={closeCompleteModal}
                className="modal-close"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {completeError ? (
                <div className="alert alert-warning mb-3" role="alert">
                  {completeError}
                </div>
              ) : (
                <>
                  <div className="confirm-message">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-content">
                      <p className="warning-text">
                        ¿Estás seguro de que quieres marcar el proyecto
                        <strong> "{selectedProject?.title}"</strong> como completado?
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Esta acción no se puede deshacer y podrás valorar al otro usuario.
                      </p>
                    </div>
                  </div>
                  <div className="complete-preview-upload mt-3">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Imagen de preview del proyecto (opcional)
                    </label>
                    <p className="text-xs text-gray-400 mb-2">
                      Esta imagen aparecerá en el perfil del creativo como trabajo completado.
                    </p>
                    <input
                      type="file"
                      id="complete-preview-image-2"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={onCompletePreviewFileChange}
                      className="complete-preview-input"
                      disabled={completing}
                    />
                    <label htmlFor="complete-preview-image-2" className="btn-secondary btn-sm mt-2 inline-block cursor-pointer">
                      {completePreviewFile ? 'Cambiar imagen' : 'Subir imagen'}
                    </label>
                    {completePreviewPreviewUrl && (
                      <div className="complete-preview-thumb-wrap mt-2">
                        <img src={completePreviewPreviewUrl} alt="Preview" className="complete-preview-thumb" />
                        <button
                          type="button"
                          className="btn-remove-preview"
                          onClick={() => { setCompletePreviewFile(null); setCompletePreviewPreviewUrl(null); URL.revokeObjectURL(completePreviewPreviewUrl); }}
                          aria-label="Quitar imagen"
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={closeCompleteModal}
                className="btn-secondary"
                disabled={completing}
              >
                {completeError ? 'Cerrar' : 'Cancelar'}
              </button>
              {!completeError && (
                <button
                  onClick={confirmCompleteProject}
                  disabled={completing}
                  className="btn-primary"
                >
                  {completing ? (
                    <>
                      <span className="loading-spinner"></span>
                      Completando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="modal-overlay">
          <div className="modal-content rating-modal">
            <div className="modal-header">
              <h3>⭐ Valorar proyecto</h3>
              <button 
                onClick={closeRatingModal} 
                className="modal-close"
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="rating-content">
                <p className="text-center mb-4">
                  ¿Cómo fue tu experiencia con este proyecto?
                </p>
                <p className="text-center text-sm text-gray-400 mb-6">
                  Proyecto: <strong>{completedProjectForRating?.title}</strong>
                </p>
                
                <div className="rating-section">
                  <label className="rating-label">Valoración:</label>
                  <div className="rating-stars-container">
                    <StarRating 
                      rating={rating} 
                      onRatingChange={setRating}
                    />
                    {rating > 0 && (
                      <span className="rating-text">
                        {rating === 1 ? 'Muy malo' :
                         rating === 2 ? 'Malo' :
                         rating === 3 ? 'Regular' :
                         rating === 4 ? 'Bueno' : 'Excelente'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-group mt-4">
                  <label>Comentario (opcional):</label>
                  <textarea
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    placeholder="Comparte tu experiencia sobre este proyecto..."
                    rows="3"
                    maxLength="500"
                    className="rating-textarea"
                  />
                  <div className="char-count">
                    {ratingComment.length}/500 caracteres
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={skipRating} 
                className="btn-secondary"
                disabled={submittingRating}
              >
                Omitir
              </button>
              <button 
                onClick={submitRating}
                disabled={submittingRating || rating === 0}
                className="btn-primary"
              >
                {submittingRating ? (
                  <>
                    <span className="loading-spinner"></span>
                    Enviando...
                  </>
                ) : (
                  'Enviar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

// ContactsView moved to components/contacts/ContactsView.jsx

// Chats View - WhatsApp-style list: project chats + direct conversations (sin proyecto)
const MOBILE_CHATS_BREAKPOINT = 900;

const ChatsView = ({ initialProjectId = null, initialConversationId = null, initialMilestoneId = null, onOpenCreateBriefForCreative, onNavigateToRewards, onNavigateToProject }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [groupConversations, setGroupConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId);
  const [selectedConversationId, setSelectedConversationId] = useState(initialConversationId);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(
    initialProjectId && initialMilestoneId ? initialMilestoneId : null
  );
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_CHATS_BREAKPOINT);

  const DISMISSED_CHATS_KEY = 'vissory_dismissed_chats';
  const [dismissedChats, setDismissedChats] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(DISMISSED_CHATS_KEY) : null;
      if (!raw) return { projectIds: [], conversationIds: [], groupIds: [] };
      const parsed = JSON.parse(raw);
      return {
        projectIds: Array.isArray(parsed.projectIds) ? parsed.projectIds : [],
        conversationIds: Array.isArray(parsed.conversationIds) ? parsed.conversationIds : [],
        groupIds: Array.isArray(parsed.groupIds) ? parsed.groupIds : []
      };
    } catch (_) {
      return { projectIds: [], conversationIds: [], groupIds: [] };
    }
  });

  const dismissChat = useCallback((item, e) => {
    e.preventDefault();
    e.stopPropagation();
    const { type, id } = item;
    setDismissedChats((prev) => {
      const next = { ...prev };
      if (type === 'project') next.projectIds = [...new Set([...prev.projectIds, id])];
      else if (type === 'conversation') next.conversationIds = [...new Set([...prev.conversationIds, id])];
      else if (type === 'group') next.groupIds = [...new Set([...prev.groupIds, id])];
      try {
        localStorage.setItem(DISMISSED_CHATS_KEY, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
    if (type === 'project' && id === selectedProjectId) {
      setSelectedProjectId(null);
      setSelectedMilestoneId(null);
    } else if (type === 'conversation' && id === selectedConversationId) setSelectedConversationId(null);
    else if (type === 'group' && id === selectedGroupId) setSelectedGroupId(null);
  }, [selectedProjectId, selectedConversationId, selectedGroupId]);

  // Si se abre un chat desde fuera (Contactar, enlace, etc.), sacarlo de "cerrados" para que aparezca en la lista
  useEffect(() => {
    if (!initialConversationId && !initialProjectId) return;
    setDismissedChats((prev) => {
      let next = { ...prev };
      let changed = false;
      if (initialConversationId && prev.conversationIds.includes(initialConversationId)) {
        next = { ...next, conversationIds: prev.conversationIds.filter((id) => id !== initialConversationId) };
        changed = true;
      }
      if (initialProjectId && prev.projectIds.includes(initialProjectId)) {
        next = { ...next, projectIds: prev.projectIds.filter((id) => id !== initialProjectId) };
        changed = true;
      }
      if (changed) {
        try {
          localStorage.setItem(DISMISSED_CHATS_KEY, JSON.stringify(next));
        } catch (_) {}
      }
      return next;
    });
  }, [initialConversationId, initialProjectId]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_CHATS_BREAKPOINT}px)`);
    const handle = () => setIsMobile(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;
      try {
        const [projectsRes, convRes, groupsRes] = await Promise.all([
          axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/group-conversations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        const allProjects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        const activeProjects = allProjects
          .filter((p) => p.status !== 'completed' && p.status !== 'cancelled')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProjects(activeProjects);
        const convList = Array.isArray(convRes.data) ? convRes.data : [];
        setConversations(convList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        const groupsList = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        setGroupConversations(groupsList);
        if (initialConversationId && convList.some((c) => c.id === initialConversationId)) {
          setSelectedConversationId(initialConversationId);
          setSelectedProjectId(null);
          setSelectedGroupId(null);
        } else if (initialProjectId && activeProjects.some((p) => p.id === initialProjectId)) {
          setSelectedProjectId(initialProjectId);
          setSelectedConversationId(null);
          setSelectedGroupId(null);
          setSelectedMilestoneId(initialMilestoneId || null);
        } else {
          const mobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_CHATS_BREAKPOINT;
          if (!mobile) {
            if (convList.length > 0) {
              setSelectedConversationId(convList[0].id);
              setSelectedProjectId(null);
              setSelectedGroupId(null);
            } else if (activeProjects.length > 0) {
              setSelectedProjectId(activeProjects[0].id);
              setSelectedConversationId(null);
              setSelectedGroupId(null);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching chats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token, initialProjectId, initialConversationId, initialMilestoneId]);

  // Refrescar listado cada 25s para actualizar el número de mensajes no leídos en el sidebar
  useEffect(() => {
    if (!token) return;
    const refreshList = async () => {
      try {
        const [projectsRes, convRes, groupsRes] = await Promise.all([
          axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/group-conversations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        const allProjects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        const activeProjects = allProjects
          .filter((p) => p.status !== 'completed' && p.status !== 'cancelled')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProjects(activeProjects);
        const convList = Array.isArray(convRes.data) ? convRes.data : [];
        setConversations(convList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        const groupsList = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        setGroupConversations(groupsList);
      } catch (_) {}
    };
    const interval = setInterval(refreshList, 25000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading) {
    return <LoadingSpinner text="Cargando chats..." />;
  }

  const hasThreadOpen = !!(selectedProjectId || selectedConversationId || selectedGroupId);
  const mobileThreadOpen = isMobile && hasThreadOpen;
  const backToList = () => {
    setSelectedProjectId(null);
    setSelectedConversationId(null);
    setSelectedGroupId(null);
    setSelectedMilestoneId(null);
  };

  const allItems = [
    ...groupConversations.map((g) => ({ type: 'group', ...g, id: g.id, created_at: g.created_at })),
    ...projects.map((p) => ({ type: 'project', ...p })),
    ...conversations.map((c) => ({ type: 'conversation', ...c, other_user_name: c.other_user_name || 'Contacto', title: null }))
  ]
    .filter((item) => {
      if (item.type === 'project') return !dismissedChats.projectIds.includes(item.id);
      if (item.type === 'conversation') return !dismissedChats.conversationIds.includes(item.id);
      if (item.type === 'group') return !dismissedChats.groupIds.includes(item.id);
      return true;
    })
    .sort((a, b) => {
      if (a.type === 'group' && b.type !== 'group') return -1;
      if (a.type !== 'group' && b.type === 'group') return 1;
      const unreadA = a.unread_message_count || 0;
      const unreadB = b.unread_message_count || 0;
      if (unreadA > 0 && unreadB === 0) return -1;
      if (unreadA === 0 && unreadB > 0) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div className={`chats-page ${mobileThreadOpen ? 'chats-mobile-thread-open' : ''}`}>
      <motion.div
        className="chats-layout"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <aside className="chats-list">
          <motion.h2
            className="chats-title"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Chats
          </motion.h2>
          <LevelUpPrompt variant="chats" showClose={true} onNavigateToRewards={onNavigateToRewards} />
          {allItems.length === 0 ? (
            <motion.div
              className="chats-empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>No tienes chats todavía.</p>
              <span>Escribe a un creativo destacado o inicia un proyecto para aparecer aquí.</span>
            </motion.div>
          ) : (
            <motion.ul
              className="chats-items"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {allItems.map((item, index) => {
                const isProject = item.type === 'project';
                const isGroup = item.type === 'group';
                const isActive = isGroup
                  ? item.id === selectedGroupId
                  : isProject
                    ? item.id === selectedProjectId
                    : item.id === selectedConversationId;
                const unread = item.unread_message_count || 0;
                const otherName = item.other_user_name || 'Contacto';
                const otherUser = item.other_user || {};
                const avatarUrl = getAvatarUrl(otherUser.profile_image, otherName);
                const displayName = isGroup ? (item.name || 'Grupo') : otherName;

                return (
                  <motion.li
                    key={isGroup ? `g-${item.id}` : isProject ? `p-${item.id}` : `c-${item.id}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + index * 0.05 }}
                    className="chats-item-row-wrap"
                  >
                    <button
                      type="button"
                      className={`chats-item ${isActive ? 'active' : ''} ${isGroup ? 'chats-item--group' : ''}`}
                      onClick={() => {
                        if (isGroup) {
                          setSelectedGroupId(item.id);
                          setSelectedProjectId(null);
                          setSelectedConversationId(null);
                          setSelectedMilestoneId(null);
                        } else if (isProject) {
                          setSelectedProjectId(item.id);
                          setSelectedConversationId(null);
                          setSelectedGroupId(null);
                          setSelectedMilestoneId(null);
                        } else {
                          setSelectedConversationId(item.id);
                          setSelectedProjectId(null);
                          setSelectedGroupId(null);
                          setSelectedMilestoneId(null);
                        }
                      }}
                    >
                      <button
                        type="button"
                        className="chats-item-close"
                        onClick={(e) => dismissChat(item, e)}
                        aria-label="Quitar de la lista"
                        title="Quitar de la lista"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                      <div className={`chats-item-avatar ${isGroup ? 'chats-item-avatar--group' : ''}`}>
                        {isGroup ? (
                          <img src={isotipoOrange} alt="" aria-hidden className="chats-item-group-logo" />
                        ) : (
                          <img src={avatarUrl} alt={displayName} />
                        )}
                      </div>
                      <div className="chats-item-info">
                        <div className="chats-item-main">
                          <span className="chats-item-name">{displayName}</span>
                          {isProject && item.title && (
                            <span className="chats-item-project">{item.title}</span>
                          )}
                          {isGroup && (
                            <span className="chats-item-project">Grupo</span>
                          )}
                          {!isProject && !isGroup && (
                            <span className="chats-item-project">Conversación directa</span>
                          )}
                        </div>
                        <div className="chats-item-meta">
                          {isProject && (
                            <span className="chats-item-status">
                              {item.status === 'active' ? 'Activo' : item.status === 'under_review' ? 'En revisión' : item.status}
                            </span>
                          )}
                          {unread > 0 && (
                            <span
                              className="chats-item-unread"
                              aria-label={`${unread} mensaje${unread !== 1 ? 's' : ''} sin leer`}
                              title={`${unread} mensaje${unread !== 1 ? 's' : ''} sin leer`}
                            >
                              {unread > 9 ? '9+' : unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </motion.li>
                );
              })}
            </motion.ul>
          )}
        </aside>

        <motion.section
          className="chats-thread"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {selectedGroupId ? (
            <GroupChat
              groupId={selectedGroupId}
              groupName={groupConversations.find((g) => g.id === selectedGroupId)?.name || 'Grupo'}
              onBack={isMobile ? backToList : null}
            />
          ) : selectedConversationId ? (
            <DirectChat
              conversationId={selectedConversationId}
              onOpenCreateBrief={onOpenCreateBriefForCreative}
              onBack={isMobile ? backToList : null}
            />
          ) : selectedProjectId ? (
            <Chat
              projectId={selectedProjectId}
              onBack={isMobile ? backToList : null}
              initialMilestoneId={selectedMilestoneId}
              onMilestoneChange={setSelectedMilestoneId}
              onOpenProject={onNavigateToProject ? (id) => onNavigateToProject(id, 'detail') : undefined}
            />
          ) : (
            <div className="chats-thread-empty">
              <p>Selecciona un chat para empezar a conversar.</p>
            </div>
          )}
        </motion.section>
      </motion.div>
    </div>
  );
};

// Mis proyectos (creativos): listar completados, visibilidad y proyecto destacado
const MyProjectsView = ({ onNavigateToRewards }) => {
  const { user, token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [featuredProjectId, setFeaturedProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [featuredLoadingId, setFeaturedLoadingId] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());

  useEffect(() => {
    if (!token || user?.role !== 'creative') return;
    axios.get(`${API}/profile/my-completed-projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data || {};
        setProjects(Array.isArray(data.projects) ? data.projects : []);
        setFeaturedProjectId(data.featured_project_id || null);
      })
      .catch(() => { setProjects([]); setFeaturedProjectId(null); })
      .finally(() => setLoading(false));
  }, [token, user?.role]);

  const setFeatured = async (projectId) => {
    const next = featuredProjectId === projectId ? null : projectId;
    setFeaturedLoadingId(projectId);
    try {
      await axios.patch(`${API}/profile/featured-project`, { project_id: next }, { headers: { Authorization: `Bearer ${token}` } });
      setFeaturedProjectId(next);
    } catch (err) {
      console.error('Error al marcar destacado:', err);
    } finally {
      setFeaturedLoadingId(null);
    }
  };

  const setShowOnProfile = async (projectId, show) => {
    setTogglingId(projectId);
    try {
      await axios.patch(`${API}/projects/${projectId}/profile-visibility`, { show_on_creative_profile: show }, { headers: { Authorization: `Bearer ${token}` } });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, show_on_creative_profile: show } : p)));
    } catch (err) {
      console.error('Error updating visibility:', err);
    } finally {
      setTogglingId(null);
    }
  };

  if (user?.role !== 'creative') {
    return (
      <div className="my-projects-view">
        <div className="page-header">
          <h2><FiFolder aria-hidden="true" /> Mis proyectos</h2>
          <p>Esta sección es solo para creativos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-projects-view">
      <div className="page-header">
        <h2><FiFolder aria-hidden="true" /> Mis proyectos</h2>
        <p>Proyectos completados como creativo. Elige cuáles se muestran en tu perfil (Trabajos) y marca uno como <strong>destacado</strong> para que aparezca en la parte superior de tu perfil público.</p>
      </div>
      {loading ? (
        <div className="my-projects-loading"><FiLoader className="spin" aria-hidden="true" /> Cargando...</div>
      ) : projects.length === 0 ? (
        <div className="my-projects-empty">
          <FiFolder aria-hidden="true" />
          <p>Aún no tienes proyectos completados como creativo.</p>
        </div>
      ) : (
        <div className="my-projects-grid">
          {projects.map((p) => {
            const showOnProfile = p.show_on_creative_profile !== false;
            const rawPreview = (p.preview_image_url || '').trim();
            const imgUrl = rawPreview
              ? (rawPreview.startsWith('http') ? rawPreview : `${UPLOAD_BASE}${rawPreview}`)
              : null;
            const showImg = imgUrl && !imageErrors.has(p.id);
            return (
              <div key={p.id} className="my-projects-card">
                <div className="my-projects-card-media">
                  {showImg ? (
                    <img
                      src={imgUrl}
                      alt={p.title || 'Proyecto'}
                      loading="lazy"
                      onError={() => setImageErrors((prev) => new Set(prev).add(p.id))}
                    />
                  ) : (
                    <div className="my-projects-card-placeholder"><FiFolder aria-hidden="true" /></div>
                  )}
                </div>
                <div className="my-projects-card-body">
                  <h3 className="my-projects-card-title">{p.title || 'Proyecto'}</h3>
                  <label className="my-projects-toggle">
                    <input
                      type="checkbox"
                      checked={showOnProfile}
                      onChange={(e) => setShowOnProfile(p.id, e.target.checked)}
                      disabled={togglingId === p.id}
                    />
                    <span className="my-projects-toggle-label">Mostrar en mi perfil público</span>
                  </label>
                  {togglingId === p.id && <span className="my-projects-saving">Guardando...</span>}
                  <button
                    type="button"
                    className={`my-projects-featured-btn ${featuredProjectId === p.id ? 'is-featured' : ''}`}
                    onClick={() => setFeatured(p.id)}
                    disabled={featuredLoadingId === p.id}
                    title={featuredProjectId === p.id ? 'Quitar como destacado' : 'Mostrar como proyecto destacado en tu perfil'}
                  >
                    {featuredLoadingId === p.id ? '...' : featuredProjectId === p.id ? '★ Destacado' : 'Destacar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Pagos: Stripe Connect (creativos) + Facturas (creador: pagos realizados; creativo: pagos recibidos)
const MonetizationView = () => {
  const { user, token, updateUser } = useAuth();
  const [stripeConnectStatus, setStripeConnectStatus] = useState({ has_account: false, onboarding_complete: false });
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [invoices, setInvoices] = useState({ as_creator: [], as_creative: [] });
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [stripeDashboardLoading, setStripeDashboardLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceFiscalName, setInvoiceFiscalName] = useState(user?.invoice_fiscal_name || '');
  const [invoiceNif, setInvoiceNif] = useState(user?.invoice_nif || '');
  const [invoiceAddress, setInvoiceAddress] = useState(user?.invoice_address || '');
  const [invoiceDataSaving, setInvoiceDataSaving] = useState(false);
  const [invoiceDataSuccess, setInvoiceDataSuccess] = useState('');
  const [requestInvoiceLoading, setRequestInvoiceLoading] = useState(null);
  const [requestInvoiceSuccess, setRequestInvoiceSuccess] = useState('');

  useEffect(() => {
    if ((user?.role !== 'creative' && user?.role !== 'admin') || !token) return;
    axios.get(`${API}/stripe/connect/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStripeConnectStatus(res.data))
      .catch(() => setStripeConnectStatus({ has_account: false, onboarding_complete: false }));
  }, [user?.role, token]);

  useEffect(() => {
    if (!token) return;
    setInvoicesLoading(true);
    axios.get(`${API}/profile/invoices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setInvoices(res.data || { as_creator: [], as_creative: [] }))
      .catch(() => setInvoices({ as_creator: [], as_creative: [] }))
      .finally(() => setInvoicesLoading(false));
  }, [token]);

  useEffect(() => {
    setInvoiceFiscalName(user?.invoice_fiscal_name || '');
    setInvoiceNif(user?.invoice_nif || '');
    setInvoiceAddress(user?.invoice_address || '');
  }, [user?.invoice_fiscal_name, user?.invoice_nif, user?.invoice_address]);

  const handleStripeConnectOnboard = async () => {
    setStripeConnectLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/stripe/connect/create-account-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data?.url) window.location.href = data.url;
      else setError('No se pudo generar el enlace de configuración.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al configurar la cuenta de pagos.');
    } finally {
      setStripeConnectLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    setStripeDashboardLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/stripe/connect/dashboard-link`, { headers: { Authorization: `Bearer ${token}` } });
      if (data?.url) window.open(data.url, '_blank');
      else setError('No se pudo abrir el panel de Stripe.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al abrir el panel de Stripe.');
    } finally {
      setStripeDashboardLoading(false);
    }
  };

  const formatInvoiceDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const downloadCreativeInvoicesCsv = () => {
    if (!invoices.as_creative?.length) return;
    const headers = ['Fecha', 'Proyecto', 'Concepto', 'Importe (€)'];
    const rows = invoices.as_creative.map((row) => [
      formatInvoiceDate(row.created_at),
      (row.project_title || '').replace(/"/g, '""'),
      (row.type_label || '').replace(/"/g, '""'),
      row.amount_eur != null ? Number(row.amount_eur).toFixed(2) : ''
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pagos-recibidos-vissory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCreatorInvoicesCsv = () => {
    if (!invoices.as_creator?.length) return;
    const headers = ['Fecha', 'Proyecto', 'Concepto', 'Importe (€)'];
    const rows = invoices.as_creator.map((row) => [
      formatInvoiceDate(row.created_at),
      (row.project_title || '').replace(/"/g, '""'),
      (row.type_label || '').replace(/"/g, '""'),
      row.amount_eur != null ? Number(row.amount_eur).toFixed(2) : ''
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pagos-realizados-vissory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveInvoiceData = async (e) => {
    e?.preventDefault();
    setInvoiceDataSaving(true);
    setError('');
    setInvoiceDataSuccess('');
    try {
      const { data } = await axios.patch(
        `${API}/auth/profile/invoice-data`,
        {
          invoice_fiscal_name: invoiceFiscalName.trim() || null,
          invoice_nif: invoiceNif.trim() || null,
          invoice_address: invoiceAddress.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(data);
      setInvoiceDataSuccess('Datos fiscales guardados. Ya puedes generar facturas para los creadores.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar.');
    } finally {
      setInvoiceDataSaving(false);
    }
  };

  const downloadFacturaForCreator = (row) => {
    const fiscalName = (user?.invoice_fiscal_name || '').trim();
    const nif = (user?.invoice_nif || '').trim();
    if (!fiscalName || !nif) {
      setError('Completa tus datos fiscales (nombre o razón social y NIF/CIF) arriba para poder emitir facturas al creador.');
      return;
    }
    setError('');
    const dateStr = formatInvoiceDate(row.created_at);
    const amount = row.amount_eur != null ? Number(row.amount_eur) : 0;
    const amountStr = amount ? `${amount.toFixed(2)} €` : '—';
    const issuerName = (fiscalName || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const issuerNif = (nif || '').replace(/</g, '&lt;');
    const issuerAddress = (user?.invoice_address || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const clientName = (row.creator_name || 'Cliente').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const concept = `${(row.project_title || 'Proyecto').replace(/</g, '&lt;')} — ${(row.type_label || 'Pago').replace(/</g, '&lt;')}`;
    const invoiceNumber = `VY-${(row.id || row.project_id || 'inv').toString().slice(-8).toUpperCase()}`;
    const today = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>Factura ${invoiceNumber}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 700px; margin: 0 auto; padding: 2.5rem; color: #1a1a1a; background: #fff; line-height: 1.5; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 3px solid #e85d04; }
    .logo { font-size: 1.5rem; font-weight: 700; color: #e85d04; letter-spacing: -0.02em; }
    .invoice-meta { text-align: right; }
    .invoice-meta .number { font-size: 1.25rem; font-weight: 700; color: #333; }
    .invoice-meta .date { font-size: 0.9rem; color: #666; margin-top: 0.25rem; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin: 1.5rem 0 2rem; }
    .party { padding: 1rem 1.25rem; background: #f8f9fa; border-radius: 10px; border: 1px solid #eee; }
    .party-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #e85d04; margin-bottom: 0.5rem; }
    .party-content { font-size: 0.95rem; color: #333; }
    .party-content .nif { color: #555; font-size: 0.9rem; margin-top: 0.25rem; }
    .details-table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
    .details-table th { background: #333; color: #fff; padding: 0.75rem 1rem; text-align: left; font-weight: 600; font-size: 0.85rem; }
    .details-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eee; }
    .details-table tr:last-child td { border-bottom: none; }
    .details-table .total-row td { background: #f8f9fa; font-weight: 700; font-size: 1.15rem; padding: 1rem; color: #1a1a1a; }
    .footer { margin-top: 2.5rem; padding-top: 1rem; border-top: 1px solid #eee; font-size: 0.8rem; color: #888; text-align: center; }
    @media print { body { padding: 1.5rem; } .party { background: #f5f5f5; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">FACTURA (BORRADOR)</div>
    <div class="invoice-meta">
      <div class="number">${invoiceNumber}</div>
      <div class="date">${today}</div>
    </div>
  </div>
  <div class="parties">
    <div class="party">
      <div class="party-label">Emisor</div>
      <div class="party-content">${issuerName}<div class="nif">NIF/CIF: ${issuerNif}</div>${issuerAddress ? '<div style="margin-top:0.35rem">' + issuerAddress + '</div>' : ''}</div>
    </div>
    <div class="party">
      <div class="party-label">Cliente</div>
      <div class="party-content">${clientName}</div>
    </div>
  </div>
  <table class="details-table">
    <thead><tr><th>Fecha</th><th>Concepto</th><th style="text-align:right;width:120px">Importe</th></tr></thead>
    <tbody>
      <tr><td>${dateStr}</td><td>${concept}</td><td style="text-align:right">${amountStr}</td></tr>
      <tr class="total-row"><td colspan="2">Total</td><td style="text-align:right">${amountStr}</td></tr>
    </tbody>
  </table>
  <div class="footer">Emitida a través de Vissory. Documento borrador; no sustituye la factura con validez fiscal Verifactu. Para cualquier aclaración, contacta con el emisor.</div>
</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factura-${invoiceNumber}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const requestInvoiceFromCreative = async (row) => {
    setRequestInvoiceSuccess('');
    setError('');
    const key = row.id || row.project_id;
    setRequestInvoiceLoading(key);
    try {
      await axios.post(
        `${API}/profile/invoices/request-invoice`,
        { project_id: row.project_id, payment_id: row.id || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequestInvoiceSuccess('Solicitud enviada. El creativo recibirá una notificación y podrá generarte la factura desde Pagos.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar la solicitud.');
    } finally {
      setRequestInvoiceLoading(null);
    }
  };

  const downloadJustificanteGasto = (row) => {
    const dateStr = formatInvoiceDate(row.created_at);
    const amountStr = row.amount_eur != null ? `${Number(row.amount_eur).toFixed(2)} €` : '—';
    const payerName = (user?.name || 'Pagador').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const providerName = (row.creative_name || 'Proveedor').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const concept = `${(row.project_title || 'Proyecto').replace(/</g, '&lt;')} — ${(row.type_label || 'Pago').replace(/</g, '&lt;')}`;
    const ref = (row.id || row.project_id || 'gasto').toString().slice(-8).toUpperCase();
    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>Justificante de gasto</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 700px; margin: 0 auto; padding: 2.5rem; color: #1a1a1a; background: #fff; line-height: 1.5; }
    .header { margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 3px solid #e85d04; }
    .header h1 { font-size: 1.35rem; font-weight: 700; color: #333; margin: 0; }
    .header .ref { font-size: 0.85rem; color: #666; margin-top: 0.35rem; }
    .details-table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
    .details-table th { background: #333; color: #fff; padding: 0.75rem 1rem; text-align: left; font-weight: 600; font-size: 0.85rem; width: 38%; }
    .details-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eee; }
    .details-table tr:last-child td { border-bottom: none; }
    .details-table .total-row td { background: #f8f9fa; font-weight: 700; font-size: 1.1rem; }
    .note { margin-top: 2rem; padding: 1rem 1.25rem; background: #f8f9fa; border-radius: 10px; font-size: 0.85rem; color: #555; border-left: 4px solid #e85d04; }
    @media print { body { padding: 1.5rem; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>JUSTIFICANTE DE GASTO</h1>
    <div class="ref">Ref. JG-${ref} · ${dateStr}</div>
  </div>
  <table class="details-table">
    <tr><th>Fecha</th><td>${dateStr}</td></tr>
    <tr><th>Pagador (quien declara el gasto)</th><td>${payerName}</td></tr>
    <tr><th>Proveedor del servicio</th><td>${providerName}</td></tr>
    <tr><th>Concepto</th><td>${concept}</td></tr>
    <tr class="total-row"><th>Importe</th><td>${amountStr}</td></tr>
  </table>
  <p class="note">Documento emitido por Vissory como justificante del pago. Para factura con NIF/CIF a efectos fiscales, solicítela al proveedor del servicio (creativo).</p>
</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `justificante-gasto-${ref}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isCreative = user?.role === 'creative';
  const canConfigurePayments = user?.role === 'creative' || user?.role === 'admin';
  const isCreator = user?.role === 'creator';

  return (
    <div className="monetization-page">
      <div className="page-header">
        <h2 className="flex items-center gap-2">
          <FiCreditCard aria-hidden="true" />
          Pagos
        </h2>
        <p className="page-header-subtitle">
          Configura cómo recibir pagos y descarga facturas o recibos de tus transacciones.
        </p>
      </div>
      {error && <div className="error-message">{error}</div>}

      {/* Creativos y admins: configuración Stripe Connect (admins para probar pagos en producción) */}
      {canConfigurePayments && (
        <div className="form-section" style={{ marginBottom: '1.5rem' }}>
          <h3 className="flex items-center gap-2">
            <FiCreditCard aria-hidden="true" />
            Recibir pagos
          </h3>
          <p className="section-description">
            Para que los clientes puedan pagarte por proyectos, configura tu cuenta de Stripe.
          </p>
          {stripeConnectStatus.onboarding_complete ? (
            <p className="success-message" style={{ margin: 0 }}>
              Cuenta de pagos configurada. Puedes recibir pagos cuando un cliente pague un proyecto.
            </p>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={handleStripeConnectOnboard}
              disabled={stripeConnectLoading}
            >
              {stripeConnectLoading ? 'Cargando...' : (stripeConnectStatus.has_account ? 'Completar datos bancarios' : 'Configurar cuenta para recibir pagos')}
            </button>
          )}
        </div>
      )}

      {/* Creativos y admins: datos fiscales para emitir facturas al creador */}
      {canConfigurePayments && (
        <div className="form-section" style={{ marginBottom: '1.5rem' }}>
          <h3 className="flex items-center gap-2">
            <FiFileText aria-hidden="true" />
            Datos fiscales para emitir facturas
          </h3>
          <p className="section-description">
            Indica tu nombre o razón social y NIF/CIF para poder generar facturas que el creador de contenido pueda usar para declarar el gasto.
          </p>
          <form onSubmit={saveInvoiceData} className="profile-invoice-data-form">
            <div className="form-group">
              <label htmlFor="invoice_fiscal_name">Nombre o razón social</label>
              <input
                id="invoice_fiscal_name"
                type="text"
                value={invoiceFiscalName}
                onChange={(e) => setInvoiceFiscalName(e.target.value)}
                placeholder="Ej. Juan Pérez / Estudio Creativo SL"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="invoice_nif">NIF/CIF</label>
              <input
                id="invoice_nif"
                type="text"
                value={invoiceNif}
                onChange={(e) => setInvoiceNif(e.target.value)}
                placeholder="Ej. 12345678A"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="invoice_address">Dirección fiscal (opcional)</label>
              <input
                id="invoice_address"
                type="text"
                value={invoiceAddress}
                onChange={(e) => setInvoiceAddress(e.target.value)}
                placeholder="Calle, número, CP, localidad"
                className="form-input"
              />
            </div>
            <button type="submit" className="btn-primary" disabled={invoiceDataSaving}>
              {invoiceDataSaving ? 'Guardando...' : 'Guardar datos fiscales'}
            </button>
            {invoiceDataSuccess && <p className="success-message" style={{ marginTop: '0.75rem', marginBottom: 0 }}>{invoiceDataSuccess}</p>}
          </form>
        </div>
      )}

      {/* Facturas: como creador (pagos realizados) o como creativo (pagos recibidos) */}
      <div className="form-section profile-invoices-section">
        <h3 className="flex items-center gap-2">
          <FiFileText aria-hidden="true" />
          {isCreative ? 'Mis facturas (pagos recibidos)' : 'Mis facturas (pagos realizados)'}
        </h3>
        <p className="section-description">
          {isCreative
            ? 'Listado de pagos que has recibido. Descarga el resumen en CSV para tener un justificante; el panel de Stripe sirve para ver saldo y transferencias.'
            : 'Para declarar el gasto: usa "Justificante" (imprimir o guardar como PDF). El recibo de Stripe complementa. Para factura con NIF, solicítala al creativo.'}
        </p>
        {isCreative && (
          <p className="section-description verifactu-notice" style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', background: 'var(--color-warning-bg, #fff8e6)', border: '1px solid var(--color-warning-border, #e6c84a)', borderRadius: '8px', color: 'var(--color-text-secondary, #555)' }}>
            Desde 2027 la factura con validez fiscal en España debe cumplir Verifactu (envío a Hacienda, firma y QR). El documento que generas aquí es un <strong>borrador</strong>; para factura oficial usa un proveedor homologado o tu gestoría.
          </p>
        )}
        {canConfigurePayments && stripeConnectStatus.has_account && (
          <>
            <p className="section-description" style={{ marginBottom: '0.75rem' }}>
              En Stripe Express puedes ver saldo, transferencias y transacciones (no siempre hay opción de descargar facturas). Usa el botón de abajo para abrir tu panel.
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleOpenStripeDashboard}
              disabled={stripeDashboardLoading}
              style={{ marginBottom: '1rem' }}
            >
              {stripeDashboardLoading ? 'Abriendo...' : 'Ver mis pagos en Stripe'}
            </button>
          </>
        )}
        {invoicesLoading ? (
          <p className="section-description" style={{ marginTop: '1rem' }}>Cargando facturas...</p>
        ) : (
          <>
            {isCreative && invoices.as_creative?.length === 0 && !invoicesLoading && (
              <p className="profile-invoices-empty">No tienes pagos recibidos aún.</p>
            )}
            {isCreator && invoices.as_creator?.length === 0 && !invoicesLoading && (
              <p className="profile-invoices-empty">No tienes pagos realizados aún.</p>
            )}
            {isCreative && invoices.as_creative?.length > 0 && (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={downloadCreativeInvoicesCsv}
                  style={{ marginBottom: '1rem' }}
                >
                  <FiDownload aria-hidden="true" className="mr-2" />
                  Descargar resumen (CSV)
                </button>
                <div className="profile-invoices-table-wrap">
                <table className="profile-invoices-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Proyecto</th>
                      <th>Concepto</th>
                      <th>Importe</th>
                      <th>Factura para el creador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.as_creative.map((row) => (
                      <tr key={row.id || row.project_id + row.created_at}>
                        <td>{formatInvoiceDate(row.created_at)}</td>
                        <td>{row.project_title}</td>
                        <td>{row.type_label}</td>
                        <td>{row.amount_eur != null ? `${Number(row.amount_eur).toFixed(2)} €` : '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-inline btn-invoice-download"
                            onClick={() => downloadFacturaForCreator(row)}
                            title="Descargar borrador de factura (HTML). Para factura con validez fiscal Verifactu, usa un proveedor homologado o tu gestoría."
                          >
                            <FiFileText aria-hidden="true" /> Descargar borrador
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
            {isCreator && invoices.as_creator?.length > 0 && (
              <>
                {requestInvoiceSuccess && <p className="success-message" style={{ marginBottom: '1rem' }}>{requestInvoiceSuccess}</p>}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={downloadCreatorInvoicesCsv}
                  style={{ marginBottom: '1rem' }}
                >
                  <FiDownload aria-hidden="true" className="mr-2" />
                  Descargar resumen (CSV)
                </button>
                <div className="profile-invoices-table-wrap">
                <table className="profile-invoices-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Proyecto</th>
                      <th>Concepto</th>
                      <th>Importe</th>
                      <th>Factura / Recibo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.as_creator.map((row) => (
                      <tr key={row.id || row.project_id + row.created_at}>
                        <td>{formatInvoiceDate(row.created_at)}</td>
                        <td>{row.project_title}</td>
                        <td>{row.type_label}</td>
                        <td>{row.amount_eur != null ? `${Number(row.amount_eur).toFixed(2)} €` : '—'}</td>
                        <td>
                          <span className="profile-invoices-creator-actions">
                            <button type="button" className="btn-inline btn-invoice-download" onClick={() => downloadJustificanteGasto(row)} title="Descargar justificante de gasto para declarar">
                              <FiFileText aria-hidden="true" /> Justificante
                            </button>
                            {row.receipt_url ? (
                              <a href={row.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-inline btn-invoice-download" title="Recibo de Stripe">
                                <FiDownload aria-hidden="true" /> Recibo
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                            <button
                              type="button"
                              className="btn-inline btn-invoice-download"
                              onClick={() => requestInvoiceFromCreative(row)}
                              disabled={requestInvoiceLoading === (row.id || row.project_id)}
                              title="Pedir factura con NIF al creativo"
                            >
                              {requestInvoiceLoading === (row.id || row.project_id) ? 'Enviando...' : (<><FiSend aria-hidden="true" /> Solicitar factura</>)}
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Catálogo de especialidades agrupadas por categoría
const SPECIALTY_OPTIONS = [
  { group: 'Diseño', items: ['Diseño gráfico', 'Diseño de marca/branding', 'Diseño de packaging', 'Diseño editorial', 'Diseño UX/UI', 'Diseño de producto digital', 'Ilustración', 'Dirección de arte'] },
  { group: 'Vídeo', items: ['Edición de vídeo', 'Producción audiovisual', 'Motion graphics', 'Videografía', 'Color grading', 'Vídeo para redes sociales', 'Vídeo corporativo'] },
  { group: 'Fotografía', items: ['Fotografía de producto', 'Fotografía de retrato', 'Fotografía de eventos', 'Fotografía gastronómica', 'Fotografía de moda', 'Retoque fotográfico'] },
  { group: 'Copywriting', items: ['Copywriting publicitario', 'Redacción de contenidos', 'Guionismo', 'Storytelling', 'UX writing', 'SEO copywriting', 'Naming'] },
  { group: 'Estrategia', items: ['Estrategia de contenidos', 'Estrategia de marca', 'Estrategia digital', 'Consultoría de marketing', 'Planificación de campañas'] },
  { group: 'Redes sociales', items: ['Community management', 'Gestión de redes sociales', 'Creación de contenido para redes', 'Paid media/publicidad digital', 'Influencer marketing'] },
  { group: 'Animación', items: ['Animación 2D', 'Animación 3D', 'Motion graphics', 'Diseño de personajes'] },
  { group: 'Desarrollo web', items: ['Diseño web', 'Desarrollo frontend', 'Desarrollo backend', 'WordPress/CMS', 'Desarrollo de e-commerce'] },
  { group: 'Otros', items: ['Locución/voz en off', 'Diseño de sonido', 'Podcasting', 'Email marketing'] },
];

// Profile Component
const Profile = ({ onNavigateToRewards, onNavigateToSuggestions, highlightPortfolio = false, onPortfolioHighlightDismiss }) => {
  const { user, realUser, token, updateUser, isAdmin, viewAsRole } = useAuth();
  const { addToast } = useToast();
  const { invalidateCache } = useCache();
  // Al ver como marca/creativo/creador (admin), mostrar las secciones de ese rol; si no, usar rol real
  const profileRole = (isAdmin && viewAsRole) ? viewAsRole : (realUser?.role ?? user?.role);
  const [founderCode, setFounderCode] = useState('');
  const [redeemFounderLoading, setRedeemFounderLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    bio: user.bio || '',
    location: user.location || '',
    profile_image: user.profile_image || '',
    languages: user.languages || [],
    portfolio_links: user.portfolio_links || [],
    experience_years: user.experience_years || '',
    hourly_rate: user.hourly_rate || '',
    availability: user.availability || 'available',
    // Creative specific
    specialties: user.specialties || [],
    work_areas: user.work_areas || '',
    schedule: user.schedule || '',
    // Creator specific
    youtube_channel: user.youtube_channel || '',
    youtube_api_key: '', // Temporal field for API key
    instagram_handle: user.instagram_handle || '',
    tiktok_handle: user.tiktok_handle || '',
    website: user.website || '',
    content_focus: user.content_focus || '',
    content_niche: user.content_niche || '',
    portfolio_preview_urls: [...((user.portfolio_preview_urls || []).slice(0, 3)), '', '', ''].slice(0, 3),
    portfolio_preview_names: [...((user.portfolio_preview_names || []).slice(0, 3)), '', '', ''].slice(0, 3),
    project_pdf_urls: [...((user.project_pdf_urls || []).slice(0, 3)), '', '', ''].slice(0, 3),
    project_pdf_names: [...((user.project_pdf_names || []).slice(0, 3)), '', '', ''].slice(0, 3),
    // Empresa (brand)
    company_legal_name: user.company_legal_name || '',
    company_type: user.company_type || '',
    industry: user.industry || '',
    company_size: user.company_size || '',
    linkedin_url: user.linkedin_url || '',
    founded_year: user.founded_year != null ? String(user.founded_year) : '',
    featured_project_title: user.featured_project_title || '',
    featured_project_image: user.featured_project_image || '',
    clear_featured_project_image: false,
    clear_profile_image: false,
    // Enlaces de redes (todos los perfiles)
    instagram_url: user.instagram_url || '',
    twitter_url: user.twitter_url || '',
    facebook_url: user.facebook_url || '',
    behance_url: user.behance_url || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageCompressing, setProfileImageCompressing] = useState(false);
  const [featuredProjectImageFile, setFeaturedProjectImageFile] = useState(null);
  const [featuredProjectImageCompressing, setFeaturedProjectImageCompressing] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const autoSaveInitializedRef = useRef(false);
  const lastSavedProfileSnapshotRef = useRef('');
  const [usernameValue, setUsernameValue] = useState(user.username || '');
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'error' | string(error msg)
  const [usernameSaving, setUsernameSaving] = useState(false);
  const usernameDebounceRef = useRef(null);
  const [portfolioPreviewUploadingIndex, setPortfolioPreviewUploadingIndex] = useState(null);
  const [portfolioPreviewImageFailed, setPortfolioPreviewImageFailed] = useState({});
  const [socialStats, setSocialStats] = useState({
    youtube_subscribers: user.youtube_subscribers || 0,
    instagram_followers: user.instagram_followers || 0,
    instagram_media_count: user.instagram_media_count ?? null,
    tiktok_followers: user.tiktok_followers || 0
  });
  const handleUsernameChange = (e) => {
    const raw = e.target.value.replace(/^@+/, '').toLowerCase();
    setUsernameValue(raw);
    setUsernameStatus('checking');
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    if (!raw) { setUsernameStatus(null); return; }
    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/check-username?username=${encodeURIComponent(raw)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsernameStatus(res.data.available ? 'available' : (res.data.error || 'taken'));
      } catch {
        setUsernameStatus('error');
      }
    }, 450);
  };

  const handleUsernameSave = async () => {
    if (!usernameValue.trim() || usernameStatus !== 'available') return;
    setUsernameSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.patch(
        `${API}/auth/profile/username`,
        { username: usernameValue.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(res.data);
      invalidateCache('featured_creatives_v2');
      setSuccess('¡Usuario actualizado correctamente!');
      setUsernameStatus('available');
      addToast({ type: 'success', title: 'Usuario guardado', description: 'Nombre de usuario actualizado.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al guardar el usuario.';
      setError(msg);
      addToast({ type: 'error', title: 'Error', description: msg });
    } finally {
      setUsernameSaving(false);
    }
  };

  const handleRedeemFounderCode = async (e) => {
    e.preventDefault();
    if (!founderCode.trim()) return;
    setRedeemFounderLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(
        `${API}/profile/redeem-founder-token`,
        { token: founderCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(res.data);
      setFounderCode('');
      setSuccess('¡Código canjeado! Ya tienes el badge Fundador.');
      addToast({ type: 'success', title: 'Código canjeado', description: 'Ya tienes el badge Fundador.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Código inválido o ya utilizado.';
      setError(msg);
      addToast({ type: 'error', title: 'Error', description: msg });
    } finally {
      setRedeemFounderLoading(false);
    }
  };

  const buildProfileSnapshot = useCallback((data, imageFile, fpImageFile) => {
    return JSON.stringify({
      ...data,
      __profile_image_file__: imageFile ? `${imageFile.name}:${imageFile.size}:${imageFile.lastModified}` : '',
      __fp_image_file__: fpImageFile ? `${fpImageFile.name}:${fpImageFile.size}:${fpImageFile.lastModified}` : ''
    });
  }, []);

  const saveProfile = useCallback(async ({ silent = false, snapshot = null } = {}) => {
    setLoading(true);
    setError('');
    if (!silent) setSuccess('');

    try {
      // Create FormData for multipart form
      const form = new FormData();
      const locationValue = (formData.location && CIUDADES_ESPANA.includes(formData.location)) ? formData.location : '';
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'location') {
          form.append(key, locationValue);
        } else if (key === 'portfolio_preview_urls' || key === 'project_pdf_urls' || key === 'portfolio_preview_names' || key === 'project_pdf_names') {
          const arr = Array.isArray(value) ? value : [];
          form.append(key + '_json', JSON.stringify(arr));
        } else if (key === 'specialties' || key === 'languages' || key === 'portfolio_links') {
          // No enviar ítems vacíos
          const arr = (Array.isArray(value) ? value : []).filter(item => (item || '').toString().trim());
          arr.forEach(item => form.append(key, (item || '').toString().trim()));
        } else if (Array.isArray(value)) {
          value.forEach(item => form.append(key, item));
        } else if (value !== null && value !== '') {
          form.append(key, value);
        }
      });

      if (profileImageFile) {
        form.append('profile_image_file', profileImageFile);
      }
      if (featuredProjectImageFile) {
        form.append('featured_project_image_file', featuredProjectImageFile);
      }

      const response = await axios.put(`${API}/auth/profile`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      updateUser(response.data);
      invalidateCache('featured_creatives_v2');
      setFormData(prev => ({
        ...prev,
        email: response.data.email || prev.email,
        name: response.data.name || prev.name,
        profile_image: response.data.profile_image || '',
        clear_profile_image: false,
        featured_project_image: response.data.featured_project_image || '',
        featured_project_title: response.data.featured_project_title || '',
        clear_featured_project_image: false,
        portfolio_preview_urls: [...((response.data.portfolio_preview_urls || []).slice(0, 3)), '', '', ''].slice(0, 3),
        portfolio_preview_names: [...((response.data.portfolio_preview_names || []).slice(0, 3)), '', '', ''].slice(0, 3),
        project_pdf_urls: [...((response.data.project_pdf_urls || []).slice(0, 3)), '', '', ''].slice(0, 3),
        project_pdf_names: [...((response.data.project_pdf_names || []).slice(0, 3)), '', '', ''].slice(0, 3)
      }));
      setProfileImageFile(null);
      setFeaturedProjectImageFile(null);
      if (snapshot) {
        lastSavedProfileSnapshotRef.current = snapshot;
      } else {
        lastSavedProfileSnapshotRef.current = buildProfileSnapshot(formData, null, null);
      }
      if (!silent) {
        setSuccess('¡Perfil actualizado exitosamente!');
        addToast({ type: 'success', title: 'Perfil guardado', description: 'Los cambios se han guardado correctamente.' });
      } else {
        addToast({ type: 'success', title: 'Autoguardado', description: 'Cambios guardados automáticamente.' });
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al actualizar el perfil';
      setError(msg);
      if (!silent) addToast({ type: 'error', title: 'Error al guardar', description: msg });
    } finally {
      setLoading(false);
    }
  }, [token, formData, profileImageFile, featuredProjectImageFile, updateUser, invalidateCache, addToast, buildProfileSnapshot]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const snapshot = buildProfileSnapshot(formData, profileImageFile, featuredProjectImageFile);
    await saveProfile({ silent: false, snapshot });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addToArray = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  const removeFromArray = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [field]: newArray
    });
  };

  // PDFs de proyectos por URL (sin subida al servidor)
  const setProjectPdfUrl = (index, value) => {
    const urls = [...(formData.project_pdf_urls || [])];
    while (urls.length < 3) urls.push('');
    urls[index] = (value || '').trim();
    setFormData(prev => ({ ...prev, project_pdf_urls: urls }));
  };

  const removeProjectPdfUrl = (index) => {
    const urls = [...(formData.project_pdf_urls || [])];
    const names = [...(formData.project_pdf_names || [])];
    while (urls.length < 3) urls.push('');
    while (names.length < 3) names.push('');
    urls[index] = '';
    names[index] = '';
    setFormData(prev => ({ ...prev, project_pdf_urls: urls, project_pdf_names: names }));
  };

  // Imágenes de preview de portafolio: solo subida (sin campo URL)
  const setPortfolioPreviewUrl = (index, value) => {
    const urls = [...(formData.portfolio_preview_urls || [])];
    while (urls.length < 3) urls.push('');
    urls[index] = (value || '').trim();
    setFormData(prev => ({ ...prev, portfolio_preview_urls: urls }));
    setPortfolioPreviewImageFailed(prev => ({ ...prev, [index]: false }));
  };

  const removePortfolioPreview = (index) => {
    const urls = [...(formData.portfolio_preview_urls || [])];
    const names = [...(formData.portfolio_preview_names || [])];
    while (urls.length < 3) urls.push('');
    while (names.length < 3) names.push('');
    urls[index] = '';
    names[index] = '';
    setFormData(prev => ({ ...prev, portfolio_preview_urls: urls, portfolio_preview_names: names }));
    setPortfolioPreviewImageFailed(prev => ({ ...prev, [index]: false }));
  };

  const PORTFOLIO_PREVIEW_MAX_MB = 1;
  const PORTFOLIO_PREVIEW_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

  const uploadPortfolioPreview = async (index, file) => {
    if (!file || !token) return;
    const maxBytes = PORTFOLIO_PREVIEW_MAX_MB * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(`Formato no permitido. Solo JPG, PNG, GIF o WebP.`);
      return;
    }
    if (file.size > maxBytes) {
      setError(`La imagen no puede superar ${PORTFOLIO_PREVIEW_MAX_MB} MB. Esta pesa ${formatFileSize(file.size)}.`);
      return;
    }
    setPortfolioPreviewUploadingIndex(index);
    setError('');
    try {
      let fileToUpload = file;
      try {
        fileToUpload = await imageCompression(file, {
          maxSizeMB: PORTFOLIO_PREVIEW_MAX_MB,
          maxWidthOrHeight: 1200,
          useWebWorker: true
        });
      } catch (_) {}
      const form = new FormData();
      form.append('file', fileToUpload, fileToUpload.name || file.name || 'image.jpg');
      const res = await axios.post(`${API}/upload`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = res.data?.url;
      if (url) {
        setPortfolioPreviewUrl(index, url);
      } else {
        setError('No se recibió la URL de la imagen.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir la imagen.');
    } finally {
      setPortfolioPreviewUploadingIndex(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const updateSocialStats = async (platform) => {
    const accountField = `${platform}_${platform === 'youtube' ? 'channel' : 'handle'}`;
    const accountIdentifier = formData[accountField];
    
    if (!accountIdentifier) {
      setError(`Por favor ingresa tu ${platform === 'youtube' ? 'canal' : 'usuario'} de ${platform} primero`);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      let response;
      
      if (platform === 'youtube') {
        if (!formData.youtube_api_key) {
          setError('Se requiere una clave de API de YouTube. Obténla en https://console.cloud.google.com/apis/credentials');
          setLoading(false);
          return;
        }
        
        const formDataApi = new FormData();
        formDataApi.append('channel_identifier', accountIdentifier);
        formDataApi.append('api_key', formData.youtube_api_key);
        
        response = await axios.post(`${API}/social/youtube/stats`, formDataApi, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const followerCount = response.data.subscriber_count;
        
        // Guardar en perfil: suscriptores e información pública del canal
        const updateFormData = new FormData();
        updateFormData.append('platform', 'youtube');
        updateFormData.append('account_identifier', response.data.channel_id);
        updateFormData.append('follower_count', followerCount);
        if (response.data.channel_title) updateFormData.append('channel_title', response.data.channel_title);
        if (response.data.view_count != null) updateFormData.append('view_count', response.data.view_count);
        if (response.data.video_count != null) updateFormData.append('video_count', response.data.video_count);
        if (response.data.thumbnail_url) updateFormData.append('thumbnail_url', response.data.thumbnail_url);
        if (response.data.description) updateFormData.append('channel_description', response.data.description);
        
        const updateRes = await axios.post(`${API}/social/update-profile`, updateFormData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        if (updateRes.data.user && updateUser) updateUser(updateRes.data.user);
        
        setSocialStats(prev => ({ ...prev, youtube_subscribers: followerCount }));
        setSuccess(`¡Canal de YouTube guardado! ${followerCount.toLocaleString()} suscriptores`);
        
      } else if (platform === 'instagram') {
        const cleanHandle = (accountIdentifier || '').replace('@', '').trim();
        if (!cleanHandle) {
          setError('Escribe tu usuario de Instagram (ej. rallugd12) y pulsa Actualizar.');
          setLoading(false);
          return;
        }
        const statsParams = new URLSearchParams();
        statsParams.append('username', cleanHandle);
        statsParams.append('instagram_handle', cleanHandle);

        response = await axios.post(`${API}/social/instagram/stats`, statsParams, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        
        const followerCount = response.data.follower_count ?? 0;
        const mediaCount = response.data.media_count ?? 0;
        setSocialStats(prev => ({ ...prev, instagram_followers: followerCount, instagram_media_count: mediaCount }));
        
        const updateParams = new URLSearchParams();
        updateParams.append('platform', 'instagram');
        updateParams.append('account_identifier', cleanHandle);
        updateParams.append('follower_count', String(followerCount));
        updateParams.append('media_count', String(mediaCount));
        try {
          const updateRes = await axios.post(`${API}/social/update-profile`, updateParams, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          });
          if (updateRes.data.user && updateUser) updateUser(updateRes.data.user);
          setSuccess(`Instagram guardado: ${followerCount.toLocaleString()} seguidores · ${mediaCount.toLocaleString()} publicaciones`);
        } catch (updateErr) {
          setSuccess(`Estadísticas cargadas: ${followerCount.toLocaleString()} seguidores · ${mediaCount.toLocaleString()} publicaciones (revisa guardado en servidor si no persisten)`);
        }
        
      } else if (platform === 'tiktok') {
        const formDataApi = new FormData();
        const cleanHandle = accountIdentifier.replace('@', '').trim();
        formDataApi.append('username', cleanHandle);
        
        response = await axios.post(`${API}/social/tiktok/stats`, formDataApi, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const followerCount = response.data.follower_count;
        
        const updateFormData = new FormData();
        updateFormData.append('platform', 'tiktok');
        updateFormData.append('account_identifier', cleanHandle);
        updateFormData.append('follower_count', followerCount);
        const updateRes = await axios.post(`${API}/social/update-profile`, updateFormData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        if (updateRes.data.user && updateUser) updateUser(updateRes.data.user);
        
        setSocialStats(prev => ({ ...prev, tiktok_followers: followerCount }));
        setSuccess(`TikTok guardado: ${followerCount.toLocaleString()} seguidores`);
      }
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = `Error al actualizar estadísticas de ${platform}`;
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('. ');
      else if (detail && typeof detail === 'object' && (detail.msg || detail.message)) message = detail.msg || detail.message;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const snapshot = buildProfileSnapshot(formData, profileImageFile, featuredProjectImageFile);
    if (!autoSaveInitializedRef.current) {
      autoSaveInitializedRef.current = true;
      lastSavedProfileSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === lastSavedProfileSnapshotRef.current) return;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveProfile({ silent: true, snapshot });
    }, 900);
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, [formData, profileImageFile, featuredProjectImageFile, buildProfileSnapshot, saveProfile]);

  return (
    <div className="profile">
      <div className="page-header">
        <div>
          <h2 className="flex items-center gap-2">
            <FiUser aria-hidden="true" />
            Mi Perfil
          </h2>
          <p>Actualiza tu información para atraer mejores oportunidades</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="profile-sections-row">
        <div className="profile-section-col">
        <div className="form-section">
          <h3 className="flex items-center gap-2">
            <FiClipboard aria-hidden="true" />
            Información General
          </h3>
          <p className="section-description">
            Datos básicos que verán otros usuarios al revisar tu perfil.
          </p>
          {(user.founder || user.verified) && (
            <div className="profile-level-badge-wrap">
              {user.founder && (
                <span className="profile-founder-badge-inline" title="Fundador">
                  <FiStar aria-hidden="true" /> Fundador
                </span>
              )}
              {user.verified && (
                <span className="profile-verified-badge" title="Verificado">
                  <FiCheck aria-hidden="true" /> Verificado
                </span>
              )}
            </div>
          )}
          {/* Nombre para mostrar */}
          <div className="form-group">
            <label htmlFor="profile-name">Nombre</label>
            <input
              id="profile-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Tu nombre o nombre artístico"
              maxLength={120}
              autoComplete="name"
            />
            <p className="field-hint">Es el nombre que verán otros usuarios y en tu perfil público.</p>
          </div>

          {/* Campo @username */}
          <div className="form-group">
            <label htmlFor="profile-username">Usuario <span className="field-hint-inline">(tu @handle público)</span></label>
            <div className={`username-input-wrap${usernameStatus === 'available' ? ' state-valid' : usernameStatus && usernameStatus !== 'checking' && usernameStatus !== 'available' ? ' state-invalid' : ''}`}>
              <span className="username-prefix">@</span>
              <input
                id="profile-username"
                type="text"
                value={usernameValue}
                onChange={handleUsernameChange}
                placeholder="tu.usuario"
                maxLength={30}
                autoComplete="off"
                spellCheck={false}
                className="username-input"
              />
              {usernameStatus === 'checking' && <span className="username-status checking"><FiLoader className="spin" aria-hidden="true" /></span>}
              {usernameStatus === 'available' && <span className="username-status valid"><FiCheck aria-hidden="true" /></span>}
              {usernameStatus && usernameStatus !== 'checking' && usernameStatus !== 'available' && (
                <span className="username-status invalid"><FiX aria-hidden="true" /></span>
              )}
            </div>
            {usernameStatus && usernameStatus !== 'checking' && usernameStatus !== 'available' && (
              <p className="field-error">{usernameStatus === 'taken' ? 'Este usuario ya está en uso.' : usernameStatus === 'error' ? 'Error al comprobar disponibilidad.' : usernameStatus}</p>
            )}
            {usernameStatus === 'available' && usernameValue !== (user.username || '') && (
              <button
                type="button"
                className="btn-primary username-save-btn"
                onClick={handleUsernameSave}
                disabled={usernameSaving}
              >
                {usernameSaving ? 'Guardando…' : 'Guardar usuario'}
              </button>
            )}
            <p className="field-hint">De 3 a 30 caracteres. Letras minúsculas, números, puntos y guiones bajos. Será tu URL pública: <strong>vissory.com/p/@{usernameValue || 'tuusuario'}</strong></p>
          </div>

          {user.is_google_user ? (
            <div className="form-group">
              <label>Correo electrónico</label>
              <p className="profile-email-readonly">{user.email}</p>
              <p className="field-hint">Correo vinculado a tu cuenta de Google. No se puede cambiar desde aquí.</p>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="profile-email">Correo electrónico</label>
              <input
                id="profile-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu@correo.com"
              />
              <p className="field-hint">Si lo cambias, recibirás un enlace para verificar el nuevo correo.</p>
            </div>
          )}
          <div className="form-group">
            <label>Biografía</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder="Cuéntanos sobre ti, tu experiencia y lo que te apasiona..."
              rows="3"
            />
          </div>

          <div className="form-group form-group-location">
            <label htmlFor="profile-location">Ubicación</label>
            <select
              id="profile-location"
              name="location"
              value={CIUDADES_ESPANA.includes(formData.location) ? formData.location : ''}
              onChange={handleInputChange}
              aria-label="Ubicación"
            >
              <option value="">Elige ubicación</option>
              {CIUDADES_ESPANA.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Imagen de perfil</label>
            <p className="field-hint" style={{ marginBottom: '0.5rem' }}>
              Sube una imagen (JPG, PNG, GIF, WebP, mín. 200×200 px) o pega una URL.
            </p>
            <div className="profile-image-upload-row">
              <input
                type="file"
                id="profile-image-file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  setProfileImageCompressing(true);
                  setError('');
                  try {
                    // Validate minimum dimensions (200×200 px)
                    const dims = await new Promise((resolve) => {
                      const img = new Image();
                      const url = URL.createObjectURL(file);
                      img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
                      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
                      img.src = url;
                    });
                    if (dims && (dims.w < 200 || dims.h < 200)) {
                      setError(`La imagen es demasiado pequeña (${dims.w}×${dims.h} px). Usa una de al menos 200×200 px.`);
                      setProfileImageCompressing(false);
                      return;
                    }
                    const compressed = await imageCompression(file, {
                      maxSizeMB: 0.5,
                      maxWidthOrHeight: 800,
                      useWebWorker: true
                    });
                    setProfileImageFile(compressed);
                    setFormData(prev => ({ ...prev, profile_image: '', clear_profile_image: false }));
                  } catch (err) {
                    setProfileImageFile(file);
                    setFormData(prev => ({ ...prev, profile_image: '', clear_profile_image: false }));
                  } finally {
                    setProfileImageCompressing(false);
                  }
                }}
                className="profile-image-file-input"
              />
              <label htmlFor="profile-image-file" className="btn-profile-image-upload">
                {profileImageCompressing ? 'Comprimiendo…' : 'Subir imagen'}
              </label>
              <input
                type="url"
                name="profile_image"
                value={formData.profile_image}
                onChange={(e) => {
                  handleInputChange(e);
                  if (e.target.value) setProfileImageFile(null);
                }}
                placeholder="O pega una URL (Imgur, Google Photos, etc.)"
                className="profile-image-url-input"
              />
            </div>
            {(formData.profile_image || profileImageFile) && (
              <div className="profile-image-preview-wrap" style={{ marginTop: '0.5rem' }}>
                <img 
                  src={profileImageFile 
                    ? URL.createObjectURL(profileImageFile) 
                    : (formData.profile_image.startsWith('http') ? formData.profile_image : `${UPLOAD_BASE}${formData.profile_image}`)
                  } 
                  alt="Vista previa" 
                  className="profile-image-preview" 
                  onError={(e) => { e.target.style.display = 'none'; }} 
                />
                <button
                  type="button"
                  className="btn-remove-profile-image"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, profile_image: '', clear_profile_image: true }));
                    setProfileImageFile(null);
                  }}
                >
                  Eliminar imagen
                </button>
              </div>
            )}
          </div>

          {profileRole === 'creator' && (
            <div className="form-group">
              <label>Tipo de contenido</label>
              <input
                type="text"
                name="content_focus"
                value={formData.content_focus}
                onChange={handleInputChange}
                placeholder="Ej: Reels, Stories, Fotografía, Vídeo largo, Tutoriales..."
              />
              <span className="field-hint">En qué tipo de contenido te especializas o te interesa crear.</span>
            </div>
          )}

          {profileRole === 'creator' && (
            <div className="form-group">
              <label>Nicho o temática</label>
              <input
                type="text"
                name="content_niche"
                value={formData.content_niche}
                onChange={handleInputChange}
                placeholder="Ej: Lifestyle, Tecnología, Gastronomía, Moda, Viajes..."
              />
              <span className="field-hint">Temática o sector de tu contenido para que los creativos te encuentren mejor.</span>
            </div>
          )}

          {profileRole === 'creative' && (
            <div className="form-group">
              <label>Horario</label>
              <input
                type="text"
                name="schedule"
                value={formData.schedule}
                onChange={handleInputChange}
                placeholder="Ej: Lunes a viernes de 8:30 a 14:30"
                maxLength={31}
              />
              <span className="field-hint">Tu horario de disponibilidad. Máx. 31 caracteres.</span>
            </div>
          )}
        </div>

        {/* Enlaces y redes (todos los perfiles) */}
        <div className="form-section">
          <h3 className="flex items-center gap-2">
            <FiLink aria-hidden="true" />
            Enlaces y redes
          </h3>
          <p className="section-description">
            Estos enlaces se mostrarán en tu perfil público. Añade los que quieras.
          </p>
          <div className="form-group">
            <label>Instagram</label>
            <input
              type="url"
              name="instagram_url"
              value={formData.instagram_url}
              onChange={handleInputChange}
              placeholder="https://www.instagram.com/tu_perfil"
            />
          </div>
          <div className="form-group">
            <label>X (Twitter)</label>
            <input
              type="url"
              name="twitter_url"
              value={formData.twitter_url}
              onChange={handleInputChange}
              placeholder="https://x.com/tu_perfil"
            />
          </div>
          <div className="form-group">
            <label>Facebook</label>
            <input
              type="url"
              name="facebook_url"
              value={formData.facebook_url}
              onChange={handleInputChange}
              placeholder="https://www.facebook.com/tu_pagina"
            />
          </div>
          <div className="form-group">
            <label>Behance</label>
            <input
              type="url"
              name="behance_url"
              value={formData.behance_url}
              onChange={handleInputChange}
              placeholder="https://www.behance.net/tu_perfil"
            />
          </div>
          <div className="form-group">
            <label>LinkedIn</label>
            <input
              type="url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleInputChange}
              placeholder="https://www.linkedin.com/in/tu_perfil o /company/tu_empresa"
            />
          </div>
          <div className="form-group">
            <label>Web</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://tuweb.com"
            />
          </div>
        </div>
        </div>

        {/* Redes Sociales (creator) en la columna derecha */}
        {profileRole === 'creator' && (
          <div className="form-section section-proximamente">
            <h3 className="flex items-center gap-2">
              <FiSmartphone aria-hidden="true" />
              Redes Sociales
              <span className="badge-proximamente">Próximamente</span>
            </h3>
            <p className="section-description">
              Conecta tus redes sociales para mostrar tu alcance a los creativos
            </p>
            <div className="section-proximamente-content">
            <div className="social-stats-grid">
              <div className="social-platform">
                <div className="platform-header">
                  <span className="platform-icon" aria-hidden="true">
                    <FiCamera />
                  </span>
                  <h4>Instagram</h4>
                </div>
                <div className="platform-preview-mock" aria-hidden="true">
                  <div className="platform-preview-banner" />
                  <div className="platform-preview-profile-row">
                    <div className="platform-preview-avatar platform-preview-avatar-insta">
                      <FiCamera />
                    </div>
                    <div className="platform-preview-info">
                      <span className="platform-preview-name">mi_perfil</span>
                      <span className="platform-preview-handle">Creador de contenido · Madrid</span>
                    </div>
                  </div>
                  <div className="platform-preview-stats">
                    <span><strong>120</strong> publicaciones</span>
                    <span><strong>8,2K</strong> seguidores</span>
                    <span><strong>340</strong> siguiendo</span>
                  </div>
                  <div className="platform-preview-feed">
                    <div className="platform-preview-feed-item" /><div className="platform-preview-feed-item" /><div className="platform-preview-feed-item" />
                  </div>
                </div>
                <div className="platform-form">
                  <input
                    type="text"
                    name="instagram_handle"
                    value={formData.instagram_handle}
                    onChange={handleInputChange}
                    placeholder="@usuario"
                    disabled
                  />
                  <button
                    type="button"
                    onClick={() => updateSocialStats('instagram')}
                    className="btn-update-stats"
                    disabled
                  >
                    <FiRefreshCcw aria-hidden="true" />
                    Actualizar
                  </button>
                </div>
              </div>
            </div>

            <div className="social-help">
              <p>
                <span className="flex items-center gap-2">
                  <FiInfo aria-hidden="true" />
                  <strong>Tip:</strong> Conectar tus redes sociales ayuda a los creativos
                </span> 
                a entender mejor tu audiencia y crear contenido más relevante para ti.
              </p>
            </div>
            </div>
          </div>
        )}

        {/* Columna derecha: Perfil de empresa (brand) */}
        {profileRole === 'brand' && (
          <div className="profile-section-col">
            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiBriefcase aria-hidden="true" />
                Perfil de empresa
              </h3>
              <div className="form-group">
                <label>Razón social</label>
                <input
                  type="text"
                  name="company_legal_name"
                  value={formData.company_legal_name}
                  onChange={handleInputChange}
                  placeholder="Ej: Mi Marca SL"
                />
                <span className="field-hint">Nombre legal de la empresa para facturación y contratos.</span>
              </div>
              <div className="form-group">
                <label>Tipo de empresa</label>
                <select
                  name="company_type"
                  value={formData.company_type}
                  onChange={handleInputChange}
                  aria-label="Tipo de empresa"
                >
                  <option value="">— Elegir —</option>
                  <option value="marca">Marca</option>
                  <option value="agencia">Agencia</option>
                  <option value="startup">Startup</option>
                  <option value="corporacion">Corporación</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sector / industria</label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  placeholder="Ej: Tecnología, Moda, Alimentación, Salud..."
                />
              </div>
              <div className="form-group">
                <label>Tamaño de la empresa</label>
                <select
                  name="company_size"
                  value={formData.company_size}
                  onChange={handleInputChange}
                  aria-label="Tamaño de la empresa"
                >
                  <option value="">— Elegir —</option>
                  <option value="1-10">1-10 empleados</option>
                  <option value="11-50">11-50 empleados</option>
                  <option value="51-200">51-200 empleados</option>
                  <option value="200+">200+ empleados</option>
                </select>
              </div>
              <div className="form-group">
                <label>LinkedIn de la empresa</label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleInputChange}
                  placeholder="https://www.linkedin.com/company/mi-empresa"
                />
              </div>
              <div className="form-group">
                <label>Año de fundación</label>
                <input
                  type="number"
                  name="founded_year"
                  value={formData.founded_year}
                  onChange={handleInputChange}
                  placeholder="Ej: 2020"
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiStar aria-hidden="true" />
                Proyecto destacado
              </h3>
              <span className="field-hint" style={{ marginBottom: '0.75rem', display: 'block' }}>
                Destaca un proyecto en tu perfil público. Sube una imagen representativa y añade un título.
              </span>
              <div className="form-group">
                <label>Imagen del proyecto</label>
                <div className="profile-image-upload-row">
                  <input
                    id="featured-project-image-file"
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const isGif = file.type === 'image/gif' || (file.name || '').toLowerCase().endsWith('.gif');
                      if (isGif) {
                        setFeaturedProjectImageFile(file);
                        setFormData(prev => ({ ...prev, featured_project_image: '', clear_featured_project_image: false }));
                      } else {
                        setFeaturedProjectImageCompressing(true);
                        try {
                          const compressed = await imageCompression(file, {
                            maxSizeMB: 0.3,
                            maxWidthOrHeight: 800,
                            useWebWorker: true
                          });
                          setFeaturedProjectImageFile(compressed);
                          setFormData(prev => ({ ...prev, featured_project_image: '', clear_featured_project_image: false }));
                        } catch {
                          setFeaturedProjectImageFile(file);
                          setFormData(prev => ({ ...prev, featured_project_image: '', clear_featured_project_image: false }));
                        } finally {
                          setFeaturedProjectImageCompressing(false);
                        }
                      }
                    }}
                    className="profile-image-file-input"
                  />
                  <label htmlFor="featured-project-image-file" className="btn-profile-image-upload">
                    {featuredProjectImageCompressing ? 'Comprimiendo…' : 'Subir imagen / GIF'}
                  </label>
                  <input
                    type="url"
                    name="featured_project_image"
                    value={formData.featured_project_image}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) setFeaturedProjectImageFile(null);
                    }}
                    placeholder="O pega una URL de imagen"
                    className="profile-image-url-input"
                  />
                </div>
                {(formData.featured_project_image || featuredProjectImageFile) && (
                  <div className="profile-image-preview-wrap" style={{ marginTop: '0.5rem' }}>
                    <img
                      src={featuredProjectImageFile
                        ? URL.createObjectURL(featuredProjectImageFile)
                        : (formData.featured_project_image.startsWith('http') ? formData.featured_project_image : `${UPLOAD_BASE}${formData.featured_project_image}`)
                      }
                      alt="Vista previa del proyecto destacado"
                      className="profile-image-preview"
                      style={{ maxHeight: 180, borderRadius: 8, objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      className="btn-remove-profile-image"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, featured_project_image: '', clear_featured_project_image: true }));
                        setFeaturedProjectImageFile(null);
                      }}
                    >
                      Eliminar imagen
                    </button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Título del proyecto</label>
                <input
                  type="text"
                  name="featured_project_title"
                  value={formData.featured_project_title}
                  onChange={handleInputChange}
                  placeholder="Ej: Campaña de verano 2026"
                  maxLength={80}
                />
              </div>
            </div>
          </div>
        )}

        {/* Columna derecha: Portafolio e idiomas + Información Profesional */}
        {user.role === 'creative' && (
        <div className="profile-section-col">
            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiFolder aria-hidden="true" />
                Portafolio e idiomas
              </h3>

              <div className="form-group profile-tags-group">
                <label>Enlaces de Portfolio</label>
                <div className="profile-tags-list profile-tags-list--links">
                  {formData.portfolio_links.map((link, index) => (
                    <div key={index} className="profile-tag-item profile-tag-item--link">
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => updateArrayItem('portfolio_links', index, e.target.value)}
                        placeholder="https://mi-portfolio.com"
                      />
                      <button
                        type="button"
                        onClick={() => removeFromArray('portfolio_links', index)}
                        className="profile-tag-remove"
                        title="Quitar enlace"
                        aria-label="Quitar enlace"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => addToArray('portfolio_links')}
                  className="profile-tag-add"
                >
                  <FiPlus aria-hidden="true" />
                  Añadir enlace
                </button>
              </div>

              <VixPortfolioCallout active={highlightPortfolio} onDismiss={onPortfolioHighlightDismiss}>
              <div className="form-group">
                <label>Imágenes de portafolio (preview)</label>
                <p className="field-hint" style={{ marginBottom: '0.75rem' }}>
                  Sube hasta 3 imágenes. Formatos: JPG, PNG, GIF, WebP. Máx. 1 MB por imagen.
                </p>
                <div className="portfolio-preview-url-grid">
                  {[0, 1, 2].map((index) => {
                    const url = (formData.portfolio_preview_urls || [])[index] || '';
                    const isUploading = portfolioPreviewUploadingIndex === index;
                    const imageFailed = portfolioPreviewImageFailed[index];
                    const displayUrl = getUploadUrl(url);
                    return (
                      <div key={index} className="portfolio-preview-slot">
                        <div className="portfolio-preview-actions">
                          <input
                            type="file"
                            id={`portfolio-preview-file-${index}`}
                            accept={PORTFOLIO_PREVIEW_ACCEPT}
                            className="portfolio-preview-file-input"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadPortfolioPreview(index, file);
                              e.target.value = '';
                            }}
                          />
                          <label htmlFor={`portfolio-preview-file-${index}`} className="btn-secondary btn-sm portfolio-preview-upload-btn">
                            {isUploading ? <FiLoader className="spin" aria-hidden="true" /> : <FiUpload aria-hidden="true" />}
                            {isUploading ? 'Subiendo…' : 'Subir imagen'}
                          </label>
                        </div>
                        {url ? (
                          <>
                            <input
                              type="text"
                              className="portfolio-item-name-input"
                              placeholder="Título (ej. Carta de presentación)"
                              maxLength={40}
                              value={(formData.portfolio_preview_names || [])[index] || ''}
                              onChange={(e) => {
                                const names = [...(formData.portfolio_preview_names || ['', '', ''])];
                                while (names.length < 3) names.push('');
                                names[index] = e.target.value;
                                setFormData(prev => ({ ...prev, portfolio_preview_names: names }));
                              }}
                            />
                            <div className="portfolio-preview-thumb-wrap">
                              {!imageFailed ? (
                                <img
                                  src={displayUrl}
                                  alt={(formData.portfolio_preview_names || [])[index] || `Preview ${index + 1}`}
                                  className="portfolio-preview-thumb"
                                  onError={() => setPortfolioPreviewImageFailed(prev => ({ ...prev, [index]: true }))}
                                />
                              ) : null}
                              {imageFailed ? (
                                <div className="portfolio-preview-thumb-fallback" title={url}>
                                  <FiImage aria-hidden="true" className="portfolio-preview-fallback-icon" />
                                  <span className="portfolio-preview-fallback-label">Imagen</span>
                                </div>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => removePortfolioPreview(index)}
                                className="portfolio-preview-remove"
                                title="Quitar imagen"
                                aria-label="Quitar imagen"
                              >
                                <FiX aria-hidden="true" />
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              </VixPortfolioCallout>

              <div className="form-group">
                <label>
                  <FiFileText className="inline-icon" aria-hidden="true" />
                  Proyectos en PDF (URL)
                </label>
                <p className="field-hint" style={{ marginBottom: '0.75rem' }}>
                  Pega la URL de cada PDF (hasta 3). Así no subimos archivos al servidor. Puedes usar Google Drive (enlace público), Dropbox, tu web, etc.
                </p>
                <div className="project-pdf-url-list">
                  {[0, 1, 2].map((index) => {
                    const url = (formData.project_pdf_urls || [])[index] || '';
                    return (
                      <div key={index} className="project-pdf-url-row">
                        <div className="project-pdf-url-fields">
                          <input
                            type="text"
                            className="portfolio-item-name-input"
                            placeholder="Título (ej. Carta de presentación)"
                            maxLength={40}
                            value={(formData.project_pdf_names || [])[index] || ''}
                            onChange={(e) => {
                              const names = [...(formData.project_pdf_names || ['', '', ''])];
                              while (names.length < 3) names.push('');
                              names[index] = e.target.value;
                              setFormData(prev => ({ ...prev, project_pdf_names: names }));
                            }}
                          />
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => setProjectPdfUrl(index, e.target.value)}
                            placeholder="https://..."
                            className="project-pdf-url-input"
                          />
                        </div>
                        {url ? (
                          <>
                            <a href={getUploadUrl(url)} target="_blank" rel="noopener noreferrer" className="btn-pdf-action" title="Abrir PDF">
                              <FiDownload aria-hidden="true" />
                            </a>
                            <button type="button" onClick={() => removeProjectPdfUrl(index)} className="btn-pdf-action btn-pdf-remove" title="Quitar">
                              <FiTrash2 aria-hidden="true" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-group profile-tags-group">
                <label>Idiomas</label>
                <div className="profile-tags-list">
                  {formData.languages.map((language, index) => (
                    <div key={index} className="profile-tag-item">
                      <input
                        type="text"
                        value={language}
                        onChange={(e) => updateArrayItem('languages', index, e.target.value)}
                        placeholder="Ej: Español (nativo)"
                      />
                      <button
                        type="button"
                        onClick={() => removeFromArray('languages', index)}
                        className="profile-tag-remove"
                        title="Quitar"
                        aria-label="Quitar idioma"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => addToArray('languages')}
                  className="profile-tag-add"
                >
                  <FiPlus aria-hidden="true" />
                  Añadir idioma
                </button>
              </div>
            </div>

        {/* Información Profesional (creativos): debajo de Portafolio e idiomas */}
            <div className="form-section">
              <h3 className="flex items-center gap-2">
                <FiPenTool aria-hidden="true" />
                Información Profesional
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Años de Experiencia</label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleInputChange}
                    min="0"
                    max="50"
                  />
                </div>
                <div className="form-group">
                  <label>Tarifa por Hora (€)</label>
                  <input
                    type="number"
                    name="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-group profile-tags-group">
                <label>Especialidades</label>
                <div className="profile-tags-list profile-tags-list--pills">
                  {formData.specialties.filter(s => (s || '').trim()).map((specialty) => {
                    const realIdx = formData.specialties.indexOf(specialty);
                    return (
                      <span key={`${specialty}-${realIdx}`} className="profile-specialty-pill">
                        {specialty}
                        <button
                          type="button"
                          onClick={() => removeFromArray('specialties', realIdx)}
                          className="profile-specialty-pill-remove"
                          title="Quitar"
                          aria-label={`Quitar ${specialty}`}
                        >
                          <FiX aria-hidden="true" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <select
                  className="filter-panel-select profile-specialty-select"
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && !formData.specialties.includes(v)) {
                      const cleaned = formData.specialties.filter(s => (s || '').trim());
                      setFormData({ ...formData, specialties: [...cleaned, v] });
                    }
                    e.target.value = '';
                  }}
                  aria-label="Añadir especialidad"
                >
                  <option value="">— Añadir especialidad —</option>
                  {SPECIALTY_OPTIONS.map(group => (
                    <optgroup key={group.group} label={group.group}>
                      {group.items
                        .filter(item => !formData.specialties.includes(item))
                        .map(item => (
                          <option key={item} value={item}>{item}</option>
                        ))
                      }
                    </optgroup>
                  ))}
                </select>
                {onNavigateToSuggestions && (
                  <p className="profile-specialty-suggestion">
                    ¿Te falta alguna?{' '}
                    <button type="button" className="profile-specialty-suggestion-link" onClick={onNavigateToSuggestions}>
                      Sugiérela en Sugerencias
                    </button>
                  </p>
                )}
              </div>
            </div>
        </div>
        )}

        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : (
              <>
                <FiSave className="mr-2" aria-hidden="true" />
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Código de fundador: al final del perfil */}
      <div className="form-section profile-founder-section">
        <h3 className="flex items-center gap-2">
          <FiStar aria-hidden="true" />
          Código de fundador
        </h3>
        <p className="section-description">
          Si tienes un código de fundador, canjéalo aquí para obtener el badge especial &quot;Fundador&quot;.
        </p>
        {user.founder ? (
          <p className="profile-founder-badge-text">
            <span className="profile-founder-badge"><FiStar aria-hidden="true" /> Fundador</span>
          </p>
        ) : (
          <>
            <p className="section-description profile-founder-cta-wrap" style={{ marginTop: '0.25rem' }}>
              ¿No tienes código?{' '}
              <a href="mailto:founders@vissory.com" className="profile-founder-cta-link" target="_blank" rel="noopener noreferrer">
                Solicitar ser fundador
              </a>
            </p>
            <form onSubmit={handleRedeemFounderCode} className="profile-founder-redeem">
            <input
              type="text"
              value={founderCode}
              onChange={(e) => setFounderCode(e.target.value)}
              placeholder="Pega tu código"
              className="profile-founder-input"
              disabled={redeemFounderLoading}
            />
            <button type="submit" className="btn-primary" disabled={redeemFounderLoading || !founderCode.trim()}>
              {redeemFounderLoading ? 'Canjeando...' : 'Canjear'}
            </button>
          </form>
          </>
        )}
      </div>
    </div>
  );
};

// Featured Creatives Component - Listado de creativos (todos visibles; orden por destacado/nivel/reciente)
const UPLOAD_BASE_CREATIVES = UPLOAD_BASE;

function mapCreativeToCard(apiUser) {
  const previewNames = apiUser.portfolio_preview_names || [];
  const portfolio = (apiUser.portfolio_preview_urls || [])
    .filter((url) => url && typeof url === 'string' && url.trim())
    .map((url, i) => ({
      id: i + 1,
      title: (previewNames[i] || '').trim() || `Preview ${i + 1}`,
      image: getUploadUrl(url.trim())
    }));
  let responseTime = '—';
  if (apiUser.response_time_hours != null) {
    const h = Number(apiUser.response_time_hours);
    if (h < 1) responseTime = '< 1 hora';
    else if (h < 2) responseTime = '< 2 horas';
    else responseTime = `< ${Math.round(h)} horas`;
  }
  const schedule = (apiUser.schedule || '').trim();
  const respuesta = schedule || responseTime;
  const specialty = (apiUser.specialties && apiUser.specialties[0]) || apiUser.work_areas || 'Creativo';
  const rating = Number(apiUser.average_rating) || 0;
  const completedProjects = Number(apiUser.completed_projects) || 0;
  return {
    id: apiUser.id,
    name: apiUser.name || 'Creativo',
    avatar: apiUser.profile_image
      ? (apiUser.profile_image.startsWith('http') ? apiUser.profile_image : `${UPLOAD_BASE_CREATIVES}${apiUser.profile_image}`)
      : null,
    specialty,
    rating,
    reviews: Number(apiUser.total_reviews) || 0,
    hourlyRate: apiUser.hourly_rate != null ? Number(apiUser.hourly_rate) : null,
    location: apiUser.location || '',
    verified: apiUser.verified !== undefined ? Boolean(apiUser.verified) : (completedProjects > 0),
    featured: Boolean(apiUser.featured),
    season_level: apiUser.season_level != null ? Number(apiUser.season_level) : null,
    season_level_name: apiUser.season_level_name || null,
    season_badge_color: apiUser.season_badge_color || null,
    bio: apiUser.bio || '',
    skills: apiUser.specialties || [],
    completedProjects,
    responseTime,
    respuesta,
    portfolio,
    projectPdfs: (() => {
      const pdfNames = apiUser.project_pdf_names || [];
      const urls = (apiUser.project_pdf_urls || []).filter(u => u && typeof u === 'string' && String(u).trim());
      return urls.slice(0, 3).map((url, idx) => ({
        id: idx + 1,
        name: (pdfNames[idx] || '').trim() || `Proyecto ${idx + 1}`,
        url: getUploadUrl(url.trim())
      }));
    })(),
    founder: Boolean(apiUser.founder),
    followers_count: Number(apiUser.followers_count) || 0,
    username: apiUser.username || null,
    languages: apiUser.languages || [],
    experience_years: apiUser.experience_years != null ? Number(apiUser.experience_years) : null
  };
}

// ==========================================
// REWARDS VIEW - SISTEMA DE RECOMPENSAS PARA CREATIVOS
// ==========================================
const RewardsView = () => {
  const { token, user, isAdmin, viewAsRole, updateUser } = useAuth();
  const { getCached, setCached, isCacheValid, invalidateCache } = useCache();
  const cachedRewards = getCached('rewards');
  const cachedLeaderboard = getCached('leaderboard');
  const [rewards, setRewards] = useState(cachedRewards || null);
  const [leaderboard, setLeaderboard] = useState(cachedLeaderboard || { leaderboard: [], current_user_rank: null });
  const [loading, setLoading] = useState(!cachedRewards);
  const [retryCount, setRetryCount] = useState(0);
  const [activeTab, setActiveTab] = useState('overview'); // overview, achievements, leaderboard
  const [leaderboardScope, setLeaderboardScope] = useState('month'); // 'month' | 'all'

  const handleRetryRewards = () => {
    invalidateCache('rewards');
    setRewards(null);
    setLoading(true);
    setRetryCount((c) => c + 1);
  };
  
  // Tour/Onboarding state
  const TOUR_STORAGE_KEY = `vissory_rewards_tour_${user?.id || 'guest'}`;
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  
  const tourSteps = [
    {
      title: '¡Bienvenido a Vissory Rewards!',
      description: 'Este es tu espacio de Vissory Rewards. Aquí podrás ver tu nivel, ganar XP y desbloquear beneficios exclusivos que te ayudarán a destacar frente a otros creativos.',
      tip: '💡 Sube de nivel completando proyectos y recibiendo buenas reseñas',
      icon: <FiZap />,
      highlight: 'intro'
    },
    {
      title: 'Sistema de Niveles',
      description: 'Existen 5 niveles con beneficios dentro de la app:\n\n• Nivel 1 - Principiante: Badge visible en tu perfil y acceso al ranking\n• Nivel 2 - Emergente: Badge Emergente; al subir a 3 obtienes sello Verificado\n• Nivel 3 - Profesional: Sello Verificado y mejor posición en la lista de creativos\n• Nivel 4 - Experto: Mayor visibilidad cuando te buscan\n• Nivel 5 - Élite: Siempre primero en la lista + badge Élite',
      tip: '🎯 Todos los creativos son visibles; sube de nivel para aparecer antes',
      icon: <FiStar />,
      highlight: 'level'
    },
    {
      title: 'Progreso y XP',
      description: 'La barra de progreso muestra cuánto XP necesitas para el siguiente nivel. El XP se acumula con cada acción positiva en la plataforma.',
      tip: '📊 Necesitas aproximadamente 200-500 XP para subir cada nivel',
      icon: <FiTarget />,
      highlight: 'progress'
    },
    {
      title: '¿Cómo Ganar XP?',
      description: 'Hay varias formas de ganar puntos de experiencia:\n\n✅ Completar un proyecto: +100 a +200 XP\n⭐ Recibir reseña 5 estrellas: +50 XP\n📩 Aplicación aceptada: +25 XP\n💬 Respuesta rápida (<2h): +15 XP\n🤝 Cliente repite contigo: +75 XP\n🏆 Primer proyecto del mes: +30 XP bonus',
      tip: '🔥 Tip: La calidad importa más que la cantidad',
      icon: <FiCheckCircle />,
      highlight: 'howto'
    },
    {
      title: 'Temporadas Mensuales',
      description: 'Cada mes es una nueva temporada de Vissory Rewards. Tu nivel y beneficios se mantienen, pero el ranking mensual se reinicia para dar oportunidad a todos.\n\nLos creativos más activos del mes reciben reconocimiento especial y badges exclusivos.',
      tip: '📅 El contador muestra los días restantes de la temporada actual',
      icon: <FiCalendar />,
      highlight: 'season'
    },
    {
      title: 'Logros Especiales',
      description: 'Desbloquea achievements completando fases importantes:\n\n🥇 Primer Proyecto - Completa tu primer trabajo\n⚡ Respuesta Rápida - Responde en menos de 1 hora\n🌟 5 Estrellas - Mantén rating perfecto\n👑 Cliente Frecuente - 3 proyectos con el mismo cliente\n🎯 Especialista - 10 proyectos en tu categoría',
      tip: '🏅 Cada logro desbloqueado te da XP bonus y aparece en tu perfil',
      icon: <FiCheckCircle />,
      highlight: 'achievements'
    },
    {
      title: 'Ranking Mensual',
      description: 'Compite con otros creativos por el top del mes. El leaderboard se actualiza en tiempo real y muestra:\n\n• Tu posición actual vs otros creativos\n• XP ganado este mes\n• Los 10 mejores del mes\n\nEstar en el top 10 te da visibilidad extra y un badge de "Top Performer".',
      tip: '🏆 Los 3 primeros del mes obtienen beneficios exclusivos',
      icon: <FiBarChart2 />,
      highlight: 'leaderboard'
    },
    {
      title: '¡Alcanza el Nivel Élite! ⚡',
      description: 'El Nivel 5 Élite es el máximo reconocimiento dentro de la app:\n\n⚡ Badge Élite destacado en tu perfil y en la lista de creativos\n🔝 Tu perfil aparece siempre el primero cuando buscan creativos\n👁️ Los clientes te ven en primer lugar\n🏅 Reconocimiento visible para todos los que visitan la app',
      tip: '🚀 Sube de nivel con proyectos y buenas reseñas para llegar a Élite',
      icon: <FiZap />,
      highlight: 'elite'
    }
  ];
  
  // Check if user has seen the tour
  useEffect(() => {
    if (user && !loading && rewards) {
      const hasSeenTour = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!hasSeenTour) {
        // Small delay to let the page render first
        setTimeout(() => setShowTour(true), 500);
      }
    }
  }, [user, loading, rewards, TOUR_STORAGE_KEY]);
  
  const handleTourNext = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      handleTourComplete();
    }
  };
  
  const handleTourPrev = () => {
    if (tourStep > 0) {
      setTourStep(tourStep - 1);
    }
  };
  
  const handleTourComplete = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setShowTour(false);
    setTourStep(0);
  };
  
  const handleTourSkip = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setShowTour(false);
    setTourStep(0);
  };

  // Sincronizar nivel del usuario cuando rewards cambia
  useEffect(() => {
    if (rewards?.level && updateUser && user) {
      const needsUpdate = 
        user.season_level !== rewards.level.level ||
        user.season_level_name !== rewards.level.name;
      
      if (needsUpdate) {
        updateUser({
          ...user,
          season_level: rewards.level.level,
          season_level_name: rewards.level.name,
          season_badge_color: rewards.level.badge_color,
          season_xp: rewards.season_xp
        });
      }
    }
  }, [rewards]);

  useEffect(() => {
    const fetchRewards = async () => {
      if (!token) return;
      
      // Al reintentar (retryCount > 0) siempre hacemos petición fresca; si no, usamos caché si es válido
      const skipCache = retryCount > 0;
      if (!skipCache && isCacheValid('rewards')) {
        const cached = getCached('rewards');
        if (cached) {
          setRewards(cached);
          if (cached?.level && updateUser && user) {
            const needsUpdate =
              user.season_level !== cached.level.level ||
              user.season_level_name !== cached.level.name;
            if (needsUpdate) {
              updateUser({
                ...user,
                season_level: cached.level.level,
                season_level_name: cached.level.name,
                season_badge_color: cached.level.badge_color,
                season_xp: cached.season_xp
              });
            }
          }
        }
        setLoading(false);
        return;
      }
      
      if (!rewards) {
        setLoading(true);
      }
      
      const startedAt = Date.now();
      const MIN_LOADING_MS = 600;
      try {
        const viewAsParam = isAdmin && viewAsRole === 'creative' ? '?view_as=creative' : '';
        const res = await axios.get(`${API}/rewards${viewAsParam}`, { headers: { Authorization: `Bearer ${token}` } });
        setRewards(res.data);
        setCached('rewards', res.data);
      } catch (err) {
        console.error('Error loading rewards:', err);
        if (skipCache) {
          // Reintento: no usar caché antiguo, dejar en estado error
        } else {
          const stale = getCached('rewards');
          if (stale) setRewards(stale);
        }
      } finally {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_LOADING_MS) {
          setTimeout(() => setLoading(false), MIN_LOADING_MS - elapsed);
        } else {
          setLoading(false);
        }
      }
    };
    fetchRewards();
  }, [token, isAdmin, viewAsRole, retryCount]);

  useEffect(() => {
    if (!token || !rewards) return;
    
    const leaderboardCacheKey = `leaderboard_${leaderboardScope}`;
    if (isCacheValid(leaderboardCacheKey)) {
      const cached = getCached(leaderboardCacheKey);
      if (cached) {
        setLeaderboard(cached);
        return;
      }
    }
    
    const seasonParam = leaderboardScope === 'all' ? '&season_id=all-time' : '';
    axios.get(`${API}/rewards/leaderboard?limit=10${seasonParam}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = {
          leaderboard: res.data.leaderboard || [],
          current_user_rank: res.data.current_user_rank ?? null,
          season_id: res.data.season_id,
        };
        setLeaderboard(data);
        setCached(leaderboardCacheKey, data);
      })
      .catch((err) => {
        console.error('Error loading leaderboard:', err);
        setLeaderboard({ leaderboard: [], current_user_rank: null });
      });
  }, [token, rewards, leaderboardScope]);

  const getAchievementIcon = (iconName) => {
    const icons = {
      // Básicos
      user: <FiUser />,
      send: <FiSend />,
      check: <FiCheck />,
      folder: <FiFolder />,
      star: <FiStar />,
      award: <FiAward />,
      trophy: <FiAward />,
      crown: <FiAward />,
      briefcase: <FiBriefcase />,
      target: <FiTarget />,
      sparkles: <FiZap />,
      // Nuevos para rewards
      gift: <FiGift />,
      users: <FiUsers />,
      'trending-up': <FiTrendingUp />,
      clock: <FiClock />,
      zap: <FiZap />,
      video: <FiVideo />,
      'check-circle': <FiCheckCircle />,
      image: <FiImage />,
      'edit-3': <FiEdit3 />,
      calendar: <FiCalendar />,
      mail: <FiMail />,
      'arrow-up': <FiArrowUp />,
      unlock: <FiUnlock />,
      'file-text': <FiFileText />,
      'book-open': <FiBookOpen />,
      link: <FiLink />,
      headphones: <FiHeadphones />,
      // Nuevos para achievements
      flame: <FiZap />,
      diamond: <FiStar />,
      heart: <FiHeart />,
      repeat: <FiRepeat />,
      layers: <FiLayers />,
      shield: <FiShield />,
      rocket: <FiTrendingUp />,
      'refresh-cw': <FiRefreshCcw />,
      'message-circle': <FiMessageCircle />,
      sunrise: <FiSun />,
      medal: <FiAward />,
    };
    return icons[iconName] || <FiStar />;
  };

  if (loading) {
    return (
      <div className="rewards-loading">
        <LoadingSpinner size="large" text="Cargando recompensas..." />
      </div>
    );
  }

  if (!rewards) {
    // Check if user is a creator (not creative)
    const isCreator = user?.role === 'creator';
    return (
      <div className="rewards-error">
        <FiAlertCircle />
        {isCreator ? (
          <>
            <p>Las recompensas son exclusivas para creativos</p>
            <p className="rewards-error-hint">
              Como creador de contenido, tu enfoque está en encontrar talento. 
              Los creativos ganan XP y suben de nivel completando proyectos.
            </p>
          </>
        ) : (
          <>
            <p>No se pudieron cargar las recompensas.</p>
            <p className="rewards-error-hint">Comprueba tu conexión e inténtalo de nuevo.</p>
            <button type="button" className="btn-primary rewards-retry-btn" onClick={handleRetryRewards}>
              Reintentar
            </button>
          </>
        )}
      </div>
    );
  }

  const season = rewards.season || {};
  const seasonXp = rewards.season_xp ?? rewards.total_xp;
  const rewardTiers = rewards.reward_tiers || rewards.all_levels || [];

  return (
    <div className="rewards-container">
      {/* Rewards Tour Modal */}
      <AnimatePresence>
        {showTour && (
          <motion.div 
            className="rewards-tour-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="rewards-tour-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <div className="rewards-tour-header">
                <div className="rewards-tour-step-indicator">
                  {tourSteps.map((_, idx) => (
                    <span 
                      key={idx} 
                      className={`rewards-tour-dot ${idx === tourStep ? 'active' : ''} ${idx < tourStep ? 'completed' : ''}`}
                    />
                  ))}
                </div>
                <button className="rewards-tour-skip" onClick={handleTourSkip}>
                  Saltar
                </button>
              </div>
              
              <div className="rewards-tour-content">
                <div className="rewards-tour-icon">
                  {tourSteps[tourStep].icon}
                </div>
                <h3 className="rewards-tour-title">{tourSteps[tourStep].title}</h3>
                <p className="rewards-tour-description">{tourSteps[tourStep].description}</p>
                {tourSteps[tourStep].tip && (
                  <div className="rewards-tour-tip">
                    {tourSteps[tourStep].tip}
                  </div>
                )}
              </div>
              
              <div className="rewards-tour-footer">
                <button 
                  className="rewards-tour-btn rewards-tour-btn-secondary"
                  onClick={handleTourPrev}
                  disabled={tourStep === 0}
                >
                  Anterior
                </button>
                <span className="rewards-tour-progress">
                  {tourStep + 1} / {tourSteps.length}
                </span>
                <button 
                  className="rewards-tour-btn rewards-tour-btn-primary"
                  onClick={handleTourNext}
                >
                  {tourStep === tourSteps.length - 1 ? '¡Entendido!' : 'Siguiente'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="rewards-header">
        <div className="rewards-header-content">
          <h1 className="rewards-title">
            <FiZap className="rewards-title-icon" />
            Recompensas
          </h1>
          <p className="rewards-subtitle">
            Vissory Rewards: gana XP y sube de nivel para desbloquear beneficios exclusivos cada mes
          </p>
        </div>
      </div>

      {/* Season banner: mes + días restantes */}
      {season.name && (
        <div className="rewards-season-banner">
          <span className="rewards-season-name">{season.name}</span>
          {typeof season.days_left === 'number' && (
            <span className="rewards-season-countdown">
              {season.days_left === 0 ? 'Último día' : season.days_left === 1 ? '1 día restante' : `${season.days_left} días restantes`}
            </span>
          )}
        </div>
      )}

      {/* Level Card (season progress) */}
      <div className="rewards-level-card">
        <div className="rewards-level-info">
          <div className="rewards-level-badge" style={{ background: rewards.level.badge_color }}>
            <span className="rewards-level-number">{rewards.level.level}</span>
          </div>
          <div className="rewards-level-details">
            <h2 className="rewards-level-name">{rewards.level.name}</h2>
            <p className="rewards-xp-total">{seasonXp.toLocaleString()} XP este mes · {rewards.total_xp.toLocaleString()} XP totales</p>
          </div>
        </div>

        {rewards.next_level && (
          <div className="rewards-progress-section">
            <div className="rewards-progress-header">
              <span>Progreso al Nivel {rewards.next_level.level}</span>
              <span className="rewards-xp-needed">{rewards.xp_to_next} XP restantes</span>
            </div>
            <div className="rewards-progress-bar">
              <div
                className="rewards-progress-fill"
                style={{
                  width: `${rewards.progress_percent}%`,
                  background: rewards.level.badge_color
                }}
              />
            </div>
            <div className="rewards-progress-labels">
              <span>{rewards.level.min_xp} XP</span>
              <span>{rewards.level.max_xp === 999999 ? '∞' : rewards.level.max_xp.toLocaleString()} XP</span>
            </div>
          </div>
        )}

        {!rewards.next_level && (
          <div className="rewards-max-level">
            <FiStar className="rewards-max-icon" />
            <span>¡Has alcanzado el nivel máximo este mes!</span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="rewards-stats-grid">
        <div className="rewards-stat-card">
          <div className="rewards-stat-icon proposals">
            <FiSend />
          </div>
          <div className="rewards-stat-content">
            <span className="rewards-stat-value">{rewards.stats.proposals_sent}</span>
            <span className="rewards-stat-label">Aplicaciones Enviadas</span>
          </div>
        </div>
        <div className="rewards-stat-card">
          <div className="rewards-stat-icon accepted">
            <FiCheckCircle />
          </div>
          <div className="rewards-stat-content">
            <span className="rewards-stat-value">{rewards.stats.proposals_accepted}</span>
            <span className="rewards-stat-label">Aplicaciones Aceptadas</span>
          </div>
        </div>
        <div className="rewards-stat-card">
          <div className="rewards-stat-icon projects">
            <FiFolder />
          </div>
          <div className="rewards-stat-content">
            <span className="rewards-stat-value">{rewards.stats.projects_completed}</span>
            <span className="rewards-stat-label">Proyectos Completados</span>
          </div>
        </div>
        <div className="rewards-stat-card">
          <div className="rewards-stat-icon rating">
            <FiStar />
          </div>
          <div className="rewards-stat-content">
            <span className="rewards-stat-value">{(rewards.stats?.average_rating ?? 0).toFixed(1)}</span>
            <span className="rewards-stat-label">Rating Promedio</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rewards-tabs">
        <button
          className={`rewards-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FiZap /> Niveles
        </button>
        <button
          className={`rewards-tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          <FiStar /> Logros ({rewards.unlocked_achievements.length}/{rewards.unlocked_achievements.length + rewards.locked_achievements.length})
        </button>
        <button
          className={`rewards-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          <FiBarChart2 /> Ranking
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="rewards-levels-section">
          <h3 className="rewards-section-title">Recompensas de este mes</h3>
          <p className="rewards-section-desc">Cada mes puedes subir de nivel y desbloquear estos beneficios. El progreso se reinicia al mes siguiente.</p>
          <div className="rewards-levels-list">
            {rewardTiers.map((level) => (
              <div
                key={level.level}
                className={`rewards-level-item ${rewards.level.level >= level.level ? 'unlocked' : 'locked'} ${rewards.level.level === level.level ? 'current' : ''}`}
              >
                <div className="rewards-level-item-header">
                  <div className="rewards-level-item-badge" style={{ background: level.badge_color }}>
                    {level.level}
                  </div>
                  <div className="rewards-level-item-title">
                    <span className="rewards-level-item-name">{level.name}</span>
                    <span className="rewards-level-item-xp">{level.min_xp.toLocaleString()} - {level.max_xp === 999999 ? '∞' : level.max_xp.toLocaleString()} XP</span>
                  </div>
                  {rewards.level.level >= level.level ? (
                    <FiCheckCircle className="rewards-level-check" />
                  ) : (
                    <FiLock className="rewards-level-lock" />
                  )}
                </div>
                {level.rewards && level.rewards.length > 0 && (
                  <div className="rewards-tier-benefits-grid">
                    {level.rewards.map((r, i) => (
                      <div key={i} className={`rewards-benefit-card ${rewards.level.level >= level.level ? 'active' : ''}`}>
                        <div className="rewards-benefit-icon" style={{ background: `${level.badge_color}20`, color: level.badge_color }}>
                          {getAchievementIcon(r.icon)}
                        </div>
                        <div className="rewards-benefit-content">
                          <span className="rewards-benefit-title">{r.title}</span>
                          <span className="rewards-benefit-desc">{r.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* XP History */}
          {rewards.xp_history && rewards.xp_history.length > 0 && (
            <div className="rewards-history-section">
              <h3 className="rewards-section-title">Actividad Reciente</h3>
              <div className="rewards-history-list">
                {rewards.xp_history.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="rewards-history-item">
                    <div className="rewards-history-xp">+{entry.amount} XP</div>
                    <div className="rewards-history-reason">{entry.reason}</div>
                    <div className="rewards-history-time">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short'
                      }) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'achievements' && (
        <div className="rewards-achievements-section">
          {/* Unlocked Achievements */}
          {rewards.unlocked_achievements.length > 0 && (
            <>
              <h3 className="rewards-section-title">
                <FiAward style={{ marginRight: '0.5rem' }} />
                Logros Desbloqueados ({rewards.unlocked_achievements.length})
              </h3>
              <div className="rewards-achievements-grid">
                {rewards.unlocked_achievements.map((achievement) => (
                  <div key={achievement.id} className={`rewards-achievement-card unlocked ${achievement.rare ? 'rare' : ''}`}>
                    <div className="rewards-achievement-icon">
                      {getAchievementIcon(achievement.icon)}
                    </div>
                    <div className="rewards-achievement-content">
                      <div className="rewards-achievement-header">
                        <h4 className="rewards-achievement-name">{achievement.name}</h4>
                        {achievement.rare && <span className="rewards-achievement-rare">✨ Raro</span>}
                      </div>
                      <p className="rewards-achievement-desc">{achievement.description}</p>
                      {achievement.xp_reward > 0 && (
                        <span className="rewards-achievement-xp">+{achievement.xp_reward} XP</span>
                      )}
                      {achievement.special_reward && (
                        <div className="rewards-achievement-special">
                          <FiGift style={{ marginRight: '0.25rem' }} />
                          <span>{achievement.special_reward}</span>
                        </div>
                      )}
                    </div>
                    <FiCheckCircle className="rewards-achievement-check" />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Locked Achievements */}
          {rewards.locked_achievements.length > 0 && (
            <>
              <h3 className="rewards-section-title" style={{ marginTop: '2rem' }}>
                <FiTarget style={{ marginRight: '0.5rem' }} />
                Logros por Desbloquear ({rewards.locked_achievements.length})
              </h3>
              <p className="rewards-section-hint">Completa estos retos para ganar XP y recompensas exclusivas</p>
              <div className="rewards-achievements-grid">
                {rewards.locked_achievements.map((achievement) => (
                  <div key={achievement.id} className={`rewards-achievement-card locked ${achievement.rare ? 'rare-locked' : ''}`}>
                    <div className="rewards-achievement-icon locked">
                      {getAchievementIcon(achievement.icon)}
                    </div>
                    <div className="rewards-achievement-content">
                      <div className="rewards-achievement-header">
                        <h4 className="rewards-achievement-name">{achievement.name}</h4>
                        {achievement.rare && <span className="rewards-achievement-rare locked">✨ Raro</span>}
                      </div>
                      <p className="rewards-achievement-desc">{achievement.description}</p>
                      <div className="rewards-achievement-rewards">
                        {achievement.xp_reward > 0 && (
                          <span className="rewards-achievement-xp">+{achievement.xp_reward} XP</span>
                        )}
                        {achievement.special_reward && (
                          <div className="rewards-achievement-special locked">
                            <FiGift style={{ marginRight: '0.25rem' }} />
                            <span>{achievement.special_reward}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <FiLock className="rewards-achievement-lock" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div className="rewards-leaderboard-section">
          <div className="rewards-leaderboard-header">
            <h3 className="rewards-section-title">Top Creativos</h3>
            <div className="rewards-leaderboard-toggle">
              <button
                type="button"
                className={leaderboardScope === 'month' ? 'active' : ''}
                onClick={() => setLeaderboardScope('month')}
              >
                Este mes
              </button>
              <button
                type="button"
                className={leaderboardScope === 'all' ? 'active' : ''}
                onClick={() => setLeaderboardScope('all')}
              >
                Todo el tiempo
              </button>
            </div>
          </div>

          {leaderboard.current_user_rank != null && (
            <div className="rewards-your-rank">
              <span>Tu posición {leaderboardScope === 'month' ? 'este mes' : 'total'}:</span>
              <strong>#{leaderboard.current_user_rank}</strong>
            </div>
          )}

          <div className="rewards-leaderboard-list">
            {Array.isArray(leaderboard.leaderboard) && leaderboard.leaderboard.length === 0 && (
              <p className="rewards-leaderboard-empty">
                {leaderboardScope === 'month' ? 'Aún no hay XP este mes. ¡Sé el primero en sumar puntos!' : 'No hay datos de ranking todavía.'}
              </p>
            )}
            {leaderboard.leaderboard?.map((entry, idx) => (
              <div
                key={entry.user_id}
                className={`rewards-leaderboard-item ${entry.user_id === user?.id ? 'is-you' : ''}`}
              >
                <div className={`rewards-leaderboard-rank rank-${entry.rank}`}>
                  {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
                </div>
                <div className="rewards-leaderboard-avatar">
                  <img src={getAvatarUrl(entry.profile_image, entry.name)} alt={entry.name} />
                </div>
                <div className="rewards-leaderboard-info">
                  <span className="rewards-leaderboard-name">
                    {entry.name}
                    {entry.user_id === user?.id && <span className="you-badge">Tú</span>}
                  </span>
                  <span className="rewards-leaderboard-level" style={{ color: entry.level.badge_color }}>
                    Nivel {entry.level.level} · {entry.level.name}
                  </span>
                </div>
                <div className="rewards-leaderboard-xp">
                  <strong>{entry.total_xp.toLocaleString()}</strong>
                  <span>XP</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Utilidades compartidas entre FeaturedCreatives y PublicProfilePage
const getInitials = (name) =>
  (name || '').split(' ').map(n => n[0]).join('').toUpperCase();

const CIUDADES_ESPANA = [
  'A Coruña','Albacete','Alcalá de Henares','Alcobendas','Alcorcón','Algeciras','Alicante',
  'Almería','Badajoz','Badalona','Barcelona','Bilbao','Burgos','Cáceres','Cádiz','Cartagena',
  'Castellón de la Plana','Córdoba','Cuenca','Elche','Fuenlabrada','Gijón','Girona','Granada',
  'Guadalajara','Huelva','Huesca','Jaén','Jerez de la Frontera','L\'Hospitalet de Llobregat',
  'La Laguna','Las Palmas de Gran Canaria','Leganés','León','Lérida','Logroño','Lugo','Madrid',
  'Málaga','Marbella','Mataró','Mérida','Murcia','Oviedo','Palencia','Palma','Pamplona',
  'Parla','Las Rozas','Sabadell','Salamanca','San Cristóbal de La Laguna','San Sebastián',
  'Santa Cruz de Tenerife','Santander','Santiago de Compostela','Segovia','Sevilla','Soria',
  'Talavera de la Reina','Tarragona','Terrassa','Toledo','Torrejón de Ardoz','Valencia',
  'Valladolid','Vigo','Vitoria-Gasteiz','Zamora','Zaragoza'
].sort();

const getRandomGradient = (id) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
  ];
  return gradients[id % gradients.length];
};

// FOLLOW BUTTON - reutilizable en modal y perfil público
// ==========================================
const FollowButton = ({ targetId, initialCount, onCountChange }) => {
  const { token, user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(initialCount || 0);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!token || !targetId || !user) return;
    axios.get(`${API}/follow/${targetId}/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setFollowing(res.data.following); setCount(res.data.followers_count); setChecked(true); })
      .catch(() => setChecked(true));
  }, [targetId, token, user]);

  const handleToggle = async () => {
    if (!token || loading) return;
    setLoading(true);
    try {
      let res;
      if (following) {
        res = await axios.delete(`${API}/follow/${targetId}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        res = await axios.post(`${API}/follow/${targetId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
      setFollowing(res.data.following);
      setCount(res.data.followers_count);
      if (onCountChange) onCountChange(res.data.followers_count, res.data.following);
    } catch (err) {
      console.error('Follow error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !checked) return null;

  return (
    <button
      type="button"
      className={`follow-btn${following ? ' following' : ''}`}
      onClick={handleToggle}
      disabled={loading}
      aria-label={following ? 'Dejar de seguir' : 'Seguir'}
    >
      {following ? <FiUserCheck aria-hidden="true" /> : <FiUserPlus aria-hidden="true" />}
      {following ? 'Siguiendo' : 'Seguir'}
      {count > 0 && <span className="follow-btn-count">{count >= 1000 ? `${(count / 1000).toFixed(1)}K` : count}</span>}
    </button>
  );
};

// ==========================================
// FEATURED AGENCY PAGE - /agencia-destacada
// Reutiliza PublicProfilePage con isFeaturedAgency=true
// ==========================================
const FeaturedAgencyPage = ({ onOpenDirectChat, onViewProfile, onMarkFeaturedAgencySeen, onCreateBrief }) => {
  const { user } = useAuth();
  const [featuredUserId, setFeaturedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noAgency, setNoAgency] = useState(false);
  const api = API;

  useEffect(() => {
    setLoading(true);
    setNoAgency(false);
    setFeaturedUserId(null);
    axios.get(`${api}/featured-agency`)
      .then(res => {
        const userId = res.data?.user_id;
        if (!userId) {
          setNoAgency(true);
        } else {
          setFeaturedUserId(userId);
        }
      })
      .catch(() => { setNoAgency(true); })
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="featured-agency-page featured-agency-page--loading">
        <FiLoader className="spin" aria-hidden="true" />
      </div>
    );
  }

  if (noAgency || !featuredUserId) {
    const canCreateBrief = user?.role === 'brand' || user?.role === 'creator' || user?.role === 'admin';
    return (
      <div className="featured-agency-page featured-agency-page--empty">
        <div className="featured-agency-empty-inner">
          <FiAward className="featured-agency-empty-icon" aria-hidden="true" />
          <h2 className="featured-agency-empty-title">Agencia Destacada</h2>
          <p className="featured-agency-empty-text">
            ¿Quieres ser la agencia destacada? Publica un brief para participar. La primera agencia que publique un brief será la destacada.
          </p>
          {canCreateBrief && (
            <button
              type="button"
              className="btn-primary featured-agency-empty-cta"
              onClick={() => onCreateBrief && onCreateBrief()}
              disabled
            >
              Publicar un proyecto
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <PublicProfilePage
      userId={featuredUserId}
      isFeaturedAgency
      onOpenDirectChat={onOpenDirectChat}
      onProfileLoaded={(p) => {
        if (p?.id && onMarkFeaturedAgencySeen) onMarkFeaturedAgencySeen(p.id);
      }}
    />
  );
};

// ==========================================
// ── Feed Post Card (con comentarios) ──
const FeedPostCard = ({ post, user, token, onToggleLike, onDelete, uploadBase }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [localCommentsCount, setLocalCommentsCount] = useState(post.comments_count || 0);

  const postAvatar = post.author_profile_image
    ? (post.author_profile_image.startsWith('http') ? post.author_profile_image : `${uploadBase}${post.author_profile_image}`)
    : null;
  const postImage = post.image_url
    ? (post.image_url.startsWith('http') ? post.image_url : `${uploadBase}${post.image_url}`)
    : null;
  const isLiked = Array.isArray(post.likes) && user && post.likes.includes(user.id);
  const isOwner = user && user.id === post.author_id;

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const api = API;
      const res = await axios.get(`${api}/posts/${post.id}/comments`);
      setComments(res.data.comments || []);
      setLocalCommentsCount(res.data.count || 0);
      setCommentsLoaded(true);
    } catch { /* silently */ }
    finally { setCommentsLoading(false); }
  }, [post.id]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoaded) fetchComments();
  };

  const handleSubmitComment = async () => {
    const text = commentText.trim();
    if (!text || commentSubmitting || !token) return;
    setCommentSubmitting(true);
    try {
      const api = API;
      const fd = new FormData();
      fd.append('content', text);
      const res = await axios.post(`${api}/posts/${post.id}/comments`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setComments(prev => [...prev, res.data]);
      setLocalCommentsCount(prev => prev + 1);
      setCommentText('');
    } catch { /* silently */ }
    finally { setCommentSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const api = API;
      await axios.delete(`${api}/posts/${post.id}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments(prev => prev.filter(c => c.id !== commentId));
      setLocalCommentsCount(prev => Math.max(0, prev - 1));
    } catch { /* silently */ }
  };

  return (
    <div className="pp-feed-card">
      <div className="pp-feed-card-header">
        <div className="pp-feed-card-avatar">
          {postAvatar ? <img src={postAvatar} alt="" onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} /> : <FiUser />}
        </div>
        <div className="pp-feed-card-meta">
          <span className="pp-feed-card-author">{post.author_name}</span>
          <span className="pp-feed-card-date">
            {new Date(post.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        </div>
        {isOwner && (
          <button className="pp-feed-card-delete" title="Eliminar publicación" onClick={() => onDelete(post.id)}>
            <FiTrash2 />
          </button>
        )}
      </div>
      <div className="pp-feed-card-content">
        {post.content.split('\n').map((line, i) => (
          <React.Fragment key={i}>{line}<br /></React.Fragment>
        ))}
      </div>
      {postImage && (
        <div className="pp-feed-card-image">
          <img src={postImage} alt="" loading="lazy" />
        </div>
      )}
      <div className="pp-feed-card-actions">
        <button
          className={`pp-feed-like-btn${isLiked ? ' pp-feed-like-btn--active' : ''}`}
          onClick={() => onToggleLike(post.id)}
          disabled={!user}
        >
          <FiHeart /> {post.likes_count || 0}
        </button>
        <button className="pp-feed-comment-btn" onClick={handleToggleComments}>
          <FiMessageCircle /> {localCommentsCount || 0}
        </button>
      </div>

      {/* Sección de comentarios expandible */}
      {showComments && (
        <div className="pp-feed-comments">
          {commentsLoading && !commentsLoaded ? (
            <div className="pp-feed-comments-loading"><FiLoader className="spin" /></div>
          ) : (
            <>
              {comments.length === 0 && (
                <p className="pp-feed-comments-empty">Sé el primero en comentar.</p>
              )}
              {comments.map(c => {
                const cAvatar = c.author_profile_image
                  ? (c.author_profile_image.startsWith('http') ? c.author_profile_image : `${uploadBase}${c.author_profile_image}`)
                  : null;
                const canDelete = user && (user.id === c.author_id || isOwner);
                return (
                  <div key={c.id} className="pp-feed-comment">
                    <div className="pp-feed-comment-avatar">
                      {cAvatar ? <img src={cAvatar} alt="" onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} /> : <FiUser />}
                    </div>
                    <div className="pp-feed-comment-body">
                      <div className="pp-feed-comment-bubble">
                        <span className="pp-feed-comment-author">{c.author_name}</span>
                        <span className="pp-feed-comment-text">{c.content}</span>
                      </div>
                      <div className="pp-feed-comment-meta">
                        <span className="pp-feed-comment-date">
                          {new Date(c.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </span>
                        {canDelete && (
                          <button className="pp-feed-comment-delete" onClick={() => handleDeleteComment(c.id)}>Eliminar</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Formulario escribir comentario */}
              {user ? (
                <div className="pp-feed-comment-form">
                  <div className="pp-feed-comment-form-avatar">
                    {user.profile_image ? (
                      <img src={user.profile_image.startsWith('http') ? user.profile_image : `${uploadBase}${user.profile_image}`} alt="" onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} />
                    ) : (
                      <FiUser />
                    )}
                  </div>
                  <input
                    className="pp-feed-comment-input"
                    type="text"
                    placeholder="Escribe un comentario..."
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    maxLength={2000}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
                  />
                  <button
                    className="pp-feed-comment-send"
                    disabled={!commentText.trim() || commentSubmitting}
                    onClick={handleSubmitComment}
                  >
                    {commentSubmitting ? <FiLoader className="spin" /> : <FiSend />}
                  </button>
                </div>
              ) : (
                <p className="pp-feed-comments-login">Inicia sesión para comentar.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// PUBLIC PROFILE PAGE - /p/:userId
// ==========================================
const UPLOAD_BASE_PUBLIC = UPLOAD_BASE;

function formatEarnings(eur) {
  if (eur == null) return null;
  if (eur >= 1000000) return `${(eur / 1000000).toFixed(1)}M€`;
  if (eur >= 1000) return `${Math.round(eur / 1000)}k€`;
  return `${Math.round(eur)}€`;
}

function formatFollowers(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const PublicProfilePage = ({ userId, onBack, onOpenDirectChat, onProfileLoaded, isFeaturedAgency = false }) => {
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [ppTab, setPpTab] = useState('about');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Feed state
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedCursor, setFeedCursor] = useState(null);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedInitial, setFeedInitial] = useState(true);
  const feedSentinelRef = useRef(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState(null);
  const [newPostSubmitting, setNewPostSubmitting] = useState(false);
  const newPostFileRef = useRef(null);
  const [portfolioPreview, setPortfolioPreview] = useState(null);

  useEffect(() => {
    if (!portfolioPreview) return;
    const onKey = (e) => { if (e.key === 'Escape') setPortfolioPreview(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [portfolioPreview]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setProfile(null);
    setPpTab('about');
    setReviews([]);
    setFeedPosts([]);
    setFeedCursor(null);
    setFeedHasMore(true);
    setFeedInitial(true);
    if (!userId) { setNotFound(true); setLoading(false); return; }
    const api = API;
    axios.get(`${api}/public/profile/${userId}`)
      .then(res => {
        setProfile(res.data);
        setLoading(false);
        if (onProfileLoaded && res.data) onProfileLoaded(res.data);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [userId]);

  // Si el perfil tiene @username y la URL actual es por UUID, reemplazar por /p/username para que se vea y comparta la URL bonita
  useEffect(() => {
    if (!profile?.username) return;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const prefix = '/p/';
    if (!path.startsWith(prefix)) return;
    const slug = path.slice(prefix.length).split('/')[0];
    if (slug && slug !== profile.username && slug === profile.id) {
      const newPath = `${prefix}${profile.username}${path.slice(prefix.length + slug.length)}`;
      window.history.replaceState(null, '', newPath);
    }
  }, [profile?.id, profile?.username]);

  useEffect(() => {
    if (!profile?.id) return;
    setReviewsLoading(true);
    const api = API;
    axios.get(`${api}/ratings/${profile.id}`)
      .then(res => setReviews(Array.isArray(res.data) ? res.data : []))
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false));
  }, [profile?.id]);

  // ── Feed: cargar posts con paginación cursor ──
  const loadFeedPosts = useCallback(async (cursorVal) => {
    if (!profile?.id) return;
    setFeedLoading(true);
    try {
      const api = API;
      const params = { limit: 10 };
      if (cursorVal) params.cursor = cursorVal;
      const res = await axios.get(`${api}/posts/user/${profile.id}`, { params });
      const data = res.data;
      setFeedPosts(prev => cursorVal ? [...prev, ...data.posts] : data.posts);
      setFeedCursor(data.next_cursor);
      setFeedHasMore(!!data.next_cursor);
    } catch {
      setFeedHasMore(false);
    } finally {
      setFeedLoading(false);
      setFeedInitial(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) loadFeedPosts(null);
  }, [profile?.id, loadFeedPosts]);

  // Infinite scroll observer
  useEffect(() => {
    if (!feedHasMore || feedLoading) return;
    const sentinel = feedSentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && feedHasMore && !feedLoading) {
        loadFeedPosts(feedCursor);
      }
    }, { threshold: 0.1 });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [feedHasMore, feedLoading, feedCursor, loadFeedPosts]);

  // Crear post
  const handleCreatePost = async () => {
    const text = newPostContent.trim();
    if (!text || newPostSubmitting) return;
    setNewPostSubmitting(true);
    try {
      const api = API;
      const fd = new FormData();
      fd.append('content', text);
      if (newPostImage) fd.append('image', newPostImage);
      await axios.post(`${api}/posts`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setNewPostContent('');
      setNewPostImage(null);
      setNewPostImagePreview(null);
      setFeedPosts([]);
      setFeedCursor(null);
      setFeedHasMore(true);
      loadFeedPosts(null);
    } catch { /* silently */ }
    finally { setNewPostSubmitting(false); }
  };

  // Toggle like
  const handleToggleLike = async (postId) => {
    if (!token) return;
    try {
      const api = API;
      const res = await axios.post(`${api}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedPosts(prev => prev.map(p => p.id === postId ? { ...p, liked: res.data.liked, likes_count: res.data.likes_count, likes: res.data.liked ? [...(p.likes || []), user?.id] : (p.likes || []).filter(x => x !== user?.id) } : p));
    } catch { /* silently */ }
  };

  // Borrar post
  const handleDeletePost = async (postId) => {
    if (!token) return;
    try {
      const api = API;
      await axios.delete(`${api}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedPosts(prev => prev.filter(p => p.id !== postId));
    } catch { /* silently */ }
  };

  if (loading) {
    return (
      <div className="pp-page pp-loading">
        <FiLoader className="spin" aria-hidden="true" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="pp-page pp-loading">
        <p style={{ color: 'var(--text-muted)' }}>Perfil no encontrado.</p>
      </div>
    );
  }

  const avatarUrl = profile.profile_image
    ? (profile.profile_image.startsWith('http') ? profile.profile_image : `${UPLOAD_BASE_PUBLIC}${profile.profile_image}`)
    : null;
  const isElite = profile.season_level === 5;
  const eliteColor = profile.season_badge_color || '#ff5500';
  const isFounder = profile.founder;

  // Hero derecho: proyecto destacado (si existe) o primera imagen del portfolio
  const featuredProject = profile.featured_project;
  const heroFromFeatured = featuredProject
    ? (() => {
        const raw = (featuredProject.preview_image_url || '').trim();
        return raw ? (raw.startsWith('http') ? raw : `${UPLOAD_BASE_PUBLIC}${raw}`) : null;
      })()
    : null;
  const heroFromPortfolio = profile.portfolio_preview_urls?.[0]
    ? (profile.portfolio_preview_urls[0].startsWith('http') ? profile.portfolio_preview_urls[0] : `${UPLOAD_BASE_PUBLIC}${profile.portfolio_preview_urls[0]}`)
    : null;
  const heroUrl = heroFromFeatured || heroFromPortfolio;
  const heroTitle = featuredProject?.title || null;
  const restPortfolio = (profile.portfolio_preview_urls || []).slice(1);

  const isBrand = profile.role === 'brand';
  const companyTypeLabel = (t) => ({ marca: 'Marca', agencia: 'Agencia', startup: 'Startup', corporacion: 'Corporación', otro: 'Otro' })[(t || '').toLowerCase()] || t;
  const companyHighlightParts = isBrand ? [
    profile.company_type && companyTypeLabel(profile.company_type),
    profile.industry && profile.industry.trim(),
    profile.company_size && profile.company_size.trim()
  ].filter(Boolean) : [];

  const metrics = [
    profile.total_earnings_eur != null && profile.total_earnings_eur > 0 && { label: 'Ganado', value: formatEarnings(profile.total_earnings_eur) },
    profile.completed_projects > 0 && { label: 'Contratado', value: `${profile.completed_projects}x` },
    profile.average_rating > 0 && { label: 'Valoración', value: `★ ${profile.average_rating.toFixed(2)}` },
    { label: 'Seguidores', value: formatFollowers(profile.followers_count) },
  ].filter(Boolean);

  return (
    <div className={`pp-page${isFounder ? ' pp-page--founder' : ''}${isFeaturedAgency ? ' pp-page--featured-agency' : ''}`}>
      <div className="pp-page-inner">
      {isFeaturedAgency && (
        <div className="featured-agency-ribbon" aria-hidden="true">
          <FiAward aria-hidden="true" /> Agencia destacada por Vissory
        </div>
      )}
      {isFounder && (
        <div className="pp-founder-accent pp-founder-accent--tl" aria-hidden="true">✦</div>
      )}
      {isFounder && (
        <div className="pp-founder-accent pp-founder-accent--br" aria-hidden="true">✦</div>
      )}

      {/* ── HERO: dos columnas ── */}
      <div className="pp-hero">

        {/* Columna izquierda */}
        <div className="pp-hero-left">

          {/* Avatar + nombre + tagline en fila */}
          <div className="pp-avatar-row">
            <div
              className={`pp-avatar${isFounder ? ' pp-avatar--founder' : ''}${isElite ? ' pp-avatar--elite' : ''}${avatarUrl ? ' pp-avatar--clickable' : ''}`}
              style={{ background: getRandomGradient(profile.id), ...(isElite ? { '--elite-color': eliteColor } : {}) }}
              role={avatarUrl ? 'button' : undefined}
              tabIndex={avatarUrl ? 0 : undefined}
              aria-label={avatarUrl ? `Ampliar foto de perfil de ${profile.name}` : undefined}
              onClick={avatarUrl ? () => setPortfolioPreview({ url: avatarUrl, name: profile.name }) : undefined}
              onKeyDown={avatarUrl ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPortfolioPreview({ url: avatarUrl, name: profile.name });
                }
              } : undefined}
            >
              {avatarUrl ? <img src={avatarUrl} alt={profile.name} onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} /> : <FiUser className="pp-avatar-icon" />}
              {profile.verified && <div className="pp-avatar-verified"><FiCheck aria-hidden="true" /></div>}
            </div>
            <div className="pp-avatar-info">
              {profile.username && <span className="pp-username-chip">@{profile.username}</span>}
              <h1 className="pp-name">{profile.name}</h1>
              {(() => {
                if (isBrand && companyHighlightParts.length > 0) {
                  return (
                    <p className="pp-company-highlight">
                      {companyHighlightParts.join(' · ')}
                    </p>
                  );
                }
                const tagParts = [
                  (profile.specialties && profile.specialties[0]) || (profile.work_areas && profile.work_areas.trim()) || null,
                  profile.location && profile.location.trim() || null,
                  profile.experience_years != null && profile.experience_years > 0 ? `${profile.experience_years} año${profile.experience_years !== 1 ? 's' : ''} de experiencia` : null
                ].filter(Boolean);
                if (tagParts.length === 0) return null;
                return <p className="pp-tagline">{tagParts.join(' · ')}</p>;
              })()}
            </div>
          </div>

          {/* Botones */}
          <div className="pp-actions">
            {user && user.id !== profile.id && token ? (
              <>
                <button
                  type="button"
                  className="pp-btn-contact"
                  onClick={async () => {
                    try {
                      const api = API;
                      const res = await axios.post(`${api}/conversations`, { other_user_id: profile.id }, { headers: { Authorization: `Bearer ${token}` } });
                      if (onOpenDirectChat) onOpenDirectChat(res.data.id);
                    } catch (err) { console.error(err); }
                  }}
                >
                  <FiZap aria-hidden="true" /> Contactar
                </button>
                <FollowButton
                  targetId={profile.id}
                  initialCount={profile.followers_count}
                  onCountChange={(newCount) => setProfile(p => ({ ...p, followers_count: newCount }))}
                />
                {profile.role === 'creative' && (
                  <AddContactButton
                    targetUserId={profile.id}
                    currentUser={user}
                    token={token}
                  />
                )}
              </>
            ) : !user ? (
              <a href="/" className="pp-btn-contact"><FiZap aria-hidden="true" /> Contactar</a>
            ) : null}
          </div>

          {/* Métricas */}
          {metrics.length > 0 && (
            <div className="pp-metrics">
              {metrics.map((m, i) => (
                <div key={i} className="pp-metric">
                  <span className="pp-metric-value">{m.value}</span>
                  <span className="pp-metric-label">{m.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Badges */}
          <div className="pp-badges">
            {isFeaturedAgency && (
              <span className="pp-badge pp-badge--featured">
                <FiAward aria-hidden="true" /> Agencia Destacada
              </span>
            )}
            {isFounder && (
              <span className="pp-badge pp-badge--founder">
                <FiStar aria-hidden="true" /> Fundador
              </span>
            )}
            {profile.season_level_name && (
              <span
                className={`pp-badge${isElite ? ' pp-badge--elite' : ' pp-badge--level'}`}
                style={isElite ? { '--elite-color': eliteColor } : { '--level-color': profile.season_badge_color || 'var(--primary-orange)' }}
              >
                {isElite && <FiZap aria-hidden="true" />}
                {profile.season_level_name}
              </span>
            )}
            {profile.specialties?.slice(0, 2).map((s, i) => (
              <span key={i} className="pp-badge pp-badge--skill">{s}</span>
            ))}
          </div>

          {/* Meta: ubicación, redes, web */}
          <div className="pp-meta">
            {profile.location && (
              <span className="pp-meta-item"><FiMapPin aria-hidden="true" />{profile.location}</span>
            )}
            {profile.instagram_url && (
              <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="Instagram">
                <FaInstagram aria-hidden="true" /> Instagram
              </a>
            )}
            {profile.twitter_url && (
              <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="X (Twitter)">
                <FaTwitter aria-hidden="true" /> X
              </a>
            )}
            {profile.facebook_url && (
              <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="Facebook">
                <FaFacebookF aria-hidden="true" /> Facebook
              </a>
            )}
            {profile.behance_url && (
              <a href={profile.behance_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="Behance">
                <FaBehance aria-hidden="true" /> Behance
              </a>
            )}
            {profile.linkedin_url && (
              <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="LinkedIn">
                <FaLinkedin aria-hidden="true" /> LinkedIn
              </a>
            )}
            {profile.hourly_rate != null && (
              <span className="pp-meta-item"><FiCreditCard aria-hidden="true" />{profile.hourly_rate}€/h</span>
            )}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link" aria-label="Web">
                <FiLink aria-hidden="true" />{profile.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {profile.languages?.length > 0 && (
              <span className="pp-meta-item"><FiGlobe aria-hidden="true" />{profile.languages.join(', ')}</span>
            )}
          </div>
        </div>

        {/* Columna derecha — proyecto destacado o imagen de portfolio */}
        {(heroUrl || featuredProject || (isFeaturedAgency)) && (
          <div className="pp-hero-right">
            <div className={`pp-hero-img${isFeaturedAgency ? ' featured-agency-hero-img' : ''}`}>
              {heroUrl ? (
                <>
                  <img src={heroUrl} alt={heroTitle || 'Proyecto destacado'} loading="lazy" />
                  {isFeaturedAgency && featuredProject && <span className="featured-agency-hero-badge">Destacado</span>}
                </>
              ) : (
                <div className={`pp-hero-placeholder${isFeaturedAgency ? ' featured-agency-hero-placeholder' : ''}`}>
                  <FiFolder aria-hidden="true" />
                  {isFeaturedAgency && !featuredProject && <span className="featured-agency-hero-placeholder-text">Sin proyecto destacado</span>}
                </div>
              )}
              {heroTitle && <span className="pp-hero-img-title">{heroTitle}</span>}
            </div>
          </div>
        )}
      </div>

      {/* ── TABS BAR ── */}
      <div className="pp-tabs-bar">
        <span
          className={`pp-tab${ppTab === 'about' ? ' pp-tab--active' : ''}`}
          onClick={() => setPpTab('about')}
        >
          {isBrand ? 'Sobre la empresa' : 'Sobre mí'}
        </span>
        {profile.completed_projects_preview?.length > 0 && (
          <span
            className={`pp-tab${ppTab === 'trabajos' ? ' pp-tab--active' : ''}`}
            onClick={() => setPpTab('trabajos')}
          >
            Trabajos
          </span>
        )}
        {reviews.length > 0 && (
          <span
            className={`pp-tab${ppTab === 'resenas' ? ' pp-tab--active' : ''}`}
            onClick={() => setPpTab('resenas')}
          >
            Reseñas
            <span className="pp-tab-count">{reviews.length}</span>
          </span>
        )}
        <span
          className="pp-tab pp-tab--disabled"
          aria-disabled="true"
        >
          Feed <span className="pp-tab-proximamente">Próximamente</span>
        </span>
      </div>

      {/* ── TAB: FEED ── */}
      {ppTab === 'feed' && (
        <div className="pp-feed">
          {/* Formulario crear post (solo si es tu perfil) */}
          {user && user.id === profile.id && (
            <div className="pp-feed-composer">
              <div className="pp-feed-composer-avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" onError={e => { e.target.style.display = 'none'; e.target.parentNode.classList.add('pp-avatar-fallback'); }} />
                ) : (
                  <FiUser />
                )}
              </div>
              <div className="pp-feed-composer-body">
                <textarea
                  className="pp-feed-composer-input"
                  placeholder="Comparte algo con tu comunidad..."
                  value={newPostContent}
                  onChange={e => setNewPostContent(e.target.value)}
                  maxLength={5000}
                  rows={3}
                />
                {newPostImagePreview && (
                  <div className="pp-feed-composer-img-preview">
                    <img src={newPostImagePreview} alt="Preview" />
                    <button type="button" className="pp-feed-composer-img-remove" onClick={() => { setNewPostImage(null); setNewPostImagePreview(null); }}>
                      <FiX />
                    </button>
                  </div>
                )}
                <div className="pp-feed-composer-actions">
                  <button type="button" className="pp-feed-composer-img-btn" onClick={() => newPostFileRef.current?.click()}>
                    <FiImage /> Imagen
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={newPostFileRef}
                    style={{ display: 'none' }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setNewPostImage(f);
                        setNewPostImagePreview(URL.createObjectURL(f));
                      }
                    }}
                  />
                  <span className="pp-feed-composer-chars">{newPostContent.length}/5000</span>
                  <button
                    type="button"
                    className="pp-feed-composer-submit"
                    disabled={!newPostContent.trim() || newPostSubmitting}
                    onClick={handleCreatePost}
                  >
                    {newPostSubmitting ? <FiLoader className="spin" /> : <><FiSend /> Publicar</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Posts */}
          {feedInitial && feedLoading ? (
            <div className="pp-tab-empty"><FiLoader className="spin" /></div>
          ) : feedPosts.length === 0 ? (
            <div className="pp-tab-empty">
              <FiEdit3 aria-hidden="true" />
              <p>Aún no hay publicaciones.</p>
            </div>
          ) : (
            <>
              {feedPosts.map(post => (
                <FeedPostCard
                  key={post.id}
                  post={post}
                  user={user}
                  token={token}
                  onToggleLike={handleToggleLike}
                  onDelete={handleDeletePost}
                  uploadBase={UPLOAD_BASE_PUBLIC}
                />
              ))}
              {feedHasMore && <div ref={feedSentinelRef} className="pp-feed-sentinel">{feedLoading && <FiLoader className="spin" />}</div>}
            </>
          )}
        </div>
      )}

      {/* ── TAB: TRABAJOS ── (solo proyectos completados que el creativo ha elegido mostrar en Mis proyectos) ── */}
      {ppTab === 'trabajos' && (
        <>
          {(profile.completed_projects_preview?.length > 0) ? (
            <div className="pp-portfolio-grid">
              {(profile.completed_projects_preview || []).map((p) => {
                const rawUrl = (p.preview_image_url || '').trim();
                const imgUrl = rawUrl
                  ? (rawUrl.startsWith('http') ? rawUrl : `${UPLOAD_BASE_PUBLIC}${rawUrl}`)
                  : null;
                return (
                  <div key={p.id} className="pp-portfolio-item pp-portfolio-item--completed">
                    {imgUrl ? (
                      <img src={imgUrl} alt={p.title || 'Proyecto completado'} loading="lazy" />
                    ) : (
                      <div className="pp-portfolio-item-placeholder">
                        <FiFolder aria-hidden="true" />
                      </div>
                    )}
                    {p.title && <span className="pp-portfolio-item-title">{p.title}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pp-tab-empty">
              <FiFileText aria-hidden="true" />
              <p>Aún no hay trabajos publicados.</p>
            </div>
          )}
        </>
      )}

      {/* ── TAB: RESEÑAS ── */}
      {ppTab === 'resenas' && (
        <div className="pp-reviews">
          {reviewsLoading ? (
            <div className="pp-tab-empty"><FiLoader className="spin" /></div>
          ) : reviews.length === 0 ? (
            <div className="pp-tab-empty">
              <FiStar aria-hidden="true" />
              <p>Aún no hay reseñas.</p>
            </div>
          ) : (
            reviews.map((r, i) => (
              <div key={r.id || i} className="pp-review-card">
                <div className="pp-review-header">
                  <div className="pp-review-avatar">
                    <FiUser />
                  </div>
                  <div className="pp-review-meta">
                    <span className="pp-review-author">{r.rater_name}</span>
                    <span className="pp-review-project">{r.project_title}</span>
                  </div>
                  <div className="pp-review-stars">
                    {[1,2,3,4,5].map(s => (
                      <span key={s} className={`pp-review-star${s <= r.rating ? ' filled' : ''}`}>★</span>
                    ))}
                    <span className="pp-review-score">{r.rating.toFixed(1)}</span>
                  </div>
                </div>
                {r.review && <p className="pp-review-text">{r.review}</p>}
                {r.created_at && (
                  <span className="pp-review-date">
                    {new Date(r.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── TAB: SOBRE MÍ ── */}
      {ppTab === 'about' && (
        <div className="pp-about">
          {isBrand && (profile.company_type || profile.industry || profile.company_size || profile.founded_year) && (
            <div className="pp-about-section">
              <h3 className="pp-section-title">
                <FiBriefcase aria-hidden="true" />
                Sobre la empresa
              </h3>
              <dl className="pp-company-details">
                {profile.company_type && (
                  <div className="pp-company-row">
                    <dt>Tipo</dt>
                    <dd>{companyTypeLabel(profile.company_type)}</dd>
                  </div>
                )}
                {profile.industry && (
                  <div className="pp-company-row">
                    <dt>Sector</dt>
                    <dd>{profile.industry}</dd>
                  </div>
                )}
                {profile.company_size && (
                  <div className="pp-company-row">
                    <dt>Tamaño</dt>
                    <dd>{profile.company_size === '200+' ? '200+ empleados' : `${profile.company_size} empleados`}</dd>
                  </div>
                )}
                {profile.founded_year && (
                  <div className="pp-company-row">
                    <dt>Fundación</dt>
                    <dd>{profile.founded_year}</dd>
                  </div>
                )}
                {profile.linkedin_url && (
                  <div className="pp-company-row">
                    <dt>LinkedIn</dt>
                    <dd>
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="pp-about-link">
                        Ver perfil de la empresa
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
          {(profile.instagram_url || profile.twitter_url || profile.facebook_url || profile.behance_url || profile.linkedin_url || profile.website) && (
            <div className="pp-about-section">
              <h3 className="pp-section-title">
                <FiLink aria-hidden="true" />
                Redes y enlaces
              </h3>
              <div className="pp-meta pp-meta--block">
                {profile.instagram_url && (
                  <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FaInstagram aria-hidden="true" /> Instagram
                  </a>
                )}
                {profile.twitter_url && (
                  <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FaTwitter aria-hidden="true" /> X (Twitter)
                  </a>
                )}
                {profile.facebook_url && (
                  <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FaFacebookF aria-hidden="true" /> Facebook
                  </a>
                )}
                {profile.behance_url && (
                  <a href={profile.behance_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FaBehance aria-hidden="true" /> Behance
                  </a>
                )}
                {profile.linkedin_url && (
                  <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FaLinkedin aria-hidden="true" /> LinkedIn
                  </a>
                )}
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noopener noreferrer" className="pp-meta-item pp-meta-link">
                    <FiLink aria-hidden="true" /> {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          )}
          {profile.bio && (
            <div className="pp-about-section">
              <h3 className="pp-section-title">Bio</h3>
              <p className="pp-about-text">{profile.bio}</p>
            </div>
          )}
          {profile.specialties?.length > 0 && (
            <div className="pp-about-section">
              <h3 className="pp-section-title">Especialidades</h3>
              <div className="pp-badges">
                {profile.specialties.map((s, i) => (
                  <span key={i} className="pp-badge pp-badge--skill">{s}</span>
                ))}
              </div>
            </div>
          )}
          {profile.languages?.length > 0 && (
            <div className="pp-about-section">
              <h3 className="pp-section-title">Idiomas</h3>
              <div className="pp-badges">
                {profile.languages.map((l, i) => (
                  <span key={i} className="pp-badge pp-badge--level">
                    <FiGlobe aria-hidden="true" style={{fontSize:'0.75rem'}} /> {l}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(() => {
            const portfolioItems = (profile.portfolio_preview_urls || []).filter(u => u && typeof u === 'string' && u.trim());
            const pdfItems = (profile.project_pdf_urls || []).filter(u => u && typeof u === 'string' && u.trim());
            const portfolioNames = profile.portfolio_preview_names || [];
            const pdfNames = profile.project_pdf_names || [];
            if (portfolioItems.length === 0 && pdfItems.length === 0) return null;
            return (
              <>
                {portfolioItems.length > 0 && (
                  <div className="pp-about-section">
                    <h3 className="pp-section-title">
                      <FiImage aria-hidden="true" />
                      Portafolio
                    </h3>
                    <div className="pp-about-portfolio-grid">
                      {portfolioItems.map((url, i) => {
                        const fullUrl = url.startsWith('http') ? url : `${UPLOAD_BASE_PUBLIC}${url}`;
                        const name = portfolioNames[i] || `Proyecto ${i + 1}`;
                        return (
                          <button
                            key={i}
                            type="button"
                            className="pp-about-portfolio-card"
                            onClick={() => setPortfolioPreview({ url: fullUrl, name })}
                          >
                            <img src={fullUrl} alt={name} loading="lazy" />
                            <span className="pp-about-portfolio-label">{name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {pdfItems.length > 0 && (
                  <div className="pp-about-section">
                    <h3 className="pp-section-title">
                      <FiFileText aria-hidden="true" />
                      Proyectos en PDF
                    </h3>
                    <div className="pp-about-pdf-grid">
                      {pdfItems.map((url, i) => {
                        const fullUrl = url.startsWith('http') ? url : `${UPLOAD_BASE_PUBLIC}${url}`;
                        const name = pdfNames[i] || `Documento ${i + 1}`;
                        return (
                          <a key={i} href={fullUrl} target="_blank" rel="noopener noreferrer" className="pp-about-pdf-card">
                            <div className="pp-about-pdf-icon"><FiFileText aria-hidden="true" /></div>
                            <span className="pp-about-pdf-label">{name}</span>
                            <span className="pp-about-pdf-action"><FiDownload aria-hidden="true" /> Ver PDF</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {!profile.bio && !profile.specialties?.length && !profile.languages?.length && !(profile.portfolio_preview_urls || []).some(u => u && u.trim()) && !(profile.project_pdf_urls || []).some(u => u && u.trim()) && !(isBrand && (profile.company_type || profile.industry || profile.company_size || profile.founded_year || profile.linkedin_url)) && !(profile.instagram_url || profile.twitter_url || profile.facebook_url || profile.behance_url || profile.linkedin_url || profile.website) && (
            <div className="pp-tab-empty">
              <p>Sin información adicional.</p>
            </div>
          )}
        </div>
      )}
      {portfolioPreview && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div
          className="modal-overlay pp-portfolio-preview-overlay"
          onClick={() => setPortfolioPreview(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa de imagen"
        >
          <div className="pp-portfolio-preview-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pp-portfolio-preview-close" onClick={() => setPortfolioPreview(null)} aria-label="Cerrar">
              <FiX aria-hidden="true" />
            </button>
            <img src={portfolioPreview.url} alt={portfolioPreview.name} className="pp-portfolio-preview-img" />
            {portfolioPreview.name && <p className="pp-portfolio-preview-caption">{portfolioPreview.name}</p>}
          </div>
        </div>,
        document.body
      )}
      </div>
    </div>
  );
};

const FeaturedCreatives = ({ onOpenDirectChat, onNavigateToRewards, onViewProfile, onNavigateToProfile }) => {
  const { user, token } = useAuth();
  const { getCached, setCached, invalidateCache } = useCache();
  const FEATURED_CREATIVES_CACHE_KEY = 'featured_creatives_v2'; // v2 para incluir username en cards
  const cachedCreatives = getCached(FEATURED_CREATIVES_CACHE_KEY);
  const [creatives, setCreatives] = useState(cachedCreatives || []);
  // Solo mostrar loading si no hay datos cacheados
  const [loading, setLoading] = useState(!cachedCreatives || cachedCreatives.length === 0);
  const [filter, setFilter] = useState('all');
  const [filterSkills, setFilterSkills] = useState([]);
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterExperienceLevel, setFilterExperienceLevel] = useState('');
  const [filterLanguages, setFilterLanguages] = useState([]);
  const [filterRateMax, setFilterRateMax] = useState('');
  const [filterMinReviews, setFilterMinReviews] = useState('');
  const [filterMinProjects, setFilterMinProjects] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [masonryCols, setMasonryCols] = useState(typeof window !== 'undefined' ? (window.innerWidth <= 700 ? 1 : window.innerWidth <= 1100 ? 2 : 3) : 3);
  const CREATIVES_PROFILE_MODAL_KEY = 'vissory_creatives_profile_modal_seen';
  const [showProfileIncompleteModal, setShowProfileIncompleteModal] = useState(false);

  // Cada vez que se entra en Creativos, invalidar caché para ver listado actualizado (nuevos creativos, etc.)
  useLayoutEffect(() => {
    invalidateCache(FEATURED_CREATIVES_CACHE_KEY);
  }, [invalidateCache]);

  const creativeProfileIncomplete = useMemo(() => {
    if (!user || user.role !== 'creative') return false;
    const hasPhoto = Boolean((user.profile_image || '').trim());
    const hasSpecialty = Array.isArray(user.specialties) && user.specialties.length >= 1;
    return !hasPhoto || !hasSpecialty;
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'creative' || !creativeProfileIncomplete) return;
    try {
      const seen = localStorage.getItem(`${CREATIVES_PROFILE_MODAL_KEY}_${user.id}`);
      if (!seen) setShowProfileIncompleteModal(true);
    } catch (_) {}
  }, [user?.id, user?.role, creativeProfileIncomplete]);

  const closeProfileIncompleteModal = useCallback((goToProfile = false) => {
    if (user?.id) {
      try { localStorage.setItem(`${CREATIVES_PROFILE_MODAL_KEY}_${user.id}`, '1'); } catch (_) {}
    }
    setShowProfileIncompleteModal(false);
    if (goToProfile && onNavigateToProfile) onNavigateToProfile();
  }, [user?.id, onNavigateToProfile]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showFilterPanel && filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterPanel]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setPreviewImageUrl(null);
        setPreviewPdf(null);
      }
    };
    if (previewImageUrl || previewPdf) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [previewImageUrl, previewPdf]);

  useEffect(() => {
    let cancelled = false;
    const fetchCreatives = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      // Siempre refetch al entrar: el caché solo sirve para mostrar datos iniciales mientras carga
      if (creatives.length === 0) {
        setLoading(true);
      }
      
      try {
        const res = await axios.get(`${API}/creatives`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled && Array.isArray(res.data)) {
          const mappedCreatives = res.data.map(mapCreativeToCard);
          setCreatives(mappedCreatives);
          setCached(FEATURED_CREATIVES_CACHE_KEY, mappedCreatives);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error cargando creativos:', err);
          setCreatives([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCreatives();
    return () => { cancelled = true; };
  }, [token]);

  const filterCategories = useMemo(() => {
    const specialtiesSet = new Set();
    const skillsSet = new Set();
    const locationsSet = new Set();
    const languagesSet = new Set();
    creatives.forEach(c => {
      if (c.specialty && c.specialty !== 'Creativo') specialtiesSet.add(c.specialty);
      (c.skills || []).forEach(s => skillsSet.add(s));
      if (c.location) locationsSet.add(c.location);
      (c.languages || []).forEach(lang => { if (lang && String(lang).trim()) languagesSet.add(String(lang).trim()); });
    });
    return {
      specialties: ['all', ...Array.from(specialtiesSet).sort()],
      skills: Array.from(skillsSet).sort(),
      locations: Array.from(locationsSet).sort(),
      languages: Array.from(languagesSet).sort(),
    };
  }, [creatives]);

  // Derivar nivel de experiencia desde experience_years (igual criterio que briefs: junior/mid/senior/expert)
  const getExperienceLevel = (years) => {
    if (years == null) return null;
    const y = Number(years);
    if (y <= 2) return 'junior';
    if (y <= 5) return 'mid';
    if (y <= 10) return 'senior';
    return 'expert';
  };

  const activeFilterCount =
    (filter !== 'all' ? 1 : 0) + filterSkills.length + (filterLocation !== 'all' ? 1 : 0) +
    (filterExperienceLevel ? 1 : 0) + filterLanguages.length + (filterRateMax !== '' && filterRateMax != null ? 1 : 0) +
    (filterMinReviews !== '' && filterMinReviews != null ? 1 : 0) + (filterMinProjects !== '' && filterMinProjects != null ? 1 : 0);

  const filteredCreatives = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    const rateMax = filterRateMax !== '' && filterRateMax != null ? parseFloat(filterRateMax) : null;
    const minReviews = filterMinReviews !== '' && filterMinReviews != null ? parseInt(filterMinReviews, 10) : null;
    const minProjects = filterMinProjects !== '' && filterMinProjects != null ? parseInt(filterMinProjects, 10) : null;
    const hashId = (id) => (id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);

    const isBareProfile = (creative) => {
      const hasPhoto = Boolean(creative?.avatar && String(creative.avatar).trim());
      const hasSpecialty = Boolean(creative?.specialty && String(creative.specialty).trim());
      const hasBio = Boolean(creative?.bio && String(creative.bio).trim());
      const hasSkills = Boolean(creative?.skills && creative.skills.some(s => s && String(s).trim()));
      const hasLocation = Boolean(creative?.location && String(creative.location).trim());
      const hasPortfolio = Boolean(Array.isArray(creative?.portfolio) && creative.portfolio.some(p => p && p.image && String(p.image).trim()));
      const hasPdfs = Boolean(Array.isArray(creative?.projectPdfs) && creative.projectPdfs.some(p => p && p.url && String(p.url).trim()));
      const hasRate = creative?.hourlyRate != null;
      const hasExperience = creative?.experience_years != null;
      const hasLanguages = Boolean(creative?.languages && creative.languages.some(l => l && String(l).trim()));
      return !(hasPhoto || hasSpecialty || hasBio || hasSkills || hasLocation || hasPortfolio || hasPdfs || hasRate || hasExperience || hasLanguages);
    };

    const profileCompletenessScore = (creative) => {
      // Ponderación simple: más señales => más arriba.
      // Importante: foto y especialidad valen más para evitar que un perfil vacío suba.
      const hasPhoto = Boolean(creative?.avatar && String(creative.avatar).trim());
      const hasSpecialty = Boolean(creative?.specialty && String(creative.specialty).trim());
      const hasBio = Boolean(creative?.bio && String(creative.bio).trim());
      const hasSkills = Boolean(creative?.skills && creative.skills.some(s => s && String(s).trim()));
      const hasLocation = Boolean(creative?.location && String(creative.location).trim());
      const hasPortfolio = Boolean(Array.isArray(creative?.portfolio) && creative.portfolio.some(p => p && p.image && String(p.image).trim()));
      const hasPdfs = Boolean(Array.isArray(creative?.projectPdfs) && creative.projectPdfs.some(p => p && p.url && String(p.url).trim()));
      const hasRate = creative?.hourlyRate != null;
      const hasExperience = creative?.experience_years != null;
      const hasLanguages = Boolean(creative?.languages && creative.languages.some(l => l && String(l).trim()));

      return (
        (hasPhoto ? 6 : 0) +
        (hasSpecialty ? 6 : 0) +
        (hasPortfolio ? 4 : 0) +
        (hasPdfs ? 2 : 0) +
        (hasBio ? 2 : 0) +
        (hasSkills ? 2 : 0) +
        (hasLocation ? 1 : 0) +
        (hasRate ? 1 : 0) +
        (hasExperience ? 1 : 0) +
        (hasLanguages ? 1 : 0)
      );
    };

    const portfolioGroupRank = (creative) => {
      const hasImagePreview = Array.isArray(creative?.portfolio) && creative.portfolio.length > 0 && creative.portfolio.some((p) => p && p.image && String(p.image).trim());
      if (hasImagePreview) return 0; // 1) con previews de imagen
      const hasPdf = Array.isArray(creative?.projectPdfs) && creative.projectPdfs.some((p) => p && p.url && String(p.url).trim());
      if (hasPdf) return 1; // 2) sin imagen pero con PDFs
      return 2; // 3) sin imagen ni PDFs
    };

    const filtered = creatives
      .map((creative, originalIndex) => ({ creative, originalIndex }))
      .filter(({ creative }) => {
        const matchesSpecialty = filter === 'all' || creative.specialty === filter;
        const matchesSkills = filterSkills.length === 0 ||
          (creative.skills && creative.skills.some(s => filterSkills.includes(s)));
        const matchesLocation = filterLocation === 'all' || creative.location === filterLocation;
        const creativeLevel = getExperienceLevel(creative.experience_years);
        const matchesExperience = !filterExperienceLevel || creativeLevel === filterExperienceLevel;
        const matchesLanguages = filterLanguages.length === 0 ||
          (creative.languages && creative.languages.some(l => filterLanguages.includes(String(l).trim())));
        const matchesRate = rateMax == null || creative.hourlyRate == null || creative.hourlyRate <= rateMax;
        const reviewCount = Number(creative.reviews) || 0;
        const projectCount = Number(creative.completedProjects) || 0;
        const matchesMinReviews = minReviews == null || Number.isNaN(minReviews) || reviewCount >= minReviews;
        const matchesMinProjects = minProjects == null || Number.isNaN(minProjects) || projectCount >= minProjects;
        const matchesSearch = !term ||
          creative.name.toLowerCase().includes(term) ||
          (creative.specialty && creative.specialty.toLowerCase().includes(term)) ||
          (creative.skills && creative.skills.some(skill => skill.toLowerCase().includes(term)));
        return matchesSpecialty && matchesSkills && matchesLocation && matchesExperience && matchesLanguages && matchesRate && matchesMinReviews && matchesMinProjects && matchesSearch;
      });

    filtered.sort((a, b) => {
      // 0) Perfiles totalmente vacíos SIEMPRE al final
      const aBare = isBareProfile(a.creative);
      const bBare = isBareProfile(b.creative);
      if (aBare !== bBare) return aBare ? 1 : -1;

      const r = portfolioGroupRank(a.creative) - portfolioGroupRank(b.creative);
      if (r !== 0) return r;

      // 1) Dentro del grupo, más completo primero
      const cs = profileCompletenessScore(b.creative) - profileCompletenessScore(a.creative);
      if (cs !== 0) return cs;

      // 2) Estable: mantener orden previo
      return a.originalIndex - b.originalIndex;
    });

    return filtered.map(({ creative }) => creative);
  }, [creatives, filter, filterSkills, filterLocation, filterExperienceLevel, filterLanguages, filterRateMax, filterMinReviews, filterMinProjects, searchTerm]);

  useEffect(() => {
    const update = () => setMasonryCols(window.innerWidth <= 700 ? 1 : window.innerWidth <= 1100 ? 2 : 3);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const masonryColumns = useMemo(() => {
    const cols = Array.from({ length: masonryCols }, () => []);
    filteredCreatives.forEach((c, i) => cols[i % masonryCols].push(c));
    return cols;
  }, [filteredCreatives, masonryCols]);

  const getPdfPreviewUrl = useCallback((rawUrl) => {
    const original = (rawUrl || '').trim();
    if (!original) return '';

    try {
      const parsed = new URL(original);
      const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

      // Google Drive: usar endpoint de preview embebible
      if (host.includes('drive.google.com')) {
        const idFromPath = original.match(/\/d\/([^/]+)/)?.[1];
        const idFromQuery = parsed.searchParams.get('id');
        const fileId = idFromPath || idFromQuery;
        if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
      }

      // Dropbox: forzar raw para que el visor pueda cargar el archivo
      if (host.includes('dropbox.com')) {
        if (parsed.searchParams.has('raw')) {
          parsed.searchParams.set('raw', '1');
        } else {
          parsed.searchParams.delete('dl');
          parsed.searchParams.set('raw', '1');
        }
        return parsed.toString();
      }
    } catch (_) {
      // Si URL no parsea, intentar fallback con visor
    }

    // Fallback genérico embebible para la mayoría de PDFs públicos
    return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(original)}`;
  }, []);

  if (loading) {
    return (
      <div className="featured-creatives">
        <LoadingSpinner size="large" text="Cargando creativos..." />
      </div>
    );
  }

  return (
    <div className="featured-creatives">
      {/* Modal: creativo con perfil incompleto (solo primera vez que entra en Creativos) */}
      {showProfileIncompleteModal && (
        <div
          className="modal-overlay modal-overlay--profile-incomplete"
          onClick={() => closeProfileIncompleteModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-incomplete-modal-title"
        >
          <div className="modal-content profile-incomplete-modal" onClick={e => e.stopPropagation()}>
            <div className="profile-incomplete-modal__icon" aria-hidden="true">
              <FiUser />
            </div>
            <div className="modal-header profile-incomplete-modal__header">
              <h3 id="profile-incomplete-modal-title" className="profile-incomplete-modal__title">Completa tu perfil para destacar en Creativos</h3>
              <button type="button" className="modal-close" onClick={() => closeProfileIncompleteModal(false)} aria-label="Cerrar"><FiX /></button>
            </div>
            <div className="modal-body profile-incomplete-modal__body">
              <p className="profile-incomplete-modal__lead">Tu perfil ya aparece en el listado, pero si completas estos datos los clientes confiarán más en ti:</p>
              <ul className="profile-incomplete-modal__checklist">
                <li><span className="profile-incomplete-modal__check">Foto de perfil</span></li>
                <li><span className="profile-incomplete-modal__check">Al menos una especialidad</span></li>
              </ul>
              <p className="profile-incomplete-modal__hint">Cuando los rellenes, se verán reflejados aquí automáticamente.</p>
              <div className="profile-incomplete-modal__actions">
                <button type="button" className="btn-primary profile-incomplete-modal__btn-primary" onClick={() => closeProfileIncompleteModal(true)}>
                  Ir a mi perfil
                </button>
                <button type="button" className="btn-secondary profile-incomplete-modal__btn-secondary" onClick={() => closeProfileIncompleteModal(false)}>
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header featured-header">
        <div className="featured-header-content">
          <div className="featured-title-section">
            <h2 className="featured-main-title">
              <FiStar className="featured-icon" aria-hidden="true" />
              Creativos
            </h2>
            <p className="featured-subtitle">
              Explora el talento creativo de la comunidad
            </p>
          </div>
        </div>
      </div>

      {/* Level Up Prompt for Creatives */}
      {user?.role === 'creative' && <LevelUpPrompt variant="featured-creatives" onNavigateToRewards={onNavigateToRewards} />}

      {/* Filters: búsqueda + botón que abre panel por categorías */}
      <div className="featured-filters">
        <div className="featured-filters-row">
          <div className="search-box">
            <FiSearch className="search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Buscar por nombre, especialidad o habilidad..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-trigger-wrap" ref={filterPanelRef}>
            <button
              type="button"
              className={`btn-filters-trigger ${showFilterPanel ? 'is-open' : ''} ${activeFilterCount > 0 ? 'has-active' : ''}`}
              onClick={() => setShowFilterPanel(prev => !prev)}
              aria-expanded={showFilterPanel}
              aria-haspopup="true"
            >
              <FiSettings aria-hidden="true" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="filter-count-badge">{activeFilterCount}</span>
              )}
            </button>
            {showFilterPanel && (
              <div className="filter-panel" role="dialog" aria-label="Filtros por categoría">
                <div className="filter-panel-section">
                  <h4 className="filter-panel-title">Especialidad</h4>
                  <select
                    className="filter-panel-select"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    aria-label="Especialidad"
                  >
                    {filterCategories.specialties.map(spec => (
                      <option key={spec} value={spec}>{spec === 'all' ? 'Todas' : spec}</option>
                    ))}
                  </select>
                </div>
                {filterCategories.locations.length > 0 && (
                  <div className="filter-panel-section">
                    <h4 className="filter-panel-title">Ubicación</h4>
                    <select
                      className="filter-panel-select"
                      value={filterLocation}
                      onChange={(e) => setFilterLocation(e.target.value)}
                      aria-label="Ubicación"
                    >
                      <option value="all">Todas</option>
                      {filterCategories.locations.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                )}
                {filterCategories.skills.length > 0 && (
                  <div className="filter-panel-section">
                    <h4 className="filter-panel-title">Habilidades</h4>
                    <select
                      className="filter-panel-select"
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && !filterSkills.includes(v)) setFilterSkills(prev => [...prev, v]);
                        e.target.value = '';
                      }}
                      aria-label="Añadir habilidad"
                    >
                      <option value="">— Añadir habilidad —</option>
                      {filterCategories.skills.filter(s => !filterSkills.includes(s)).map(skill => (
                        <option key={skill} value={skill}>{skill}</option>
                      ))}
                    </select>
                    {filterSkills.length > 0 && (
                      <div className="filter-panel-pills">
                        {filterSkills.map(skill => (
                          <span key={skill} className="filter-panel-pill">
                            {skill}
                            <button type="button" className="filter-panel-pill-remove" onClick={() => setFilterSkills(prev => prev.filter(s => s !== skill))} aria-label={`Quitar ${skill}`}><FiX /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="filter-panel-section">
                  <h4 className="filter-panel-title">Experiencia</h4>
                  <select
                    className="filter-panel-select"
                    value={filterExperienceLevel}
                    onChange={(e) => setFilterExperienceLevel(e.target.value)}
                    aria-label="Nivel de experiencia"
                  >
                    <option value="">Cualquier nivel</option>
                    <option value="junior">Junior (0-2 años)</option>
                    <option value="mid">Intermedio (3-5 años)</option>
                    <option value="senior">Senior (6-10 años)</option>
                    <option value="expert">Experto (11+ años)</option>
                  </select>
                </div>
                {filterCategories.languages.length > 0 && (
                  <div className="filter-panel-section">
                    <h4 className="filter-panel-title">Idiomas</h4>
                    <select
                      className="filter-panel-select"
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && !filterLanguages.includes(v)) setFilterLanguages(prev => [...prev, v]);
                        e.target.value = '';
                      }}
                      aria-label="Añadir idioma"
                    >
                      <option value="">— Añadir idioma —</option>
                      {filterCategories.languages.filter(l => !filterLanguages.includes(l)).map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                    {filterLanguages.length > 0 && (
                      <div className="filter-panel-pills">
                        {filterLanguages.map(lang => (
                          <span key={lang} className="filter-panel-pill">
                            {lang}
                            <button type="button" className="filter-panel-pill-remove" onClick={() => setFilterLanguages(prev => prev.filter(l => l !== lang))} aria-label={`Quitar ${lang}`}><FiX /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="filter-panel-section">
                  <h4 className="filter-panel-title">Tarifa máx. (€/h)</h4>
                  <input
                    type="number"
                    className="filter-panel-input filter-panel-input--rate"
                    placeholder="Ej. 40"
                    min="1"
                    step="1"
                    value={filterRateMax}
                    onChange={(e) => setFilterRateMax(e.target.value)}
                    aria-label="Tarifa máxima por hora en euros"
                  />
                </div>
                <div className="filter-panel-section">
                  <h4 className="filter-panel-title">Reseñas (mínimo)</h4>
                  <select
                    className="filter-panel-select"
                    value={filterMinReviews}
                    onChange={(e) => setFilterMinReviews(e.target.value)}
                    aria-label="Número mínimo de reseñas"
                  >
                    <option value="">Cualquiera</option>
                    <option value="1">Al menos 1 reseña</option>
                    <option value="3">3 o más</option>
                    <option value="5">5 o más</option>
                    <option value="10">10 o más</option>
                  </select>
                </div>
                <div className="filter-panel-section">
                  <h4 className="filter-panel-title">Proyectos completados (mínimo)</h4>
                  <select
                    className="filter-panel-select"
                    value={filterMinProjects}
                    onChange={(e) => setFilterMinProjects(e.target.value)}
                    aria-label="Número mínimo de proyectos completados"
                  >
                    <option value="">Cualquiera</option>
                    <option value="1">Al menos 1 proyecto</option>
                    <option value="3">3 o más</option>
                    <option value="5">5 o más</option>
                    <option value="10">10 o más</option>
                  </select>
                </div>
                {(filter !== 'all' || filterSkills.length > 0 || filterLocation !== 'all' || filterExperienceLevel || filterLanguages.length > 0 || (filterRateMax !== '' && filterRateMax != null) || (filterMinReviews !== '' && filterMinReviews != null) || (filterMinProjects !== '' && filterMinProjects != null)) && (
                  <button
                    type="button"
                    className="filter-panel-clear"
                    onClick={() => {
                      setFilter('all');
                      setFilterSkills([]);
                      setFilterLocation('all');
                      setFilterExperienceLevel('');
                      setFilterLanguages([]);
                      setFilterRateMax('');
                      setFilterMinReviews('');
                      setFilterMinProjects('');
                    }}
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Creatives Grid - Masonry bento para que cards con/sin preview tengan altura natural */}
      {filteredCreatives.length === 0 ? (
        <div className="creatives-grid creatives-grid--empty">
          <div className="empty-state">
            <FiSearch className="empty-icon" aria-hidden="true" />
            <h3>No se encontraron creativos</h3>
            <p>Intenta cambiar los filtros o el término de búsqueda</p>
          </div>
        </div>
      ) : (
        <div className="creatives-masonry creatives-masonry-columns">
          {masonryColumns.map((col, colIdx) => (
            <div key={colIdx} className="creatives-masonry-col">
              {col.map(creative => {
                const isElite = creative.season_level === 5;
                const eliteColor = creative.season_badge_color || '#ff5500';
                const card = (
            <motion.div
              key={creative.id}
              className={`creative-card ${isElite ? 'is-elite' : ''} ${creative.featured && !isElite ? 'is-featured' : ''} ${creative.founder && !isElite ? 'is-founder' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={isElite ? undefined : { y: -4 }}
              onClick={() => setSelectedCreative(creative)}
            >
              {isElite && (
                <div
                  className="creative-badge elite-badge"
                  style={{ '--elite-color': eliteColor }}
                >
                  <FiZap aria-hidden="true" />
                  Élite
                </div>
              )}
              {creative.featured && !isElite && (
                <div className="featured-badge">
                  <FiZap aria-hidden="true" />
                  Top Rated
                </div>
              )}

              <div className="creative-card-header">
                <div
                  className={`creative-avatar-large${creative.founder ? ' avatar-founder' : ''}`}
                  style={{ background: getRandomGradient(creative.id) }}
                >
                  <img
                    src={creative.avatar || DEFAULT_USER_AVATAR_DATA_URI}
                    alt={creative.name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                    }}
                  />
                  {creative.verified && (
                    <div className="verified-badge" title="Verificado">
                      <FiCheck aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="creative-header-info">
                  {creative.username && (
                    <span className="creative-username-chip">@{creative.username}</span>
                  )}
                  <div className="creative-name-row">
                    <h3 className="creative-name">{creative.name}</h3>
                    {creative.founder && (
                      <span className="founder-card-badge">
                        <FiStar aria-hidden="true" /> Fundador
                      </span>
                    )}
                  </div>
                  {creative.specialty && <p className="creative-specialty">{creative.specialty}</p>}
                  {(creative.season_level != null || creative.season_level_name) && (
                    <p className="creative-level-badge" style={creative.season_badge_color ? { '--level-color': creative.season_badge_color } : undefined}>
                      {creative.season_level_name || `Nivel ${creative.season_level}`}
                    </p>
                  )}
                  {creative.location && creative.location.trim() && (
                    <div className="creative-location">
                      <span>{creative.location.trim()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="creative-rating-row">
                <div className="rating-stars">
                  <FiStar className="star-filled" aria-hidden="true" />
                  <span className="rating-value">{creative.rating || '—'}</span>
                  <span className="rating-count">({creative.reviews} reseñas)</span>
                </div>
                {creative.hourlyRate != null && (
                  <div className="hourly-rate">
                    <span>{creative.hourlyRate}€/hr</span>
                  </div>
                )}
              </div>

              <p className="creative-bio">{creative.bio && creative.bio.trim() ? creative.bio.trim() : (() => {
                const placeholders = [
                  'Este creativo está en modo avión. Pronto despega.',
                  'Desbloqueando logro: completar perfil.',
                  'Nuevo personaje. Aún por equipar.',
                  'Tutorial completado. Siguiente misión: el perfil.',
                  'Un creativo ha aparecido. Pero su bio no.',
                  'Nuevo jugador se ha unido a la partida.',
                  'Próximamente: sus superpoderes',
                  'Ha entrado al servidor. Esperando primer movimiento.',
                  'Loading... creatividad al 99%. Solo falta el perfil.',
                  'Acaba de elegir su personaje. La partida empieza pronto.'
                ];
                const idx = (creative.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % placeholders.length;
                return placeholders[idx];
              })()}</p>

              {(() => {
                const skills = (creative.skills || []).filter(s => s && String(s).trim());
                if (skills.length > 0) {
                  return (
                    <div className="creative-skills">
                      {skills.slice(0, 4).map((skill, index) => (
                        <span key={index} className="skill-tag">{skill}</span>
                      ))}
                      {skills.length > 4 && (
                        <span className="skill-tag more">+{skills.length - 4}</span>
                      )}
                    </div>
                  );
                }
                const hasPhoto = Boolean(creative.avatar && String(creative.avatar).trim());
                const hasSpecialty = Boolean(creative.specialty && String(creative.specialty).trim());
                const hasBio = Boolean(creative.bio && String(creative.bio).trim());
                const hasLocation = Boolean(creative.location && String(creative.location).trim());
                const hasPortfolio = Boolean(creative.portfolio && creative.portfolio.some(p => p && p.image && String(p.image).trim()));
                const hasPdfs = Boolean(creative.projectPdfs && creative.projectPdfs.some(p => p && p.url && String(p.url).trim()));
                const hasRate = creative.hourlyRate != null;
                const hasExperience = creative.experience_years != null;
                const hasLanguages = Boolean(creative.languages && creative.languages.some(l => l && String(l).trim()));
                const bare = !(hasPhoto || hasSpecialty || hasBio || hasLocation || hasPortfolio || hasPdfs || hasRate || hasExperience || hasLanguages);
                if (bare) {
                  const specialtyPlaceholders = [
                    'Modo sigilo',
                    'Build en progreso',
                    'Clase por elegir',
                    'Sin equipar',
                    'Arma secreta',
                    'Poder por desbloquear',
                    'Stats: ???'
                  ];
                  const specialtyIdx = (creative.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % specialtyPlaceholders.length;
                  return (
                    <div className="creative-skills">
                      <span className="skill-tag">{specialtyPlaceholders[specialtyIdx]}</span>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="creative-stats-row">
                <div className="stat-item">
                  <FiFolder aria-hidden="true" />
                  <span>{creative.completedProjects} proyectos</span>
                </div>
                <div className="stat-item">
                  <FiClock aria-hidden="true" />
                  <span>{creative.respuesta}</span>
                </div>
              </div>

              {creative.portfolio && creative.portfolio.length > 0 && (
                <div className="portfolio-preview portfolio-count-1">
                  {creative.portfolio.slice(0, 1).map((item, index) => (
                    <div
                      key={item.id || index}
                      className="portfolio-thumb"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(item.image); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setPreviewImageUrl(item.image); } }}
                      aria-label={`Ver imagen ${index + 1} del portafolio en grande`}
                    >
                      <img
                        src={item.image}
                        alt={item.title || ''}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const fallback = e.target.nextElementSibling;
                          if (fallback) fallback.classList.add('visible');
                        }}
                      />
                      <div className="portfolio-thumb-fallback" aria-hidden="true">
                        <FiImage />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {creative.projectPdfs && creative.projectPdfs.length > 0 && (
                <div className="creative-pdfs-preview">
                  <FiFileText className="pdfs-icon" aria-hidden="true" />
                  <span>{creative.projectPdfs.length} {creative.projectPdfs.length === 1 ? 'proyecto en PDF' : 'proyectos en PDF'}</span>
                </div>
              )}

              <button
                className="btn-contact-creative"
                onClick={(e) => { e.stopPropagation(); if (onViewProfile) onViewProfile(creative.id); }}
              >
                <FiUser aria-hidden="true" />
                Ver perfil
              </button>
            </motion.div>
            );
                return (
                  <div key={creative.id} className="creative-card-wrapper">
                    {isElite ? (
                      <motion.div
                        whileHover={{ y: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ElectricBorder
                          color={eliteColor}
                          speed={0.8}
                          chaos={0.06}
                          borderRadius={20}
                          hoverOnly={true}
                        >
                          {card}
                        </ElectricBorder>
                      </motion.div>
                    ) : card}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Creative Detail Modal */}
      <AnimatePresence>
        {selectedCreative && (
          <motion.div
            className="creative-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCreative(null)}
          >
            <motion.div
              className="creative-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close"
                onClick={() => setSelectedCreative(null)}
                aria-label="Cerrar modal"
              >
                <FiX aria-hidden="true" />
              </button>

              <div className="modal-header">
                <div
                  className="modal-avatar"
                  style={{ background: getRandomGradient(selectedCreative.id) }}
                >
                  <img
                    src={selectedCreative.avatar || DEFAULT_USER_AVATAR_DATA_URI}
                    alt={selectedCreative.name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_USER_AVATAR_DATA_URI;
                    }}
                  />
                </div>
                <div className="modal-header-info">
                  {selectedCreative.username && (
                    <span className="modal-username-chip">@{selectedCreative.username}</span>
                  )}
                  <div className="modal-name-row">
                    <h2>{selectedCreative.name}</h2>
                    {selectedCreative.founder && (
                      <span className="modal-founder-badge" title="Fundador de Vissory">
                        <FiStar aria-hidden="true" /> Fundador
                      </span>
                    )}
                    {(selectedCreative.season_level != null || selectedCreative.season_level_name) && (
                      <span
                        className={selectedCreative.season_level === 5 ? 'modal-elite-badge' : 'modal-level-badge'}
                        style={(selectedCreative.season_level === 5 ? { '--elite-color': selectedCreative.season_badge_color || '#ff5500' } : { '--level-color': selectedCreative.season_badge_color || 'var(--primary-orange)' })}
                      >
                        {selectedCreative.season_level === 5 && <FiZap aria-hidden="true" />}
                        {selectedCreative.season_level_name || `Nivel ${selectedCreative.season_level}`}
                      </span>
                    )}
                    {selectedCreative.verified && (
                      <span className="verified-inline">
                        <FiCheck aria-hidden="true" /> Verificado
                      </span>
                    )}
                  </div>
                  <p className="modal-specialty">{selectedCreative.specialty}</p>
                  <p className="modal-location">{selectedCreative.location}</p>
                </div>
              </div>

              <div className="modal-stats">
                <div className="modal-stat">
                  <FiStar className="star-filled" aria-hidden="true" />
                  <span className="stat-value">{selectedCreative.rating ?? '—'}</span>
                  <span className="stat-label">{selectedCreative.reviews} reseñas</span>
                </div>
                <div className="modal-stat">
                  <FiFolder aria-hidden="true" />
                  <span className="stat-value">{selectedCreative.completedProjects ?? 0}</span>
                  <span className="stat-label">proyectos</span>
                </div>
                <div className="modal-stat">
                  <FaEuroSign aria-hidden="true" />
                  <span className="stat-value">{selectedCreative.hourlyRate != null ? `${selectedCreative.hourlyRate}€` : '—'}</span>
                  <span className="stat-label">por hora</span>
                </div>
                <div className="modal-stat">
                  <FiClock aria-hidden="true" />
                  <span className="stat-value">{selectedCreative.respuesta || selectedCreative.responseTime || '—'}</span>
                  <span className="stat-label">respuesta</span>
                </div>
              </div>

              {selectedCreative.bio && (
                <div className="modal-section">
                  <h3>Sobre mí</h3>
                  <p>{selectedCreative.bio}</p>
                </div>
              )}

              {selectedCreative.skills && selectedCreative.skills.length > 0 && (
                <div className="modal-section">
                  <h3>Habilidades</h3>
                  <div className="modal-skills">
                    {selectedCreative.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedCreative.portfolio && selectedCreative.portfolio.length > 0 && (
                <div className="modal-section">
                  <h3>Portafolio</h3>
                  <div className="modal-portfolio">
                    {selectedCreative.portfolio.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        className="portfolio-item portfolio-item-clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => setPreviewImageUrl(item.image)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewImageUrl(item.image); } }}
                        aria-label={`Ver imagen en grande: ${item.title || 'Portafolio'}`}
                      >
                        <img src={item.image} alt={item.title || ''} />
                        <div className="portfolio-overlay">
                          <span>{item.title || 'Ver en grande'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCreative.projectPdfs && selectedCreative.projectPdfs.length > 0 && (
                <div className="modal-section">
                  <h3>
                    <FiFileText className="section-icon" aria-hidden="true" />
                    Proyectos en PDF
                  </h3>
                  <div className="modal-pdfs">
                    {selectedCreative.projectPdfs.map(pdf => (
                      <div key={pdf.id} className="modal-pdf-item">
                        <div className="pdf-file-icon">
                          <FiFileText aria-hidden="true" />
                        </div>
                        <div className="pdf-file-info">
                          <span className="pdf-file-name">{pdf.name}</span>
                          <span className="pdf-file-type">PDF</span>
                        </div>
                        <div className="modal-pdf-actions">
                          <button
                            type="button"
                            className="btn-download-pdf"
                            title="Vista previa del PDF"
                            onClick={() => setPreviewPdf({ url: pdf.url, name: pdf.name, previewUrl: getPdfPreviewUrl(pdf.url) })}
                          >
                            <FiEye aria-hidden="true" />
                          </button>
                          <a href={pdf.url} target="_blank" rel="noopener noreferrer" className="btn-download-pdf" title="Abrir en nueva pestaña">
                            <FiExternalLink aria-hidden="true" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCreative && (
                <div className="modal-actions">
                  {user && selectedCreative.id !== user.id && (
                    <button
                      type="button"
                      className="btn-primary-large"
                      onClick={async () => {
                        if (!selectedCreative || !token || !onOpenDirectChat) return;
                        try {
                          const res = await axios.post(
                            `${API}/conversations`,
                            { other_user_id: selectedCreative.id },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setSelectedCreative(null);
                          onOpenDirectChat(res.data.id);
                        } catch (err) {
                          console.error('Error opening chat:', err);
                        }
                      }}
                    >
                      <FiSend aria-hidden="true" />
                      Enviar Mensaje
                    </button>
                  )}
                  {user && selectedCreative.id !== user.id && (
                    <FollowButton targetId={selectedCreative.id} initialCount={selectedCreative.followers_count || 0} />
                  )}
                  <button
                    type="button"
                    className="btn-outline-large"
                    onClick={() => {
                      if (onViewProfile) {
                        setSelectedCreative(null);
                        onViewProfile(selectedCreative.id);
                      }
                    }}
                  >
                    <FiUser aria-hidden="true" />
                    Ver perfil
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox: preview de imagen de portafolio en grande (portal to body to avoid transform issues) */}
      {previewImageUrl && ReactDOM.createPortal(
        <div
          className="portfolio-image-lightbox-overlay"
          onClick={() => setPreviewImageUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa de imagen"
        >
          <button
            type="button"
            className="portfolio-image-lightbox-close"
            onClick={() => setPreviewImageUrl(null)}
            aria-label="Cerrar vista previa"
          >
            <FiX aria-hidden="true" />
          </button>
          <div className="portfolio-image-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={previewImageUrl} alt="Vista previa del portafolio" />
          </div>
        </div>,
        document.body
      )}

      {/* Lightbox: preview rápida de PDF (sin descarga obligatoria) */}
      {previewPdf && ReactDOM.createPortal(
        <div
          className="portfolio-image-lightbox-overlay"
          onClick={() => setPreviewPdf(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Vista previa de PDF: ${previewPdf.name || 'Documento'}`}
        >
          <button
            type="button"
            className="portfolio-image-lightbox-close"
            onClick={() => setPreviewPdf(null)}
            aria-label="Cerrar vista previa de PDF"
          >
            <FiX aria-hidden="true" />
          </button>
          <div className="pdf-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-lightbox-header">
              <span className="pdf-lightbox-title">{previewPdf.name || 'Documento PDF'}</span>
              <a href={previewPdf.url} target="_blank" rel="noopener noreferrer" className="pdf-lightbox-open" title="Abrir en nueva pestaña">
                <FiExternalLink aria-hidden="true" />
                Abrir en pestaña
              </a>
            </div>
            <iframe
              src={previewPdf.previewUrl || previewPdf.url}
              title={previewPdf.name || 'Vista previa de PDF'}
              className="pdf-lightbox-iframe"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Settings Component
const Settings = ({ settings, onUpdate }) => {
  const navigate = useNavigate();
  const { user, realUser, token, logout } = useAuth();
  const [requestingRole, setRequestingRole] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [roleSuccess, setRoleSuccess] = useState('');
  const [requestedRoleOption, setRequestedRoleOption] = useState('');
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const isAdmin = realUser?.role === 'admin';
  const roleForDisplay = isAdmin ? user?.role : (realUser || user)?.role;
  const showRoleChangeSection = roleForDisplay !== 'admin';

  const [brands, setBrands] = useState([]);
  const [featuredAgencyId, setFeaturedAgencyId] = useState('');
  const [featuredAgencyLoading, setFeaturedAgencyLoading] = useState(false);
  const [featuredAgencySaving, setFeaturedAgencySaving] = useState(false);
  const [featuredAgencyMessage, setFeaturedAgencyMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!isAdmin || !token) return;
    setFeaturedAgencyLoading(true);
    Promise.all([
      axios.get(`${API}/admin/brands`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/featured-agency`)
    ])
      .then(([brandsRes, featuredRes]) => {
        setBrands(Array.isArray(brandsRes.data) ? brandsRes.data : []);
        setFeaturedAgencyId(featuredRes.data?.user_id || '');
      })
      .catch(() => setBrands([]))
      .finally(() => setFeaturedAgencyLoading(false));
  }, [isAdmin, token]);

  const handleSaveFeaturedAgency = async (e) => {
    e.preventDefault();
    if (!token || !isAdmin) return;
    setFeaturedAgencySaving(true);
    setFeaturedAgencyMessage({ type: '', text: '' });
    try {
      await axios.patch(
        `${API}/admin/featured-agency`,
        { user_id: featuredAgencyId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeaturedAgencyMessage({ type: 'success', text: 'Agencia destacada actualizada.' });
    } catch (err) {
      setFeaturedAgencyMessage({ type: 'error', text: err.response?.data?.detail || 'Error al guardar.' });
    } finally {
      setFeaturedAgencySaving(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    onUpdate({
      ...settings,
      showProjectsInBriefs: checked
    });
  };

  const handleRoleChangeRequest = async (e) => {
    e.preventDefault();
    const requested = requestedRoleOption?.trim().toLowerCase();
    if (!requested || requested === roleForDisplay || !token) return;
    setRoleError('');
    setRoleSuccess('');
    setRequestingRole(true);
    try {
      const response = await axios.post(
        `${API}/auth/role-change-request`,
        { requested_role: requested },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoleSuccess(response.data?.message || 'Solicitud enviada. Te contactaremos cuando hayamos procesado el cambio.');
      setRequestedRoleOption('');
    } catch (err) {
      setRoleError(err.response?.data?.detail || 'No se pudo enviar la solicitud.');
    } finally {
      setRequestingRole(false);
    }
  };

  const roleLabel = (r) => {
    if (r === 'creative') return 'Creativo';
    if (r === 'brand') return 'Empresa (marca o agencia)';
    return 'Creador de contenido';
  };

  const handleDeleteAccountClick = () => setShowDeleteAccountConfirm(true);
  const handleDeleteAccountCancel = () => setShowDeleteAccountConfirm(false);
  const handleDeleteAccountConfirm = async () => {
    if (!token || !logout) return;
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
      if (logout) logout();
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (_) {}
      navigate('/', { replace: true });
    } catch (err) {
      setDeletingAccount(false);
      setShowDeleteAccountConfirm(false);
      const msg = err.response?.data?.detail || 'No se pudo borrar la cuenta. Inténtalo de nuevo.';
      alert(msg);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2>
            <FiSettings aria-hidden="true" />
            Configuración
          </h2>
          <p>Configura tu experiencia en Vissory.</p>
        </div>
      </div>

      <div className="settings-grid">
        {showRoleChangeSection && (
        <section className="settings-card">
          <h3>Tipo de cuenta</h3>
            <p className="settings-description">
              Tu tipo de cuenta actual es <strong>{roleLabel(roleForDisplay)}</strong>. Si te equivocaste al elegir el tipo de cuenta al registrarte o al configurar tu perfil, puedes solicitar un cambio. Enviaremos tu petición al equipo y te contactaremos cuando lo hayamos procesado (es un cambio excepcional, no un cambio que se pueda hacer cada vez que se quiera).
            </p>
          <form onSubmit={handleRoleChangeRequest} className="settings-role-select-wrap">
            <label htmlFor="settings-request-role" className="settings-label">Solicitar cambio a</label>
            <select
              id="settings-request-role"
              className="settings-role-select"
              value={requestedRoleOption}
              onChange={(e) => setRequestedRoleOption(e.target.value)}
              disabled={requestingRole}
              aria-label="Solicitar cambio de tipo de cuenta a"
            >
              <option value="">— Elegir tipo —</option>
              <option value="creative">{roleLabel('creative')}</option>
              <option value="creator">{roleLabel('creator')}</option>
              <option value="brand">{roleLabel('brand')}</option>
            </select>
            <button
              type="submit"
              className="btn-primary settings-role-submit"
              disabled={requestingRole || !requestedRoleOption || requestedRoleOption === roleForDisplay}
            >
              {requestingRole ? 'Enviando...' : 'Solicitar cambio de tipo de cuenta'}
            </button>
            {roleError && <p className="settings-role-error">{roleError}</p>}
            {roleSuccess && <p className="settings-role-success">{roleSuccess}</p>}
          </form>
        </section>
        )}

        {isAdmin && (
          <section className="settings-card">
            <h3>Agencia destacada</h3>
            <p className="settings-description">
              Elige la empresa (marca o agencia) que aparecerá en &quot;Agencia Destacada&quot; del menú. Solo usuarios con tipo de cuenta Empresa.
            </p>
            {featuredAgencyLoading ? (
              <p className="settings-description">Cargando empresas...</p>
            ) : (
              <form onSubmit={handleSaveFeaturedAgency} className="settings-role-select-wrap">
                <label htmlFor="settings-featured-agency" className="settings-label">Empresa destacada</label>
                <select
                  id="settings-featured-agency"
                  className="settings-role-select"
                  value={featuredAgencyId}
                  onChange={(e) => setFeaturedAgencyId(e.target.value)}
                  disabled={featuredAgencySaving}
                  aria-label="Elegir agencia destacada"
                >
                  <option value="">— Ninguna —</option>
                  {brands.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}{b.company_legal_name ? ` (${b.company_legal_name})` : ''}{b.industry ? ` · ${b.industry}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="btn-primary settings-role-submit"
                  disabled={featuredAgencySaving}
                >
                  {featuredAgencySaving ? 'Guardando...' : 'Guardar agencia destacada'}
                </button>
                {featuredAgencyMessage.text && (
                  <p className={featuredAgencyMessage.type === 'error' ? 'settings-role-error' : 'settings-role-success'}>
                    {featuredAgencyMessage.text}
                  </p>
                )}
              </form>
            )}
          </section>
        )}

        {(user.role === 'creator' || user.role === 'brand') && (
          <section className="settings-card">
            <h3>Mis Proyectos</h3>
            <p className="settings-description">
              Controla si en la sección de &quot;Mis Proyectos&quot; quieres ver solo los briefs abiertos
              o también aquellos que ya tienen un proyecto creado.
            </p>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={!!settings.showProjectsInBriefs}
                onChange={handleCheckboxChange}
              />
              <span className="toggle-switch" aria-hidden="true"></span>
              <span className="toggle-label">
                Mostrar también briefs con proyecto creado en &quot;Mis Proyectos&quot;
              </span>
            </label>
          </section>
        )}

        {isAdmin && (
          <section className="settings-card">
            <h3>Campañas de email</h3>
            <p className="settings-description">
              Envía correos masivos a creativos, creadores o marcas desde aquí (SendGrid). Solo administradores.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/campanas')}
              aria-label="Abrir campañas de email"
            >
              <FiMail aria-hidden="true" style={{ marginRight: '0.5rem' }} />
              Gestionar campañas de email
            </button>
          </section>
        )}

        <section className="settings-card settings-card--danger">
          <h3>Borrar cuenta</h3>
          <p className="settings-description">
            Si borras tu cuenta se eliminarán tus datos de perfil y ya no podrás acceder. Los proyectos y briefs en los que hayas participado pueden conservar referencias por motivos legales o de historial. Esta acción no se puede deshacer.
          </p>
          <button
            type="button"
            className="btn-danger settings-delete-account-btn"
            onClick={handleDeleteAccountClick}
            disabled={deletingAccount}
          >
            <FiTrash2 aria-hidden="true" />
            Borrar mi cuenta
          </button>
        </section>
      </div>

      {showDeleteAccountConfirm && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-account-modal-title" onClick={(e) => e.target === e.currentTarget && handleDeleteAccountCancel()}>
          <div className="modal-content modal-content--danger" onClick={e => e.stopPropagation()}>
            <h2 id="delete-account-modal-title" className="modal-title">¿Borrar tu cuenta?</h2>
            <p className="modal-description">
              Se eliminará tu cuenta y tus datos de perfil de forma permanente. No podrás volver a iniciar sesión con esta cuenta. Esta acción no se puede deshacer.
            </p>
            <p className="modal-description"><strong>¿Estás seguro?</strong></p>
            <div className="modal-actions modal-actions--row">
              <button
                type="button"
                className="btn-outline"
                onClick={handleDeleteAccountCancel}
                disabled={deletingAccount}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteAccountConfirm}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Borrando...' : 'Sí, borrar mi cuenta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Vista solo administradores: campañas de email (SendGrid) a creativos, creadores o marcas
const CampanasView = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const API = API;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('creative');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const previewDebounceRef = useRef(null);
  const previewIframeRef = useRef(null);

  useEffect(() => {
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    const b = (body || '').trim();
    if (!b) { setPreviewHtml(''); return; }
    previewDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/admin/preview-campaign-email`,
          { body: b },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        setPreviewHtml(res.data?.html || '');
      } catch (_) {}
    }, 500);
    return () => { if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current); };
  }, [body, token, API]);

  useEffect(() => {
    const iframe = previewIframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(previewHtml || '<html><body style="font-family:sans-serif;color:#bbb;display:flex;align-items:center;justify-content:center;height:100%;margin:0;"><p style="text-align:center;font-style:italic;">Escribe el mensaje para ver la preview…</p></body></html>');
    doc.close();
    const resize = () => {
      try { iframe.style.height = (doc.documentElement.scrollHeight || 400) + 'px'; } catch (_) {}
    };
    setTimeout(resize, 100);
    setTimeout(resize, 500);
  }, [previewHtml]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    const subj = subject.trim();
    const b = body.trim();
    if (!subj || !b) {
      setError('Asunto y mensaje son obligatorios.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/send-campaign-email`,
        { subject: subj, body: b, target },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al enviar.');
    } finally {
      setSending(false);
    }
  };

  const targetLabel = { creative: 'Creativos', creator: 'Creadores de contenido', brand: 'Marcas' };

  return (
    <div className="campanas-page">
      <div className="campanas-inner">
        <button
          type="button"
          className="campanas-back-link"
          onClick={() => navigate('/ajustes')}
          aria-label="Volver a Configuración"
        >
          ← Volver a Configuración
        </button>
        <h1 className="campanas-title">Campañas de email</h1>
        <p className="campanas-description">
          Envía un correo a todos los usuarios de un tipo (creativos, creadores o marcas) desde aquí. Los envíos se realizan por SendGrid. Solo administradores.
        </p>
        <div className="campanas-layout">
          <form onSubmit={handleSubmit} className="campanas-form">
            <div className="form-group">
              <label htmlFor="campanas-subject" className="campanas-label">Asunto</label>
              <input
                id="campanas-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Ej: Completa tu perfil en Vissory"
                className="campanas-input"
                disabled={sending}
              />
            </div>
            <div className="form-group">
              <label htmlFor="campanas-body" className="campanas-label">Mensaje</label>
              <textarea
                id="campanas-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Escribe el texto del correo en texto plano. Los saltos de línea se conservarán."
                className="campanas-textarea"
                rows={14}
                disabled={sending}
              />
            </div>
            <div className="form-group">
              <span className="campanas-label">Enviar a</span>
              <div className="campanas-target-row">
                {(['creative', 'creator', 'brand']).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`campanas-target-btn ${target === t ? 'active' : ''}`}
                    onClick={() => setTarget(t)}
                    disabled={sending}
                  >
                    {targetLabel[t]}
                  </button>
                ))}
              </div>
            </div>
            {error && <p className="campanas-error">{error}</p>}
            <button type="submit" className="btn-primary campanas-submit" disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar correo'}
            </button>
          </form>

          <div className="campanas-preview">
            <span className="campanas-preview-label">Preview del correo</span>
            <div className="campanas-preview-header-bar">
              <span className="campanas-preview-from">De: <strong>Vissory</strong> &lt;noreply@vissory.com&gt;</span>
              <span className="campanas-preview-to">Para: <strong>{targetLabel[target]}</strong></span>
              <span className="campanas-preview-subject-bar">Asunto: <strong>{subject.trim() || '(Sin asunto)'}</strong></span>
            </div>
            <div className="campanas-preview-iframe-wrap">
              <iframe
                ref={previewIframeRef}
                title="Preview del correo"
                className="campanas-preview-iframe"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>

        {result && (
          <div className="campanas-result">
            <h2 className="campanas-result-title">Resultado</h2>
            <p className="campanas-result-message">{result.message}</p>
            <p className="campanas-result-counts">
              Enviados: <strong>{result.sent}</strong> · Fallidos: <strong>{result.failed}</strong>
              {result.total != null && <> · Total destinatarios: <strong>{result.total}</strong></>}
            </p>
            {result.emails && result.emails.length > 0 && (
              <ul className="campanas-result-list">
                {result.emails.map((item, i) => (
                  <li key={i} className={item.sent ? 'sent' : 'failed'}>
                    {item.email} {item.name ? `(${item.name})` : ''} — {item.sent ? 'Enviado' : 'Fallido'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Página de actualizaciones / changelog (accesible desde el icono de bug en Síguenos)
const ChangelogPage = () => {
  const navigate = useNavigate();
  const entries = CHANGELOG_ENTRIES || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="changelog-page">
      <div className="changelog-page-header">
        <h2 className="changelog-page-title">Actualizaciones</h2>
        <p className="changelog-page-subtitle">
          Correcciones, mejoras y nuevas funcionalidades en los últimos cambios.
        </p>
        <SuggestionsBanner onGoToSuggestions={() => navigate('/sugerencias')} />
      </div>
      <div className="changelog-page-content">
        {entries.length === 0 ? (
          <p className="changelog-empty">Aún no hay registros de actualizaciones.</p>
        ) : (
          <ul className="changelog-list">
            {entries.map((entry, index) => (
              <li key={`${entry.date}-${index}`} className="changelog-entry">
                <time className="changelog-entry-date" dateTime={entry.date}>
                  {formatDate(entry.date)}
                </time>
                <ul className="changelog-entry-items">
                  {(entry.items || []).map((item, i) => {
                    const colonIndex = item.indexOf(':');
                    const label = colonIndex >= 0 ? item.slice(0, colonIndex) : '';
                    const rest = colonIndex >= 0 ? item.slice(colonIndex) : item;
                    return (
                      <li key={i} className="changelog-entry-item">
                        {colonIndex >= 0 ? <><strong>{label}</strong>{rest}</> : item}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Página de sugerencias públicas: dejar sugerencia, listado público con votos up/down; solo admins responden (como Vissory)
const SugerenciasPage = ({ token, isAdmin }) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTexts, setReplyTexts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [filterCompletadas, setFilterCompletadas] = useState('all'); // 'all' | 'completed' | 'open'

  const fetchSuggestions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await axios.get(`${API}/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setFetchError(err.response?.data?.detail || err.message || 'No se pudieron cargar las sugerencias.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchSuggestions();
  }, [token, fetchSuggestions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/suggestions`, { text }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewText('');
      fetchSuggestions();
    } catch (err) {
      console.error('Error creating suggestion:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (suggestionId) => {
    const replyText = (replyTexts[suggestionId] || '').trim();
    if (!replyText) return;
    setReplyingId(suggestionId);
    try {
      await axios.patch(`${API}/suggestions/${suggestionId}/reply`, { reply_text: replyText }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplyTexts(prev => ({ ...prev, [suggestionId]: '' }));
      fetchSuggestions();
    } catch (err) {
      console.error('Error replying:', err);
    } finally {
      setReplyingId(null);
    }
  };

  const handleUpvote = async (suggestionId) => {
    if (votingId) return;
    setVotingId(suggestionId);
    try {
      const res = await axios.post(`${API}/suggestions/${suggestionId}/upvote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, ...res.data } : s));
    } catch (err) {
      console.error('Error upvoting:', err);
    } finally {
      setVotingId(null);
    }
  };

  const handleDownvote = async (suggestionId) => {
    if (votingId) return;
    setVotingId(suggestionId);
    try {
      const res = await axios.post(`${API}/suggestions/${suggestionId}/downvote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, ...res.data } : s));
    } catch (err) {
      console.error('Error downvoting:', err);
    } finally {
      setVotingId(null);
    }
  };

  const handleComplete = async (suggestionId) => {
    if (completingId) return;
    setCompletingId(suggestionId);
    try {
      const res = await axios.patch(`${API}/suggestions/${suggestionId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, ...res.data } : s));
    } catch (err) {
      console.error('Error completing suggestion:', err);
    } finally {
      setCompletingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredSuggestions = suggestions.filter(s => {
    if (filterCompletadas === 'completed') return s.completed === true;
    if (filterCompletadas === 'open') return !s.completed;
    return true;
  });

  return (
    <div className="sugerencias-page">
      <div className="page-header sugerencias-page-header">
        <h2 className="flex items-center gap-2">
          <FiMessageCircle aria-hidden="true" />
          Sugerencias
        </h2>
        <p className="sugerencias-page-subtitle">Comparte tu feedback y vota las ideas de la comunidad.</p>
      </div>
      <div className="sugerencias-page-content">
        <section className="sugerencias-form-section">
          <form onSubmit={handleSubmit} className="sugerencias-form">
            <label htmlFor="sugerencias-new-text">Deja tu sugerencia</label>
            <textarea
              id="sugerencias-new-text"
              value={newText}
              onChange={e => setNewText(e.target.value)}
              placeholder="Escribe tu sugerencia o feedback..."
              rows={3}
              maxLength={5000}
            />
            <button type="submit" className="btn-primary" disabled={submitting || !newText.trim()}>
              {submitting ? 'Enviando...' : 'Enviar sugerencia'}
            </button>
          </form>
        </section>
        <section className="sugerencias-list-section">
          <div className="sugerencias-list-header">
            <h3>Sugerencias de la comunidad</h3>
            <div className="sugerencias-filter-tabs">
              <button
                type="button"
                className={`sugerencias-filter-tab ${filterCompletadas === 'all' ? 'active' : ''}`}
                onClick={() => setFilterCompletadas('all')}
              >
                Todas
              </button>
              <button
                type="button"
                className={`sugerencias-filter-tab ${filterCompletadas === 'open' ? 'active' : ''}`}
                onClick={() => setFilterCompletadas('open')}
              >
                Sin completar
              </button>
              <button
                type="button"
                className={`sugerencias-filter-tab ${filterCompletadas === 'completed' ? 'active' : ''}`}
                onClick={() => setFilterCompletadas('completed')}
              >
                Completadas
              </button>
            </div>
          </div>
          {fetchError ? (
            <p className="sugerencias-error" role="alert">
              {fetchError} Puedes intentar recargar la página.
            </p>
          ) : loading ? (
            <p className="sugerencias-loading">Cargando...</p>
          ) : filteredSuggestions.length === 0 ? (
            <p className="sugerencias-empty">
              {suggestions.length === 0
                ? 'Aún no hay sugerencias. ¡Sé el primero en dejar la tuya!'
                : filterCompletadas === 'completed'
                  ? 'No hay sugerencias completadas.'
                  : 'No hay sugerencias sin completar.'}
            </p>
          ) : (
            <ul className="sugerencias-list">
              {filteredSuggestions.map(s => (
                <li key={s.id} className={`sugerencia-item ${s.completed ? 'sugerencia-item-completed' : ''}`}>
                  {s.completed && (
                    <div className="sugerencia-completada-banner">
                      <span className="sugerencia-completada-label">✓ Completada</span>
                      {s.completed_at && (
                        <span className="sugerencia-completada-date">
                          Completada el {formatDate(s.completed_at)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="sugerencia-meta">
                    <div className="sugerencia-author">
                      {s.user_id ? (
                        <button
                          type="button"
                          className="sugerencia-author-link"
                          onClick={() => navigate(`/p/${s.user_id}`)}
                          aria-label={`Ver perfil de ${s.user_name || 'autor'}`}
                        >
                          {s.user_profile_image ? (
                            <img
                              src={getUploadUrl(s.user_profile_image)}
                              alt=""
                              className="sugerencia-avatar"
                              onError={(e) => { e.target.onerror = null; e.target.src = getAvatarUrl(null, s.user_name); }}
                            />
                          ) : (
                            <span className="sugerencia-avatar sugerencia-avatar-placeholder" aria-hidden="true">
                              {(s.user_name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                          {s.user_name && <span className="sugerencia-user">{s.user_name}</span>}
                        </button>
                      ) : (
                        <>
                          {s.user_name && <span className="sugerencia-user">{s.user_name}</span>}
                        </>
                      )}
                    </div>
                    <span className="sugerencia-date">{formatDate(s.created_at)}</span>
                  </div>
                  <p className="sugerencia-text">{s.text}</p>
                  <div className="sugerencia-votes">
                    <button
                      type="button"
                      className={`sugerencia-vote-btn sugerencia-upvote ${s.user_vote === 'up' ? 'voted' : ''}`}
                      onClick={() => handleUpvote(s.id)}
                      disabled={votingId === s.id}
                      aria-label="Upvote"
                      title="Upvote"
                    >
                      <FiArrowUp size={18} aria-hidden="true" />
                      <span className="sugerencia-vote-count">{s.upvote_count ?? 0}</span>
                    </button>
                    <button
                      type="button"
                      className={`sugerencia-vote-btn sugerencia-downvote ${s.user_vote === 'down' ? 'voted' : ''}`}
                      onClick={() => handleDownvote(s.id)}
                      disabled={votingId === s.id}
                      aria-label="Downvote"
                      title="Downvote"
                    >
                      <FiArrowDown size={18} aria-hidden="true" />
                      <span className="sugerencia-vote-count">{s.downvote_count ?? 0}</span>
                    </button>
                  </div>
                  {s.reply_text && (
                    <div className="sugerencia-reply">
                      <strong>Respuesta de Vissory:</strong>
                      <p>{s.reply_text}</p>
                    </div>
                  )}
                  {isAdmin && (
                    <>
                      <div className="sugerencia-admin-reply">
                        <span className="sugerencia-admin-reply-hint">Tu respuesta se publicará siempre a nombre de Vissory.</span>
                        <textarea
                          value={replyTexts[s.id] ?? s.reply_text ?? ''}
                          onChange={e => setReplyTexts(prev => ({ ...prev, [s.id]: e.target.value }))}
                          placeholder="Escribe la respuesta de Vissory..."
                          rows={2}
                        />
                        <button
                          type="button"
                          className="btn-primary btn-small"
                          disabled={replyingId === s.id}
                          onClick={() => handleReply(s.id)}
                        >
                          {replyingId === s.id ? 'Guardando...' : (s.reply_text ? 'Actualizar respuesta' : 'Responder como Vissory')}
                        </button>
                      </div>
                      {!s.completed && (
                        <div className="sugerencia-admin-complete">
                          <button
                            type="button"
                            className="btn-secondary btn-small"
                            disabled={completingId === s.id}
                            onClick={() => handleComplete(s.id)}
                          >
                            {completingId === s.id ? 'Completando...' : 'Marcar como completada'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

// Página independiente Confían en Vissory (fundadores)
const ConfianPage = () => {
  const navigate = useNavigate();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/founders`)
      .then(res => { if (!cancelled) setFounders(Array.isArray(res.data) ? res.data : []); })
      .catch(err => { if (!cancelled) console.error('Error fetching founders:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const roleLabel = (r) => ({ creative: 'Creativo', creator: 'Creador de contenido', brand: 'Marca / Agencia', admin: 'Admin' }[r] || r);

  return (
    <div className="confian-page">
      <div className="page-header confian-page-header">
        <h2 className="flex items-center gap-2">
          <FiUsers aria-hidden="true" />
          Confían en Vissory
        </h2>
        <p className="confian-page-subtitle">Fundadores y primeras voces de la comunidad.</p>
      </div>
      <div className="confian-page-content">
        {loading ? (
          <p className="sugerencias-loading">Cargando...</p>
        ) : founders.length === 0 ? (
          <p className="sugerencias-empty">Aún no hay fundadores destacados.</p>
        ) : (
          <div className="founders-grid">
            {founders.map((f) => (
              <button
                key={f.id}
                type="button"
                className="founder-card"
                onClick={() => navigate(`/p/${f.id}`)}
              >
                <div className="founder-card-avatar-wrap">
                  <div className="founder-card-avatar" style={{ background: getRandomGradient(f.id) }}>
                    {f.profile_image ? (
                      <img src={getAvatarUrl(f.profile_image, f.name)} alt={f.name} />
                    ) : (
                      <img src={isotipoWhite} alt="" className="founder-card-avatar-logo" aria-hidden="true" />
                    )}
                  </div>
                  <span className="founder-card-badge">
                    <FiStar aria-hidden="true" /> Fundador
                  </span>
                </div>
                <div className="founder-card-info">
                  {f.username && <span className="founder-card-username">@{f.username}</span>}
                  <h4 className="founder-card-name">{f.name}</h4>
                  <span className="founder-card-role">{roleLabel(f.role)}</span>
                  {f.bio && <p className="founder-card-bio">{f.bio.length > 100 ? f.bio.slice(0, 100) + '…' : f.bio}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Header Component with Notifications and Profile
const Header = ({ user, unreadCount, onLogout, onOpenProfile, onOpenPublicProfile, onOpenSettings, onNotificationRead, setActiveTab, onNotificationClick, isAdmin, viewAsRole, onViewAsRoleChange, onToggleSidebar, sidebarOpen }) => {
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [markingAsRead, setMarkingAsRead] = useState(new Set());
  const [loadingMarkAll, setLoadingMarkAll] = useState(false);
  const profileContainerRef = useRef(null);
  const notificationsContainerRef = useRef(null);
  const { token, updateUser, realUser } = useAuth();
  const roleForBadge = (realUser || user)?.role;

  const handleAvailabilityChange = async (e) => {
    const value = e.target.value;
    if (!token) return;
    try {
      const response = await axios.patch(
        `${API}/auth/profile/availability`,
        { availability: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (updateUser) updateUser(response.data);
    } catch (err) {
      console.error('Error al actualizar disponibilidad:', err);
    }
  };

  // Cerrar dropdown del perfil al hacer clic fuera
  useEffect(() => {
    if (!showProfileDropdown) return;
    const handleClickOutside = (e) => {
      if (profileContainerRef.current && !profileContainerRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  // Cerrar dropdown de notificaciones al hacer clic fuera
  useEffect(() => {
    if (!showNotificationsDropdown) return;
    const handleClickOutside = (e) => {
      if (notificationsContainerRef.current && !notificationsContainerRef.current.contains(e.target)) {
        setShowNotificationsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationsDropdown]);

  // Fetch recent notifications for dropdown
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Solo mostrar notificaciones no leídas
        const unreadNotifications = response.data.filter(n => !n.read);
        setNotifications(unreadNotifications);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (showNotificationsDropdown && token) {
      fetchNotifications();
    }
  }, [showNotificationsDropdown, token]);

  const markAsRead = async (notificationId) => {
    setMarkingAsRead(prev => new Set([...prev, notificationId]));
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remover la notificación del estado local inmediatamente
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Notificar al componente padre para actualizar el contador
      if (onNotificationRead) {
        onNotificationRead();
      }
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const markAllAsRead = async () => {
    setLoadingMarkAll(true);
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Limpiar todas las notificaciones del popup
      setNotifications([]);
      
      // Notificar al componente padre
      if (onNotificationRead) {
        onNotificationRead();
      }
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoadingMarkAll(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message': return <FiMessageCircle />;
      case 'proposal_accepted': return <FiCheckCircle />;
      case 'creative_selected_project_opened': return <FiAward />;
      case 'proposal_received': return <FiMail />;
      case 'project_update': return <FiActivity />;
      case 'invoice_requested': return <FiFileText />;
      default: return <FiBell />;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Hace un momento';
    if (diffInHours < 24) return `Hace ${diffInHours}h`;
    if (diffInHours < 168) return `Hace ${Math.floor(diffInHours / 24)}d`;
    return date.toLocaleDateString('es-ES');
  };

  return (
    <nav className="header">
      <div className="header-container">
        <div className="header-left">
          <button
            type="button"
            className="header-hamburger"
            aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={!!sidebarOpen}
            onClick={onToggleSidebar}
          >
            <FiMenu size={24} aria-hidden="true" />
          </button>
          <div className="header-brand">
            <button
              type="button"
              className="header-logo-btn"
              onClick={() => setActiveTab('dashboard')}
              title="Ir al inicio"
              aria-label="Ir al inicio"
            >
              <Logo type="horizontal" theme="dark" className="header-logo" />
            </button>
            <span className="header-beta">BETA</span>
          </div>
        </div>
        <div className="header-actions">
          {/* Admin Role Selector */}
          {isAdmin && (
            <div className="admin-role-selector">
              <span className="admin-badge">
                <FiShield aria-hidden="true" />
                Admin
              </span>
              <select
                value={viewAsRole}
                onChange={(e) => onViewAsRoleChange(e.target.value)}
                className="admin-role-select"
                title="Ver la app como..."
              >
                <option value="creative">Ver como Creativo</option>
                <option value="creator">Ver como Creador</option>
                <option value="brand">Ver como Marca</option>
              </select>
            </div>
          )}

          {/* Nivel (creativos) en el navbar */}
          {user?.role === 'creative' && (user.season_level != null || user.season_level_name) && (
            <button
              type="button"
              className="header-level-btn"
              onClick={() => setActiveTab('rewards')}
              title="Ver recompensas y nivel"
            >
              <span className="header-level-icon" style={user.season_badge_color ? { background: user.season_badge_color } : undefined}>
                <FiZap />
              </span>
              <span className="header-level-info">
                <span className="header-level-number">Nivel {user.season_level || 1}</span>
                <span className="header-level-name">{user.season_level_name || 'Principiante'}</span>
              </span>
            </button>
          )}

          {/* Notifications */}
          <div className="header-item notifications-container" ref={notificationsContainerRef}>
            <button 
              className="notification-btn"
              onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
            >
              <span className="notification-icon" aria-hidden="true">
                <FiBell />
              </span>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>
            
            {showNotificationsDropdown && (
              <motion.div 
                className="notifications-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="dropdown-header">
                  <h4>Notificaciones {unreadCount > 0 && <span className="unread-count">({unreadCount} sin leer)</span>}</h4>
                  <div className="dropdown-actions">
                    {unreadCount > 0 && (
                      <button 
                        className="mark-all-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAllAsRead();
                        }}
                        disabled={loadingMarkAll}
                        title="Marcar todas como leídas"
                      >
                        {loadingMarkAll ? '⏳' : '✓'}
                      </button>
                    )}
                    <button 
                      className="view-all-btn"
                      onClick={() => {
                        setShowNotificationsDropdown(false);
                        setActiveTab('notifications');
                      }}
                    >
                      Ver todas
                    </button>
                  </div>
                </div>
                
                <div className="notifications-list">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 5).map(notification => (
                      <div 
                        key={notification.id} 
                        role="button"
                        tabIndex={0}
                        className={`notification-item ${!notification.read ? 'unread' : ''} ${onNotificationClick ? 'clickable' : ''} ${notification.type === 'creative_selected_project_opened' ? 'notification-creative-selected' : ''}`}
                        onClick={() => {
                          if (onNotificationClick) {
                            setShowNotificationsDropdown(false);
                            if (!notification.read) markAsRead(notification.id);
                            onNotificationClick(notification);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (onNotificationClick && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            setShowNotificationsDropdown(false);
                            if (!notification.read) markAsRead(notification.id);
                            onNotificationClick(notification);
                          }
                        }}
                      >
                        <div className="notification-content">
                          <div className="notification-icon-small">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="notification-text">
                            <p className="notification-title">{notification.title}</p>
                            <p className="notification-message">{notification.message}</p>
                            <span className="notification-time">
                              {formatTimeAgo(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="notification-item-actions">
                          {!notification.read && (
                            <button 
                              className="btn-quick-read"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              disabled={markingAsRead.has(notification.id)}
                              title="Marcar como leída"
                            >
                              {markingAsRead.has(notification.id) ? '⏳' : '✓'}
                            </button>
                          )}
                          <button 
                            className="btn-quick-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            title="Eliminar"
                          >
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        </div>
                        
                        {!notification.read && <div className="unread-dot"></div>}
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications">
                      <div className="no-notifications-icon" aria-hidden="true">
                        <FiBell />
                      </div>
                      <p>No hay notificaciones</p>
                      <span>Te avisaremos cuando tengas actualizaciones</span>
                    </div>
                  )}
                </div>
                
                {notifications.length > 5 && (
                  <div className="dropdown-footer">
                    <button 
                      className="view-more-btn"
                      onClick={() => {
                        setShowNotificationsDropdown(false);
                        setActiveTab('notifications');
                      }}
                    >
                      Ver {notifications.length - 5} más...
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Profile */}
          <div className="header-item profile-container" ref={profileContainerRef}>
            <button 
              className="profile-btn"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            >
              <div className="profile-avatar">
                <img src={getAvatarUrl(user.profile_image, user.name)} alt={user.name} />
              </div>
              <div className="profile-info">
                <span className="profile-name">{user.name}</span>
                <span className="profile-role">{roleForBadge === 'creative' ? 'Creativo' : roleForBadge === 'brand' ? 'Empresa' : roleForBadge === 'admin' ? 'Admin' : 'Creador de contenido'}</span>
              </div>
              <span className="dropdown-arrow">▼</span>
            </button>
            
            {showProfileDropdown && (
              <div className="profile-dropdown">
                <div className="dropdown-user-info">
                  <div className="user-avatar">
                    <img src={getAvatarUrl(user.profile_image, user.name)} alt={user.name} />
                  </div>
                  <div>
                    <p className="user-name">{user.name}</p>
                    <p className="user-email">{user.email}</p>
                    <p className="user-role-badge flex items-center gap-2">
                      {roleForBadge === 'creative' ? (
                        <>
                          <FiPenTool aria-hidden="true" />
                          Creativo
                        </>
                      ) : roleForBadge === 'brand' ? (
                        <>
                          <FiBriefcase aria-hidden="true" />
                          Empresa
                        </>
                      ) : roleForBadge === 'admin' ? (
                        <>
                          <FiShield aria-hidden="true" />
                          Admin
                        </>
                      ) : (
                        <>
                          <FiSmartphone aria-hidden="true" />
                          Creador de contenido
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="dropdown-availability-wrap">
                  <label className="dropdown-availability-label">Disponibilidad</label>
                  <select
                    className="dropdown-availability-select"
                    value={user.availability || 'available'}
                    onChange={handleAvailabilityChange}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Disponibilidad"
                  >
                    <option value="available">Disponible</option>
                    <option value="busy">Ocupado</option>
                    <option value="unavailable">No disponible</option>
                  </select>
                </div>
                
                <div className="dropdown-menu">
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      onOpenProfile();
                      setShowProfileDropdown(false);
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <FiUser />
                    </span>
                    Mi Perfil
                  </button>
                  {onOpenPublicProfile && (
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        onOpenPublicProfile();
                        setShowProfileDropdown(false);
                      }}
                    >
                      <span className="item-icon" aria-hidden="true">
                        <FiEye />
                      </span>
                      Tu perfil público
                    </button>
                  )}
                  {user?.role === 'creative' && (
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setActiveTab('my-projects');
                        setShowProfileDropdown(false);
                      }}
                    >
                      <span className="item-icon" aria-hidden="true">
                        <FiFolder />
                      </span>
                      Mis proyectos
                    </button>
                  )}
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setActiveTab('monetization');
                      setShowProfileDropdown(false);
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <FiCreditCard />
                    </span>
                    Pagos
                  </button>
                  {(user?.role === 'creator' || user?.role === 'brand' || user?.role === 'creative') && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setActiveTab('contacts');
                        setShowProfileDropdown(false);
                      }}
                    >
                      <span className="item-icon" aria-hidden="true">
                        <FiUsers />
                      </span>
                      Contactos
                    </button>
                  )}
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      if (onOpenSettings) {
                        onOpenSettings();
                      } else {
                        setActiveTab('settings');
                      }
                      setShowProfileDropdown(false);
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <FiSettings />
                    </span>
                    Configuración
                  </button>
                  
                  <hr className="dropdown-divider" />
                  
                  <button 
                    className="dropdown-item logout-item"
                    onClick={() => {
                      onLogout();
                      setShowProfileDropdown(false);
                    }}
                  >
                    <span className="item-icon" aria-hidden="true">
                      <FiLogOut />
                    </span>
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Rutas reales: path <-> tab id
const PATH_TO_TAB = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/crear-brief': 'create-brief',
  '/briefs': 'briefs',
  '/agencia-destacada': 'featured-agency',
  '/creativos': 'featured-creatives',
  '/proyectos': 'projects',
  '/chats': 'chats',
  '/sugerencias': 'suggestions',
  '/recompensas': 'rewards',
  '/aplicaciones': 'proposals',
  '/propuestas': 'proposals',
  '/perfil': 'profile',
  '/mis-proyectos': 'my-projects',
  '/monetization': 'monetization',
  '/ajustes': 'settings',
  '/notificaciones': 'notifications',
  '/changelog': 'changelog',
  '/confian-en-vissory': 'confian',
  '/contactos': 'contacts'
  , '/campanas': 'campanas'
};
const TAB_TO_PATH = {
  'dashboard': '/',
  'create-brief': '/crear-brief',
  'briefs': '/briefs',
  'featured-agency': '/agencia-destacada',
  'featured-creatives': '/creativos',
  'projects': '/proyectos',
  'contacts': '/contactos',
  'chats': '/chats',
  'suggestions': '/sugerencias',
  'confian': '/confian-en-vissory',
  'rewards': '/recompensas',
  'proposals': '/aplicaciones',
  'profile': '/perfil',
  'my-projects': '/mis-proyectos',
  'monetization': '/monetization',
  'settings': '/ajustes',
  'notifications': '/notificaciones',
  'changelog': '/changelog'
  , 'campanas': '/campanas'
};
function pathToTab(pathname) {
  if (!pathname) return 'dashboard';
  if (pathname.startsWith('/p/')) return 'public-profile';
  if (/^\/brief\/[^/]+$/.test(pathname)) return 'brief-detail';
  if (pathname === '/briefs' || /^\/briefs\/[^/]+$/.test(pathname)) return 'briefs';
  return PATH_TO_TAB[pathname] || 'dashboard';
}
function tabToPath(tabId) {
  return TAB_TO_PATH[tabId] ?? '/';
}

// Main App Component
const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname || '/';
  const activeTab = pathToTab(pathname);
  const publicProfileId = pathname.startsWith('/p/') ? pathname.replace(/^\/p\//, '').split('/')[0] : null;
  const briefIdFromUrl = useMemo(() => {
    const m = (pathname || '').match(/^\/briefs\/([^/]+)$/);
    return m ? m[1] : null;
  }, [pathname]);
  const briefDetailIdFromUrl = useMemo(() => {
    const m = (pathname || '').match(/^\/brief\/([^/]+)$/);
    return m ? m[1] : null;
  }, [pathname]);
  const applyBriefIdFromQuery = useMemo(() => {
    try {
      return new URLSearchParams(location.search || '').get('apply');
    } catch {
      return null;
    }
  }, [location.search]);

  const { addToast } = useToast();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [publicProfileIsFounder, setPublicProfileIsFounder] = useState(false);
  useEffect(() => {
    setPublicProfileIsFounder(false);
  }, [publicProfileId]);

  const setActiveTab = useCallback((tab) => {
    if (tab === 'public-profile') return;
    const path = tabToPath(tab);
    if (path) navigate(path);
  }, [navigate]);

  const [unreadCount, setUnreadCount] = useState(0);
  // Admin: permite ver la app como creative o creator
  const [viewAsRole, setViewAsRole] = useState(() => {
    const stored = localStorage.getItem('vissory_admin_view_as');
    return stored || 'creative';
  });
  const [briefsRefreshTrigger, setBriefsRefreshTrigger] = useState(0);
  const [projectsRefreshTrigger, setProjectsRefreshTrigger] = useState(0);
  const [projectNavigation, setProjectNavigation] = useState({
    projectId: null,
    view: 'list',
    initialDetailTab: null // 'overview' | 'chat' | 'milestones' | 'deliverables' | 'payments' — applied when opening project detail
  });
  const [proposalsInitialSubView, setProposalsInitialSubView] = useState(null); // 'solicitudes' | null — open MyProposals on this sub-tab
  const [chatNavigation, setChatNavigation] = useState({
    projectId: null,
    conversationId: null,
    milestoneId: null
  });
  const [createBriefTargetCreativeId, setCreateBriefTargetCreativeId] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [dockChatProjectId, setDockChatProjectId] = useState(null);
  const [chatDockRefreshKey, setChatDockRefreshKey] = useState(0);
  const [dismissedDockProjects, setDismissedDockProjects] = useState([]);
  const [showGoogleRoleModal, setShowGoogleRoleModal] = useState(false);
  const [googleRoleModalRole, setGoogleRoleModalRole] = useState('');
  const [googleRoleModalError, setGoogleRoleModalError] = useState('');
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [showPortfolioBubble, setShowPortfolioBubble] = useState(false);
  const [portfolioHighlight, setPortfolioHighlight] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsedDesktop, setSidebarCollapsedDesktop] = useState(() => {
    try {
      return localStorage.getItem('vissory_sidebar_collapsed_desktop') === '1';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapsedDesktop = useCallback(() => {
    setSidebarCollapsedDesktop((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('vissory_sidebar_collapsed_desktop', next ? '1' : '0');
      } catch (_) {}
      return next;
    });
  }, []);
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem('vissory_settings');
      return stored ? JSON.parse(stored) : { showProjectsInBriefs: false };
    } catch {
      return { showProjectsInBriefs: false };
    }
  });

  const [featuredAgencyUserId, setFeaturedAgencyUserId] = useState(null);
  const [featuredAgencySeenUserId, setFeaturedAgencySeenUserId] = useState(() => localStorage.getItem('vissory_featured_agency_seen') || '');
  const featuredAgencyToastShownRef = useRef(null);

  const updateSettings = (nextSettings) => {
    setSettings(nextSettings);
    try {
      localStorage.setItem('vissory_settings', JSON.stringify(nextSettings));
    } catch (e) {
      console.error('Error saving settings', e);
    }
  };

  // Handler para cambiar el rol de visualización (solo admins): guarda en BD y recarga para aplicar la vista
  const handleViewAsRoleChange = async (newRole) => {
    const prev = viewAsRole;
    setViewAsRole(newRole);
    localStorage.setItem('vissory_admin_view_as', newRole);
    try {
      await axios.patch(`${API}/auth/profile/admin-view-as`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.reload();
    } catch (err) {
      setViewAsRole(prev);
      localStorage.setItem('vissory_admin_view_as', prev);
      addToast({
        type: 'error',
        title: 'Error al cambiar la vista',
        description: err.response?.data?.detail || 'No se pudo guardar. Inténtalo de nuevo.'
      });
    }
  };

  // Sincronizar viewAsRole con el valor guardado en BD cuando el usuario (admin) se carga desde el API
  useEffect(() => {
    if (user?.role === 'admin' && user.admin_view_as_role && ['creative', 'creator', 'brand'].includes(user.admin_view_as_role)) {
      setViewAsRole(user.admin_view_as_role);
      try {
        localStorage.setItem('vissory_admin_view_as', user.admin_view_as_role);
      } catch (_) {}
    }
  }, [user?.id, user?.role, user?.admin_view_as_role]);

  // Calcular el rol efectivo: para admins es el viewAsRole; brand se trata como creator (misma vista)
  const isAdmin = user?.role === 'admin';
  const effectiveRole = isAdmin ? viewAsRole : (user?.role === 'brand' ? 'creator' : user?.role);
  
  // Crear un usuario "efectivo" con el rol modificado para pasar a los componentes
  const effectiveUser = useMemo(() => {
    if (!user) return null;
    if (isAdmin) return { ...user, role: effectiveRole };
    if (user.role === 'brand') return { ...user, role: 'creator' }; // marca/agencia = misma vista que creator
    return user;
  }, [user, isAdmin, effectiveRole]);

  // Cierre de sesión automático si no ha entrado en la plataforma en X días
  const INACTIVITY_LOGOUT_DAYS = 30;
  useEffect(() => {
    if (!token) return;
    const key = 'vissory_last_entered_at';
    const lastStr = localStorage.getItem(key);
    const now = Date.now();
    const maxAgeMs = INACTIVITY_LOGOUT_DAYS * 24 * 60 * 60 * 1000;
    if (lastStr) {
      const last = parseInt(lastStr, 10);
      if (!Number.isNaN(last) && now - last > maxAgeMs) {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem(key);
        setDismissedDockProjects([]);
        return;
      }
    }
    localStorage.setItem(key, String(now));
  }, [token]);

  // Check for existing user data on app load and refresh from server (profile_image, etc.)
  useEffect(() => {
    if (token) {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
      // Refrescar usuario desde el servidor para tener profile_image y datos actualizados
      const api = API;
      axios.get(`${api}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (res.data && res.data.id) {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
          }
        })
        .catch(() => {});
    }
  }, [token]);

  // Show onboarding tour for users with incomplete profile (also on page reload)
  useEffect(() => {
    if (!user?.id) return;
    const role = user.role;
    if (!['creative', 'creator', 'brand'].includes(role)) return;
    const onboardingDone = localStorage.getItem(`${ONBOARDING_STORAGE_KEY}_${user.id}`);
    const hasBio = Boolean(user.bio && String(user.bio).trim());

    let profileIncomplete = false;
    if (role === 'creative') {
      const hasSpecialty = Array.isArray(user.specialties) && user.specialties.length > 0;
      profileIncomplete = !hasBio || !hasSpecialty;
    } else if (role === 'creator') {
      profileIncomplete = !hasBio || !user.content_focus;
    } else if (role === 'brand') {
      profileIncomplete = !hasBio || !user.company_type || !user.industry;
    }

    if (!onboardingDone) {
      setShowOnboardingTour(true);
    }
  }, [user?.id, user?.role]);

  // Show portfolio bubble to creatives and brands until they dismiss it
  useEffect(() => {
    if (!user?.id) return;
    if (user.role !== 'creative' && user.role !== 'brand') return;
    const dismissed = localStorage.getItem(`${PORTFOLIO_BUBBLE_KEY}_${user.id}`);
    if (!dismissed) setShowPortfolioBubble(true);
  }, [user?.id, user?.role]);

  // Agencia destacada: cargar y mostrar toast si hay una nueva
  const apiBase = API;
  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${apiBase}/featured-agency`)
      .then((res) => {
        const uid = res.data?.user_id || null;
        setFeaturedAgencyUserId(uid);
      })
      .catch(() => setFeaturedAgencyUserId(null));
  }, [user?.id]);

  useEffect(() => {
    if (!featuredAgencyUserId || featuredAgencyUserId === featuredAgencySeenUserId) return;
    if (featuredAgencyToastShownRef.current === featuredAgencyUserId) return;
    featuredAgencyToastShownRef.current = featuredAgencyUserId;
    addToast({
      type: 'info',
      title: 'Hay una nueva agencia destacada',
      description: 'Descubre la empresa que hemos destacado esta vez.',
      action: {
        label: 'Ir a ver',
        onClick: () => {
          setFeaturedAgencySeenUserId(featuredAgencyUserId);
          try { localStorage.setItem('vissory_featured_agency_seen', featuredAgencyUserId); } catch (_) {}
          navigate('/agencia-destacada');
        }
      }
    });
  }, [featuredAgencyUserId, featuredAgencySeenUserId, addToast, navigate]);

  // Changelog: toast "Hay nuevas actualizaciones" cuando hay una entrada más reciente que la última vista
  const changelogToastShownForRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;
    const latest = CHANGELOG_ENTRIES?.[0]?.date;
    if (!latest) return;
    if (changelogToastShownForRef.current === latest) return;
    const seen = localStorage.getItem('vissory_changelog_seen') || '';
    if (seen >= latest) return;
    changelogToastShownForRef.current = latest;
    addToast({
      type: 'info',
      title: 'Hay nuevas actualizaciones',
      description: 'Échale un vistazo.',
      action: {
        label: 'Ver actualizaciones',
        onClick: () => {
          try { localStorage.setItem('vissory_changelog_seen', latest); } catch (_) {}
          navigate('/changelog');
        }
      }
    });
  }, [user?.id, addToast, navigate]);

  // Handle Stripe payment return (3DS redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get('payment_intent');
    const proposalId = sessionStorage.getItem('pending_accept_proposal_id');
    const completeProjectId = sessionStorage.getItem('pending_complete_project_id');
    if (paymentIntentId && proposalId && token) {
      sessionStorage.removeItem('pending_accept_proposal_id');
      const api = API;
      axios.post(`${api}/proposals/${proposalId}/accept-with-payment`, { payment_intent_id: paymentIntentId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then((res) => {
        setProjectNavigation({ projectId: res.data.project_id, view: 'detail' });
        navigate('/proyectos', { replace: true });
      }).catch(() => {
        navigate('/', { replace: true });
      });
    } else if (paymentIntentId && completeProjectId && token) {
      sessionStorage.removeItem('pending_complete_project_id');
      const api = API;
      axios.post(`${api}/projects/${completeProjectId}/complete-with-payment`, { payment_intent_id: paymentIntentId }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        setProjectsRefreshTrigger(t => t + 1);
        setProjectNavigation({ projectId: completeProjectId, view: 'detail' });
        navigate('/proyectos', { replace: true });
      }).catch((err) => {
        const detail = err?.response?.data?.detail;
        console.error('Error completing project after payment redirect:', detail || err);
        // Navigate to project so user can see status and retry if needed
        setProjectsRefreshTrigger(t => t + 1);
        setProjectNavigation({ projectId: completeProjectId, view: 'detail' });
        navigate('/proyectos', { replace: true });
      });
    } else {
      const pendingMilestone = sessionStorage.getItem('pending_milestone_payment');
      if (paymentIntentId && pendingMilestone && token) {
        try {
          const { projectId, milestoneId } = JSON.parse(pendingMilestone);
          sessionStorage.removeItem('pending_milestone_payment');
          const api = API;
          axios.post(`${api}/projects/${projectId}/milestones/${milestoneId}/pay-with-stripe`, { payment_intent_id: paymentIntentId }, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(() => {
            setProjectsRefreshTrigger(t => t + 1);
            setProjectNavigation({ projectId, view: 'detail' });
            navigate('/proyectos', { replace: true });
          }).catch((err) => {
            const detail = err?.response?.data?.detail;
            console.error('Error recording milestone payment after redirect:', detail || err);
            setProjectsRefreshTrigger(t => t + 1);
            setProjectNavigation({ projectId, view: 'detail' });
            navigate('/proyectos', { replace: true });
          });
        } catch (_) {
          sessionStorage.removeItem('pending_milestone_payment');
          navigate('/', { replace: true });
        }
      }
    }
  }, [token, navigate]);

  // Mostrar modal de rol solo a usuarios que acaban de entrar con Google
  useEffect(() => {
    if (user && sessionStorage.getItem('showGoogleRoleModal') === '1') {
      setShowGoogleRoleModal(true);
    }
  }, [user]);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token || !user) return;
      
      try {
        const response = await axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const unreadNotifications = response.data.filter(n => !n.read);
        setUnreadCount(unreadNotifications.length);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
  }, [token, user]);

  // Reset project navigation when changing tabs (except when explicitly navigating to projects)
  useEffect(() => {
    if (activeTab !== 'projects') {
      setProjectNavigation({
        projectId: null,
        view: 'list',
        initialDetailTab: null
      });
    }
    // Reset chat navigation only when leaving both chats and projects (mantener chat del proyecto al ver detalle)
    if (activeTab !== 'chats' && activeTab !== 'projects') {
      setChatNavigation({ projectId: null, conversationId: null, milestoneId: null });
    }
  }, [activeTab]);

  const refreshUnreadCount = useCallback(async () => {
    if (!token || !user) return;
    
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const unreadNotifications = response.data.filter(n => !n.read);
      setUnreadCount(unreadNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token, user]);

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleAuthSuccess = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('vissory_last_entered_at', String(Date.now()));
    // Show onboarding tour for users who haven't completed their profile
    const r = userData?.role;
    if (['creative', 'creator', 'brand'].includes(r)) {
      const onboardingDone = localStorage.getItem(`${ONBOARDING_STORAGE_KEY}_${userData.id}`);
      const hasBio = Boolean(userData.bio && String(userData.bio).trim());
      const incomplete =
        r === 'creative' ? (!hasBio || !(Array.isArray(userData.specialties) && userData.specialties.length > 0)) :
        r === 'creator'  ? (!hasBio || !userData.content_focus) :
        /* brand */        (!hasBio || !userData.company_type || !userData.industry);
      if (!onboardingDone) setShowOnboardingTour(true);
    }
  };

  const handleGoogleRoleSubmit = async () => {
    if (!googleRoleModalRole || !token) return;
    setGoogleRoleModalError('');
    try {
      const response = await axios.patch(
        `${API}/auth/role`,
        { role: googleRoleModalRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      sessionStorage.removeItem('showGoogleRoleModal');
      setShowGoogleRoleModal(false);
    } catch (err) {
      setGoogleRoleModalError(err.response?.data?.detail || 'Error al guardar el rol');
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('vissory_last_entered_at');
    setDismissedDockProjects([]);
  };

  // Notificar desde cualquier parte de la app que se ha enviado un mensaje en un proyecto
  const notifyProjectMessaged = useCallback((projectId) => {
    // Forzar recarga de proyectos en el dock
    setChatDockRefreshKey((key) => key + 1);
    // Si estaba oculto manualmente, volver a mostrarlo
    setDismissedDockProjects((current) =>
      current.includes(projectId) ? current.filter((id) => id !== projectId) : current
    );
  }, []);

  const navigateToProject = (projectId, view = 'list', initialDetailTab = null) => {
    setProjectNavigation({
      projectId: projectId,
      view: view,
      initialDetailTab: initialDetailTab || null
    });
    if (projectId) {
      setChatNavigation({ projectId, conversationId: null, milestoneId: null });
    }
    setActiveTab('projects');
  };

  const navigateToChat = (projectId, milestoneId = null) => {
    setChatNavigation({
      projectId: projectId,
      conversationId: null,
      milestoneId: milestoneId
    });
    setActiveTab('chats');
  };

  const navigateToDirectChat = (conversationId) => {
    setChatNavigation({
      projectId: null,
      conversationId: conversationId
    });
    setActiveTab('chats');
  };

  const onNotificationClick = useCallback((notification) => {
    const meta = notification.meta || {};
    const relatedId = notification.related_id;
    const type = notification.type;

    switch (type) {
      case 'proposal_view_request':
        setProposalsInitialSubView('solicitudes');
        setActiveTab('proposals');
        break;
      case 'proposal_view_accepted':
      case 'proposal_rejected':
        setActiveTab('proposals');
        break;
      case 'proposal_accepted': {
        const projectId = meta.project_id || relatedId;
        if (projectId) {
          setProjectNavigation({ projectId, view: 'detail', initialDetailTab: null });
          setActiveTab('projects');
        }
        break;
      }
      case 'creative_selected_project_opened': {
        const projectId = meta.project_id || relatedId;
        if (projectId) {
          setProjectNavigation({ projectId, view: 'detail', initialDetailTab: null });
          setActiveTab('projects');
        }
        break;
      }
      case 'message':
        if (meta.link === 'conversation' && meta.conversation_id) {
          setChatNavigation({ projectId: null, conversationId: meta.conversation_id });
          setActiveTab('chats');
        } else {
          const projectId = meta.project_id || relatedId;
          if (projectId) {
            setChatNavigation({ projectId, conversationId: null, milestoneId: null });
            setActiveTab('chats');
          }
        }
        break;
      case 'deliverable_uploaded': {
        const projectId = meta.project_id || relatedId;
        if (projectId) {
          setProjectNavigation({ projectId, view: 'detail', initialDetailTab: 'deliverables' });
          setActiveTab('projects');
        }
        break;
      }
      case 'milestone_creation_approval':
      case 'milestone_creation_approved':
      case 'milestone_creation_rejected':
      case 'milestone_deletion_approval':
      case 'milestone_deletion_approved':
      case 'milestone_deletion_rejected':
      case 'milestone_completion_approval':
      case 'milestone_completion_approved':
      case 'milestone_completion_rejected':
      case 'task_creation_approval':
      case 'task_creation_approved':
      case 'task_creation_rejected':
      case 'task_edit_approval':
      case 'task_edit_approved':
      case 'task_edit_rejected':
      case 'task_deletion_approval':
      case 'task_deletion_approved':
      case 'task_deletion_rejected':
      case 'task_completion_approval':
      case 'task_completion_approved':
      case 'task_completion_rejected': {
        const projectId = meta.project_id || relatedId;
        if (projectId) {
          setProjectNavigation({ projectId, view: 'detail', initialDetailTab: 'milestones' });
          setActiveTab('projects');
        }
        break;
      }
      case 'project_completed':
      case 'project_cancelled':
      case 'rating_received': {
        const projectId = meta.project_id || relatedId;
        if (projectId) {
          setProjectNavigation({ projectId, view: 'detail', initialDetailTab: null });
          setActiveTab('projects');
        }
        break;
      }
      case 'brief_application': {
        const briefId = meta.brief_id || relatedId;
        if (briefId) {
          navigate(`/briefs/${briefId}`);
        } else {
          setActiveTab('briefs');
        }
        break;
      }
      case 'proposal_received':
        setActiveTab('briefs');
        break;
      case 'invoice_requested':
        setActiveTab('monetization');
        break;
      default:
        setActiveTab('notifications');
        break;
    }
  }, [navigate]);

  const openCreateBriefForCreative = (creativeId) => {
    setCreateBriefTargetCreativeId(creativeId);
    setActiveTab('create-brief');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Dashboard 
              navigateToProject={navigateToProject} 
              onStatsLoaded={setDashboardStats}
              onActiveProjectsLoaded={setRecentProjects}
              onNavigateToRewards={() => setActiveTab('rewards')}
              onNavigateToCreateBrief={() => setActiveTab('create-brief')}
              onNavigateToSuggestions={() => setActiveTab('suggestions')}
            />
          </>
        );
      case 'create-brief':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <CreateBrief
          targetCreativeId={createBriefTargetCreativeId}
          onClearTargetCreativeId={() => setCreateBriefTargetCreativeId(null)}
          onSuccess={() => {
            setBriefsRefreshTrigger(prev => prev + 1);
            setCreateBriefTargetCreativeId(null);
            setActiveTab('briefs');
          }}
            />
          </>
        );
      case 'briefs':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <BriefsList
          activeTab={activeTab}
          refreshTrigger={briefsRefreshTrigger}
          setActiveTab={setActiveTab}
          navigateToProject={navigateToProject}
          settings={settings}
          onNavigateToRewards={() => setActiveTab('rewards')}
          initialBriefId={briefIdFromUrl}
          initialApplyBriefId={applyBriefIdFromQuery}
          onOpenBrief={(id) => navigate(`/briefs/${id}`)}
          onCloseBrief={() => navigate('/briefs')}
          onApplyModalOpened={() => {
            if (applyBriefIdFromQuery) navigate('/briefs', { replace: true });
          }}
            />
          </>
        );
      case 'brief-detail':
        return (
          <BriefDetailPage
            briefId={briefDetailIdFromUrl}
            user={effectiveUser}
            token={token}
          />
        );
      case 'proposals':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <MyProposals initialSubView={proposalsInitialSubView} onNavigateToRewards={() => setActiveTab('rewards')} />
          </>
        );
      case 'featured-creatives':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <FeaturedCreatives
              onOpenDirectChat={navigateToDirectChat}
              onNavigateToRewards={() => setActiveTab('rewards')}
              onViewProfile={(id) => navigate(`/p/${id}`)}
              onNavigateToProfile={() => setActiveTab('profile')}
            />
          </>
        );
      case 'featured-agency':
        return (
          <FeaturedAgencyPage
            onOpenDirectChat={navigateToDirectChat}
            onViewProfile={(id) => navigate(`/p/${id}`)}
            onCreateBrief={() => setActiveTab('create-brief')}
            onMarkFeaturedAgencySeen={(id) => {
              if (id) {
                setFeaturedAgencySeenUserId(id);
                try { localStorage.setItem('vissory_featured_agency_seen', id); } catch (_) {}
              }
            }}
          />
        );
      case 'rewards':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <RewardsView />
          </>
        );
      case 'projects':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Projects 
          initialProjectId={projectNavigation.projectId} 
          initialView={projectNavigation.view}
          initialDetailTab={projectNavigation.initialDetailTab}
          navigateToChat={navigateToChat}
          onNavigateToRewards={() => setActiveTab('rewards')}
          refreshTrigger={projectsRefreshTrigger}
            />
          </>
        );
      case 'contacts':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <ContactsView
              user={user}
              token={token}
              onOpenDirectChat={navigateToDirectChat}
              onCreateBrief={() => setActiveTab('create-brief')}
              onOpenProfile={(userId) => navigate(`/p/${userId}`)}
            />
          </>
        );
      case 'chats':
        return (
          <>
            <ChatsView
          initialProjectId={chatNavigation.projectId}
          initialConversationId={chatNavigation.conversationId}
          initialMilestoneId={chatNavigation.milestoneId}
          onOpenCreateBriefForCreative={openCreateBriefForCreative}
          onNavigateToRewards={() => setActiveTab('rewards')}
          onNavigateToProject={navigateToProject}
            />
          </>
        );
      case 'suggestions':
        return <SugerenciasPage token={token} isAdmin={isAdmin} />;
      case 'confian':
        return <ConfianPage />;
      case 'changelog':
        return <ChangelogPage />;
      case 'profile':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Profile
              onNavigateToRewards={() => setActiveTab('rewards')}
              onNavigateToSuggestions={() => setActiveTab('suggestions')}
              highlightPortfolio={portfolioHighlight}
              onPortfolioHighlightDismiss={() => setPortfolioHighlight(false)}
            />
          </>
        );
      case 'my-projects':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <MyProjectsView onNavigateToRewards={() => setActiveTab('rewards')} />
          </>
        );
      case 'monetization':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <MonetizationView />
          </>
        );
      case 'settings':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Settings settings={settings} onUpdate={updateSettings} />
          </>
        );
      case 'campanas':
        return <CampanasView />;
      case 'notifications':
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Notifications />
          </>
        );
      case 'public-profile':
        return (
          <PublicProfilePage
            userId={publicProfileId}
            onBack={() => navigate('/creativos')}
            onOpenDirectChat={navigateToDirectChat}
            onProfileLoaded={(profile) => setPublicProfileIsFounder(!!profile?.founder)}
          />
        );
      default:
        return (
          <>
            <SuggestionsBanner onGoToSuggestions={() => setActiveTab('suggestions')} />
            <Dashboard
            navigateToProject={navigateToProject} 
            onStatsLoaded={setDashboardStats}
            onActiveProjectsLoaded={setRecentProjects}
            onNavigateToRewards={() => setActiveTab('rewards')}
            onNavigateToCreateBrief={() => setActiveTab('create-brief')}
            onNavigateToSuggestions={() => setActiveTab('suggestions')}
            />
          </>
        );
    }
  };

  const showFeaturedAgencyNewBadge = Boolean(featuredAgencyUserId && featuredAgencyUserId !== featuredAgencySeenUserId);
  // Sidebar: principales + bloque "Próximamente" debajo de Chats
  const mainSidebarTabs = (effectiveRole === 'creator' || effectiveRole === 'brand') ? [
    { id: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
    { id: 'create-brief', label: 'Crear Proyecto', icon: FiFileText },
    { id: 'briefs', label: 'Mis Proyectos', icon: FiClipboard },
    { id: 'featured-agency', label: 'Agencia Destacada', icon: FiAward, badge: showFeaturedAgencyNewBadge ? 'Nuevo' : undefined },
    { id: 'featured-creatives', label: 'Creativos', icon: FiStar },
    { id: 'projects', label: 'Proyectos', icon: FiActivity },
    { id: 'chats', label: 'Chats', icon: FiMessageCircle }
  ] : [
    { id: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
    { id: 'rewards', label: 'Recompensas', icon: FiZap, highlighted: true, badge: 'Nuevo' },
    { id: 'briefs', label: 'Buscar Proyectos', icon: FiSearch },
    { id: 'proposals', label: 'Mis Aplicaciones', icon: FiMail },
    { id: 'featured-agency', label: 'Agencia Destacada', icon: FiAward, badge: showFeaturedAgencyNewBadge ? 'Nuevo' : undefined },
    { id: 'featured-creatives', label: 'Creativos', icon: FiStar },
    { id: 'projects', label: 'Proyectos', icon: FiActivity },
    { id: 'chats', label: 'Chats', icon: FiMessageCircle }
  ];
  const comingSoonSidebarTabs = [
    { id: 'following', label: 'Siguiendo', icon: FiUsers, disabled: true },
    { id: 'calendar', label: 'Calendario', icon: FiCalendar, disabled: true }
  ];

  if (!user) {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const verifyToken = searchParams?.get('token');
    if (pathname === '/verify-email' && verifyToken) {
      return <VerifyEmailPage token={verifyToken} />;
    }
    if (pathname === '/reset-password') {
      const resetToken = searchParams?.get('token');
      return <ResetPasswordPage token={resetToken} />;
    }
    if (pathname.startsWith('/p/')) {
      const profileId = pathname.replace('/p/', '').split('/')[0];
      return (
        <AuthContext.Provider value={{ user: null, realUser: null, token: null, updateUser: () => {}, notifyProjectMessaged: () => {}, isAdmin: false, viewAsRole: 'creative' }}>
          <div className="public-profile-standalone">
            <header className="public-profile-standalone-header">
              <a href="/" className="public-profile-standalone-logo" aria-label="Ir al inicio">
                <img src={logoHorizontalLight} alt="Vissory" className="public-profile-standalone-logo-img" />
              </a>
              <nav className="public-profile-standalone-nav">
                <a href="/" className="public-profile-standalone-login">Iniciar sesión</a>
                <a href="/" className="public-profile-standalone-register">Crear cuenta gratis</a>
              </nav>
            </header>
            <div className="public-profile-standalone-body">
              <PublicProfilePage userId={profileId} />
            </div>
            <div className="public-profile-standalone-cta-bar">
              <div className="public-profile-standalone-cta-inner">
                <div className="public-profile-standalone-cta-text">
                  <img src={isotipoWhite} alt="" className="public-profile-standalone-cta-iso" />
                  <div>
                    <strong>Únete a Vissory</strong>
                    <span>Conecta con los mejores creativos, gestiona proyectos y haz crecer tu marca.</span>
                  </div>
                </div>
                <a href="/" className="public-profile-standalone-cta-btn">Crear cuenta gratis</a>
              </div>
            </div>
          </div>
        </AuthContext.Provider>
      );
    }
    if (pathname === '/agencia-destacada') {
      return (
        <PublicFeaturedAgencyStandalone
          PublicProfilePage={PublicProfilePage}
          AuthContext={AuthContext}
        />
      );
    }
    if (pathname.startsWith('/brief/')) {
      const briefIdPublic = pathname.replace('/brief/', '').split('/')[0];
      return (
        <AuthContext.Provider value={{ user: null, realUser: null, token: null, updateUser: () => {}, notifyProjectMessaged: () => {}, isAdmin: false, viewAsRole: 'creative' }}>
          <div className="public-profile-standalone">
            <header className="public-profile-standalone-header">
              <a href="/" className="public-profile-standalone-logo" aria-label="Ir al inicio">
                <img src={logoHorizontalLight} alt="Vissory" className="public-profile-standalone-logo-img" />
              </a>
              <nav className="public-profile-standalone-nav">
                <a href="/" className="public-profile-standalone-login">Iniciar sesión</a>
                <a href="/" className="public-profile-standalone-register">Crear cuenta gratis</a>
              </nav>
            </header>
            <div className="public-profile-standalone-body">
              <BriefDetailPage
                briefId={briefIdPublic}
                user={null}
                token={null}
                onNavigateAuth={() => navigate('/')}
              />
            </div>
          </div>
        </AuthContext.Provider>
      );
    }
    return <AuthPage onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <CacheProvider>
    <AuthContext.Provider value={{ user: effectiveUser, realUser: user, token, updateUser, notifyProjectMessaged, isAdmin, viewAsRole, logout: handleLogout }}>
      <ClearCacheOnAuthChange />
      <Axios401Interceptor />
      {showOnboardingTour && !showGoogleRoleModal && (
        <VixOnboardingTour
          user={user}
          token={token}
          updateUser={updateUser}
          onComplete={() => setShowOnboardingTour(false)}
          onSkip={() => {
            if (user?.id) {
              try { localStorage.setItem(`${ONBOARDING_STORAGE_KEY}_${user.id}`, '1'); } catch (_) {}
            }
            setShowOnboardingTour(false);
          }}
        />
      )}
      {showPortfolioBubble && !showOnboardingTour && (
        <VixFloatingBubble
          userId={user?.id}
          role={user?.role}
          onGoToProfile={() => {
            setShowPortfolioBubble(false);
            setPortfolioHighlight(true);
            setActiveTab('profile');
          }}
        />
      )}
      {showGoogleRoleModal && (
        <div className="modal-overlay google-role-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="google-role-modal-title">
          <div className="modal-content google-role-modal-content">
            <h2 id="google-role-modal-title" className="auth-login-title" style={{ marginTop: 0 }}>¿Cuál es tu objetivo?</h2>
            <p className="auth-login-subtitle" style={{ marginBottom: '1.5rem' }}>
              Elige cómo planeas usar Vissory para personalizar tu experiencia.
            </p>
            {googleRoleModalError && <div className="error-message" style={{ marginBottom: '1rem' }}>{googleRoleModalError}</div>}
            <div className="auth-role-list auth-role-list-inline">
              <button
                type="button"
                className={`auth-role-card auth-role-card-inline ${googleRoleModalRole === 'creative' ? 'is-selected' : ''}`}
                onClick={() => setGoogleRoleModalRole('creative')}
              >
                <div className="auth-role-icon" aria-hidden="true">
                  <FiPenTool />
                </div>
                <div className="auth-role-body">
                  <h3 className="auth-role-title">Soy Creativo</h3>
                  <p className="auth-role-description">
                    Quiero mostrar mi trabajo, conseguir clientes y gestionar mis proyectos.
                  </p>
                </div>
              </button>
              <button
                type="button"
                className={`auth-role-card auth-role-card-inline ${googleRoleModalRole === 'creator' ? 'is-selected' : ''}`}
                onClick={() => setGoogleRoleModalRole('creator')}
              >
                <div className="auth-role-icon" aria-hidden="true">
                  <FiBriefcase />
                </div>
                <div className="auth-role-body">
                  <h3 className="auth-role-title">Soy Creador de contenido</h3>
                  <p className="auth-role-description">
                    Publico briefs y colaboro con talento creativo en mis proyectos.
                  </p>
                </div>
              </button>
              <button
                type="button"
                className={`auth-role-card auth-role-card-inline ${googleRoleModalRole === 'brand' ? 'is-selected' : ''}`}
                onClick={() => setGoogleRoleModalRole('brand')}
              >
                <div className="auth-role-icon" aria-hidden="true">
                  <FiLayers />
                </div>
                <div className="auth-role-body">
                  <h3 className="auth-role-title">Marca o agencia</h3>
                  <p className="auth-role-description">
                    Somos una marca o agencia y queremos publicar proyectos y trabajar con creativos.
                  </p>
                </div>
              </button>
            </div>
            <div className="auth-step-cta-wrap">
              <button
                type="button"
                className="btn-primary auth-login-btn"
                onClick={handleGoogleRoleSubmit}
                disabled={!googleRoleModalRole}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
      {user && user.active === false ? (
        <ActivateAccountScreen token={token} updateUser={updateUser} onBackToLogin={handleLogout} />
      ) : (
      <div className="app-layout">
        {/* New Header with Notifications and Profile */}
        <Header
          user={effectiveUser}
          unreadCount={unreadCount}
          onLogout={handleLogout}
          onOpenProfile={() => setActiveTab('profile')}
          onOpenPublicProfile={user?.id ? () => navigate(`/p/${user.id}`) : undefined}
          onOpenSettings={() => setActiveTab('settings')}
          onNotificationRead={refreshUnreadCount}
          setActiveTab={setActiveTab}
          onNotificationClick={onNotificationClick}
          isAdmin={isAdmin}
          viewAsRole={viewAsRole}
          onViewAsRoleChange={handleViewAsRoleChange}
          onToggleSidebar={() => setSidebarOpen(prev => !prev)}
          sidebarOpen={sidebarOpen}
        />

        {/* Main Content */}
        <div className="main-content">
          {/* Sidebar overlay (mobile): tap to close */}
          {sidebarOpen && (
            <button
              type="button"
              className="sidebar-overlay"
              aria-label="Cerrar menú"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          {/* Simplified Sidebar */}
          <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsedDesktop ? 'sidebar--collapsed-desktop' : ''}`}>
            <div className="sidebar-desktop-toggle-wrap">
              <button
                type="button"
                className="sidebar-desktop-toggle"
                onClick={toggleSidebarCollapsedDesktop}
                aria-expanded={!sidebarCollapsedDesktop}
                aria-label={sidebarCollapsedDesktop ? 'Expandir menú lateral' : 'Contraer menú lateral'}
                title={sidebarCollapsedDesktop ? 'Expandir menú' : 'Contraer menú'}
              >
                {sidebarCollapsedDesktop ? (
                  <FiChevronsRight aria-hidden="true" />
                ) : (
                  <FiChevronsLeft aria-hidden="true" />
                )}
              </button>
            </div>
            <div className="sidebar-main">
              {mainSidebarTabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`sidebar-tab ${activeTab === tab.id && !tab.disabled ? 'active' : ''} ${tab.highlighted ? 'highlighted' : ''} ${tab.disabled ? 'sidebar-tab--disabled' : ''}`}
                    title={tab.label}
                    aria-label={sidebarCollapsedDesktop ? tab.label : undefined}
                    onClick={() => {
                      if (!tab.disabled) {
                        setActiveTab(tab.id);
                        setSidebarOpen(false);
                      }
                    }}
                    disabled={tab.disabled}
                    aria-disabled={tab.disabled}
                  >
                    <span className="tab-icon" aria-hidden="true">
                      <IconComponent />
                    </span>
                    <span className="tab-label">{tab.label}</span>
                    {tab.badge && <span className={`tab-badge ${tab.badge === 'Próximamente' ? 'tab-badge--coming-soon' : ''}`}>{tab.badge}</span>}
                  </button>
                );
              })}

              <div className="sidebar-separator" />
              <div className="sidebar-coming-soon">
                <p className="sidebar-coming-soon-label" aria-hidden="true">Próximamente</p>
                {comingSoonSidebarTabs.map(tab => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className={`sidebar-tab sidebar-tab--disabled`}
                      title={tab.label}
                      aria-label={sidebarCollapsedDesktop ? tab.label : undefined}
                      disabled
                      aria-disabled="true"
                    >
                      <span className="tab-icon" aria-hidden="true">
                        <IconComponent />
                      </span>
                      <span className="tab-label">{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {recentProjects.length > 0 && (
                <>
                  <div className="sidebar-separator" />
                  <div className="sidebar-recent-projects">
                    <p className="sidebar-recent-title">Proyectos recientes</p>
                    {recentProjects.map((project) => (
                      <button
                        key={project.id}
                        className="sidebar-recent-item"
                        onClick={() => {
                          navigateToProject(project.id, 'detail');
                          setSidebarOpen(false);
                        }}
                      >
                        <span className="sidebar-recent-dot" aria-hidden="true" />
                        <span className="sidebar-recent-text">
                          {project.title.length > 26
                            ? `${project.title.substring(0, 26)}…`
                            : project.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="sidebar-footer sidebar-footer--suggestions">
              <button
                type="button"
                className={`sidebar-tab sidebar-tab--footer ${activeTab === 'suggestions' ? 'active' : ''} highlighted`}
                title="Sugerencias"
                aria-label={sidebarCollapsedDesktop ? 'Sugerencias' : undefined}
                onClick={() => {
                  setActiveTab('suggestions');
                  setSidebarOpen(false);
                }}
              >
                <span className="tab-icon" aria-hidden="true">
                  <FiSend />
                </span>
                <span className="tab-label">Sugerencias</span>
                <span className="tab-badge">¡Opina!</span>
              </button>
              <button
                type="button"
                className={`sidebar-tab sidebar-tab--footer ${activeTab === 'confian' ? 'active' : ''}`}
                title="Confían en Vissory"
                aria-label={sidebarCollapsedDesktop ? 'Confían en Vissory' : undefined}
                onClick={() => {
                  setActiveTab('confian');
                  setSidebarOpen(false);
                }}
              >
                <span className="tab-icon" aria-hidden="true">
                  <FiUsers />
                </span>
                <span className="tab-label">Confían en Vissory</span>
              </button>
            </div>
            <div className="sidebar-footer sidebar-footer--social">
              <p className="sidebar-footer-label">Síguenos</p>
              <div className="sidebar-social-row">
                <div className="sidebar-social" role="list">
                  <a
                    href="https://x.com/_vissory"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sidebar-social-link"
                    aria-label="Vissory en X (Twitter)"
                    title="X (Twitter)"
                  >
                    <FaTwitter aria-hidden="true" />
                  </a>
                  <a
                    href="https://linkedin.com/company/vissory"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sidebar-social-link"
                    aria-label="Vissory en LinkedIn"
                    title="LinkedIn"
                  >
                    <FaLinkedin aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.instagram.com/_vissory/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sidebar-social-link"
                    aria-label="Vissory en Instagram"
                    title="Instagram"
                  >
                    <FaInstagram aria-hidden="true" />
                  </a>
                </div>
                <div className="sidebar-social sidebar-social--changelog-wrap">
                  <button
                    type="button"
                    className="sidebar-social-link sidebar-social-link--changelog"
                    onClick={() => navigate('/changelog')}
                    aria-label="Actualizaciones y cambios recientes"
                    title="Actualizaciones"
                  >
                    <FaBug aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Content Area */}
          <main id="content-area" className={`content-area ${activeTab === 'chats' ? 'content-area--chats' : ''} ${activeTab === 'suggestions' ? 'content-area--suggestions' : ''} ${activeTab === 'changelog' ? 'content-area--changelog' : ''} ${activeTab === 'confian' ? 'content-area--confian' : ''} ${activeTab === 'public-profile' ? 'content-area--public-profile' : ''} ${activeTab === 'public-profile' && publicProfileIsFounder ? 'content-area--public-profile--founder' : ''} ${activeTab === 'featured-agency' ? 'content-area--featured-agency' : ''} ${activeTab === 'campanas' ? 'content-area--campanas' : ''}`}>
            {activeTab === 'public-profile' && publicProfileIsFounder && (
              <div className="aurora-founder" aria-hidden="true" />
            )}
            <div className="content-area-inner">
              {renderContent()}
            </div>
          </main>
        </div>

        {/* Floating avatars for active project chats with unread messages */}
        <ChatNotificationDock
          unreadCount={unreadCount}
          refreshKey={chatDockRefreshKey}
          dismissList={dismissedDockProjects}
          onOpenInlineChat={(projectId) =>
            setDockChatProjectId((current) =>
              current === projectId ? null : projectId
            )
          }
        />

        {/* Inline chat widget like a small chatbot window */}
        {dockChatProjectId && (
          <InlineChatWidget
            projectId={dockChatProjectId}
            onClose={() => setDockChatProjectId(null)}
            onOpenFullChat={(projectId) => {
              setDockChatProjectId(null);
              navigateToProject(projectId, 'chat');
            }}
            onDismissChat={(projectId) => {
              // Cerrar el widget
              setDockChatProjectId(null);
              // Ocultar la burbuja correspondiente
              setDismissedDockProjects((current) =>
                current.includes(projectId) ? current : [...current, projectId]
              );
              // Forzar que el dock vuelva a cargar proyectos y aplique el filtro
              setChatDockRefreshKey((key) => key + 1);
            }}
          />
        )}

      </div>
      )}
    </AuthContext.Provider>
    </CacheProvider>
  );
};

export default App;

